<template>
  <div class="people-page">
    <div v-if="isInitialLoading" class="initial-loading-overlay">
      <div class="initial-loading-spinner">
        <i class="fas fa-spinner fa-spin"></i>
        <div class="loading-text">
          {{
            isDailyRefresh
              ? 'Daily refresh of all work orders. Please wait...'
              : 'Loading work orders...'
          }}
        </div>
      </div>
    </div>

    <!-- MOTD Modal Component -->
    <MotdModal :route="$route.path" />

    <div class="title-bar">
      <span @click="toggleDebugPanel"
        >Work Orders <small>({{ filteredWorkOrders.length }})</small></span
      >
      <div class="title-buttons">
        <button
          class="title-button"
          @click="toggleConfirmationsDialog"
          title="confirmations"
        >
          <i class="fas fa-exclamation"></i>
        </button>
        <button class="title-button" @click="goBack">
          <i class="fas fa-arrow-left"></i>
        </button>
        <button
          v-if="!isMobile"
          @click="sendClose"
          class="title-button"
          title="Close"
        >
          <i class="fas fa-close"></i>
        </button>
        <button
          class="title-button"
          title="Home"
          @click="$router.push('/mhome')"
        >
          <i class="fas fa-home"></i>
        </button>
      </div>
    </div>
    <div class="content-wrapper">
      <div v-if="showWorkOrder" class="work-orders subpanel-form">
        <!-- Search Bar for work orders -->
        <div class="email-search-bar">
          <input
            style="font-size: 16px"
            ref="workOrderFilter"
            v-model="workOrderFilter"
            :placeholder="filteredByAgeIds ? '(age filter)' : 'search'"
            @dblclick="this.workOrderFilter = ''"
          />
          <button @click="clearSearch" class="clear-search" title="Clear search">
            <i class="fas fa-backspace"></i>
          </button>
          <button
            @click="openReviewFromClassic"
            class="clear-search review-toolbar-btn"
            title="Review open work orders"
          >
            <i class="fas fa-inbox"></i>
          </button>
          <div
            v-if="showAgeFilterHint"
            class="age-filter-hint"
            :class="{ 'fade-out': !filteredByAgeIds }"
          >
            click to clear age filter <i class="fas fa-arrow-up"></i>
          </div>
        </div>
        <!-- Action buttons row -->
        <div class="search-action-buttons">
          <button
            @click="sortByPriority"
            :class="{
              'clear-search': true,
              'sort-active': prioritySortOrder !== null,
            }"
          >
            <i
              :class="
                prioritySortOrder === 'desc'
                  ? 'fas fa-angles-down'
                  : 'fas fa-angles-up'
              "
            ></i>
          </button>
          <button
            @click="this.hideDone = !this.hideDone"
            :class="{ 'clear-search': true, 'hide-done': hideDone }"
          >
            <i :class="hideDone ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
          </button>
          <button
            @click="showHardReloadConfirm = true"
            class="clear-search"
            title="Hard Reload"
          >
            <i class="fas fa-cloud-download-alt"></i>
          </button>
          <button @click="refreshAndShowSummary" class="clear-search">
            <i class="fas fa-refresh"></i>
          </button>
          <button @click="toggleNewWorkOrder" class="clear-search">
            <i class="fas fa-add"></i>
          </button>
          <button
            @click="openWorkOrderMerge"
            class="clear-search"
            title="Merge work orders at this property"
          >
            <i class="fas fa-code-merge"></i>
          </button>
          <button @click="voiceRecorder" class="clear-search" title="Record">
            <i class="fas fa-microphone"></i>
          </button>
          <button
            @click="toggleReceiptsPopup"
            class="clear-search"
            title="Receipts"
          >
            <i class="fas fa-receipt"></i>
          </button>
          <button
            @click="showListView = !showListView"
            :class="{
              'clear-search': true,
              'sort-active': showListView,
            }"
            title="List View"
          >
            <i class="fas fa-list"></i>
          </button>
        </div>

        <div v-if="showIconBar == 3" class="voice-recorder-widget">
          <div class="icon-item" :class="{ disabled: !audioData }">
            <i :class="iconClass" @click="toggleRecording"></i>
            <span class="icon-label">{{ recordingLabel }}</span>
          </div>
          <div class="icon-item" :class="{ disabled: !audioData }">
            <i
              class="fas fa-play"
              @click="playAudio"
              :class="{
                disabled: !audioData,
                'green-playing': isPlaying,
              }"
            ></i>

            <span class="icon-label">Play {{ formattedDuration }}</span>
          </div>
          <div class="icon-item" :class="{ disabled: !audioData }">
            <i
              class="fas fa-trash"
              @click="deleteRecording"
              :class="{ disabled: !audioData }"
            ></i>
            <span class="icon-label">Delete</span>
          </div>
          <div class="icon-item" :class="{ disabled: !audioData }">
            <i
              class="fas fa-paper-plane"
              @click="sendRecording"
              :class="{ disabled: !audioData }"
            ></i>
            <span class="icon-label">Send</span>
          </div>
        </div>

        <!-- Confirmations Dialog replaces inner-wrapper -->
        <div v-if="showConfirmationsDialog" class="confirmation-dialog">
          <div class="confirmation-dialog-content">
            <div class="confirmation-dialog-header">
              <h3 class="confirmation-dialog-title">Confirmations</h3>
              <button
                @click="showConfirmationsDialog = false"
                class="close-button"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
            <div class="confirmation-tabs">
              <button
                :class="[
                  'confirmation-tab',
                  { active: activeTab === 'needed' },
                ]"
                @click="activeTab = 'needed'"
              >
                Confirmations Needed
              </button>
              <button
                :class="[
                  'confirmation-tab',
                  { active: activeTab === 'manage' },
                ]"
                @click="activeTab = 'manage'"
              >
                Manage
              </button>
            </div>

            <div class="confirmation-dialog-body">
              <div class="tab-content">
                <!-- Confirmations Needed Tab -->
                <div v-if="activeTab === 'needed'">
                  <div v-if="unconfirmedItems.length === 0" class="empty-state">
                    No confirmations needed
                  </div>
                  <div
                    v-for="confirmation in unconfirmedItems"
                    :key="confirmation.id"
                    class="confirmation-card"
                  >
                    <div
                      class="confirmation-card-header"
                      style="display: flex; flex-direction: column"
                    >
                      <div
                        class="assigned-to-display"
                        style="
                          margin-bottom: 8px;
                          text-align: left !important;
                          display: block;
                          width: 100%;
                        "
                      >
                        <small
                          style="
                            color: #666;
                            text-align: left !important;
                            display: inline-block;
                            width: 100%;
                          "
                        >
                          To:
                          {{
                            confirmation.assignedToName ||
                            (confirmation.assignedTo
                              ? confirmation.assignedTo
                              : 'nobody')
                          }}
                        </small>
                      </div>
                      <div
                        style="
                          display: flex;
                          justify-content: space-between;
                          align-items: flex-start;
                        "
                      >
                        <p class="confirmation-text" style="flex: 1; margin: 0">
                          {{ confirmation.confirmationText }}
                        </p>
                        <span
                          class="confirmation-status"
                          :class="confirmation.confirmationState"
                          style="margin-left: 10px"
                        >
                          {{ confirmation.confirmationState }}
                        </span>
                      </div>
                    </div>
                    <div class="confirmation-card-body">
                      <div class="confirmation-info">
                        <strong>Requested by:</strong>
                        {{ confirmation.requestedByName }}
                      </div>
                      <div class="confirmation-info">
                        <strong>Requested on:</strong>
                        {{ formatDate(confirmation.requestedOn) }}
                      </div>
                      <div class="confirmation-actions">
                        <button
                          @click="confirmItem(confirmation, 'yes')"
                          class="btn btn-primary"
                        >
                          Yes
                        </button>
                        <button
                          @click="showResponseFor(confirmation.id)"
                          class="btn btn-secondary"
                        >
                          No
                        </button>
                      </div>
                      <div
                        v-if="showingResponseFor === confirmation.id"
                        class="response-section"
                      >
                        <textarea
                          v-model="confirmationResponse"
                          placeholder="(optional) Please explain why..."
                          class="response-textarea"
                        ></textarea>
                        <button
                          @click="submitResponse(confirmation, 'no')"
                          class="btn btn-primary"
                        >
                          Submit Response
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Manage Tab -->
                <div v-if="activeTab === 'manage'">
                  <div class="create-confirmation-section">
                    <h4>
                      {{
                        editingConfirmationId
                          ? 'Edit Confirmation'
                          : 'Create New Confirmation'
                      }}
                    </h4>

                    <div class="form-group">
                      <select
                        id="assignedTo"
                        v-model="selectedAssignedTo"
                        class="form-control"
                        style="
                          padding: 8px 12px;
                          border-radius: 6px;
                          border: 1px solid #ddd;
                          background-color: white;
                          min-height: 38px;
                        "
                      >
                        <option value="">Select a user...</option>
                        <option
                          v-for="user in users"
                          :key="user.id"
                          :value="user.id"
                        >
                          {{ user.displayName }}
                        </option>
                      </select>
                    </div>

                    <textarea
                      v-model="newConfirmationText"
                      placeholder="Enter confirmation text..."
                      class="create-confirmation-textarea"
                    ></textarea>
                    <div class="confirmation-form-actions">
                      <button
                        @click="
                          editingConfirmationId
                            ? updateConfirmation()
                            : createConfirmation()
                        "
                        class="btn btn-primary"
                      >
                        {{
                          editingConfirmationId
                            ? 'Update Confirmation'
                            : 'Create Confirmation'
                        }}
                      </button>
                      <button
                        v-if="editingConfirmationId"
                        @click="cancelEdit()"
                        class="btn btn-secondary"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>

                  <div
                    v-if="sortedConfirmationsList.length === 0"
                    class="empty-state"
                  >
                    No confirmations yet
                  </div>
                  <div
                    v-for="confirmation in sortedConfirmationsList"
                    :key="confirmation.id"
                    class="confirmation-card"
                    style="cursor: pointer"
                    @click="toggleConfirmationActions(confirmation.id)"
                  >
                    <div
                      class="confirmation-card-header"
                      style="display: flex; flex-direction: column"
                    >
                      <div
                        class="assigned-to-display"
                        style="
                          margin-bottom: 8px;
                          text-align: left !important;
                          display: block;
                          width: 100%;
                        "
                      >
                        <small
                          style="
                            color: #666;
                            text-align: left !important;
                            display: inline-block;
                            width: 100%;
                          "
                        >
                          To:
                          {{
                            confirmation.assignedToName ||
                            (confirmation.assignedTo
                              ? confirmation.assignedTo
                              : 'nobody')
                          }}
                        </small>
                      </div>
                      <div
                        style="
                          display: flex;
                          justify-content: space-between;
                          align-items: flex-start;
                        "
                      >
                        <p class="confirmation-text" style="flex: 1; margin: 0">
                          {{ confirmation.confirmationText }}
                        </p>
                        <span
                          class="confirmation-status"
                          :class="confirmation.confirmationState"
                          style="margin-left: 10px"
                        >
                          {{ confirmation.confirmationState }}
                        </span>
                      </div>
                    </div>
                    <div class="confirmation-card-body">
                      <div class="confirmation-info">
                        <strong>Requested by:</strong>
                        {{ confirmation.requestedByName }}
                      </div>
                      <div class="confirmation-info">
                        <strong>Requested on:</strong>
                        {{ formatDate(confirmation.requestedOn) }}
                      </div>
                      <div
                        v-if="confirmation.confirmedBy"
                        class="confirmation-info"
                      >
                        <strong>Confirmed by:</strong>
                        {{ confirmation.confirmedByName }} on
                        {{ formatDate(confirmation.confirmedOn) }}
                      </div>
                      <div
                        v-if="confirmation.response"
                        class="confirmation-info"
                      >
                        <strong>Response:</strong> {{ confirmation.response }}
                      </div>
                      <div
                        v-if="selectedConfirmationId === confirmation.id"
                        class="confirmation-actions"
                      >
                        <button
                          class="btn btn-secondary"
                          @click="
                            editConfirmation(confirmation);
                            $event.stopPropagation();
                          "
                        >
                          Edit
                        </button>
                        <button
                          @click="
                            deleteConfirmation(confirmation.id);
                            $event.stopPropagation();
                          "
                          class="btn btn-secondary"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Age Filter Status Display -->
        <div
          v-if="showWorkOrder && !showConfirmationsDialog"
          class="age-filter-status"
        >
          <span v-if="ageFilterLabel">
            Filtering for: <strong>{{ ageFilterLabel }}</strong>
          </span>
          <span v-else-if="!hideDone">
            Showing: <strong>All work orders</strong>
          </span>
          <span v-else> Showing: <strong>Open work orders only</strong> </span>
        </div>

        <div
          v-if="showWorkOrder && !showConfirmationsDialog"
          ref="innerWrapper"
          class="inner-wrapper"
          :class="{
            'no-scroll': selectedTileIndex !== null,
            'is-mobile': isMobile,
          }"
        >
          <!-- Icons 1 Bar -->
          <div
            v-if="selectedTileIndex !== null && showIconBar == 1"
            class="icons-bar"
          >
            <div
              class="icon-item"
              :class="{
                completed:
                  filteredWorkOrders[this.selectedTileIndex]?.completedOn !==
                  null,
              }"
            >
              <i
                :class="
                  filteredWorkOrders[this.selectedTileIndex]?.completedOn
                    ? 'fas fa-undo'
                    : 'fas fa-check'
                "
                @click="markDone"
              ></i>
              <span class="icon-label">
                {{
                  filteredWorkOrders[this.selectedTileIndex]?.completedOn
                    ? 'Re-open'
                    : 'Done'
                }}
              </span>
            </div>
            <div class="icon-item">
              <i class="fas fa-edit" @click="editWorkOrder"></i>
              <span class="icon-label">Edit</span>
            </div>

            <div class="icon-item">
              <i class="fas fa-calendar-alt" @click="openScheduleDialog"></i>
              <span class="icon-label">{{
                filteredWorkOrders[selectedTileIndex]?.calendarEventId
                  ? 'Resched'
                  : 'Schedule'
              }}</span>
            </div>
            <div class="icon-item">
              <i class="fas fa-share-nodes" @click="share"></i>
              <span class="icon-label">share</span>
            </div>
            <div class="icon-item">
              <i class="fas fa-angles-up" @click="incrementPriority"></i>
              <span class="icon-label">Pri up</span>
            </div>
            <div class="icon-item">
              <i class="fas fa-angles-down" @click="decrementPriority"></i>
              <span class="icon-label">Pri Dn</span>
            </div>
            <div class="icon-item">
              <i
                class="fas"
                :class="
                  speakingWorkOrderId ===
                  filteredWorkOrders[selectedTileIndex]?.id
                    ? 'fa-volume-up tts-active'
                    : 'fa-volume-off'
                "
                @click="
                  speakProblemDescription(filteredWorkOrders[selectedTileIndex])
                "
              ></i>
              <span class="icon-label">{{
                speakingWorkOrderId ===
                filteredWorkOrders[selectedTileIndex]?.id
                  ? 'Stop'
                  : 'Read'
              }}</span>
            </div>
            <div class="icon-item">
              <i class="fas fa-ellipsis" @click="switchIcons"></i>
              <span class="icon-label">More</span>
            </div>
          </div>
          <!-- Icons 2 Bar -->
          <div
            v-if="selectedTileIndex !== null && showIconBar == 2"
            class="icons-bar"
          >
            <div class="icon-item">
              <i class="fas fa-angles-right" @click="nothing"></i>
              <span class="icon-label">Assign</span>
            </div>
            <div class="icon-item">
              <i class="fas fa-paperclip" @click="attachDocument"></i>
              <span class="icon-label">Attach</span>
            </div>
            <div class="icon-item">
              <i class="fas fa-edit" @click="editWorkOrder"></i>
              <span class="icon-label">Edit</span>
            </div>
            <div class="icon-item" v-if="currentUserRole < 3">
              <i class="fas fa-trash" @click="hardDeleteWorkOrder"></i>
              <span class="icon-label">Delete</span>
            </div>
            <div class="icon-item" v-else>
              <i class="fas fa-cancel" @click="nothing"></i>
              <span class="icon-label">---</span>
            </div>
            <div
              class="icon-item"
              :class="{ disabled: singleWorkOrderPrintLoading }"
            >
              <i class="fas fa-print" @click="printSelectedWorkOrder"></i>
              <span class="icon-label">Print</span>
            </div>
            <div class="icon-item">
              <i class="fas fa-arrow-left" @click="switchIcons"></i>
              <span class="icon-label">Back</span>
            </div>
          </div>
          <!-- Work Orders List -->
          <div
            v-for="(workOrder, index) in filteredWorkOrders"
            @click="selectTile(index)"
            @dblclick="editWorkOrderFromTile(index)"
            :key="workOrder.id"
            class="maint-tile"
            :class="{ selected: selectedTileIndex === index }"
            v-show="selectedTileIndex === null || selectedTileIndex === index"
            :data-work-order-id="workOrder.id"
          >
            <!-- Scrollable content area -->
            <div class="tile-content">
              <span class="wo-id-label">#{{ workOrder.id }}</span>
              <!-- Status icons at top of tile -->
              <div class="maint-tile-icons">
                <div
                  v-if="!workOrder.calendarEventId"
                  class="schedule-status-group"
                >
                  <button
                    class="schedule-this-btn"
                    :class="{ 'awaiting-tenant-btn': workOrder.tentativeDate }"
                    @click.stop="openScheduleDialog(workOrder)"
                  >
                    <i
                      :class="
                        workOrder.tentativeDate
                          ? 'fas fa-clock'
                          : 'fas fa-calendar-plus'
                      "
                    ></i>
                    {{
                      workOrder.tentativeDate
                        ? 'Awaiting Tenant'
                        : 'Schedule'
                    }}
                  </button>
                  <div v-if="workOrder.tentativeDate" class="tentative-note">
                    tentative: {{ formatTentativeDateTime(workOrder) }}
                  </div>
                </div>
                <button
                  v-else-if="workOrder.scheduledDate"
                  class="schedule-this-btn scheduled-on-btn"
                  @click.stop="openScheduleDialog(workOrder)"
                >
                  <i class="fas fa-calendar-check"></i>
                  {{ formatScheduledDate(workOrder) }}
                </button>
                <div class="tile-icon-container">
                  <i
                    class="fas fa-envelope tile-icon tenant-email-icon"
                    :class="{
                      'tenant-email-icon-disabled':
                        !workOrder.peopleId && !workOrder.name,
                    }"
                    @click.stop="openTenantEmailDialog(workOrder)"
                    title="Email tenant"
                  ></i>
                  <i
                    class="fas fa-lock tile-icon lock-code-icon"
                    :class="{ 'lock-code-icon-disabled': !workOrder.propertyId }"
                    @click.stop="openMaintenanceLockCode(workOrder)"
                    title="Maintenance lock code"
                  ></i>
                  <i
                    class="fas fa-calendar-check tile-icon"
                    :class="
                      workOrder.calendarEventId
                        ? 'calendar-linked'
                        : 'calendar-unlinked'
                    "
                    @click.stop="openScheduleDialog(workOrder)"
                    :title="
                      workOrder.calendarEventId ? 'Scheduled' : 'Not scheduled'
                    "
                  ></i>
                  <i
                    class="fas tile-icon tts-icon"
                    :class="
                      speakingWorkOrderId === workOrder.id
                        ? 'fa-volume-up tts-active'
                        : 'fa-volume-off'
                    "
                    @click.stop="speakProblemDescription(workOrder)"
                    title="Read problem description aloud"
                  ></i>
                  <i
                    v-if="workOrder.note && workOrder.note.trim().length > 0"
                    class="fas fa-notes-medical tile-icon has-content"
                    @click.stop="showNotesForWorkOrder(workOrder.id)"
                  ></i>
                  <i
                    v-if="workOrder.photoCount && workOrder.photoCount > 0"
                    class="fas fa-images tile-icon has-content"
                    @click.stop="showNotesForWorkOrder(workOrder.id)"
                  ></i>
                  <i
                    v-if="!workOrder.note && !workOrder.photoCount"
                    class="fas fa-plus tile-icon add-icon"
                    @click.stop="showNotesForWorkOrder(workOrder.id)"
                  ></i>
                  <i
                    class="fas fa-sync-alt tile-icon refresh-icon"
                    @click.stop="refreshSingleWorkOrder(workOrder.id)"
                  ></i>
                </div>
              </div>
              <!-- Formatted content -->
              <div
                v-html="formattedRequestContent(workOrder)"
                @click="onTileContentClick($event, workOrder)"
                @dblclick="onTileContentDblClick"
              ></div>

              <div class="tile-comment-block">
              <div
                v-if="latestCommentForWorkOrder(workOrder)"
                class="review-tenant-reply"
                @click.stop="openWorkOrderComments(workOrder.id)"
                @dblclick.stop
              >
                <div class="review-tenant-reply-main">
                  <div class="review-tenant-reply-label">
                    {{ latestCommentLabelForWorkOrder(workOrder) }}
                  </div>
                  <div
                    class="review-tenant-reply-text"
                    v-html="
                      reviewCommentPhoneHtml(
                        latestCommentForWorkOrder(workOrder).message,
                      )
                    "
                    @click="onReviewCommentPhoneClick"
                  ></div>
                </div>
                <div class="review-tenant-reply-icon-wrap">
                  <span class="review-comment-count"
                    >({{ commentCountForWorkOrder(workOrder) }})</span
                  >
                  <i
                    class="fas fa-comment-dots review-tenant-reply-icon"
                    title="Open comments"
                  ></i>
                </div>
              </div>
              <div v-else class="review-comment-start-row">
                <span class="review-comment-count"
                  >({{ commentCountForWorkOrder(workOrder) }})</span
                >
                <button
                  type="button"
                  class="review-comment-start"
                  title="Add a comment"
                  @click.stop="openWorkOrderComments(workOrder.id)"
                  @dblclick.stop
                >
                  <i class="fas fa-comment-dots"></i>
                </button>
              </div>
              </div>

              <!-- Priority badge - always visible at bottom right -->
              <div class="priority-container">
                <div class="priority-label">Pri</div>
                <div class="priority-display">
                  {{ workOrder.priority }}
                </div>
              </div>
            </div>

            <!-- Fixed footer with close button (only shown when selected) -->
            <div v-if="selectedTileIndex === index" class="tile-footer">
              <div
                class="close-tile-button"
                @click.stop="closeTileAndRestoreScroll"
              >
                close
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showDebug" class="log-message-container">
      <button
        class="log-message-refresh"
        @click="fetchRecentUsageData"
        title="Refresh usage data"
      >
        <i class="fas fa-sync-alt"></i>
      </button>
      <div class="log-message" v-html="formattedLogMessage"></div>
    </div>
  </div>
  <!-- Confirmation Dialog -->
  <div v-if="showConfirm" class="confirm-dialog">
    <div class="confirm-dialog-content">
      <p>{{ confirmMessage }}</p>
      <button @click="handleOk">OK</button>
      <button @click="handleCancel">Cancel</button>
    </div>
  </div>

  <div
    v-if="showNewWorkOrder"
    class="new-work-order-form"
    :class="{ 'new-work-order-form-over-review': showReviewPopup }"
  >
    <div class="new-work-order-title">New Work Order</div>
    <form @submit.prevent="addNewWorkOrder">
      <div class="form-group">
        <div class="label-with-me">
          <label for="name">Name</label>
          <button
            type="button"
            class="me-button"
            @click="fillMyInfo"
            title="Fill with my info"
          >
            Me
          </button>
        </div>
        <div class="autocomplete-wrapper">
          <input
            ref="workOrderName"
            id="name"
            v-model="newWorkOrder.name"
            type="text"
            :class="{ 'input-error': fieldErrors.name }"
            @input="handleNameInput"
            @focus="showNameSuggestions = true"
            @blur="handleNameBlur"
            @keydown="handleNameKeydown"
            autocomplete="off"
          />
          <div
            v-if="showNameSuggestions && nameSuggestions.length > 0"
            class="autocomplete-dropdown"
          >
            <div
              v-for="(suggestion, index) in nameSuggestions"
              :key="suggestion.id"
              :class="[
                'autocomplete-item',
                { highlighted: selectedSuggestionIndex === index },
              ]"
              @mousedown.prevent="selectSuggestion(suggestion)"
              @mouseover="selectedSuggestionIndex = index"
            >
              <div class="suggestion-name">{{ suggestion.fullName }}</div>
              <div class="suggestion-details">
                <span v-if="suggestion.Phone" class="suggestion-phone">{{
                  suggestion.Phone
                }}</span>
                <span v-if="suggestion.Address" class="suggestion-address">{{
                  suggestion.Address
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="form-group">
        <label for="phone">Phone</label>
        <input
          id="phone"
          v-model="newWorkOrder.phone"
          type="text"
          @blur="validatePhone"
          :class="{ 'input-error': fieldErrors.phone }"
        />
      </div>
      <div class="form-group">
        <label for="propertyAddress">Property Address</label>
        <select
          id="propertyAddress"
          v-model="newWorkOrder.selectedPropertyId"
          @change="onPropertySelected"
          :class="{ 'input-error': fieldErrors.propertyAddress }"
        >
          <option :value="null" disabled>Select a property...</option>
          <optgroup label="Houses">
            <option
              v-for="prop in propertyOptions.filter((p) => p.isParent)"
              :key="prop.id"
              :value="prop.id"
            >
              {{ prop.display }}
            </option>
          </optgroup>
          <optgroup label="Units">
            <option
              v-for="prop in propertyOptions.filter((p) => !p.isParent)"
              :key="prop.id"
              :value="prop.id"
            >
              {{ prop.display }}
            </option>
          </optgroup>
          <option value="other">Other</option>
        </select>
      </div>
      <div class="form-group">
        <div class="label-with-action">
          <label for="problemDescription">Problem Description</label>
          <button
            type="button"
            class="photo-attach-btn"
            :disabled="isCreatingWorkOrder"
            @click="saveNewWorkOrderAndOpenNotes"
            title="Save work order, then add notes or photos"
          >
            <i
              :class="
                isCreatingWorkOrder
                  ? 'fas fa-spinner fa-spin'
                  : 'fas fa-camera'
              "
            ></i>
          </button>
        </div>
        <textarea
          id="problemDescription"
          v-model="newWorkOrder.problemDescription"
          :class="{ 'input-error': fieldErrors.problemDescription }"
        ></textarea>
      </div>
      <div class="form-group checkbox">
        <input
          style="width: 30px; margin-top: -4px; margin-left: -7px"
          id="allowEntry"
          v-model="newWorkOrder.allowEntry"
          type="checkbox"
        />
        <label for="allowEntry">Allow Entry</label>
      </div>
      <!-- Add validation error message area -->
      <div v-if="validationError" class="validation-error-message">
        {{ validationError }}
      </div>
      <div class="form-actions">
        <button type="submit" class="btn btn-primary">Add Work Order</button>
        <button
          @click="toggleNewWorkOrder"
          type="button"
          class="btn btn-secondary"
        >
          Cancel
        </button>
      </div>
    </form>
  </div>

  <div
    v-if="showTransferDialog"
    class="new-work-order-form wo-transfer-dialog"
    @click.stop
    @dblclick.stop
  >
    <div class="new-work-order-title">Transfer to tenant</div>
    <div class="form-group">
      <label for="transferName">Name</label>
      <div class="autocomplete-wrapper">
        <input
          ref="transferNameInput"
          id="transferName"
          v-model="transferQuery"
          type="text"
          autocomplete="off"
          @input="handleTransferNameInput"
          @keydown="handleTransferNameKeydown"
        />
        <div
          v-if="transferSuggestions.length > 0"
          class="autocomplete-dropdown"
        >
          <div
            v-for="(suggestion, index) in transferSuggestions"
            :key="suggestion.id + '-' + (suggestion.propertyId || index)"
            :class="[
              'autocomplete-item',
              { highlighted: transferSuggestionIndex === index },
            ]"
            @mousedown.prevent="chooseTransferTenant(suggestion)"
            @mouseover="transferSuggestionIndex = index"
          >
            <div class="suggestion-name">{{ suggestion.fullName }}</div>
            <div class="suggestion-details">
              <span v-if="suggestion.Phone" class="suggestion-phone">{{
                suggestion.Phone
              }}</span>
              <span v-if="suggestion.Address" class="suggestion-address">{{
                suggestion.Address
              }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="transferSelected" class="wo-transfer-chosen">
      {{ transferSelected.fullName }}
      <span v-if="transferSelected.Phone">{{ transferSelected.Phone }}</span>
      <span v-if="transferSelected.Address">{{
        transferSelected.Address
      }}</span>
    </div>
    <div v-if="transferError" class="validation-error-message">
      {{ transferError }}
    </div>
    <div class="form-actions">
      <button
        type="button"
        class="btn btn-primary"
        :disabled="!transferSelected || transferSaving"
        @click="confirmTransfer"
      >
        {{ transferSaving ? 'Transferring...' : 'Transfer' }}
      </button>
      <button
        type="button"
        class="btn btn-secondary"
        @click="closeTransferDialog"
      >
        Cancel
      </button>
    </div>
  </div>

  <!-- Receipts Popup -->
  <!-- List View -->
  <div v-if="showListView" class="list-view-overlay">
    <div class="list-view-modal">
      <div class="list-view-header">
        <h3>Work Orders</h3>
        <div class="list-view-header-actions">
          <button
            @click="shareListViewLink"
            class="menu-btn"
            title="Share list view link"
          >
            <i class="fas fa-share-nodes"></i>
          </button>
          <button
            @click="listViewHideDone = !listViewHideDone"
            :class="['menu-btn', { 'sort-active': !listViewHideDone }]"
            :title="listViewHideDone ? 'Show completed' : 'Hide completed'"
          >
            <i
              :class="listViewHideDone ? 'fas fa-eye-slash' : 'fas fa-eye'"
            ></i>
          </button>
          <button @click="showListView = false" class="menu-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
      <div class="list-view-search">
        <input
          v-model="listViewSearch"
          placeholder="Filter..."
          class="list-view-search-input"
          @dblclick="listViewSearch = ''"
        />
        <button
          v-if="listViewSearch"
          @click="listViewSearch = ''"
          class="list-view-search-clear"
        >
          <i class="fas fa-backspace"></i>
        </button>
        <div class="list-view-search-actions">
          <button
            class="list-view-group-btn"
            title="Print list"
            :disabled="listViewPrintLoading"
            @click="printListView"
          >
            <i class="fas fa-print"></i>
            <span class="list-view-btn-label">print</span>
          </button>
          <button
            @click="listViewGroupBy = 'date'"
            :class="[
              'list-view-group-btn',
              { active: listViewGroupBy === 'date' },
            ]"
            title="Group by date"
          >
            <i class="fas fa-calendar"></i>
            <span class="list-view-btn-label">date</span>
          </button>
          <button
            @click="listViewGroupBy = 'building'"
            :class="[
              'list-view-group-btn',
              { active: listViewGroupBy === 'building' },
            ]"
            title="Group by location"
          >
            <i class="fas fa-building"></i>
            <span class="list-view-btn-label">location</span>
          </button>
        </div>
      </div>
      <div class="list-view-container">
        <div v-if="listViewWorkOrders.length === 0" class="list-view-empty">
          No work orders found
        </div>
        <div v-else class="list-view-events">
          <!-- Group by date -->
          <template v-if="listViewGroupBy === 'date'">
            <template
              v-for="(orders, dateStr) in listViewGrouped"
              :key="dateStr"
            >
              <div class="list-view-date-header">
                {{ formatListViewDateHeader(dateStr) }}
                <span class="list-view-date-count">({{ orders.length }})</span>
              </div>
              <div
                v-for="order in orders"
                :key="order.id"
                class="list-view-card"
                @click="openWorkOrderFromList(order)"
              >
                <div class="list-view-card-address">
                  <span class="list-view-card-address-text">{{
                    stripHtml(order.property_address) || 'No address'
                  }}</span>
                  <i
                    v-if="order.calendarEventId"
                    class="fas fa-calendar-check list-view-entry-icon list-view-scheduled"
                    title="Scheduled"
                  ></i>
                  <i
                    v-if="order.allow_entry"
                    class="fas fa-lock-open list-view-entry-icon entry-allowed"
                  ></i>
                  <i
                    v-else
                    class="fas fa-ban list-view-entry-icon entry-denied"
                  ></i>
                </div>
                <div class="list-view-card-desc">
                  {{ stripHtml(order.problem_description) || 'No description' }}
                </div>
              </div>
            </template>
          </template>
          <!-- Group by building -->
          <template v-else>
            <div
              v-for="group in listViewGroupedByBuilding"
              :key="group.parentId"
              class="list-view-building-group"
            >
              <div
                class="list-view-date-header list-view-building-header"
                @click="toggleListViewGroup(group.parentId)"
              >
                <span>
                  <i
                    class="fas"
                    :class="
                      listViewCollapsedGroups.has(group.parentId)
                        ? 'fa-chevron-right'
                        : 'fa-chevron-down'
                    "
                    style="font-size: 0.75em; margin-right: 6px"
                  ></i>
                  {{ group.parentAddress }}
                </span>
                <span class="list-view-date-count"
                  >({{ group.orders.length }})</span
                >
              </div>
              <template v-if="!listViewCollapsedGroups.has(group.parentId)">
                <div
                  v-for="order in group.orders"
                  :key="order.id"
                  class="list-view-card"
                  @click="openWorkOrderFromList(order)"
                >
                  <div class="list-view-card-address">
                    <span class="list-view-card-address-text">{{
                      stripHtml(order.property_address) || 'No address'
                    }}</span>
                    <i
                      v-if="order.calendarEventId"
                      class="fas fa-calendar-check list-view-entry-icon list-view-scheduled"
                      title="Scheduled"
                    ></i>
                    <i
                      v-if="order.allow_entry"
                      class="fas fa-lock-open list-view-entry-icon entry-allowed"
                    ></i>
                    <i
                      v-else
                      class="fas fa-ban list-view-entry-icon entry-denied"
                    ></i>
                  </div>
                  <div class="list-view-card-desc">
                    {{
                      stripHtml(order.problem_description) || 'No description'
                    }}
                  </div>
                  <div class="list-view-card-date">
                    {{ formatCardAge(order.date) }}
                  </div>
                </div>
              </template>
            </div>
          </template>
        </div>
      </div>
      <div class="list-view-footer">
        <span class="list-view-count"
          >{{ listViewWorkOrders.length }} work orders</span
        >
        <button @click="showListView = false" class="menu-btn">Close</button>
      </div>
    </div>
    <div v-if="listViewPrintLoading" class="list-view-print-loading-overlay">
      <div class="list-view-print-loading-spinner">
        <i class="fas fa-spinner fa-spin"></i>
        <div class="loading-text">Preparing print preview...</div>
      </div>
    </div>
  </div>

  <div v-if="showReceiptsPopup" class="receipts-popup">
    <div class="receipts-popup-content">
      <div class="receipts-popup-title">
        <span>Material Receipts</span>
        <div class="receipts-title-actions">
          <button
            @click="addNewReceipt"
            class="add-receipt-btn"
            title="Add Receipt"
          >
            <i class="fas fa-plus"></i>
          </button>
          <button @click="toggleReceiptsPopup" class="close-receipts-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
      <div class="receipts-filter-row">
        <input
          type="text"
          v-model="receiptSearchInput"
          @input="onReceiptSearchInput"
          placeholder="Search receipts..."
          class="receipt-search-input"
        />
        <select
          v-model="receiptYear"
          @change="onReceiptYearChange"
          class="receipt-year-select"
        >
          <option v-for="year in receiptYearOptions" :key="year" :value="year">
            {{ year }}
          </option>
        </select>
        <button
          @click="loadAllReceipts"
          class="load-all-btn"
          title="Load All Years"
        >
          All
        </button>
      </div>
      <div class="receipts-list">
        <div
          v-for="receipt in filteredReceipts"
          :key="receipt.id"
          class="receipt-card"
          :class="{ editing: editingReceiptId === receipt.id }"
          @click="startEditReceipt(receipt)"
        >
          <!-- View Mode -->
          <template v-if="editingReceiptId !== receipt.id">
            <div class="receipt-card-header">
              <span
                class="receipt-supplier"
                v-html="highlightSearch(receipt.supplier)"
              ></span>
              <span class="receipt-amount"
                >$<span
                  v-html="highlightSearch(formatAmount(receipt.amount))"
                ></span
              ></span>
            </div>
            <div class="receipt-card-body">
              <div
                class="receipt-description"
                v-html="highlightSearch(receipt.description)"
              ></div>
              <div class="receipt-address">
                For: <span v-html="highlightSearch(receipt.address)"></span>
              </div>
            </div>
            <div class="receipt-card-footer">
              <span
                class="receipt-initials"
                v-html="highlightSearch(receipt.createdByInitials)"
              >
              </span>
              <span
                class="receipt-date"
                v-html="highlightSearch(formatReceiptDate(receipt.modifiedOn))"
              >
              </span>
              <button
                v-if="currentUserRole < 3"
                @click.stop="deleteReceipt(receipt)"
                class="delete-receipt-btn"
                title="Delete Receipt"
              >
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </template>
          <!-- Edit Mode -->
          <template v-else>
            <div class="receipt-edit-form" @click.stop>
              <div class="receipt-form-group">
                <label>Supplier</label>
                <select v-model="editingReceipt.supplier">
                  <option value="">Select Supplier</option>
                  <option
                    v-for="supplier in supplierOptions"
                    :key="supplier"
                    :value="supplier"
                  >
                    {{ supplier }}
                  </option>
                </select>
              </div>
              <div class="receipt-form-group">
                <label>Description</label>
                <textarea
                  v-model="editingReceipt.description"
                  placeholder="Item description"
                  maxlength="512"
                  rows="5"
                  class="receipt-description-input"
                ></textarea>
              </div>
              <div class="receipt-form-group">
                <label>Property</label>
                <select v-model="editingReceipt.address">
                  <option value="">Select Property</option>
                  <option value="Other">Other...</option>
                  <option
                    v-for="prop in propertyOptions"
                    :key="prop.id"
                    :value="prop.display"
                  >
                    {{ prop.display }}
                  </option>
                </select>
              </div>
              <div class="receipt-form-group">
                <label>Amount</label>
                <input
                  type="number"
                  step="0.01"
                  v-model="editingReceipt.amount"
                  placeholder="0.00"
                />
              </div>
              <div class="receipt-edit-actions">
                <button @click.stop="saveReceipt" class="btn btn-primary">
                  Save
                </button>
                <button
                  @click.stop="cancelEditReceipt"
                  class="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </template>
        </div>
        <div v-if="receipts.length === 0" class="no-receipts">
          No receipts yet. Tap + to add one.
        </div>
      </div>
    </div>
  </div>

  <!-- Delete Confirmation Popup -->
  <div
    v-if="showDeleteConfirm"
    class="delete-confirm-overlay"
    @click.self="cancelDelete"
  >
    <div class="delete-confirm-dialog">
      <div class="delete-confirm-message">
        Are you sure you want to delete this receipt?
      </div>
      <div class="delete-confirm-actions">
        <button @click="confirmDelete" class="btn btn-danger">Delete</button>
        <button @click="cancelDelete" class="btn btn-secondary">Cancel</button>
      </div>
    </div>
  </div>

  <!-- Notes Popup -->
  <div v-if="showNotesPopup" class="notes-popup">
    <div class="notes-popup-content">
      <div class="notes-popup-title">Edit Notes</div>

      <!-- Voice Recorder Button Row -->
      <div class="notes-button-row">
        <button @click="voiceRecorderInNotes" class="voice-recorder-btn">
          <i class="fas fa-microphone"></i>
        </button>
      </div>

      <!-- Voice Recorder Widget (shown when activated) -->
      <div v-if="showIconBar == 3" class="voice-recorder-widget">
        <div class="icon-item" :class="{ disabled: !audioData }">
          <i :class="iconClass" @click="toggleRecording"></i>
          <span class="icon-label">{{ recordingLabel }}</span>
        </div>
        <div class="icon-item" :class="{ disabled: !audioData }">
          <i
            class="fas fa-play"
            @click="playAudio"
            :class="{
              disabled: !audioData,
              'green-playing': isPlaying,
            }"
          ></i>
          <span class="icon-label">Play {{ formattedDuration }}</span>
        </div>
        <div class="icon-item" :class="{ disabled: !audioData }">
          <i
            class="fas fa-trash"
            @click="deleteRecording"
            :class="{ disabled: !audioData }"
          ></i>
          <span class="icon-label">Delete</span>
        </div>
        <div class="icon-item" :class="{ disabled: !audioData }">
          <i
            class="fas fa-paper-plane"
            @click="sendRecording"
            :class="{ disabled: !audioData }"
          ></i>
          <span class="icon-label">Send</span>
        </div>
      </div>

      <textarea
        v-model="workOrderNote"
        class="notes-textarea"
        placeholder="add notes to describe the work here and attach photos, or leave a voice note to describe the work done..."
      ></textarea>

      <!-- Photo Carousel Section -->
      <div class="photo-carousel-section">
        <div class="photos-header">
          <h4>Photos</h4>
          <div class="header-buttons">
            <button @click="selectPhoto" class="round-action-button">
              <i class="fas fa-plus"></i>
            </button>
            <button
              v-if="workOrderPhotos.length > 0"
              @click="deletePhoto(currentPhoto.id)"
              class="round-action-button delete-button"
            >
              <i class="fas fa-trash"></i>
            </button>
          </div>
        </div>
        <div class="photo-carousel">
          <!-- Loading spinner for photos -->
          <div v-if="isLoadingPhotos" class="loading-photos">
            <div class="spinner">
              <i class="fas fa-spinner fa-spin"></i>
            </div>
            <div class="loading-text">Loading photos...</div>
          </div>
          <div v-else-if="workOrderPhotos.length === 0" class="no-photos">
            No photos yet
          </div>
          <div v-else class="photo-carousel-container">
            <button
              class="carousel-nav prev"
              @click="prevPhoto"
              :disabled="currentPhotoIndex === 0"
            >
              <i class="fas fa-chevron-left"></i>
            </button>

            <div class="photo-carousel-item" @click="openFullSizePhoto">
              <img
                :src="currentPhoto.photoUrl"
                :alt="currentPhoto.caption || 'Work Order Photo'"
                class="carousel-photo"
              />
              <div class="photo-caption">
                {{ currentPhoto.caption || 'No caption' }}
              </div>
            </div>

            <button
              class="carousel-nav next"
              @click="nextPhoto"
              :disabled="currentPhotoIndex >= workOrderPhotos.length - 1"
            >
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>

          <!-- Hidden file input -->
          <input
            type="file"
            ref="photoInput"
            @change="handlePhotoUpload"
            accept="image/*"
            style="display: none"
          />
        </div>
      </div>

      <div class="notes-popup-actions">
        <button
          type="button"
          @click="saveWorkOrderNotes(false)"
          class="btn btn-primary"
          :disabled="savingWorkOrderNotes"
        >
          Save
        </button>
        <button
          v-if="pendingCompletionWorkOrderId"
          type="button"
          @click="saveWorkOrderNotes(true)"
          class="btn btn-success"
          :disabled="savingWorkOrderNotes"
        >
          Save and Done
        </button>
        <button @click="closeNotes" class="btn btn-secondary">Cancel</button>
      </div>
    </div>
  </div>

  <!-- Full-size Photo Overlay -->
  <div
    v-if="showFullSizePhoto"
    class="full-size-photo-overlay"
    @click="closeFullSizePhoto"
    @touchstart="handleTouchStart"
    @touchmove="handleTouchMove"
    @touchend="handleTouchEnd"
    @dblclick="handleDoubleTap"
  >
    <div class="full-size-photo-content">
      <img
        :src="currentPhoto.photoUrl"
        :alt="currentPhoto.caption || 'Work Order Photo'"
        class="full-size-photo"
        :style="{
          transform: `scale(${photoScale}) translate(${photoTranslateX}px, ${photoTranslateY}px)`,
        }"
      />
      <div class="photo-viewer-caption" v-if="currentPhoto">
        {{ currentPhoto.caption || 'No caption' }}
        <button class="edit-caption-button" @click.stop="openEditCaptionDialog">
          <i class="fas fa-pencil-alt"></i>
        </button>
      </div>
      <div class="photo-viewer-close">tap to close</div>
    </div>
  </div>

  <!-- Edit Caption Dialog -->
  <div v-if="showEditCaptionDialog" class="dialog-overlay" @click.stop>
    <div class="dialog-content" @click.stop>
      <div class="dialog-header">Edit Caption</div>
      <div class="dialog-body">
        <input
          v-model="editingCaption"
          placeholder="Enter caption"
          class="caption-input"
        />
      </div>
      <div class="dialog-actions">
        <button @click="cancelEditCaption">Cancel</button>
        <button class="confirm-button" @click="updatePhotoCaption">Save</button>
      </div>
    </div>
  </div>

  <div
    v-if="showWorkOrderMerge"
    class="notes-popup wo-merge-overlay"
    @click.self="closeWorkOrderMerge"
  >
    <div class="notes-popup-content wo-merge-dialog" @click.stop>
      <div class="notes-popup-title">
        Merge into #{{ workOrderMergePrimary && workOrderMergePrimary.id }}
      </div>
      <input
        v-model="workOrderMergeQuery"
        class="wo-merge-search"
        type="text"
        placeholder="Search open work orders here"
      />
      <div v-if="!workOrderMergeVisible.length" class="wo-merge-empty">
        No other open work orders at this property.
      </div>
      <div v-else class="wo-merge-list">
        <label
          v-for="order in workOrderMergeVisible"
          :key="order.id"
          class="wo-merge-row"
        >
          <input
            v-model="workOrderMergeSelected"
            class="wo-merge-check"
            type="checkbox"
            :value="order.id"
          />
          <span class="wo-merge-copy">
            <span class="wo-merge-id">#{{ order.id }}</span>
            <span class="wo-merge-summary">{{
              stripHtml(order.problem_description) || 'No description'
            }}</span>
          </span>
        </label>
      </div>
      <div v-if="workOrderMergeError" class="wo-merge-error">
        {{ workOrderMergeError }}
      </div>
      <div class="notes-popup-actions">
        <button
          class="btn btn-primary"
          :disabled="!workOrderMergeSelected.length || workOrderMergeSaving"
          @click="confirmWorkOrderMerge"
        >
          {{ workOrderMergeSaving ? 'Merging…' : 'Merge' }}
        </button>
        <button class="btn btn-secondary" @click="closeWorkOrderMerge">
          Cancel
        </button>
      </div>
    </div>
  </div>
  <!-- Document Attachment Popup -->
  <div v-if="showDocumentPopup" class="notes-popup">
    <div class="notes-popup-content">
      <div class="notes-popup-title">Attach Document</div>
      <div class="form-group">
        <label for="documentId">Enter Document ID:</label>
        <input
          ref="documentIdInput"
          id="documentId"
          v-model="documentIdToAttach"
          type="text"
          placeholder="e.g. 13030"
          @keyup.enter="confirmAttachDocument"
        />
      </div>
      <div class="notes-popup-actions">
        <button @click="confirmAttachDocument" class="btn btn-primary">
          Attach
        </button>
        <button @click="closeDocumentPopup" class="btn btn-secondary">
          Cancel
        </button>
      </div>
    </div>
  </div>
  <!-- Work Order Edit Popup -->
  <div v-if="showEditWorkOrderPopup" class="notes-popup edit-wo-overlay">
    <div class="notes-popup-content edit-wo-popup">
      <div class="notes-popup-title">Edit Work Order</div>

      <div class="edit-wo-scrollable">
        <div class="form-group">
          <label for="editName">Name</label>
          <input
            type="text"
            id="editName"
            v-model="editedName"
            placeholder="Enter name..."
            class="form-input"
          />
        </div>

        <div class="form-group">
          <label for="editPhone">Phone</label>
          <input
            type="text"
            id="editPhone"
            v-model="editedPhone"
            placeholder="Enter phone..."
            class="form-input"
          />
        </div>

        <div class="form-group">
          <label for="editPropertyAddress"
            >Property Address
            <span
              v-if="editedPropertyAddress"
              style="
                font-size: 0.8em;
                color: #888;
                font-weight: normal;
                margin-left: 6px;
              "
              >{{ editedPropertyAddress }}</span
            >
          </label>
          <select
            id="editPropertyAddress"
            v-model="editedPropertyId"
            @change="onEditPropertySelected"
            class="form-input"
          >
            <option :value="null">-- Select Property --</option>
            <optgroup label="Houses">
              <option
                v-for="prop in propertyOptions.filter((p) => p.isParent)"
                :key="prop.id"
                :value="prop.id"
              >
                {{ prop.display }}
              </option>
            </optgroup>
            <optgroup label="Units">
              <option
                v-for="prop in propertyOptions.filter((p) => !p.isParent)"
                :key="prop.id"
                :value="prop.id"
              >
                {{ prop.display }}
              </option>
            </optgroup>
            <option value="other">Other</option>
          </select>
        </div>

        <textarea
          v-model="editedProblemDescription"
          class="notes-textarea"
          placeholder="Enter problem description..."
        ></textarea>
        <div class="form-group checkbox-allow-entry">
          <label for="editAllowEntry">Allow Entry</label>
          <input
            type="checkbox"
            id="editAllowEntry"
            v-model="editedAllowEntry"
          />
        </div>

        <!-- Pet and Special Instructions fields - only show if peopleId exists -->
        <div v-if="currentEditPeopleId" class="pet-special-section">
          <div class="form-group checkbox-allow-entry">
            <label for="editPet">Has Pet</label>
            <input type="checkbox" id="editPet" v-model="editedPet" />
          </div>

          <div v-if="editedPet" class="form-group">
            <label for="editPetName">Pet Names</label>
            <input
              type="text"
              id="editPetName"
              v-model="editedPetName"
              placeholder="Enter pet names..."
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label for="editSpecialInstr">Special Instructions</label>
            <textarea
              id="editSpecialInstr"
              v-model="editedWorkOrderSpecialInstr"
              placeholder="Enter special instructions..."
              class="form-textarea"
            ></textarea>
          </div>
        </div>
      </div>

      <div class="notes-popup-actions">
        <button @click="saveEditedWorkOrder" class="btn btn-primary">
          Save
        </button>
        <button @click="closeEditWorkOrder" class="btn btn-secondary">
          Cancel
        </button>
      </div>
    </div>
  </div>

  <!-- Notification Popup -->
  <div
    v-if="showNotification"
    class="notification-popup"
    :class="{ fadeOut: notificationFading }"
  >
    {{ notificationMessage }}
  </div>

  <div
    v-if="showCompletedWorkOrderNotice"
    class="dialog-overlay"
    @click.stop="dismissCompletedWorkOrderNotice"
  >
    <div class="dialog-content" @click.stop>
      <div class="dialog-body">
        <p>This work order has been completed.</p>
      </div>
      <div class="dialog-actions">
        <button
          type="button"
          class="confirm-button"
          @click="dismissCompletedWorkOrderNotice"
        >
          OK
        </button>
      </div>
    </div>
  </div>

  <!-- Photo Upload Dialog -->
  <div
    v-if="showPhotoUploadDialog"
    class="dialog-overlay"
    @click="closePhotoUploadDialog"
  >
    <div class="dialog-content" @click.stop>
      <div class="dialog-header">Upload Photos</div>
      <div class="dialog-body">
        <div class="upload-instructions">
          Select images from your device to upload. On a phone, a camera shot
          is also kept on the device.
        </div>
        <div class="upload-options">
          <label class="upload-option">
            <input
              type="file"
              accept="image/*,.heic,.heif,image/heic,image/heif"
              @change="handlePhotoUpload"
              class="hidden-input"
              multiple
            />
            <div class="option-button">
              <i class="fas fa-images"></i>
              <div>Gallery</div>
            </div>
          </label>
          <label class="upload-option">
            <input
              type="file"
              accept="image/*,.heic,.heif,image/heic,image/heif"
              capture="environment"
              @change="handlePhotoUpload($event, 'camera')"
              class="hidden-input"
            />
            <div class="option-button">
              <i class="fas fa-camera"></i>
              <div>Camera</div>
            </div>
          </label>
        </div>
        <div class="preview-container" v-if="photoPreviewUrls.length > 0">
          <div
            v-for="(preview, index) in photoPreviewUrls"
            :key="index"
            class="photo-preview-item"
          >
            <img
              v-if="!preview.isHeic"
              :src="preview.url"
              class="photo-preview"
            />
            <div v-else class="photo-preview photo-preview-heic" title="HEIC">
              <i class="fas fa-image"></i>
            </div>
            <button
              @click.stop="removePreviewPhoto(index)"
              class="remove-photo-btn"
            >
              <i class="fas fa-times"></i>
            </button>
            <button
              v-if="preview.gallerySave === 'needs-tap'"
              type="button"
              class="save-to-photos-btn"
              @click.stop="savePreviewToPhotos(index)"
            >
              Save a copy
            </button>
          </div>
        </div>
        <input
          v-model="photoCaption"
          placeholder="Caption (optional)"
          class="caption-input"
        />
      </div>
      <div class="dialog-actions">
        <button @click="closePhotoUploadDialog">Cancel</button>
        <button
          class="confirm-button"
          :disabled="photoPreviewUrls.length === 0"
          @click="uploadPhoto"
        >
          <span v-if="isUploading">
            <i class="fas fa-spinner fa-spin"></i> Uploading...
          </span>
          <span v-else>Upload</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Maintenance Lock Code Popup -->
  <div
    v-if="showMaintenanceLockCodePopup"
    class="wo-lock-code-overlay"
    @click.self="closeMaintenanceLockCodePopup"
  >
    <div class="wo-lock-code-dialog" @click.stop>
      <div class="wo-lock-code-header">Maintenance Lock Code</div>
      <div class="wo-lock-code-body">
        <div v-if="maintenanceLockCodeLoading" class="wo-lock-code-status">
          Loading...
        </div>
        <div
          v-else-if="maintenanceLockCodeError"
          class="wo-lock-code-status wo-lock-code-error"
        >
          {{ maintenanceLockCodeError }}
        </div>
        <template v-else>
          <div class="wo-lock-code-address">
            {{ maintenanceLockCodeAddress || 'Unknown address' }}
          </div>
          <div class="wo-lock-code-value">
            {{
              maintenanceLockCodeValue
                ? maintenanceLockCodeValue
                : 'No maintenance code on file'
            }}
          </div>
        </template>
      </div>
      <div class="wo-lock-code-actions">
        <button @click="closeMaintenanceLockCodePopup">Close</button>
        <div
          v-if="maintenanceLockCodeCountdown > 0"
          class="wo-lock-code-countdown"
        >
          Closing in {{ maintenanceLockCodeCountdown }}s
        </div>
      </div>
    </div>
  </div>

  <!-- Email tenant short message -->
  <div
    v-if="showTenantEmailDialog"
    class="wo-lock-code-overlay"
    @click.self="closeTenantEmailDialog"
  >
    <div class="wo-tenant-email-dialog" @click.stop>
      <div class="wo-lock-code-header">Email Tenant</div>
      <div class="wo-tenant-email-body">
        <div class="wo-lock-code-address">
          {{ tenantEmailDialogMeta }}
        </div>
        <label class="wo-tenant-email-label" for="wo-tenant-email-message">
          Short message
        </label>
        <textarea
          id="wo-tenant-email-message"
          v-model="tenantEmailMessage"
          class="wo-tenant-email-textarea"
          rows="5"
          maxlength="2000"
          placeholder="Type a short message to the tenant…"
          :disabled="tenantEmailSending"
        ></textarea>
        <div
          v-if="tenantEmailError"
          class="wo-lock-code-status wo-lock-code-error"
        >
          {{ tenantEmailError }}
        </div>
      </div>
      <div class="wo-tenant-email-actions">
        <button
          type="button"
          class="wo-tenant-email-cancel"
          :disabled="tenantEmailSending"
          @click="closeTenantEmailDialog"
        >
          Cancel
        </button>
        <button
          type="button"
          class="wo-tenant-email-send"
          :disabled="tenantEmailSending || !tenantEmailMessage.trim()"
          @click="sendTenantEmailMessage"
        >
          <i
            v-if="tenantEmailSending"
            class="fas fa-spinner fa-spin"
          ></i>
          {{ tenantEmailSending ? 'Sending…' : 'Send' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Upload Error Dialog -->
  <div
    v-if="showUploadErrorDialog"
    class="dialog-overlay"
    @click="showUploadErrorDialog = false"
  >
    <div class="dialog-content" @click.stop>
      <div class="dialog-header">Upload Error</div>
      <div class="dialog-body">
        <p>{{ uploadErrorMessage }}</p>
        <p v-if="uploadErrorDetails" class="error-details">
          <strong>Details:</strong> {{ uploadErrorDetails }}
        </p>
      </div>
      <div class="dialog-actions">
        <button @click="showUploadErrorDialog = false">Close</button>
      </div>
    </div>
  </div>

  <!-- Share Popup -->
  <div v-if="showSharePopup" class="dialog-overlay" @click="closeShareDialog">
    <div class="dialog-content" @click.stop>
      <div class="dialog-header">{{ shareTitle }}</div>
      <div class="dialog-body">
        <div
          class="share-text-content"
          ref="shareTextContent"
          @click="selectAllShareText"
          style="cursor: text"
        >
          {{ shareText }}
        </div>
      </div>
      <div class="dialog-actions">
        <button @click="closeShareDialog">Close</button>
      </div>
    </div>
  </div>

  <!-- Summary Statistics Popup -->
  <div
    v-if="showSummaryPopup"
    class="dialog-overlay"
    @click="closeSummaryPopup"
  >
    <div class="summary-dialog-content" @click.stop>
      <div class="summary-dialog-header">Work Order Summary</div>
      <div class="dialog-body">
        <table class="summary-table" v-if="summaryStats">
          <tbody>
            <tr
              class="summary-row clickable"
              @click="filterByAge('newToday', 'New Today')"
              :class="{ active: ageFilterLabel === 'New Today' }"
            >
              <td class="summary-label">Open for Today:</td>
              <td class="summary-value">{{ summaryStats.newToday.count }}</td>
            </tr>
            <tr
              class="summary-row clickable"
              @click="filterByAge('doneToday', 'Done Today')"
              :class="{ active: ageFilterLabel === 'Done Today' }"
            >
              <td class="summary-label">Done today:</td>
              <td class="summary-value">{{ summaryStats.doneToday.count }}</td>
            </tr>
            <tr
              class="summary-row clickable"
              @click="filterByAge('newThisWeek', 'New This Week')"
              :class="{ active: ageFilterLabel === 'New This Week' }"
            >
              <td class="summary-label">Open for this week:</td>
              <td class="summary-value">
                {{ summaryStats.newThisWeek.count }}
              </td>
            </tr>
            <tr
              class="summary-row clickable"
              @click="filterByAge('doneThisWeek', 'Done This Week')"
              :class="{ active: ageFilterLabel === 'Done This Week' }"
            >
              <td class="summary-label">Done this week:</td>
              <td class="summary-value">
                {{ summaryStats.doneThisWeek.count }}
              </td>
            </tr>
            <tr
              class="summary-row clickable"
              @click="
                filterByAge('openFromLastWeek', 'Still Open from Last Week')
              "
              :class="{
                active: ageFilterLabel === 'Still Open from Last Week',
              }"
            >
              <td class="summary-label">Still open from last week:</td>
              <td class="summary-value">
                {{ summaryStats.openFromLastWeek.count }}
              </td>
            </tr>
            <tr
              class="summary-row clickable"
              @click="
                filterByAge('openFromLastMonth', 'Still Open from Last Month')
              "
              :class="{
                active: ageFilterLabel === 'Still Open from Last Month',
              }"
            >
              <td class="summary-label">Still open from last month:</td>
              <td class="summary-value">
                {{ summaryStats.openFromLastMonth.count }}
              </td>
            </tr>
            <tr
              class="summary-row clickable"
              @click="filterByAge('doneLastMonth', 'Done Last Month')"
              :class="{ active: ageFilterLabel === 'Done Last Month' }"
            >
              <td class="summary-label">Done last month:</td>
              <td class="summary-value">
                {{ summaryStats.doneLastMonth.count }}
              </td>
            </tr>
            <tr
              class="summary-row clickable"
              @click="filterByAge('openOlderThanMonth', 'Still Open > 1 Month')"
              :class="{ active: ageFilterLabel === 'Still Open > 1 Month' }"
            >
              <td class="summary-label">Still open from &gt; 1 month:</td>
              <td class="summary-value">
                {{ summaryStats.openOlderThanMonth.count }}
              </td>
            </tr>
            <tr
              class="summary-row clickable"
              @click="filterByAge('highPriorityOpen', 'Long Term Open')"
              :class="{ active: ageFilterLabel === 'Long Term Open' }"
            >
              <td class="summary-label">Long term priority (1+):</td>
              <td class="summary-value">
                {{ summaryStats.highPriorityOpen.count }}
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else class="loading-summary">
          <i class="fas fa-spinner fa-spin"></i> Loading summary...
        </div>
      </div>
      <div class="dialog-actions">
        <button @click="clearAgeFilter" v-if="filteredByAgeIds">
          Clear Filter
        </button>
        <button
          class="review-open-btn"
          @click="openReviewPopup"
          :disabled="!reviewOpenWorkOrders.length"
          title="Step through all open work orders"
        >
          Review
        </button>
        <button @click="closeSummaryPopup">Close</button>
      </div>
    </div>
  </div>

  <!-- Full-screen Review Open Work Orders -->
  <div v-if="showReviewPopup" class="review-fullscreen" @click.stop>
    <div class="review-fullscreen-inner">
      <div class="review-header">
        <button
          type="button"
          class="review-header-btn"
          title="Add new work order"
          @click="toggleNewWorkOrder"
        >
          <i class="fas fa-plus"></i>
        </button>
        <span class="review-title">Review Open Work Orders</span>
        <div class="review-header-actions">
          <button
            type="button"
            class="review-header-btn"
            :class="{ 'review-header-btn-pinned': reviewDefaultPinned }"
            :title="
              reviewDefaultPinned
                ? 'Unpin Review as default view'
                : 'Pin Review as default view when opening Work Orders'
            "
            :aria-pressed="reviewDefaultPinned ? 'true' : 'false'"
            @click="toggleReviewDefaultPin"
          >
            <i class="fas fa-thumbtack"></i>
          </button>
          <button
            type="button"
            class="review-header-btn"
            title="Refresh work orders"
            :disabled="reviewRefreshing"
            @click="refreshReviewWorkOrders"
          >
            <i
              class="fas fa-sync-alt"
              :class="{ 'fa-spin': reviewRefreshing }"
            ></i>
          </button>
          <button
            type="button"
            class="review-header-btn"
            title="Maintenance lock code"
            :disabled="!reviewCurrentWorkOrder?.propertyId"
            @click="openMaintenanceLockCode(reviewCurrentWorkOrder)"
          >
            <i class="fas fa-lock"></i>
          </button>
          <button
            type="button"
            class="review-header-btn"
            title="Home"
            @click="$router.push('/mhome')"
          >
            <i class="fas fa-home"></i>
          </button>
        </div>
      </div>

      <div class="review-controls">
        <div class="review-controls-top">
          <button
            type="button"
            class="review-control-btn"
            :title="
              reviewSortDir === 'asc'
                ? 'Sorted by ID ascending — click for descending'
                : 'Sorted by ID descending — click for ascending'
            "
            @click="toggleReviewSortById"
          >
            <i
              class="fas"
              :class="
                reviewSortDir === 'asc'
                  ? 'fa-sort-amount-up'
                  : 'fa-sort-amount-down'
              "
            ></i>
            <span class="review-control-label"
              >ID {{ reviewSortDir === 'asc' ? '↑' : '↓' }}</span
            >
          </button>
          <div
            v-if="!reviewPersonFilter && !reviewAssigneeFilter"
            class="review-location-slot"
          >
            <div v-if="reviewSearchOpen" class="review-search">
              <input
                ref="reviewSearchInput"
                class="review-search-input"
                type="text"
                v-model="reviewSearchDraft"
                placeholder="Search..."
                aria-label="Filter work orders"
                autocomplete="off"
                enterkeyhint="search"
                @blur="commitReviewSearch"
                @keydown.enter.prevent="blurReviewSearch"
              />
              <button
                type="button"
                class="review-search-clear"
                title="Clear"
                aria-label="Clear search"
                @mousedown.prevent
                @click="reviewSearchDraft = ''"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
            <select
              v-else
              class="review-location-select"
              v-model="reviewLocationKey"
              title="Filter by location (parent building)"
              @change="onReviewLocationChange"
            >
              <option value="">All locations</option>
              <option
                v-for="loc in reviewLocationOptions"
                :key="loc.key"
                :value="loc.key"
              >
                {{ loc.label }} ({{ loc.count }})
              </option>
            </select>
          </div>
          <div v-else-if="reviewPersonFilter" class="review-person-filter">
            <span
              class="review-person-filter-label"
              :title="reviewPersonFilter.name"
            >
              {{ reviewPersonFilter.name }}
            </span>
            <button
              type="button"
              class="review-person-filter-clear"
              title="Clear name filter"
              @click="clearReviewPersonFilter"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div v-else class="review-person-filter">
            <span
              class="review-person-filter-label"
              :title="reviewAssigneeFilter.name"
            >
              {{ reviewAssigneeFilter.name }}
            </span>
            <button
              type="button"
              class="review-person-filter-clear"
              title="Clear assigned filter"
              @click="clearReviewAssigneeFilter"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
          <button
            v-if="!reviewPersonFilter && !reviewAssigneeFilter"
            type="button"
            class="review-search-btn"
            :class="{ 'review-search-btn-active': !!reviewSearchQuery }"
            :title="
              reviewSearchQuery
                ? `Filtering by “${reviewSearchQuery}”`
                : 'Search work orders'
            "
            :aria-pressed="reviewSearchOpen ? 'true' : 'false'"
            @mousedown="onReviewSearchButtonMouseDown"
            @click="onReviewSearchButtonClick"
          >
            <i class="fas fa-search"></i>
          </button>
          <span class="review-counter" v-if="reviewOpenWorkOrders.length">
            {{ reviewIndex + 1 }} of {{ reviewOpenWorkOrders.length }}
          </span>
          <span class="review-counter" v-else>None open</span>
        </div>
        <div
          class="review-slider-wrap"
          v-if="reviewOpenWorkOrders.length > 1"
        >
          <input
            type="range"
            class="review-slider"
            :min="0"
            :max="reviewOpenWorkOrders.length - 1"
            :step="1"
            :value="reviewIndex"
            @input="onReviewSliderInput"
            :aria-label="
              reviewSortDir === 'asc'
                ? 'Scrub from old to new work orders'
                : 'Scrub from new to old work orders'
            "
          />
          <div class="review-slider-labels">
            <span>{{ reviewSortDir === 'asc' ? 'old' : 'new' }}</span>
            <span>{{ reviewSortDir === 'asc' ? 'new' : 'old' }}</span>
          </div>
        </div>
      </div>

      <div
        class="review-body"
        v-if="reviewCurrentWorkOrder"
        title="Double-click to edit. Long-press to open in classic view."
        @touchstart.passive="onReviewTouchStart"
        @touchmove.passive="onReviewTouchMove"
        @touchend.passive="onReviewTouchEnd"
        @touchcancel.passive="onReviewTouchCancel"
        @dblclick="editReviewWorkOrder"
      >
        <div class="review-card-top">
          <div class="review-wo-id-col">
            <div class="review-wo-id">
              <span class="review-wo-hash">#</span>
              <input
                ref="reviewIdInput"
                class="review-wo-id-input"
                v-model="reviewIdInput"
                inputmode="numeric"
                enterkeyhint="go"
                autocomplete="off"
                aria-label="Jump to work order number"
                @mousedown="onReviewIdMouseDown"
                @click.stop
                @dblclick.stop
                @touchstart.stop="onReviewIdTap"
                @focus="beginReviewIdEdit"
                @keydown.enter.prevent="submitReviewIdJump"
                @blur="onReviewIdBlur"
              />
              <button
                type="button"
                class="review-wo-back"
                :disabled="!reviewLastWorkOrderId"
                :title="
                  reviewLastWorkOrderId
                    ? `Back to #${reviewLastWorkOrderId}`
                    : 'No saved work order'
                "
                @mousedown.prevent
                @click.stop="goBackToStoredReviewWorkOrder"
                @dblclick.stop
                @touchstart.stop
              >
                <i class="fas fa-arrow-left"></i>
              </button>
            </div>
          </div>
          <div class="review-card-top-actions">
            <button
              type="button"
              class="review-photo-btn"
              title="Upload photos"
              @click.stop="openReviewPhotoUpload"
              @dblclick.stop
              @touchstart.stop
            >
              <i class="fas fa-camera"></i>
            </button>
            <button
              type="button"
              class="review-notify-btn"
              title="Schedule this work order or notify the tenant"
              @click.stop="openReviewNoticeDialog"
              @dblclick.stop
              @touchstart.stop
            >
              <i class="fas fa-calendar-alt"></i>
              <span class="review-notify-bar" aria-hidden="true"></span>
              <i class="fas fa-bell"></i>
            </button>
            <button
              type="button"
              class="review-start-btn"
              :class="{
                'review-start-btn-done': !!reviewCurrentWorkOrder.startedOn,
              }"
              :disabled="
                !!reviewCurrentWorkOrder.startedOn ||
                startingWorkOrderId === reviewCurrentWorkOrder.id
              "
              :title="
                reviewCurrentWorkOrder.startedOn
                  ? 'Work order started'
                  : 'Mark this work order as started'
              "
              @click.stop="startReviewWorkOrder(reviewCurrentWorkOrder)"
              @dblclick.stop
              @touchstart.stop
            >
              {{
                reviewStartButtonText(reviewCurrentWorkOrder)
              }}
            </button>
          </div>
        </div>
        <div class="review-copy">
          <div class="review-copy-tools">
            <button
              type="button"
              class="review-tts-btn"
              title="Merge work orders at this property"
              @click.stop="openWorkOrderMergeFromReview"
              @dblclick.stop
              @touchstart.stop
            >
              <i class="fas fa-code-merge"></i>
            </button>
            <div class="review-voice-slot">
              <button
                type="button"
                class="review-tts-btn review-voice-btn"
                :class="{ 'review-voice-btn-has-note': !!reviewVoiceNote }"
                :title="
                  reviewVoiceNote
                    ? 'Play voice message'
                    : 'Record a voice message'
                "
                @click.stop="openReviewVoice(!!reviewVoiceNote)"
                @dblclick.stop
                @touchstart.stop
              >
                <i v-if="reviewVoiceNote" class="fas fa-play"></i>
                <span v-else class="review-voice-square" aria-hidden="true"></span>
              </button>
              <span v-if="reviewVoiceNote" class="review-voice-waiting">
                message<br />waiting
              </span>
            </div>
            <button
              type="button"
              class="review-tts-btn"
              title="share"
              @click.stop="share(reviewCurrentWorkOrder)"
              @dblclick.stop
              @touchstart.stop
            >
              <i class="fas fa-share-nodes"></i>
            </button>
            <div class="review-voice-slot">
              <button
                type="button"
                class="review-tts-btn"
                :class="{
                  'review-tts-btn-active':
                    speakingWorkOrderId === reviewCurrentWorkOrder.id,
                }"
                :title="
                  speakingWorkOrderId === reviewCurrentWorkOrder.id
                    ? 'Stop reading'
                    : 'Read problem description aloud'
                "
                @click.stop="speakProblemDescription(reviewCurrentWorkOrder)"
                @dblclick.stop
                @touchstart.stop
              >
                <i class="fas fa-headphones"></i>
              </button>
              <span class="review-voice-waiting">read it<br />to me</span>
            </div>
          </div>
          <div
            v-if="reviewStartedLabel(reviewCurrentWorkOrder)"
            class="review-started-line"
          >
            {{ reviewStartedLabel(reviewCurrentWorkOrder) }}
          </div>
          <div
            class="review-details"
          v-html="
            formattedRequestContent(reviewCurrentWorkOrder, {
              clickableName: true,
              clickableAddress: true,
              showScheduled: true,
              showPersonFlags: true,
            })
          "
          @click="onReviewDetailsClick"
          @dblclick="onReviewDetailsDblClick"
          ></div>
        </div>
        <div
          v-if="
            reviewCurrentWorkOrder.note &&
            reviewCurrentWorkOrder.note.trim().length
          "
          class="review-note-block"
        >
          <strong>Notes:</strong>
          <div class="review-note-text">{{ reviewCurrentWorkOrder.note }}</div>
        </div>
        <div
          v-if="latestReviewComment"
          class="review-tenant-reply"
          @click.stop="openReviewThread"
          @dblclick.stop
          @touchstart.stop
        >
          <div class="review-tenant-reply-main">
            <div class="review-tenant-reply-label">{{ latestReviewCommentLabel }}</div>
            <div
              class="review-tenant-reply-text"
              v-html="reviewCommentPhoneHtml(latestReviewComment.message)"
              @click="onReviewCommentPhoneClick"
            ></div>
          </div>
          <div class="review-tenant-reply-icon-wrap">
            <span class="review-comment-count"
              >({{ commentCountForWorkOrder(reviewCurrentWorkOrder) }})</span
            >
            <i
              class="fas fa-comment-dots review-tenant-reply-icon"
              title="Open comments"
            ></i>
          </div>
        </div>
        <div v-else class="review-comment-start-row">
          <span class="review-comment-count"
            >({{ commentCountForWorkOrder(reviewCurrentWorkOrder) }})</span
          >
          <button
            type="button"
            class="review-comment-start"
            title="Add a comment"
            @click.stop="openReviewThread"
            @dblclick.stop
            @touchstart.stop
          >
            <i class="fas fa-comment-dots"></i>
          </button>
        </div>
        <div v-if="reviewPhotoLoading" class="review-photo-loading">
          <i class="fas fa-spinner fa-spin"></i>
        </div>
        <div
          v-else-if="reviewCurrentPhotoUrl"
          class="review-photo-carousel"
        >
          <button
            v-if="reviewPhotos.length > 1"
            type="button"
            class="review-photo-nav"
            title="Previous photo"
            @click.stop="reviewPhotoPrev"
            @dblclick.stop
            @touchstart.stop
          >
            <i class="fas fa-chevron-left"></i>
          </button>
          <div class="review-photo-frame">
            <img
              :src="reviewCurrentPhotoUrl"
              class="review-first-photo"
              alt="Work order photo"
              @click.stop="openReviewPhotoFullscreen"
              @dblclick.stop
              @touchstart.stop
            />
            <p
              v-if="reviewCurrentPhotoCaption"
              class="review-photo-caption"
            >
              {{ reviewCurrentPhotoCaption }}
            </p>
            <p
              v-if="reviewPhotos.length > 1"
              class="review-photo-count"
            >
              {{ reviewPhotoIndex + 1 }} / {{ reviewPhotos.length }}
            </p>
          </div>
          <button
            v-if="reviewPhotos.length > 1"
            type="button"
            class="review-photo-nav"
            title="Next photo"
            @click.stop="reviewPhotoNext"
            @dblclick.stop
            @touchstart.stop
          >
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
      <div
        v-else
        class="review-body review-empty"
        @touchstart.passive="onReviewTouchStart"
        @touchmove.passive="onReviewTouchMove"
        @touchend.passive="onReviewTouchEnd"
        @touchcancel.passive="onReviewTouchCancel"
      >
        No open work orders to review.
      </div>

      <div
        v-if="showBriefingHint && !showDailyBriefing"
        class="briefing-hint"
        role="note"
        @click="hideBriefingHint"
      >
        <div class="briefing-hint-reminder">
          <p class="briefing-hint-reminder-title">Reminder:</p>
          <ul>
            <li>Always press 'Start' when arriving to a work order</li>
            <li>
              Take photos using the camera button on the work order when you're
              done
            </li>
            <li>
              Always use the scheduling feature to keep tenants informed
            </li>
            <li>
              Use the new recording feature on a work order to easily leave
              voice notes for that work order
            </li>
          </ul>
        </div>
        <div class="briefing-hint-callout">
          <button
            type="button"
            class="briefing-hint-dismiss"
            @click.stop="hideBriefingHintForever"
          >
            don't show this again
          </button>
          <p class="briefing-hint-text">check out<br />new feature</p>
          <svg
            class="briefing-hint-arrow"
            viewBox="0 0 40 72"
            aria-hidden="true"
          >
            <line
              x1="20"
              y1="2"
              x2="20"
              y2="46"
              stroke="currentColor"
              stroke-width="4"
              stroke-linecap="round"
            />
            <polygon points="4,42 20,68 36,42" fill="currentColor" />
          </svg>
        </div>
      </div>

      <div class="review-footer">
        <button
          type="button"
          class="review-btn review-btn-bar review-briefing-btn"
          title="Daily briefing"
          @click="openDailyBriefing"
        >
          <i class="fas fa-file-alt" aria-hidden="true"></i>
        </button>
        <button
          type="button"
          class="review-btn review-btn-bar"
          :disabled="!reviewCurrentWorkOrder"
          @click="openReviewAssignPopup"
        >
          Assign
        </button>
        <button type="button" class="review-btn" @click="closeReviewPopup">
          Classic
        </button>
        <button
          type="button"
          class="review-btn review-btn-bar"
          :disabled="!reviewCurrentWorkOrder"
          @click="reviewMarkDone"
        >
          Mark Done
        </button>
        <div
          class="review-assigned-name review-assigned-name-clickable"
          title="Filter by assignee"
          @click="onReviewAssignedNameClick"
        >
          <i
            v-if="!reviewAssignedToLabel"
            class="fas fa-filter review-assigned-filter-icon"
            aria-hidden="true"
          ></i>
          <span v-else class="review-assigned-name-label">{{
            reviewAssignedToLabel
          }}</span>
        </div>
      </div>
    </div>

    <div
      v-if="showDailyBriefing"
      class="daily-briefing"
      @click.stop
    >
      <div class="daily-briefing-title">
        <span>daily briefing</span>
        <div class="daily-briefing-title-actions">
          <button
            type="button"
            class="review-header-btn"
            title="Print"
            :disabled="dailyBriefingLoading"
            @click="printDailyBriefing"
          >
            <i class="fas fa-print"></i>
          </button>
          <button
            type="button"
            class="review-header-btn"
            title="Close"
            @click="closeDailyBriefing"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
      <div class="daily-briefing-scroll">
        <div
          v-if="dailyBriefingLoading"
          class="daily-briefing-status"
        >
          Loading…
        </div>
        <textarea
          v-else-if="dailyBriefingEditing"
          v-model="dailyBriefingDraft"
          class="daily-briefing-editor"
          spellcheck="true"
        ></textarea>
        <div
          v-else
          class="daily-briefing-note"
          v-html="dailyBriefingHtml"
          @click="onDailyBriefingCalendarChip"
        ></div>
      </div>
      <div class="daily-briefing-bar">
        <span class="daily-briefing-meta">{{ dailyBriefingMeta }}</span>
        <button
          type="button"
          class="daily-briefing-edit"
          :disabled="dailyBriefingLoading || dailyBriefingSaving"
          @click="onDailyBriefingEdit"
        >
          {{ dailyBriefingEditing ? 'Save' : 'Edit' }}
        </button>
      </div>
    </div>

    <div
      v-if="showReviewVoice"
      class="review-voice-overlay"
      @click.self="closeReviewVoice"
    >
      <div
        class="review-voice-dialog"
        role="dialog"
        aria-label="Work order voice message"
        @click.stop
      >
        <div class="review-voice-dialog-bar">
          <span>Voice message</span>
          <button
            type="button"
            class="review-voice-close"
            title="Close"
            @click="closeReviewVoice"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
        <audio
          ref="reviewVoicePlayer"
          class="review-voice-player"
          playsinline
          webkit-playsinline
          preload="auto"
        ></audio>
        <canvas
          ref="reviewVoiceSpectrum"
          class="review-voice-spectrum"
          :class="{ 'review-voice-spectrum-live': reviewVoiceRecording }"
          aria-hidden="true"
        ></canvas>
        <div class="review-voice-actions">
          <button
            type="button"
            class="review-voice-action"
            :disabled="reviewVoiceSaving"
            @click.stop="toggleReviewVoiceRecord"
          >
            <span
              class="review-voice-square"
              :class="{ 'review-voice-square-live': reviewVoiceRecording }"
              aria-hidden="true"
            ></span>
            <span>{{
              reviewVoiceRecording
                ? 'Stop'
                : reviewVoiceSaving
                  ? 'Saving…'
                  : 'Record'
            }}</span>
          </button>
          <button
            type="button"
            class="review-voice-action"
            :disabled="
              !reviewVoiceNote || reviewVoiceRecording || reviewVoiceSaving
            "
            :title="
              reviewVoicePlaying && !reviewVoicePaused ? 'Pause' : 'Play audio'
            "
            @click.stop="toggleReviewVoicePlay"
          >
            <i
              class="fas"
              :class="
                reviewVoicePlaying && !reviewVoicePaused
                  ? 'fa-pause'
                  : 'fa-play'
              "
            ></i>
            <span>{{
              reviewVoicePlaying && !reviewVoicePaused ? 'Pause' : 'Play'
            }}</span>
          </button>
          <button
            type="button"
            class="review-voice-action"
            title="Delete"
            :disabled="
              !reviewVoiceNote || reviewVoiceDeleting || reviewVoiceRecording
            "
            @click.stop="deleteReviewVoice"
          >
            <i
              :class="
                reviewVoiceDeleting
                  ? 'fas fa-spinner fa-spin'
                  : 'fas fa-trash'
              "
            ></i>
            <span>Delete</span>
          </button>
        </div>
        <div
          class="review-voice-scrub"
          :class="{
            'review-voice-scrub-idle': !(reviewVoicePlaying || reviewVoicePaused),
          }"
          @click.stop
        >
          <span class="review-voice-scrub-time">{{
            reviewVoiceClock(reviewVoiceCurrent)
          }}</span>
          <input
            ref="reviewVoiceScrub"
            class="review-voice-scrub-bar"
            type="range"
            min="0"
            step="0.05"
            :max="reviewVoiceDuration || 0"
            :value="reviewVoiceCurrent"
            :disabled="!(reviewVoicePlaying || reviewVoicePaused)"
            aria-label="Playback position"
            @input="onReviewVoiceScrubInput"
            @change="onReviewVoiceScrubCommit"
          />
          <span class="review-voice-scrub-time">{{
            reviewVoiceClock(reviewVoiceDuration)
          }}</span>
        </div>
        <div v-if="reviewVoiceError" class="review-voice-error">
          {{ reviewVoiceError }}
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="showReviewNoticeDialog"
    class="notes-popup review-thread-overlay"
    @click.self="closeReviewNoticeDialog"
  >
    <div class="notes-popup-content review-notice-popup" @click.stop>
      <div class="notes-popup-title review-notice-title">
        <span>Schedule or notify</span>
        <button
          type="button"
          class="review-assign-close"
          title="Close"
          @click="closeReviewNoticeDialog"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
      <p class="review-notice-when">
        {{ reviewScheduledDateLabel || 'Not scheduled' }}
      </p>
      <button
        type="button"
        class="review-notice-time"
        @click="editReviewNoticeSchedule"
      >
        Schedule this
      </button>
      <hr class="review-notice-divider" />
      <div class="review-notice-actions">
        <button
          type="button"
          class="review-notice-send"
          :disabled="!!reviewNoticeSending"
          @click="sendReviewTenantNotice('request_access')"
        >
          {{
            reviewNoticeSending === 'request_access'
              ? 'Sending…'
              : 'Request Access'
          }}
        </button>
        <button
          type="button"
          class="review-notice-send"
          :disabled="!!reviewNoticeSending"
          @click="sendReviewTenantNotice('arriving_soon')"
        >
          {{
            reviewNoticeSending === 'arriving_soon'
              ? 'Sending…'
              : 'Arriving Soon'
          }}
        </button>
      </div>
      <p class="review-notice-result">
        {{ reviewNoticeResult || '\u00a0' }}
      </p>
    </div>
  </div>

  <div
    v-if="showReviewAssignPopup"
    class="notes-popup review-thread-overlay"
    @click.self="closeReviewAssignPopup"
  >
    <div class="notes-popup-content review-assign-popup" @click.stop>
      <div class="notes-popup-title review-assign-title">
        <span>{{
          reviewAssignPopupMode === 'filter' ? 'Filter by' : 'Assign to'
        }}</span>
        <button
          type="button"
          class="review-assign-close"
          title="Close"
          @click="closeReviewAssignPopup"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="review-assign-list">
        <button
          v-if="reviewAssignPopupMode === 'assign'"
          type="button"
          class="review-assign-user"
          :class="{ 'review-assign-user-selected': reviewAssignNobodySelected }"
          :disabled="reviewAssignSaving"
          @click="assignReviewWorkOrder(null)"
        >
          Nobody
        </button>
        <button
          v-for="user in reviewStaffUsers"
          :key="user.id"
          type="button"
          class="review-assign-user"
          :class="{
            'review-assign-user-selected': reviewAssignUserSelected(user),
          }"
          :disabled="reviewAssignPopupMode !== 'filter' && reviewAssignSaving"
          @click="onReviewAssignUserClick(user)"
        >
          {{ reviewStaffUserLabel(user) }}
        </button>
        <div v-if="!reviewStaffUsers.length" class="review-assign-empty">
          No staff users found.
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="showReviewThread"
    class="notes-popup review-thread-overlay"
    @click.self="closeReviewThread"
  >
    <div class="notes-popup-content review-thread-popup" @click.stop>
      <div class="notes-popup-title">Work order comments</div>
      <div class="review-thread-tenant-notice">
        Anything you comment or reply here is sent to tenants.
      </div>
      <div v-if="reviewCommentsLoading" class="review-thread-status">
        Loading…
      </div>
      <div v-else-if="!reviewComments.length" class="review-thread-status">
        No comments yet.
      </div>
      <div v-else class="review-thread-list">
        <div
          v-for="g in reviewCommentThreads"
          :key="g.parent.id"
          class="review-thread-group"
        >
          <div
            class="review-thread-item"
            :class="[
              reviewCommentClass(g.parent),
              {
                'review-thread-item-selected':
                  reviewSelectedCommentId === g.parent.id,
              },
            ]"
            @click.stop="toggleReviewCommentSelect(g.parent)"
            @dblclick.stop="startReviewCommentEdit(g.parent)"
            @touchstart.stop="onCommentTouchStart($event, g.parent)"
            @touchmove.stop.passive="onCommentTouchMove"
            @touchend.stop="onCommentTouchEnd"
            @touchcancel.stop="onCommentTouchCancel"
            :title="
              canEditReviewComments
                ? 'Double-click or long-press to edit'
                : undefined
            "
          >
            <div class="review-thread-meta">
              {{ reviewCommentAuthor(g.parent) }}
              <span v-if="g.parent.createdAt">
                · {{ formatWorkOrderStartedAt(g.parent.createdAt) }}</span
              >
            </div>
            <template v-if="reviewEditingCommentId === g.parent.id">
              <textarea
                v-model="reviewEditDraft"
                class="review-thread-edit"
                rows="3"
                maxlength="2000"
                @click.stop
                @dblclick.stop
              ></textarea>
              <div class="review-thread-edit-actions">
                <button
                  type="button"
                  class="btn btn-secondary"
                  @click.stop="cancelReviewCommentEdit"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="btn btn-primary"
                  :disabled="reviewEditSaving || !reviewEditDraft.trim()"
                  @click.stop="saveReviewCommentEdit"
                >
                  {{ reviewEditSaving ? 'Saving…' : 'Save' }}
                </button>
              </div>
            </template>
            <div
              v-else
              class="review-thread-text"
              v-html="reviewCommentPhoneHtml(g.parent.message)"
              @click="onReviewCommentPhoneClick"
              @dblclick="onReviewCommentPhoneClick"
            ></div>
          </div>
          <div
            v-for="r in g.replies"
            :key="r.id"
            class="review-thread-item review-thread-reply"
            :class="[
              reviewCommentClass(r),
              {
                'review-thread-item-selected':
                  reviewSelectedCommentId === r.id,
              },
            ]"
            @click.stop="toggleReviewCommentSelect(r)"
            @dblclick.stop="startReviewCommentEdit(r)"
            @touchstart.stop="onCommentTouchStart($event, r)"
            @touchmove.stop.passive="onCommentTouchMove"
            @touchend.stop="onCommentTouchEnd"
            @touchcancel.stop="onCommentTouchCancel"
            :title="
              canEditReviewComments
                ? 'Double-click or long-press to edit'
                : undefined
            "
          >
            <div class="review-thread-meta">
              {{ reviewCommentAuthor(r) }}
              <span v-if="r.createdAt">
                · {{ formatWorkOrderStartedAt(r.createdAt) }}</span
              >
            </div>
            <template v-if="reviewEditingCommentId === r.id">
              <textarea
                v-model="reviewEditDraft"
                class="review-thread-edit"
                rows="3"
                maxlength="2000"
                @click.stop
                @dblclick.stop
              ></textarea>
              <div class="review-thread-edit-actions">
                <button
                  type="button"
                  class="btn btn-secondary"
                  @click.stop="cancelReviewCommentEdit"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="btn btn-primary"
                  :disabled="reviewEditSaving || !reviewEditDraft.trim()"
                  @click.stop="saveReviewCommentEdit"
                >
                  {{ reviewEditSaving ? 'Saving…' : 'Save' }}
                </button>
              </div>
            </template>
            <div
              v-else
              class="review-thread-text"
              v-html="reviewCommentPhoneHtml(r.message)"
              @click="onReviewCommentPhoneClick"
              @dblclick="onReviewCommentPhoneClick"
            ></div>
          </div>
        </div>
      </div>
      <textarea
        v-model="reviewCommentDraft"
        class="notes-textarea review-thread-input"
        rows="3"
        maxlength="2000"
        :placeholder="
          !reviewComments.length
            ? 'Add a comment…'
            : reviewSelectedCommentId
              ? 'Reply to the selected comment…'
              : 'Reply to the latest comment…'
        "
      ></textarea>
      <div class="notes-popup-actions">
        <button type="button" class="btn btn-secondary" @click="closeReviewThread">
          Close
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="reviewCommentSaving || !reviewCommentDraft.trim()"
          @click="submitReviewThreadReply"
        >
          {{
            reviewCommentSaving
              ? 'Sending…'
              : !reviewComments.length
                ? 'Comment'
                : 'Reply'
          }}
        </button>
      </div>
    </div>
  </div>

  <div
    v-if="showReviewPhotoFullscreen && reviewCurrentPhotoUrl"
    class="review-photo-fullscreen"
    @click.self="closeReviewPhotoFullscreen"
  >
    <button
      type="button"
      class="review-photo-fullscreen-close"
      title="Close"
      @click.stop="closeReviewPhotoFullscreen"
    >
      <i class="fas fa-times"></i>
    </button>
    <button
      v-if="reviewPhotos.length > 1 && !reviewPhotoZoomed"
      type="button"
      class="review-photo-fullscreen-nav review-photo-fullscreen-prev"
      title="Previous photo"
      @click.stop="reviewPhotoPrev"
    >
      <i class="fas fa-chevron-left"></i>
    </button>
    <div class="review-photo-fullscreen-frame">
      <img
        :src="reviewCurrentPhotoUrl"
        class="review-photo-fullscreen-img"
        alt="Work order photo"
        draggable="false"
        :style="reviewPhotoFullscreenStyle"
        @click.stop="onReviewPhotoFullscreenClick"
        @touchstart.stop="onReviewPhotoTouchStart"
        @touchmove.stop.prevent="onReviewPhotoTouchMove"
        @touchend.stop="onReviewPhotoTouchEnd"
        @touchcancel.stop="onReviewPhotoTouchEnd"
      />
      <p
        v-if="reviewCurrentPhotoCaption"
        class="review-photo-fullscreen-caption"
      >
        {{ reviewCurrentPhotoCaption }}
      </p>
      <p
        v-if="reviewPhotos.length > 1"
        class="review-photo-fullscreen-count"
      >
        {{ reviewPhotoIndex + 1 }} / {{ reviewPhotos.length }}
      </p>
    </div>
    <button
      v-if="reviewPhotos.length > 1 && !reviewPhotoZoomed"
      type="button"
      class="review-photo-fullscreen-nav review-photo-fullscreen-next"
      title="Next photo"
      @click.stop="reviewPhotoNext"
    >
      <i class="fas fa-chevron-right"></i>
    </button>
  </div>

  <!-- Hard Reload Confirmation Dialog -->
  <div v-if="showHardReloadConfirm" class="dialog-overlay" @click.stop>
    <div class="dialog-content" @click.stop>
      <div class="dialog-header">Confirm Hard Reload</div>
      <div class="dialog-body">
        <p>
          This will clear the local cache and reload all work orders from the
          server.
        </p>
        <p>Are you sure you want to continue?</p>
      </div>
      <div class="dialog-actions">
        <button @click="cancelHardReload">Cancel</button>
        <button class="confirm-button" @click="confirmHardReload">
          Reload
        </button>
      </div>
    </div>
  </div>

  <!-- Schedule Work Order Dialog -->
  <div
    v-if="showScheduleDialog"
    class="sched-overlay"
    @click.self="closeScheduleDialog"
  >
    <div class="sched-dialog" @click.stop>
      <div class="sched-dialog-header">
        <h3>{{ scheduleDialogTitle }}</h3>
        <i class="fas fa-times sched-close" @click="closeScheduleDialog"></i>
      </div>
      <div class="sched-dialog-body">
        <!-- Step: Choice -->
        <div v-if="scheduleStep === 'choice'" class="sched-stage">
          <p class="sched-instruction">
            {{ scheduleChoicePrompt }}
          </p>
          <div
            v-if="selectedScheduleWO && selectedScheduleWO.allow_entry === 1"
            class="sched-info-box"
          >
            <i class="fas fa-check-circle" style="color: #4caf50"></i>
            Tenant has granted permission to enter.
          </div>
          <div class="sched-choice-buttons">
            <button class="sched-choice-btn" @click="startSelfSchedule">
              <i class="fas fa-calendar-plus"></i>
              <span>Add to Calendar</span>
              <!-- eslint-disable-next-line prettier/prettier -->
              <small
                >Self-schedule this work order<br />tenant permission isn't
                required.</small
              >
            </button>
            <button class="sched-choice-btn" @click="startAskTenant">
              <i class="fas fa-envelope"></i>
              <span>Ask Tenants</span>
              <small>Email tenant<br />for permission / scheduling</small>
            </button>
          </div>
        </div>

        <!-- Step: Day Picker -->
        <div v-if="scheduleStep === 'dayPicker'" class="sched-stage">
          <div class="sched-fee-line">
            <label
              >Fee: $<input
                type="number"
                v-model="scheduleFee"
                min="0"
                step="1"
                class="sched-fee-input"
            /></label>
          </div>
          <div v-if="scheduleMode === 'tenant'" class="sched-info-box">
            Select a tentative time slot. The tenant will be contacted to
            confirm or reschedule.
          </div>
          <p class="sched-instruction">Select a day:</p>
          <div v-if="scheduleLoading" class="sched-loading">
            <i class="fas fa-spinner fa-spin"></i> Loading available slots...
          </div>
          <div v-else class="sched-day-gallery">
            <div
              v-for="n in scheduleDayOffset"
              :key="'spacer-' + n"
              class="sched-day-spacer"
            ></div>
            <div
              v-for="day in scheduleWorkDays"
              :key="day.date"
              class="sched-day-card"
              :class="{
                'sched-day-unavailable': !day.blocks.some(
                  (b) => b.available || b.conflictTitle,
                ),
              }"
              @click="onScheduleDayCardClick(day)"
            >
              <div class="sched-day-name">{{ day.dayOfWeek }}</div>
              <div class="sched-day-date">
                {{ day.month }}/{{ day.dayNumber }}
              </div>
            </div>
          </div>
          <div class="sched-day-nav">
            <button class="sched-btn-back" @click="scheduleStep = 'choice'">
              <i class="fas fa-arrow-left"></i> Back
            </button>
            <button
              v-if="scheduleStartDate"
              class="sched-btn-back"
              @click="schedulePreviousDays"
            >
              <i class="fas fa-arrow-left"></i> Previous
            </button>
            <button class="sched-btn-back" @click="scheduleNextDays">
              Future <i class="fas fa-arrow-right"></i>
            </button>
          </div>
          <div v-if="scheduleError" class="sched-error">
            {{ scheduleError }}
          </div>
        </div>

        <!-- Step: Time Picker -->
        <div v-if="scheduleStep === 'timePicker'" class="sched-stage">
          <p class="sched-instruction">
            {{ scheduleSelectedDay?.dayOfWeek }},
            {{ scheduleSelectedDay?.month }}/{{
              scheduleSelectedDay?.dayNumber
            }}
            —
            {{
              scheduleMode === 'self'
                ? 'Tap one or more consecutive slots:'
                : 'Select a time:'
            }}
          </p>
          <div
            v-if="scheduleMode === 'self' && scheduleSelectedBlocks.length > 0"
            class="sched-selection-summary"
          >
            <span
              >{{ scheduleSelectedBlocks.length }} slot{{
                scheduleSelectedBlocks.length > 1 ? 's' : ''
              }}
              selected: {{ scheduleSelectionLabel }}</span
            >
            <button class="sched-clear-btn" @click="clearScheduleBlocks">
              Clear
            </button>
          </div>
          <div class="sched-time-list">
            <div
              v-for="block in scheduleSelectedDay?.blocks"
              :key="block.start"
              class="sched-time-card"
              :class="{
                'sched-time-unavailable':
                  !block.available && !block.conflictTitle,
                'sched-time-conflict': block.conflictTitle,
                'sched-time-selected': isBlockSelected(block),
              }"
              @click="selectScheduleBlock(block)"
            >
              {{ block.label }}
              <span
                v-if="!block.available && !block.conflictTitle"
                class="sched-time-badge"
                >Unavailable</span
              >
              <span
                v-if="block.conflictTitle"
                class="sched-time-badge sched-time-badge-conflict"
                >existing appt.</span
              >
              <i
                v-if="isBlockSelected(block)"
                class="fas fa-check"
                style="color: #4caf50"
              ></i>
            </div>
          </div>
          <div class="sched-time-actions">
            <button class="sched-btn-back" @click="scheduleStep = 'dayPicker'">
              <i class="fas fa-arrow-left"></i> Back to days
            </button>
            <button
              v-if="
                scheduleMode === 'self' && scheduleSelectedBlocks.length > 0
              "
              class="sched-btn-confirm-time"
              @click="confirmSelectedBlocks"
            >
              Confirm Time <i class="fas fa-arrow-right"></i>
            </button>
          </div>
          <div v-if="scheduleError" class="sched-error">
            {{ scheduleError }}
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Schedule Confirmation Popup -->
  <div v-if="showScheduleConfirm" class="sched-overlay" @click.stop>
    <div class="sched-confirm-popup" @click.stop>
      <h3>
        {{
          scheduleMode === 'self'
            ? 'Confirm Schedule'
            : 'Confirm &amp; Send to Tenant'
        }}
      </h3>
      <p>
        <strong>Address:</strong>
        {{ stripMarkTags(selectedScheduleWO?.property_address) }}
      </p>
      <p>
        <strong>Date:</strong> {{ scheduleSelectedDay?.dayOfWeek }},
        {{ scheduleSelectedDay?.month }}/{{ scheduleSelectedDay?.dayNumber }}
      </p>
      <p>
        <strong>Time:</strong>
        {{ scheduleSelectionLabel || scheduleSelectedBlock?.label }}
      </p>
      <p v-if="scheduleMode === 'self'">
        <strong>Fee:</strong> ${{ scheduleFee || '0' }}
      </p>
      <p v-if="scheduleMode === 'tenant'">
        <strong>Fee for custom scheduling:</strong> ${{
          normalizedScheduleFeeDisplay
        }}
      </p>
      <div class="sched-confirm-buttons">
        <button @click="cancelScheduleConfirm" class="sched-btn-cancel">
          Cancel
        </button>
        <button
          @click="confirmSchedule"
          :disabled="scheduleLoading"
          class="sched-btn-confirm"
        >
          {{
            scheduleLoading
              ? 'Saving...'
              : scheduleMode === 'self'
                ? 'Confirm'
                : 'Send Email'
          }}
        </button>
      </div>
    </div>
  </div>

  <!-- Double-book confirmation popup -->
  <div v-if="showDoubleBookConfirm" class="sched-overlay" @click.stop>
    <div class="sched-confirm-popup" @click.stop>
      <h3>Double-book?</h3>
      <p>
        This time slot overlaps with
        <strong>{{ doubleBookBlock?.conflictTitle }}</strong
        >.
      </p>
      <p>Do you still want to double-book?</p>
      <div class="sched-confirm-buttons">
        <button @click="cancelDoubleBook" class="sched-btn-cancel">
          Cancel
        </button>
        <button @click="confirmDoubleBook" class="sched-btn-confirm">
          Yes
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked';
import axios from '@/axiosConfig'; // Adjust based on your axios configuration
import { readNavigatorOnline } from '@/utils/networkBanner';
import { OUTBOUND_HOLD_TEXT, deliverOrQueue } from '@/utils/outboundQueue';
import { io } from 'socket.io-client'; // Import socket.io-client
import eventBus from './eventBus.js'; // Import eventBus
import imageCompression from 'browser-image-compression'; // Add this import
import MotdModal from './MotdModal.vue'; // Import MOTD modal component
import QRCode from 'qrcode';
import {
  PHOTO_CACHE_LIMIT,
  cachedPhotoCount,
  forgetOldPhotoDatabases,
  getCachedPhotosForWorkOrder,
  putCachedPhoto,
  putCachedPhotoPrefill,
  removeCachedPhoto,
  touchCachedPhotos,
} from '@/utils/workOrderPhotoCache';
import {
  cameraPhotoFile,
  saveCameraPhotoToGallery,
  gallerySaveState,
  gallerySaveMessage,
} from '@/utils/saveCameraPhotoToGallery';
import { workOrderCardDate } from '@/utils/workOrderCardDate';
import {
  reviewLocationKeyForOrder,
  workOrderMatchesReviewSearch,
  formatPropertyAddressLine,
  reviewPersonFlagHtml,
  reviewDetailsInteractiveTarget,
  parseReviewWorkOrderId,
  workOrderIdFromSearchQuery,
  workOrderIdFromReviewQuery,
  reviewVoiceLocalhostUrl,
  reviewVoicePageCanRecord,
  shouldOpenReviewForWorkOrderLink,
  workOrderLinkIsFinished,
  reviewOpenFocusId,
  newestWorkOrderIndex,
  reviewEscapeResetsToDefault,
} from '@/utils/reviewWorkOrderFilters';
import {
  workOrderTransferButtonHtml,
  isTransferButtonTarget,
} from '@/utils/workOrderTransfer';
import {
  staffUserLabel,
  sortStaffUsers,
  assignedToFooterLabel,
  assigneeFilterFromStaffUser,
  reviewAssignedNameClick,
  reviewAssigneePickerId,
  workOrderMatchesReviewAssignee,
} from '@/utils/reviewWorkOrderAssign';
import {
  stepPhotoIndex,
  reviewPhotoCaption,
  reviewPhotoIsZoomed,
  startReviewPhotoGesture,
  updateReviewPhotoGesture,
  finishReviewPhotoGesture,
  rememberedReviewPhotos,
  mergeDisplayedPhotos,
  sameReviewPhotoSet,
} from '@/utils/reviewWorkOrderPhotos';
import { notesDialogStepsAfterSave } from '@/utils/workOrderNotesDialog';
import { nextScheduleBlocks } from '@/utils/scheduleTimeBlocks';
import { reviewStartButtonLabel } from '@/utils/reviewWorkOrderStart';
import {
  briefingMetaLine,
  briefingHintSeen,
  markBriefingHintSeen,
  briefingIdsToCloseTogether,
  removeClosedWorkOrdersFromBriefing,
} from '@/utils/dailyBriefing';
import {
  formatVoicePlaybackClock,
  clampVoiceSeekTime,
  sniffAudioMimeFromBytes,
  voicePlaybackParams,
  readAudioDuration,
  revealAudioDuration,
  measureBlobDuration,
  applyScrubBar,
} from '@/utils/voiceMessages';
import {
  renderNoteMarkdown,
  repairTruncatedMarkdownLinks,
} from '@/utils/noteMarkdown';
import { calendarChipDate, calendarListPath } from '@/utils/calendarDates';
import {
  workOrderMergeCandidates,
  filterWorkOrderMergeCandidates,
  mergedProblemDescription,
  mergeCloseNote,
} from '@/utils/workOrderMerge';
import {
  pickFemaleSpeechVoice,
  speechVoicesFrom,
} from '@/utils/speechVoice';
import { linkPhoneTelHtml } from '@/utils/chatPhoneLinks';
import {
  workOrderCacheNeedsFullReload,
  workOrderCacheRecord,
} from '@/utils/workOrderCache';

let socket;

const REVIEW_SORT_STORAGE_KEY = 'mworkorders-review-sort-dir';
const REVIEW_DEFAULT_PIN_KEY = 'mworkorders-review-default-pinned';
const REVIEW_LAST_ID_KEY = 'mworkorders-review-last-id';

function loadReviewLastWorkOrderId() {
  try {
    return parseReviewWorkOrderId(localStorage.getItem(REVIEW_LAST_ID_KEY));
  } catch {
    return null;
  }
}

function saveReviewLastWorkOrderId(id) {
  const parsed = parseReviewWorkOrderId(id);
  try {
    if (!parsed) {
      localStorage.removeItem(REVIEW_LAST_ID_KEY);
    } else {
      localStorage.setItem(REVIEW_LAST_ID_KEY, String(parsed));
    }
  } catch {
    /* ignore quota / private mode */
  }
  return parsed;
}

function loadReviewSortDir() {
  try {
    const v = localStorage.getItem(REVIEW_SORT_STORAGE_KEY);
    return v === 'desc' ? 'desc' : 'asc';
  } catch {
    return 'asc';
  }
}

function saveReviewSortDir(dir) {
  try {
    localStorage.setItem(
      REVIEW_SORT_STORAGE_KEY,
      dir === 'desc' ? 'desc' : 'asc',
    );
  } catch {
    /* ignore quota / private mode */
  }
}

function loadReviewDefaultPinned() {
  try {
    return localStorage.getItem(REVIEW_DEFAULT_PIN_KEY) === '1';
  } catch {
    return false;
  }
}

function saveReviewDefaultPinned(pinned) {
  try {
    if (pinned) {
      localStorage.setItem(REVIEW_DEFAULT_PIN_KEY, '1');
    } else {
      localStorage.removeItem(REVIEW_DEFAULT_PIN_KEY);
    }
  } catch {
    /* ignore quota / private mode */
  }
}

function stripHtmlSimple(str) {
  return str ? String(str).replace(/<[^>]*>/g, '').trim() : '';
}

function reviewLocationLabelForOrder(order) {
  return (
    stripHtmlSimple(order?.parentAddress) ||
    stripHtmlSimple(order?.property_address) ||
    'Unknown'
  );
}

export default {
  name: 'WorkOrders',
  components: {
    MotdModal,
  },

  data() {
    return {
      showNewWorkOrder: false,

      newWorkOrder: {
        name: '',
        phone: '',
        date: new Date().toISOString().split('T')[0],
        propertyAddress: '',
        selectedPropertyId: null,
        propertyId: null,
        peopleId: null,
        problemDescription: '',
        allowEntry: false,
      },

      propertyOptions: [],
      isCreatingWorkOrder: false,

      // Autocomplete functionality
      nameSuggestions: [],
      showNameSuggestions: false,
      selectedSuggestionIndex: -1,
      searchTimeout: null,
      showTransferDialog: false,
      transferWorkOrderId: null,
      transferQuery: '',
      transferSuggestions: [],
      transferSelected: null,
      transferSuggestionIndex: -1,
      transferSearchTimeout: null,
      transferSaving: false,
      transferError: '',

      validationError: '',
      fieldErrors: {
        name: false,
        phone: false,
        propertyAddress: false,
        problemDescription: false,
      },

      showConfirm: false,
      confirmMessage: '',
      resolveConfirm: null,
      showIconBar: 1,
      selectedTileIndex: null,
      showWorkOrder: true,
      workOrders: [],
      workOrderFilter: '',
      onlyCriticalIssues: false,
      prioritySortOrder: null, // null = no priority sorting, 'asc' = ascending, 'desc' = descending
      // voice recorder
      isRecording: false,
      isPlaying: false,
      audioData: null,
      mediaRecorder: null,
      chunks: [],
      recordingLabel: 'Record',
      countdownValue: 3,
      countdownInterval: null,
      recordingDuration: 0,
      showDebug: false,
      logMessage: 'Initializing application...',
      db: null,
      intervalId: null,
      hideDone: true,
      showNotesPopup: false,
      savingWorkOrderNotes: false,
      workOrderNote: '',
      currentWorkOrderId: null,
      pendingCompletionWorkOrderId: null, // Track work order pending completion
      showDocumentPopup: false,
      documentIdToAttach: '',
      showWorkOrderMerge: false,
      workOrderMergePrimaryId: null,
      workOrderMergeQuery: '',
      workOrderMergeSelected: [],
      workOrderMergeSaving: false,
      workOrderMergeError: '',
      showEditWorkOrderPopup: false,
      editingWorkOrderId: null,
      editedName: '',
      editedPhone: '',
      editedPropertyAddress: '',
      editedPropertyId: null,
      editedProblemDescription: '',
      editedAllowEntry: false,
      editedPet: false,
      editedPetName: '',
      editedWorkOrderSpecialInstr: '',
      currentEditPeopleId: null,
      workOrderPhotos: [],
      currentPhotoIndex: 0,
      showFullSizePhoto: false,
      audioDataURL: null,
      audioElement: null,
      showNotification: false,
      notificationMessage: '',
      notificationFading: false,
      notificationTimeout: null,
      showEditCaptionDialog: false,
      editingCaption: '',
      showPhotoUploadDialog: false,
      photoFiles: [], // Changed from photoFile to photoFiles array for multiple photos
      photoCaption: '',
      isUploading: false,
      // Pinch-to-zoom properties
      photoScale: 1,
      photoTranslateX: 0,
      photoTranslateY: 0,
      initialTouchDistance: 0,
      initialScale: 1,
      lastTouchX: 0,
      lastTouchY: 0,
      isPanning: false,
      isZooming: false,
      photoPreviewUrls: [], // Changed from photoPreviewUrl to array for multiple previews
      uploadProgress: 0,

      showUploadErrorDialog: false,
      uploadErrorMessage: '',
      uploadErrorDetails: '',
      filesToUpload: [], // Changed from fileToUpload to array
      isLoadingPhotos: false,
      isInitialLoading: true, // Add loading state for initial mount
      isDailyRefresh: false, // Track if we're doing a daily hard reload
      showSharePopup: false,
      shareText: '',
      shareTitle: '',
      recentUsageData: [], // Store recent work orders usage data

      // Confirmations functionality
      showConfirmationsDialog: false,
      activeTab: 'needed', // 'needed' or 'manage'
      unconfirmedItems: [],
      confirmationsList: [], // List of all confirmations for manage tab
      allConfirmations: [],
      showAddConfirmation: false,
      newConfirmationText: '',
      confirmationResponse: '',
      showingResponseFor: null, // ID of confirmation showing response for
      users: [], // List of all users for assignedTo dropdown
      selectedAssignedTo: null, // Selected user ID for new confirmations
      lastServiceCheckTime: 0, // Track last service check time to avoid rate limiting
      selectedConfirmationId: null, // Track which confirmation is selected for edit/delete
      editingConfirmationId: null, // Track which confirmation is being edited
      editingConfirmationText: '', // Text being edited

      // Summary popup functionality
      showSummaryPopup: false,
      summaryStats: null,
      filteredByAgeIds: null, // Array of IDs to filter by
      ageFilterLabel: '', // Label for the active age filter
      showAgeFilterHint: false, // Control hint visibility with fade
      ageFilterHintTimeout: null, // Store timeout ID for cleanup

      // Full-screen open work order review
      showReviewPopup: false,
      startingWorkOrderId: null,
      reviewIndex: 0,
      reviewIdInput: '',
      reviewLastWorkOrderId: loadReviewLastWorkOrderId(),
      reviewUserNavigated: false,
      reviewLandOnNewest: false,
      reviewIdEditing: false,
      reviewRefreshing: false,
      reviewSortDir: loadReviewSortDir(), // persisted in localStorage
      // When true, open Review as the default view on mount (localStorage)
      reviewDefaultPinned: loadReviewDefaultPinned(),
      reviewDeepLinkId: null,
      showCompletedWorkOrderNotice: false,
      reviewLocationKey: '', // '' = all; else parentId/propertyId key (list-view location)
      reviewSearchOpen: false,
      reviewSearchDraft: '',
      reviewSearchQuery: '',
      reviewPersonFilter: null, // { peopleId, name } when filtering Review by person
      reviewAssigneeFilter: null, // { assignedTo, name } when filtering Review by assignee
      reviewAssignPopupMode: 'assign',
      showReviewAssignPopup: false,
      showDailyBriefing: false,
      showReviewVoice: false,
      reviewVoiceNote: null,
      reviewVoiceRecording: false,
      reviewVoiceSaving: false,
      reviewVoiceDeleting: false,
      reviewVoiceError: '',
      reviewVoicePlaying: false,
      reviewVoicePaused: false,
      reviewVoiceCurrent: 0,
      reviewVoiceDuration: 0,
      reviewVoiceSeeking: false,
      showBriefingHint: false,
      dailyBriefingBody: '',
      dailyBriefingDraft: '',
      dailyBriefingUpdatedAt: null,
      dailyBriefingUpdatedByName: '',
      dailyBriefingEditing: false,
      dailyBriefingLoading: false,
      dailyBriefingSaving: false,
      showReviewNoticeDialog: false,
      reviewNoticeSending: '',
      reviewNoticeResult: '',
      reviewAssignSaving: false,
      reviewTouchStartX: null,
      reviewTouchStartY: null,
      reviewTouchStartTime: 0,
      reviewLongPressTimer: null,
      reviewLongPressFired: false,
      reviewPhotoUrl: '',
      reviewPhotos: [],
      reviewPhotoIndex: 0,
      showReviewPhotoFullscreen: false,
      reviewPhotoScale: 1,
      reviewPhotoPanX: 0,
      reviewPhotoPanY: 0,
      reviewPhotoLoading: false,
      reviewPhotoLoadToken: 0,
      reviewPhotoSets: {},
      reviewPhotoSetOrder: [],

      showReviewThread: false,
      commentThreadWorkOrderId: null,
      reviewSelectedCommentId: null,
      reviewEditingCommentId: null,
      reviewEditDraft: '',
      reviewEditSaving: false,
      commentClickTimer: null,
      commentLongPressTimer: null,
      commentLongPressFired: false,
      commentTouchX: null,
      commentTouchY: null,
      reviewComments: [],
      reviewCommentsForId: null,
      reviewCommentsLoadToken: 0,
      reviewCommentsLoading: false,
      reviewCommentDraft: '',
      reviewCommentSaving: false,
      reviewPrefetchKey: '',

      // Hard reload confirmation
      showHardReloadConfirm: false,

      // View tracking for IntersectionObserver
      intersectionObserver: null,
      viewedWorkOrders: new Set(), // Track which work orders have been viewed

      // Text-to-speech
      speakingWorkOrderId: null,

      // Maintenance lock code popup
      showMaintenanceLockCodePopup: false,
      maintenanceLockCodeLoading: false,
      maintenanceLockCodeError: '',
      maintenanceLockCodeValue: '',
      maintenanceLockCodeAddress: '',
      maintenanceLockCodeCountdown: 0,
      maintenanceLockCodeTimerId: null,

      // Email tenant from active work order card
      showTenantEmailDialog: false,
      tenantEmailWorkOrder: null,
      tenantEmailMessage: '',
      tenantEmailSending: false,
      tenantEmailError: '',

      // Scroll position memory
      savedScrollPosition: 0,

      // List view
      showListView: false,
      openedFromListView: false,
      listViewHideDone: true,
      listViewSearch: '',
      listViewGroupBy: 'date',
      listViewCollapsedGroups: new Set(),
      listViewPrintLoading: false,
      singleWorkOrderPrintLoading: false,

      // Receipts functionality
      showReceiptsPopup: false,
      receipts: [],
      editingReceiptId: null,
      editingReceipt: {
        id: null,
        supplier: '',
        description: '',
        address: '',
        amount: 0,
      },
      supplierOptions: [
        'Ace Hardware',
        'Ferguson',
        'Menards',
        'Lowes',
        'Godwin',
        'Other',
      ],
      currentUserRole: 99, // Default to public role
      currentUserId: null,
      currentUserDisplayName: '',
      currentUserPhone: '',
      // Delete confirmation
      showDeleteConfirm: false,
      receiptToDelete: null,

      // Scheduling
      showScheduleDialog: false,
      scheduleStep: 'choice', // 'choice', 'dayPicker', 'timePicker'
      scheduleMode: null, // 'self' or 'tenant'
      scheduleWorkDays: [],
      scheduleSelectedDay: null,
      scheduleSelectedBlock: null,
      scheduleSelectedBlocks: [],
      scheduleLastRejectedStart: null,
      scheduleFee: '0',
      scheduleLoading: false,
      scheduleError: '',
      scheduleStartDate: null,
      showScheduleConfirm: false,
      showDoubleBookConfirm: false,
      doubleBookBlock: null,
      selectedScheduleWO: null,
      // Receipt search and filter
      receiptSearchInput: '',
      receiptSearchQuery: '',
      receiptSearchDebounceTimer: null,
      receiptYear: new Date().getFullYear(),
      receiptLoadAll: false,
    };
  },

  computed: {
    workOrderMergePrimary() {
      if (this.workOrderMergePrimaryId == null) return null;
      return (
        this.workOrders.find(
          (order) =>
            String(order.id) === String(this.workOrderMergePrimaryId),
        ) || null
      );
    },
    workOrderMergePool() {
      return workOrderMergeCandidates(
        this.workOrders,
        this.workOrderMergePrimary,
      );
    },
    workOrderMergeVisible() {
      return filterWorkOrderMergeCandidates(
        this.workOrderMergePool,
        this.workOrderMergeQuery,
      );
    },
    tenantEmailDialogMeta() {
      const wo = this.tenantEmailWorkOrder;
      if (!wo) return '';
      const name = (wo.name || '').trim() || 'Tenant';
      const address = this.stripHtml
        ? this.stripHtml(wo.property_address || '')
        : (wo.property_address || '').replace(/<[^>]+>/g, '');
      const parts = [`WO #${wo.id}`, name];
      if (address) parts.push(address);
      return parts.join(' · ');
    },

    scheduleDialogTitle() {
      if (!this.selectedScheduleWO) return 'Schedule Work Order';
      return this.selectedScheduleWO.calendarEventId
        ? 'Reschedule Work Order'
        : 'Schedule Work Order';
    },

    scheduleChoicePrompt() {
      if (!this.selectedScheduleWO) {
        return 'How would you like to schedule this work order?';
      }
      return this.selectedScheduleWO.calendarEventId
        ? 'How would you like to re-schedule this work order?'
        : 'How would you like to schedule this work order?';
    },

    scheduleDayOffset() {
      if (!this.scheduleWorkDays || this.scheduleWorkDays.length === 0)
        return 0;
      const dayMap = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4 };
      return dayMap[this.scheduleWorkDays[0].dayOfWeek] || 0;
    },

    scheduleSelectionLabel() {
      if (this.scheduleSelectedBlocks.length === 0) return '';
      const first = this.scheduleSelectedBlocks[0];
      const last =
        this.scheduleSelectedBlocks[this.scheduleSelectedBlocks.length - 1];
      const fmt = (t) => {
        const [h, m] = t.split(':');
        const hr = parseInt(h);
        const ampm = hr >= 12 ? 'PM' : 'AM';
        const hr12 = hr > 12 ? hr - 12 : hr === 0 ? 12 : hr;
        return `${hr12}:${m} ${ampm}`;
      };
      return `${fmt(first.start)} - ${fmt(last.end)}`;
    },

    normalizedScheduleFee() {
      const parsed = parseFloat(this.scheduleFee);
      return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    },

    normalizedScheduleFeeDisplay() {
      return this.normalizedScheduleFee.toFixed(2);
    },

    usersDebug() {
      console.log('Computed - users array:', this.users);
      return this.users;
    },

    receiptYearOptions() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let y = currentYear; y >= 2025; y--) {
        years.push(y);
      }
      return years;
    },

    filteredReceipts() {
      if (!this.receiptSearchQuery) {
        return this.receipts;
      }

      const term = this.receiptSearchQuery.toLowerCase();
      let filteredList = [...this.receipts];

      // Handle comma-separated tokens as logical AND
      const tokens = term
        .split(',')
        .map((token) => token.trim())
        .filter((token) => token !== '');

      if (tokens.length > 0) {
        tokens.forEach((token) => {
          const isExclusion = token.startsWith('!');
          const actualToken = isExclusion ? token.substring(1) : token;

          if (actualToken) {
            filteredList = filteredList.filter((receipt) => {
              const supplier = (receipt.supplier || '').toLowerCase();
              const description = (receipt.description || '').toLowerCase();
              const address = (receipt.address || '').toLowerCase();
              const amount = this.formatAmount(receipt.amount).toLowerCase();
              const initials = (receipt.createdByInitials || '').toLowerCase();
              const date = this.formatReceiptDate(
                receipt.modifiedOn,
              ).toLowerCase();

              const matchesToken =
                supplier.includes(actualToken) ||
                description.includes(actualToken) ||
                address.includes(actualToken) ||
                amount.includes(actualToken) ||
                initials.includes(actualToken) ||
                date.includes(actualToken);

              return isExclusion ? !matchesToken : matchesToken;
            });
          }
        });
      }

      return filteredList;
    },

    isMobile() {
      return window.innerWidth <= 500; // Adjust the width threshold as needed
    },
    iconClass() {
      return this.isRecording
        ? 'white-recording fas fa-stop'
        : 'red-record fas fa-microphone';
    },

    formattedDuration() {
      return this.recordingDuration > 0 ? `(${this.recordingDuration} s)` : '';
    },

    /**
     * Location options for Review filter — same grouping as list view
     * “location” mode: parentId || propertyId, label parentAddress || address.
     */
    reviewLocationOptions() {
      const open = (this.workOrders || []).filter(
        (wo) => wo && wo.completedOn == null,
      );
      const map = {};
      open.forEach((order) => {
        const key = reviewLocationKeyForOrder(order);
        const label = reviewLocationLabelForOrder(order);
        if (!map[key]) {
          map[key] = { key, label, count: 0 };
        }
        map[key].count += 1;
        // Prefer a non-empty parentAddress if we see one later
        if (
          order.parentAddress &&
          (!map[key].label || map[key].label === 'Unknown')
        ) {
          map[key].label = stripHtmlSimple(order.parentAddress);
        }
      });
      return Object.values(map).sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
      );
    },

    dailyBriefingHtml() {
      const text = this.briefingDisplayText(String(this.dailyBriefingBody || ''));
      if (!text.trim()) return '<p>No briefing yet.</p>';
      const html = renderNoteMarkdown(text);
      return html || '<p>Could not render the briefing.</p>';
    },

    dailyBriefingMeta() {
      return briefingMetaLine(
        this.dailyBriefingUpdatedByName,
        this.dailyBriefingUpdatedAt,
      );
    },

    /** Open work orders for Review (location, search, person, or assignee). */
    reviewOpenWorkOrders() {
      const dir = this.reviewSortDir === 'desc' ? -1 : 1;
      const locKey = this.reviewLocationKey;
      const person = this.reviewPersonFilter;
      const assignee = this.reviewAssigneeFilter;
      return (this.workOrders || [])
        .filter((wo) => {
          if (!wo || wo.completedOn != null) return false;
          if (wo.deleted) return false;
          if (person) {
            return this.workOrderMatchesReviewPerson(wo, person);
          }
          if (assignee) {
            return workOrderMatchesReviewAssignee(wo, assignee);
          }
          if (locKey !== '' && reviewLocationKeyForOrder(wo) !== locKey) {
            return false;
          }
          return workOrderMatchesReviewSearch(wo, this.reviewSearchQuery);
        })
        .slice()
        .sort((a, b) => ((a.id || 0) - (b.id || 0)) * dir);
    },

    reviewCurrentWorkOrder() {
      const list = this.reviewOpenWorkOrders;
      if (!list.length) return null;
      const idx = Math.min(
        Math.max(this.reviewIndex, 0),
        list.length - 1,
      );
      return list[idx] || null;
    },

    reviewStaffUsers() {
      return sortStaffUsers(this.users, { assignableOnly: true });
    },

    reviewScheduledDateLabel() {
      const wo = this.reviewCurrentWorkOrder;
      const date = wo ? this.formatScheduledDate(wo) : '';
      return date ? `Scheduled: ${date}` : '';
    },

    reviewAssignedToLabel() {
      return assignedToFooterLabel(this.reviewCurrentWorkOrder);
    },

    reviewCurrentPhoto() {
      return this.reviewPhotos[this.reviewPhotoIndex] || null;
    },
    reviewCurrentPhotoUrl() {
      return this.reviewCurrentPhoto?.photoUrl || this.reviewPhotoUrl || '';
    },
    reviewCurrentPhotoCaption() {
      return reviewPhotoCaption(this.reviewCurrentPhoto);
    },
    reviewPhotoZoomed() {
      return reviewPhotoIsZoomed(this.reviewPhotoScale);
    },
    reviewPhotoFullscreenStyle() {
      return {
        transform: `translate(${this.reviewPhotoPanX}px, ${this.reviewPhotoPanY}px) scale(${this.reviewPhotoScale})`,
      };
    },
    latestReviewComment() {
      return this.latestCommentForWorkOrder(this.reviewCurrentWorkOrder);
    },

    latestReviewCommentLabel() {
      const c = this.latestReviewComment;
      if (!c) return '';
      if (String(c.authorType || '').toLowerCase() === 'tenant') {
        return 'Latest from tenant';
      }
      return 'Latest from staff';
    },

    canEditReviewComments() {
      return Number(this.currentUserRole) === 1;
    },

    commentThreadWorkOrder() {
      const id = this.commentThreadWorkOrderId;
      if (!id) return this.reviewCurrentWorkOrder;
      return (
        (this.workOrders || []).find((wo) => wo.id === id) ||
        this.reviewCurrentWorkOrder
      );
    },

    reviewCommentThreads() {
      return this.nestCommentsByParent(this.reviewComments);
    },

    filteredWorkOrders() {
      const term = this.workOrderFilter.toLowerCase();
      const tokens = term.split(',').map((token) => token.trim());

      let filteredWorkOrders = (this.workOrders || []).filter(
        (wo) => !wo?.deleted,
      );

      // If filtering by age-based IDs, apply that filter first
      if (this.filteredByAgeIds && this.filteredByAgeIds.length > 0) {
        const idsSet = new Set(this.filteredByAgeIds);
        filteredWorkOrders = filteredWorkOrders.filter((request) =>
          idsSet.has(request.id),
        );
      }

      // Hide completed work orders if hideDone is true
      if (this.hideDone) {
        filteredWorkOrders = filteredWorkOrders.filter(
          (request) => request.completedOn == null,
        );
      }

      if (/^:\d+$/.test(term)) {
        // Check for ":" followed by a number (specific ID search)
        const idToFilter = parseInt(term.slice(1), 10);
        filteredWorkOrders = filteredWorkOrders.filter(
          (request) => request.id === idToFilter,
        );
      }
      // Filter by critical issues if needed
      else if (this.onlyCriticalIssues) {
        filteredWorkOrders = filteredWorkOrders.filter(
          (request) => request.isCritical,
        );
      }

      // Text search - apply only if we have search tokens and no ID-based filtering
      if (tokens.length > 0 && tokens[0] !== '' && !/^:\d+$/.test(term)) {
        // For debugging, let's log a sample work order's searchable values
        if (filteredWorkOrders.length > 0) {
          const sampleRequest = filteredWorkOrders[0];
          Object.keys(sampleRequest)
            .filter(
              (key) =>
                !['id', 'peopleId', 'propertyId', 'allow_entry'].includes(key),
            )
            .map((key) => {
              const value = sampleRequest[key];
              return {
                key,
                value: value ? value.toString().toLowerCase() : '',
              };
            });
        }

        filteredWorkOrders = filteredWorkOrders.filter((request) => {
          // Convert all request values to lowercase strings for searching
          const requestValues = Object.keys(request)
            .filter(
              (key) =>
                !['id', 'peopleId', 'propertyId', 'allow_entry'].includes(key),
            )
            .map((key) => {
              const value = request[key];
              return value ? value.toString().toLowerCase() : '';
            });

          // Check if ANY token matches ANY value in the work order
          const matches = tokens.some((token) =>
            requestValues.some((value) => value.includes(token)),
          );

          return matches;
        });
      }

      // Apply highlighting to filtered results
      const highlightedResults = filteredWorkOrders.map((request) => {
        const highlightedRequest = {};

        for (const [key, value] of Object.entries(request)) {
          if (!['id', 'peopleId', 'propertyId', 'allow_entry'].includes(key)) {
            // Apply highlighting only to non-excluded fields
            highlightedRequest[key] = value
              ? this.highlightMatch(this.workOrderFilter, value)
              : value;
          } else {
            highlightedRequest[key] = value; // Simply copy without modification
          }
        }

        return highlightedRequest;
      });

      // Apply priority sorting if prioritySortOrder is set
      if (this.prioritySortOrder !== null) {
        highlightedResults.sort((a, b) => {
          const priorityA = a.priority || 0;
          const priorityB = b.priority || 0;

          if (this.prioritySortOrder === 'asc') {
            return priorityA - priorityB; // Lower priority first
          } else {
            return priorityB - priorityA; // Higher priority first
          }
        });
      }

      return highlightedResults;
    },

    listViewWorkOrders() {
      const stripHtml = (str) =>
        str ? str.replace(/<[^>]*>/g, '').trim() : '';
      let orders = this.workOrders;
      if (this.listViewHideDone) {
        orders = orders.filter((o) => o.completedOn == null);
      }
      if (this.listViewSearch.trim()) {
        const term = this.listViewSearch.trim().toLowerCase();
        orders = orders.filter((o) => {
          const addr = stripHtml(o.property_address || '').toLowerCase();
          const desc = stripHtml(o.problem_description || '').toLowerCase();
          const name = stripHtml(o.name || '').toLowerCase();
          return (
            addr.includes(term) || desc.includes(term) || name.includes(term)
          );
        });
      }
      return orders;
    },

    listViewGrouped() {
      const stripHtml = (str) =>
        str ? str.replace(/<[^>]*>/g, '').trim() : '';
      const sorted = [...this.listViewWorkOrders].sort((a, b) => {
        const dateA = new Date(stripHtml(a.date) || 0);
        const dateB = new Date(stripHtml(b.date) || 0);
        return dateB - dateA;
      });
      const grouped = {};
      sorted.forEach((order) => {
        const raw = stripHtml(order.date);
        const dateStr = raw
          ? new Date(raw).toISOString().split('T')[0]
          : 'unknown';
        if (!grouped[dateStr]) grouped[dateStr] = [];
        grouped[dateStr].push(order);
      });
      return grouped;
    },

    listViewGroupedByBuilding() {
      const stripHtml = (str) =>
        str ? str.replace(/<[^>]*>/g, '').trim() : '';
      const orders = this.listViewWorkOrders;
      const buildingMap = {};
      orders.forEach((order) => {
        const parentId = order.parentId || order.propertyId || 0;
        const parentAddr =
          order.parentAddress || stripHtml(order.property_address) || 'Unknown';
        if (!buildingMap[parentId]) {
          buildingMap[parentId] = {
            parentId,
            parentAddress: parentAddr,
            orders: [],
          };
        }
        buildingMap[parentId].orders.push(order);
      });
      return Object.values(buildingMap).sort((a, b) =>
        a.parentAddress.localeCompare(b.parentAddress),
      );
    },

    currentPhoto() {
      return this.workOrderPhotos[this.currentPhotoIndex] || {};
    },

    formattedLogMessage() {
      let message = this.logMessage || '';

      if (this.recentUsageData && this.recentUsageData.length > 0) {
        message += '<br><br><strong>Recent Work Orders Usage:</strong>';
        message +=
          '<div style="display: flex; flex-direction: column; gap: 4px; margin-top: 8px; font-family: monospace; font-size: 12px;">';

        // Header
        message +=
          '<div style="display: flex; font-weight: bold; border-bottom: 1px solid #ccc; padding-bottom: 4px;">';
        message += '<div style="flex: 1; padding-right: 12px;">Date</div>';
        message += '<div style="flex: 1;">User</div>';
        message += '</div>';

        // Data rows
        this.recentUsageData.forEach((row) => {
          const date = new Date(row.date).toLocaleString();
          message += '<div style="display: flex;">';
          message += `<div style="flex: 1; padding-right: 12px;">${date}</div>`;
          message += `<div style="flex: 1;">${row.displayName}</div>`;
          message += '</div>';
        });

        message += '</div>';
      }

      return message;
    },

    sortedConfirmationsList() {
      // Sort confirmations with pending items first, then by date descending
      if (!this.confirmationsList || !Array.isArray(this.confirmationsList)) {
        return [];
      }
      return [...this.confirmationsList].sort((a, b) => {
        // Pending items first
        if (
          a.confirmationState === 'pending' &&
          b.confirmationState !== 'pending'
        ) {
          return -1;
        }
        if (
          b.confirmationState === 'pending' &&
          a.confirmationState !== 'pending'
        ) {
          return 1;
        }

        // Then sort by date descending (newest first)
        const dateA = new Date(a.requestedOn || a.confirmedOn);
        const dateB = new Date(b.requestedOn || b.confirmedOn);
        return dateB - dateA;
      });
    },
  },

  methods: {
    sendClose() {
      eventBus.emit('requestCloseMWorkOrders');
    },

    getAuthToken() {
      return localStorage.getItem('token');
    },

    getCurrentUserDisplayName() {
      try {
        return this.$store?.getters?.currentUser?.displayName || 'Unknown';
      } catch (storeError) {
        // If store is not available, decode token to get user info
        const token = this.getAuthToken();
        if (token) {
          try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
              atob(base64)
                .split('')
                .map(
                  (c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2),
                )
                .join(''),
            );
            const tokenData = JSON.parse(jsonPayload);
            return tokenData.displayName || tokenData.username || 'Unknown';
          } catch (tokenError) {
            console.warn('Could not decode token for user name:', tokenError);
            return 'Unknown';
          }
        }
        return 'Unknown';
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);

      // Format: MM/dd/yy h:mmam
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);

      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'pm' : 'am';

      hours = hours % 12;
      hours = hours ? hours : 12; // 0 should be 12

      return `${month}/${day}/${year} ${hours}:${minutes}${ampm}`;
    },

    stripHtml(str) {
      return str ? str.replace(/<[^>]*>/g, '').trim() : '';
    },

    prepareTextForSpeech(str) {
      if (!str) return '';

      let text = String(str);

      text = text.replace(/<br\s*\/?>/gi, ' ');
      text = text.replace(/<\/?p[^>]*>/gi, ' ');
      text = text.replace(/<\/?div[^>]*>/gi, ' ');
      text = text.replace(/<[^>]*>/g, ' ');

      text = text
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, ' and ')
        .replace(/&lt;/gi, '')
        .replace(/&gt;/gi, '')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'")
        .replace(/&apos;/gi, "'");

      text = text.replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1');
      text = text.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1');
      text = text.replace(/\*\*([^*]+)\*\*/g, '$1');
      text = text.replace(/__([^_]+)__/g, '$1');
      text = text.replace(/\*([^*]+)\*/g, '$1');
      text = text.replace(/_([^_]+)_/g, '$1');
      text = text.replace(/~~([^~]+)~~/g, '$1');
      text = text.replace(/```[\s\S]*?```/g, ' ');
      text = text.replace(/`([^`]+)`/g, '$1');
      text = text.replace(/^#{1,6}\s+/gm, '');
      text = text.replace(/^>\s?/gm, '');
      text = text.replace(/^[\s]*[-*+]\s+/gm, '');
      text = text.replace(/^[\s]*\d+\.\s+/gm, '');
      text = text.replace(/^[-*_]{3,}\s*$/gm, ' ');
      text = text.replace(/[<>*_~`#|[\]\\]/g, ' ');
      text = text.replace(/\s+/g, ' ').trim();

      return text;
    },

    formatListViewDateHeader(dateStr) {
      if (dateStr === 'unknown') return 'Unknown Date';
      const date = new Date(dateStr + 'T12:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const target = new Date(dateStr + 'T12:00:00');
      target.setHours(0, 0, 0, 0);

      const diffTime = today.getTime() - target.getTime();
      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

      const formatted = date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
      });

      if (diffDays === 0) return 'Today - ' + formatted;
      if (diffDays === 1) return 'Yesterday - ' + formatted;
      return formatted + ` (${diffDays}d ago)`;
    },

    openWorkOrderFromList(order) {
      this.showListView = false;
      this.openedFromListView = true;
      const rawId = order.id;
      const idx = this.filteredWorkOrders.findIndex((wo) => wo.id === rawId);
      if (idx !== -1) {
        this.selectTile(idx);
      }
    },

    toggleListViewGroup(parentId) {
      const s = new Set(this.listViewCollapsedGroups);
      if (s.has(parentId)) {
        s.delete(parentId);
      } else {
        s.add(parentId);
      }
      this.listViewCollapsedGroups = s;
    },

    listViewEscapeHtml(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },

    formatListViewPrintDescription(order) {
      const raw = order.problem_description;
      if (!raw || !String(raw).trim()) {
        return '<p class="wo-print-empty">No description</p>';
      }
      return `<div class="wo-print-body">${this.markedCleanBody(String(raw))}</div>`;
    },

    formatListViewPrintNotes(order) {
      const raw = order.note;
      const hasNotes = raw && String(raw).trim();
      const bodyHtml = hasNotes
        ? `<div class="wo-print-notes-body">${this.markedCleanBody(String(raw))}</div>`
        : '';
      return `<div class="wo-print-notes"><div class="wo-print-notes-label">Notes:</div>${bodyHtml}</div>`;
    },

    blobToDataUrl(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    },

    async fetchWorkOrderFirstPhotoDataUrl(workOrderId) {
      try {
        const response = await axios.get(
          `/api/work-orders/${workOrderId}/photos`,
        );
        const photos = response.data;
        if (!Array.isArray(photos) || photos.length === 0) {
          return '';
        }

        const imageResponse = await axios.get(`/api/photos/${photos[0].id}`, {
          responseType: 'blob',
        });
        if (imageResponse.status !== 200) {
          return '';
        }

        const blob = imageResponse.data;
        if (!(blob instanceof Blob) || blob.size === 0) {
          return '';
        }

        const blobType = blob.type || '';
        if (
          blobType.includes('json') ||
          blobType.includes('text/html') ||
          blobType.includes('text/plain')
        ) {
          console.error(
            `Invalid photo response for work order ${workOrderId}`,
          );
          return '';
        }

        return await this.blobToDataUrl(blob);
      } catch (err) {
        console.error(`Failed to fetch photo for work order ${workOrderId}:`, err);
        return '';
      }
    },

    async buildListViewPrintPhotoMap() {
      const photoByOrderId = {};

      await Promise.all(
        this.listViewWorkOrders.map(async (order) => {
          const dataUrl = await this.fetchWorkOrderFirstPhotoDataUrl(order.id);
          if (dataUrl) {
            photoByOrderId[order.id] = dataUrl;
          }
        }),
      );

      return photoByOrderId;
    },

    waitForPrintWindowImages(printWindow) {
      const images = Array.from(printWindow.document.images || []);
      if (!images.length) {
        return Promise.resolve();
      }

      return Promise.all(
        images.map(
          (img) =>
            new Promise((resolve) => {
              if (img.complete && img.naturalWidth > 0) {
                resolve();
                return;
              }
              img.addEventListener('load', resolve, { once: true });
              img.addEventListener('error', resolve, { once: true });
            }),
        ),
      );
    },

    async buildWorkOrderQrDataUrl(orderId) {
      const url = `https://pixie.bigtapapp.com/mworkorders?search=:${orderId}`;
      try {
        return await QRCode.toDataURL(url, {
          width: 120,
          margin: 1,
        });
      } catch (err) {
        console.error('Failed to generate QR code:', err);
        return '';
      }
    },

    async buildListViewPrintQrMap() {
      const qrByOrderId = {};
      await Promise.all(
        this.listViewWorkOrders.map(async (order) => {
          qrByOrderId[order.id] = await this.buildWorkOrderQrDataUrl(order.id);
        }),
      );
      return qrByOrderId;
    },

    buildListViewPrintOrderCard(order, options = {}) {
      const address = this.listViewEscapeHtml(
        this.stripHtml(order.property_address) || 'No address',
      );
      const name = this.listViewEscapeHtml(this.stripHtml(order.name) || '');
      const phone = this.listViewEscapeHtml(this.stripHtml(order.phone) || '');
      const scheduled = order.calendarEventId ? 'Scheduled' : 'Not scheduled';
      const entry = order.allow_entry ? 'Entry allowed' : 'Entry not allowed';
      const dateText = this.listViewEscapeHtml(
        this.formatCardAge(order.date) ||
          this.formatDate(this.stripHtml(order.date)) ||
          'Unknown date',
      );
      const dateLine = options.showDate
        ? `<div class="wo-print-meta-line">Date: ${dateText}</div>`
        : '';

      const enteredLine =
        options.showEnteredDate && !options.showDate
          ? `<div class="wo-print-meta-line">Entered: ${this.listViewEscapeHtml(
              this.formatDate(this.stripHtml(order.date)) || 'Unknown date',
            )}</div>`
          : '';

      const tenantLine =
        name || phone
          ? `<div class="wo-print-meta-line">${[name, phone].filter(Boolean).join(' · ')}</div>`
          : '';

      const photoDataUrl = options.photoDataUrl || '';
      const photoImgHtml = photoDataUrl
        ? `<img src="${photoDataUrl}" alt="Work order #${order.id} photo" />`
        : '<span class="wo-print-photo-placeholder">photo</span>';
      const photoHtml = `<div class="wo-print-entry-photo${photoDataUrl ? '' : ' wo-print-entry-photo-empty'}">${photoImgHtml}</div>`;

      const qrDataUrl = options.qrDataUrl || '';
      const qrHtml = qrDataUrl
        ? `<div class="wo-print-entry-qr"><img src="${qrDataUrl}" alt="Work order #${order.id}" /></div>`
        : '<div class="wo-print-entry-qr"></div>';

      return `
        <article class="wo-print-entry">
          <div class="wo-print-entry-row">
            <div class="wo-print-entry-main">
              <div class="wo-print-entry-header">
                <div class="wo-print-address">#${order.id} — ${address}</div>
                ${enteredLine}
                ${dateLine}
                ${tenantLine}
                <div class="wo-print-flags">${scheduled} · ${entry}</div>
              </div>
              ${this.formatListViewPrintDescription(order)}
              ${this.formatListViewPrintNotes(order)}
            </div>
            ${photoHtml}
            ${qrHtml}
          </div>
        </article>
      `;
    },

    buildListViewPrintByDate(qrByOrderId, photoByOrderId) {
      const grouped = this.listViewGrouped;
      return Object.entries(grouped)
        .map(([dateStr, orders]) => {
          const header = this.listViewEscapeHtml(
            this.formatListViewDateHeader(dateStr),
          );
          const cards = orders
            .map((order) =>
              this.buildListViewPrintOrderCard(order, {
                qrDataUrl: qrByOrderId[order.id] || '',
                photoDataUrl: photoByOrderId[order.id] || '',
                showEnteredDate: true,
              }),
            )
            .join('');
          return `
            <section class="wo-print-section wo-print-section-grouped">
              <div class="wo-print-group-header">${header} <span class="wo-print-count">(${orders.length})</span></div>
              ${cards}
            </section>
          `;
        })
        .join('');
    },

    buildListViewPrintByBuilding(qrByOrderId, photoByOrderId) {
      return this.listViewGroupedByBuilding
        .map((group) => {
          const header = this.listViewEscapeHtml(group.parentAddress);
          const cards = group.orders
            .map((order) =>
              this.buildListViewPrintOrderCard(order, {
                showDate: true,
                qrDataUrl: qrByOrderId[order.id] || '',
                photoDataUrl: photoByOrderId[order.id] || '',
              }),
            )
            .join('');
          return `
            <section class="wo-print-section wo-print-section-grouped">
              <div class="wo-print-group-header">${header} <span class="wo-print-count">(${group.orders.length})</span></div>
              ${cards}
            </section>
          `;
        })
        .join('');
    },

    getWorkOrderPrintCss() {
      return `
        @page { size: Letter portrait; margin: 0.55in; }
        body {
          font-family: Arial, sans-serif;
          color: #222;
          margin: 0;
          font-size: 12px;
          line-height: 1.4;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        h1 {
          margin: 0 0 4px;
          font-size: 18px;
        }
        h2 {
          margin: 18px 0 8px;
          font-size: 14px;
          color: #4a3d52;
          border-bottom: 1px solid #ccc;
          padding-bottom: 4px;
        }
        .wo-print-meta {
          margin-bottom: 10px;
          color: #555;
          font-size: 11px;
        }
        .wo-print-count {
          font-weight: 400;
          color: #666;
          font-size: 12px;
        }
        .wo-print-group-header {
          display: block;
          width: auto;
          background-color: #d0d0d0 !important;
          margin: 14px -0.55in 10px;
          padding: 7px 0.55in;
          font-size: 14px;
          font-weight: 600;
          color: #333;
          border-top: 1px solid #a8a8a8;
          border-bottom: 1px solid #a8a8a8;
          box-decoration-break: clone;
          -webkit-box-decoration-break: clone;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .wo-print-section-grouped:first-of-type .wo-print-group-header {
          margin-top: 8px;
        }
        .wo-print-entry {
          margin: 0 0 12px;
          padding: 0 0 10px;
          border-bottom: 1px solid #e4e4e4;
          page-break-inside: avoid;
        }
        .wo-print-entry:last-child {
          border-bottom: none;
        }
        .wo-print-entry-row {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
          /* Percent flex bases + gap previously exceeded 100% and clipped QR on the right */
          overflow: visible;
        }
        .wo-print-entry-main {
          flex: 1 1 auto;
          min-width: 0;
          width: auto;
          max-width: none;
        }
        .wo-print-entry-photo {
          flex: 0 0 88px;
          width: 88px;
          max-width: 88px;
          text-align: center;
          box-sizing: border-box;
          border: 1px solid #bbb;
          border-radius: 4px;
          min-height: 80px;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .wo-print-entry-photo-empty {
          background: #fafafa;
        }
        .wo-print-photo-placeholder {
          color: #bbb;
          font-size: 11px;
          line-height: 1;
          text-align: center;
        }
        .wo-print-entry-photo img {
          display: block;
          width: 100%;
          max-width: 100%;
          max-height: 140px;
          height: auto;
          object-fit: contain;
        }
        .wo-print-entry-qr {
          flex: 0 0 88px;
          width: 88px;
          max-width: 88px;
          flex-shrink: 0;
          text-align: center;
          box-sizing: border-box;
          overflow: visible;
        }
        .wo-print-entry-qr img {
          display: block;
          width: 88px;
          height: 88px;
          max-width: 88px;
          margin: 0 auto;
        }
        .wo-print-address {
          font-weight: 700;
          font-size: 12px;
          margin-bottom: 2px;
        }
        .wo-print-meta-line,
        .wo-print-flags {
          font-size: 11px;
          color: #555;
          margin-bottom: 2px;
        }
        .wo-print-body {
          margin-top: 6px;
          white-space: normal;
          word-wrap: break-word;
          overflow: visible;
          font-size: 14px;
          line-height: 1.45;
          color: #333;
        }
        .wo-print-body p {
          margin: 4px 0;
        }
        .wo-print-body ul,
        .wo-print-body ol {
          margin: 4px 0;
          padding-left: 18px;
        }
        .wo-print-empty {
          color: #777;
          font-style: italic;
          font-size: 14px;
          margin: 4px 0 0;
        }
        .wo-print-notes {
          margin-top: 8px;
          padding-top: 6px;
          border-top: 1px solid #e8e8e8;
        }
        .wo-print-notes-label {
          font-size: 11px;
          font-weight: 700;
          color: #4a3d52;
          margin-bottom: 2px;
        }
        .wo-print-notes-body {
          white-space: normal;
          word-wrap: break-word;
          overflow: visible;
          font-size: 12px;
          color: #333;
        }
        .wo-print-notes-body p {
          margin: 4px 0;
        }
      `;
    },

    /**
     * Open the print window in the same turn as the click. Browsers block
     * window.open() after await (QR/photo prep), which caused the first-time
     * "Please allow pop-ups" message.
     */
    openPrintWindowShell(title = 'Print preview') {
      const printWindow = window.open('', '_blank', 'width=960,height=720');
      if (!printWindow) return null;
      try {
        printWindow.document.open();
        printWindow.document.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${this.listViewEscapeHtml(title)}</title>
  </head>
  <body style="font-family: Arial, sans-serif; padding: 28px; color: #555;">
    <p style="margin:0 0 8px;font-size:15px;">Preparing print preview…</p>
    <p style="margin:0;font-size:12px;color:#888;">Building QR codes and photos. This window will update shortly.</p>
  </body>
</html>`);
        printWindow.document.close();
      } catch (e) {
        console.warn('Could not write print shell:', e);
      }
      return printWindow;
    },

    async openWorkOrderPrintDocument({
      printWindow: existingWindow = null,
      windowTitle,
      headingHtml,
      metaHtml = '',
      bodyHtml,
    }) {
      const printHtml = `
        <!doctype html>
        <html>
          <head>
            <meta charset="utf-8" />
            <title>${this.listViewEscapeHtml(windowTitle)}</title>
            <style>${this.getWorkOrderPrintCss()}</style>
          </head>
          <body>
            ${headingHtml}
            ${metaHtml}
            ${bodyHtml}
          </body>
        </html>
      `;

      // Prefer window opened synchronously on click; only open here as last resort
      let printWindow = existingWindow;
      if (!printWindow || printWindow.closed) {
        printWindow = window.open('', '_blank', 'width=960,height=720');
      }
      if (!printWindow) {
        window.alert(
          'Please allow pop-ups for this site to use print preview.',
        );
        return false;
      }
      printWindow.document.open();
      printWindow.document.write(printHtml);
      printWindow.document.close();
      printWindow.focus();
      await this.waitForPrintWindowImages(printWindow);
      printWindow.print();
      return true;
    },

    async printSelectedWorkOrder() {
      const order = this.filteredWorkOrders[this.selectedTileIndex];
      if (!order) {
        window.alert('Please select a work order to print.');
        return;
      }
      if (this.singleWorkOrderPrintLoading) {
        return;
      }

      // Must open before any await so the popup is not blocked
      const printWindow = this.openPrintWindowShell(`Work Order #${order.id}`);
      if (!printWindow) {
        window.alert(
          'Please allow pop-ups for this site to use print preview.',
        );
        return;
      }

      this.singleWorkOrderPrintLoading = true;
      try {
        const [qrDataUrl, photoDataUrl] = await Promise.all([
          this.buildWorkOrderQrDataUrl(order.id),
          this.fetchWorkOrderFirstPhotoDataUrl(order.id),
        ]);

        const bodyHtml = this.buildListViewPrintOrderCard(order, {
          qrDataUrl,
          photoDataUrl,
          showEnteredDate: true,
        });
        const generatedAt = new Date().toLocaleString();

        await this.openWorkOrderPrintDocument({
          printWindow,
          windowTitle: `Work Order #${order.id}`,
          headingHtml: `<h1>Work Order #${order.id}</h1>`,
          metaHtml: `<div class="wo-print-meta">Generated: ${this.listViewEscapeHtml(generatedAt)}</div>`,
          bodyHtml,
        });
      } catch (error) {
        console.error('Error preparing work order print preview:', error);
        try {
          if (printWindow && !printWindow.closed) printWindow.close();
        } catch (_) {
          /* ignore */
        }
        window.alert('Unable to prepare print preview.');
      } finally {
        this.singleWorkOrderPrintLoading = false;
      }
    },

    async printListView() {
      if (!this.listViewWorkOrders.length) {
        window.alert('No work orders to print.');
        return;
      }
      if (this.listViewPrintLoading) {
        return;
      }

      // Must open before any await so the popup is not blocked
      const printWindow = this.openPrintWindowShell('Work Orders List');
      if (!printWindow) {
        window.alert(
          'Please allow pop-ups for this site to use print preview.',
        );
        return;
      }

      this.listViewPrintLoading = true;
      try {
        const [qrByOrderId, photoByOrderId] = await Promise.all([
          this.buildListViewPrintQrMap(),
          this.buildListViewPrintPhotoMap(),
        ]);

        const generatedAt = new Date().toLocaleString();
        const groupLabel =
          this.listViewGroupBy === 'date' ? 'By Date' : 'By Property';
        const filterNotes = [];
        if (this.listViewHideDone) filterNotes.push('Completed orders hidden');
        if (this.listViewSearch.trim()) {
          filterNotes.push(`Filter: "${this.listViewSearch.trim()}"`);
        }
        const filterLine = filterNotes.length
          ? `<div class="wo-print-meta">Filters: ${this.listViewEscapeHtml(filterNotes.join(' · '))}</div>`
          : '';

        const bodyHtml =
          this.listViewGroupBy === 'date'
            ? this.buildListViewPrintByDate(qrByOrderId, photoByOrderId)
            : this.buildListViewPrintByBuilding(qrByOrderId, photoByOrderId);

        // Dismiss spinner before print() — it blocks until the dialog is closed
        this.listViewPrintLoading = false;
        await this.openWorkOrderPrintDocument({
          printWindow,
          windowTitle: 'Work Orders List',
          headingHtml: `<h1>Work Orders — ${this.listViewEscapeHtml(groupLabel)}</h1>`,
          metaHtml: `<div class="wo-print-meta">Generated: ${this.listViewEscapeHtml(generatedAt)} · ${this.listViewWorkOrders.length} work orders</div>${filterLine}`,
          bodyHtml,
        });
      } catch (error) {
        console.error('Error preparing list view print preview:', error);
        try {
          if (printWindow && !printWindow.closed) printWindow.close();
        } catch (_) {
          /* ignore */
        }
        window.alert('Unable to prepare print preview.');
      } finally {
        this.listViewPrintLoading = false;
      }
    },

    formatCardAge(dateStr) {
      const raw = this.stripHtml(dateStr);
      if (!raw) return '';
      const date = new Date(raw);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const target = new Date(date);
      target.setHours(0, 0, 0, 0);
      const diffDays = Math.round(
        (today.getTime() - target.getTime()) / (1000 * 60 * 60 * 24),
      );
      const formatted = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      return `${formatted} (${diffDays}d ago)`;
    },

    handleNewWorkOrders(newWorkOrders) {
      // Create a set of existing work order IDs for quick lookup
      const existingIds = new Set(this.workOrders.map((order) => order.id));

      // Filter and add only those new work orders that have unique IDs
      const filteredNewWorkOrders = newWorkOrders.filter(
        (order) => !existingIds.has(order.id),
      );

      // Merge filtered new work orders with existing work orders
      this.workOrders = [...filteredNewWorkOrders, ...this.workOrders];

      // If summary popup is open, refresh the stats dynamically
      if (this.showSummaryPopup) {
        this.fetchSummaryStats();
      }
    },

    // Function to connect to WebSocket server
    async connectSocket() {
      try {
        const socketUrl = process.env.VUE_APP_SOCKET_URL;

        if (!socketUrl) {
          console.error('Socket URL is undefined.');
          return;
        }

        const token = this.getAuthToken();

        socket = io(socketUrl, {
          query: {
            token: token,
          },
        });

        socket.on('connect', () => {
          console.log('Connected to WebSocket server');
        });

        socket.on('disconnect', () => {
          console.log('Disconnected from WebSocket server');
        });

        socket.on('workOrderUpdated', (updatedData) => {
          this.handleWorkOrderUpdate(updatedData);
        });
        socket.on('workOrderComment', this.handleWorkOrderComment);
      } catch (error) {
        console.error('Error connecting to WebSocket server:', error);
      }
    },

    nestCommentsByParent(comments) {
      const list = comments || [];
      const byId = {};
      for (const c of list) byId[c.id] = c;
      const chrono = [...list].sort((a, b) => (a.id || 0) - (b.id || 0));
      const parentOf = {};
      let lastRoot = null;
      for (const c of chrono) {
        const explicit =
          c.parentCommentId && byId[c.parentCommentId]
            ? c.parentCommentId
            : null;
        if (explicit) {
          parentOf[c.id] = explicit;
        } else if (
          String(c.authorType || '').toLowerCase() !== 'tenant' &&
          lastRoot
        ) {
          parentOf[c.id] = lastRoot.id;
        } else {
          parentOf[c.id] = null;
          lastRoot = c;
        }
        if (!parentOf[c.id]) lastRoot = c;
      }
      const rootIdOf = (id) => {
        let cur = parentOf[id];
        const seen = new Set();
        while (cur && byId[cur] && !seen.has(cur)) {
          seen.add(cur);
          if (!parentOf[cur]) return cur;
          cur = parentOf[cur];
        }
        return null;
      };
      const groups = [];
      const groupByRoot = {};
      for (const c of chrono) {
        const rid = parentOf[c.id] ? rootIdOf(c.id) : null;
        if (!rid || !groupByRoot[rid]) {
          const g = { parent: c, replies: [] };
          groups.push(g);
          groupByRoot[c.id] = g;
        } else {
          groupByRoot[rid].replies.push(c);
        }
      }
      for (const g of groups) g.replies.reverse();
      return groups.reverse();
    },

    toggleReviewCommentSelect(comment) {
      if (!comment?.id || this.reviewEditingCommentId) return;
      if (this.commentLongPressFired) {
        this.commentLongPressFired = false;
        return;
      }
      if (this.commentClickTimer) clearTimeout(this.commentClickTimer);
      this.commentClickTimer = setTimeout(() => {
        this.commentClickTimer = null;
        this.reviewSelectedCommentId =
          this.reviewSelectedCommentId === comment.id ? null : comment.id;
      }, 250);
    },

    clearCommentLongPress() {
      if (this.commentLongPressTimer) {
        clearTimeout(this.commentLongPressTimer);
        this.commentLongPressTimer = null;
      }
    },

    reviewCommentPhoneHtml(text) {
      return linkPhoneTelHtml(text);
    },

    onReviewCommentPhoneClick(event) {
      const el = event.target;
      const link =
        el && el.closest
          ? el.closest('a.review-phone-chip, a.review-comment-link')
          : null;
      if (!link) return;
      event.stopPropagation();
    },

    onCommentTouchStart(event, comment) {
      const target = event.target;
      if (
        target &&
        target.closest &&
        target.closest('a.review-phone-chip, a.review-comment-link')
      ) {
        return;
      }
      if (!this.canEditReviewComments || this.reviewEditingCommentId) return;
      if (!event.touches || event.touches.length !== 1) return;
      const t = event.touches[0];
      this.commentTouchX = t.clientX;
      this.commentTouchY = t.clientY;
      this.commentLongPressFired = false;
      this.clearCommentLongPress();
      this.commentLongPressTimer = setTimeout(() => {
        this.commentLongPressTimer = null;
        this.commentLongPressFired = true;
        if (typeof navigator !== 'undefined' && navigator.vibrate) {
          try {
            navigator.vibrate(30);
          } catch {
            /* ignore */
          }
        }
        this.startReviewCommentEdit(comment);
      }, 550);
    },

    onCommentTouchMove(event) {
      if (this.commentTouchX == null || !event.touches?.length) return;
      const t = event.touches[0];
      const dx = Math.abs(t.clientX - this.commentTouchX);
      const dy = Math.abs(t.clientY - this.commentTouchY);
      if (dx > 12 || dy > 12) this.clearCommentLongPress();
    },

    onCommentTouchEnd() {
      this.clearCommentLongPress();
      this.commentTouchX = null;
      this.commentTouchY = null;
      if (this.commentLongPressFired) {
        setTimeout(() => {
          this.commentLongPressFired = false;
        }, 400);
      }
    },

    onCommentTouchCancel() {
      this.clearCommentLongPress();
      this.commentTouchX = null;
      this.commentTouchY = null;
      this.commentLongPressFired = false;
    },

    startReviewCommentEdit(comment) {
      if (!this.canEditReviewComments || !comment?.id) return;
      if (this.commentClickTimer) {
        clearTimeout(this.commentClickTimer);
        this.commentClickTimer = null;
      }
      this.reviewEditingCommentId = comment.id;
      this.reviewEditDraft = comment.message || '';
    },

    cancelReviewCommentEdit() {
      this.reviewEditingCommentId = null;
      this.reviewEditDraft = '';
    },

    async saveReviewCommentEdit() {
      const woId =
        this.commentThreadWorkOrderId || this.reviewCurrentWorkOrder?.id;
      const commentId = this.reviewEditingCommentId;
      const message = String(this.reviewEditDraft || '').trim();
      if (!woId || !commentId || !message || this.reviewEditSaving) return;
      this.reviewEditSaving = true;
      try {
        const { data } = await axios.put(
          `/api/maintenance-requests/${woId}/comments/${commentId}`,
          { message },
        );
        const updated = data?.comment;
        if (updated) {
          this.reviewComments = this.reviewComments.map((c) =>
            c.id === commentId ? { ...c, ...updated } : c,
          );
        } else {
          const local = this.reviewComments.find((c) => c.id === commentId);
          if (local) local.message = message;
        }
        this.cancelReviewCommentEdit();
      } catch (err) {
        console.error('Failed to edit work order comment:', err);
        window.alert(
          err.response?.data?.error || 'Could not save that comment.',
        );
      } finally {
        this.reviewEditSaving = false;
      }
    },

    reviewCommentClass(c) {
      return String(c?.authorType || '').toLowerCase() === 'tenant'
        ? 'review-thread-tenant'
        : 'review-thread-staff';
    },

    reviewCommentAuthor(c) {
      if (String(c?.authorType || '').toLowerCase() === 'tenant') {
        return c.authorName || 'Tenant';
      }
      return c.authorName || 'Staff';
    },

    handleWorkOrderComment(comment) {
      if (!comment || !comment.maintenanceRequestId) return;
      const woId = comment.maintenanceRequestId;
      const local = this.workOrders.find((wo) => wo.id === woId);
      const inLoadedThread = this.reviewCommentsForId === woId;
      const alreadyInThread =
        inLoadedThread && this.reviewComments.some((c) => c.id === comment.id);
      if (local) {
        local.latestComment = comment;
        local.latestCommentLoaded = true;
        if (String(comment.authorType || '').toLowerCase() === 'tenant') {
          local.latestTenantComment = comment.message;
          local.latestTenantCommentAt = comment.createdAt;
        }
        if (!alreadyInThread && !inLoadedThread) {
          local.commentCount = (Number(local.commentCount) || 0) + 1;
        }
      }
      if (
        this.reviewCommentsForId !== woId &&
        this.commentThreadWorkOrderId !== woId &&
        this.reviewCurrentWorkOrder?.id !== woId
      ) {
        return;
      }
      if (alreadyInThread) return;
      this.reviewComments = [comment, ...this.reviewComments];
      this.reviewCommentsForId = woId;
      if (local) local.commentCount = this.reviewComments.length;
    },

    handleWorkOrderUpdate(updatedData) {
      // Find and update the work order in the local array
      const workOrder = this.workOrders.find((wo) => wo.id === updatedData.id);
      if (workOrder) {
        if (updatedData.completedOn !== undefined) {
          workOrder.completedOn = updatedData.completedOn;
        }
        if (updatedData.completedBy !== undefined) {
          workOrder.completedBy = updatedData.completedBy;
        }
        if (updatedData.startedOn !== undefined) {
          workOrder.startedOn = updatedData.startedOn;
        }
        if (updatedData.startedByInitials !== undefined) {
          workOrder.startedByInitials = updatedData.startedByInitials;
        }
        if (updatedData.modifiedOn !== undefined) {
          workOrder.modifiedOn = updatedData.modifiedOn;
        }
        if (updatedData.deleted !== undefined) {
          workOrder.deleted = updatedData.deleted;
        }
        if (updatedData.deletedOn !== undefined) {
          workOrder.deletedOn = updatedData.deletedOn;
        }

        if (workOrder.deleted) {
          this.$nextTick(() => {
            const max = Math.max(this.reviewOpenWorkOrders.length - 1, 0);
            if (this.reviewIndex > max) this.reviewIndex = max;
            if (!this.reviewOpenWorkOrders.length && this.showReviewPopup) {
              this.showReviewPopup = false;
            }
          });
        }

        if (this.showSummaryPopup) {
          this.fetchSummaryStats();
        }
      }
    },

    staffInitialsFromName(name) {
      if (!name || !String(name).trim()) return '';
      const parts = String(name).trim().split(/\s+/);
      if (parts.length === 1) {
        return parts[0].substring(0, 2).toUpperCase();
      }
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    },

    formatWorkOrderStartedAt(value) {
      if (!value) return '';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return '';
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        month: 'numeric',
        day: 'numeric',
        year: '2-digit',
      }).formatToParts(d);
      const get = (type) => parts.find((p) => p.type === type)?.value || '';
      const period = get('dayPeriod').toLowerCase().startsWith('p') ? 'p' : 'a';
      return `${get('hour')}:${get('minute')}${period} on ${get('month')}/${get('day')}/${get('year')}`;
    },

    async loadReviewComments(workOrderId) {
      if (!workOrderId) {
        this.reviewComments = [];
        this.reviewCommentsForId = null;
        return;
      }
      if (this.reviewCommentsForId === workOrderId) {
        return;
      }
      this.reviewComments = [];
      this.reviewSelectedCommentId = null;
      const token = ++this.reviewCommentsLoadToken;
      this.reviewCommentsLoading = true;
      try {
        const { data } = await axios.get(
          `/api/maintenance-requests/${workOrderId}/comments`,
        );
        if (token !== this.reviewCommentsLoadToken) return;
        this.reviewComments = data.comments || [];
        this.reviewCommentsForId = workOrderId;
        const latestTenant = this.reviewComments.find(
          (c) => c.authorType === 'tenant',
        );
        const local = this.workOrders.find((wo) => wo.id === workOrderId);
        if (local) {
          local.latestComment = this.reviewComments[0] || null;
          local.latestCommentLoaded = true;
          local.commentCount = this.reviewComments.length;
          local.latestTenantComment = latestTenant
            ? latestTenant.message
            : null;
          local.latestTenantCommentAt = latestTenant
            ? latestTenant.createdAt
            : null;
        }
      } catch (err) {
        if (token !== this.reviewCommentsLoadToken) return;
        console.error('Failed to load work order comments:', err);
        this.reviewComments = [];
        this.reviewCommentsForId = workOrderId;
      } finally {
        if (token === this.reviewCommentsLoadToken) {
          this.reviewCommentsLoading = false;
        }
      }
    },

    latestCommentForWorkOrder(wo) {
      if (!wo) return null;
      if (this.reviewCommentsForId === wo.id && this.reviewComments.length) {
        return this.reviewComments[0];
      }
      if (wo.latestComment) return wo.latestComment;
      if (wo.latestTenantComment) {
        return {
          message: wo.latestTenantComment,
          createdAt: wo.latestTenantCommentAt,
          authorType: 'tenant',
        };
      }
      return null;
    },

    latestCommentLabelForWorkOrder(wo) {
      const c = this.latestCommentForWorkOrder(wo);
      if (!c) return '';
      if (String(c.authorType || '').toLowerCase() === 'tenant') {
        return 'Latest from tenant';
      }
      return 'Latest from staff';
    },

    commentCountForWorkOrder(wo) {
      if (!wo) return 0;
      if (this.reviewCommentsForId === wo.id && this.reviewComments.length) {
        return this.reviewComments.length;
      }
      if (wo.commentCount != null && wo.commentCount !== '') {
        const n = Number(wo.commentCount);
        if (Number.isFinite(n) && n >= 0) return n;
      }
      return this.latestCommentForWorkOrder(wo) ? 1 : 0;
    },

    openWorkOrderComments(workOrderId) {
      this.commentThreadWorkOrderId = workOrderId || null;
      this.showReviewThread = true;
      if (workOrderId) this.loadReviewComments(workOrderId);
    },

    openReviewThread() {
      this.openWorkOrderComments(this.reviewCurrentWorkOrder?.id);
    },

    closeReviewThread() {
      this.showReviewThread = false;
      this.commentThreadWorkOrderId = null;
      this.reviewCommentDraft = '';
      this.reviewSelectedCommentId = null;
      this.clearCommentLongPress();
      this.commentLongPressFired = false;
      this.cancelReviewCommentEdit();
    },

    async submitReviewThreadReply() {
      const id =
        this.commentThreadWorkOrderId || this.reviewCurrentWorkOrder?.id;
      const message = String(this.reviewCommentDraft || '').trim();
      if (!id || !message || this.reviewCommentSaving) return;
      this.reviewCommentSaving = true;
      try {
        const { data } = await axios.post(
          `/api/maintenance-requests/${id}/comments`,
          {
            message,
            parentCommentId: this.reviewSelectedCommentId || undefined,
          },
        );
        if (data?.comment) {
          this.reviewComments = [
            data.comment,
            ...this.reviewComments.filter((c) => c.id !== data.comment.id),
          ];
          const local = this.workOrders.find((wo) => wo.id === id);
          if (local) {
            local.latestComment = data.comment;
            local.latestCommentLoaded = true;
            local.commentCount = this.reviewComments.length;
          }
        }
        this.reviewCommentDraft = '';
      } catch (err) {
        console.error('Failed to add work order reply:', err);
        window.alert(
          err.response?.data?.error || 'Could not send that reply.',
        );
      } finally {
        this.reviewCommentSaving = false;
      }
    },

    reviewStartButtonText(workOrder) {
      return reviewStartButtonLabel(
        workOrder && workOrder.startedOn,
        this.startingWorkOrderId === (workOrder && workOrder.id),
      );
    },

    reviewStartedLabel(workOrder) {
      if (!workOrder?.startedOn) return '';
      const when = this.formatWorkOrderStartedAt(workOrder.startedOn);
      const initials =
        workOrder.startedByInitials ||
        this.staffInitialsFromName(workOrder.startedByName);
      if (!when) return initials;
      return initials ? `${initials} · ${when}` : when;
    },

    openReviewPhotoUpload() {
      const wo = this.reviewCurrentWorkOrder;
      if (!wo || wo.id == null) return;
      this.currentWorkOrderId = wo.id;
      this.selectPhoto();
    },

    async openReviewNoticeDialog() {
      const wo = this.reviewCurrentWorkOrder;
      if (!wo) return;
      this.reviewNoticeResult = '';
      this.reviewNoticeSending = '';
      await this.refreshSingleWorkOrder(wo.id);
      if (!this.reviewCurrentWorkOrder) return;
      this.showReviewNoticeDialog = true;
    },

    closeReviewNoticeDialog() {
      if (this.reviewNoticeSending) return;
      this.showReviewNoticeDialog = false;
    },

    editReviewNoticeSchedule() {
      const wo = this.reviewCurrentWorkOrder;
      if (!wo) return;
      this.openScheduleDialog(wo);
      this.startSelfSchedule();
    },

    async sendReviewTenantNotice(kind) {
      const wo = this.reviewCurrentWorkOrder;
      if (!wo || this.reviewNoticeSending) return;
      this.reviewNoticeSending = kind;
      this.reviewNoticeResult = '';
      try {
        const { data } = await axios.post(
          `/api/maintenance-requests/${wo.id}/tenant-notice`,
          { kind },
        );
        const bits = [];
        if (data.email === 'sent') bits.push('email sent');
        else if (data.email === 'failed') bits.push('email failed');
        if (data.chat === 'sent') bits.push('chat sent');
        else if (data.chat === 'failed') bits.push('chat failed');
        if (data.sms === 'sent') bits.push('text sent');
        else if (data.sms === 'skipped') bits.push('text skipped');
        else if (data.sms === 'failed') bits.push('text failed');
        this.reviewNoticeResult = data.delivered
          ? bits.join(', ')
          : data.error || bits.join(', ') || 'Nothing was sent.';
      } catch (err) {
        this.reviewNoticeResult =
          err.response?.data?.error || 'Could not send that notice.';
      } finally {
        this.reviewNoticeSending = '';
      }
    },

    async startReviewWorkOrder(workOrder) {
      if (!workOrder?.id || workOrder.startedOn) return;
      if (this.startingWorkOrderId) return;
      this.startingWorkOrderId = workOrder.id;
      try {
        const { data } = await axios.put(
          `/api/maintenance-requests/${workOrder.id}/start`,
        );
        const startedOn = data?.startedOn;
        const startedByInitials = data?.startedByInitials || '';
        const local = this.workOrders.find((wo) => wo.id === workOrder.id);
        if (local) {
          local.startedOn = startedOn;
          local.startedByInitials = startedByInitials;
        }
        workOrder.startedOn = startedOn;
        workOrder.startedByInitials = startedByInitials;
        await this.refreshSingleWorkOrder(workOrder.id);
      } catch (err) {
        console.error('Failed to start work order:', err);
        window.alert('Could not mark this work order as started.');
      } finally {
        this.startingWorkOrderId = null;
      }
    },

    clearSearch() {
      this.selectedTileIndex = null;
      this.workOrderFilter = this.workOrderFilter === '' ? ':d' : '';
      // Also clear age filter if active
      if (this.filteredByAgeIds) {
        this.clearAgeFilter();
      }
    },

    sortByPriority() {
      // Toggle sort order: null -> 'asc' -> 'desc' -> null
      if (this.prioritySortOrder === null) {
        this.prioritySortOrder = 'asc'; // Start with lowest priority first (0 at top)
      } else if (this.prioritySortOrder === 'asc') {
        this.prioritySortOrder = 'desc';
      } else {
        this.prioritySortOrder = null;
      }
    },

    async incrementPriority() {
      if (this.selectedTileIndex === null) return;

      const workOrder = this.filteredWorkOrders[this.selectedTileIndex];
      if (!workOrder) return;

      const workOrderId = workOrder.id;
      const currentPriority = workOrder.priority || 0;
      const newPriority = currentPriority + 1;

      try {
        await this.updateWorkOrderPriority(workOrderId, newPriority);

        // Update the local data in the workOrders array
        const originalWorkOrder = this.workOrders.find(
          (wo) => wo.id === workOrderId,
        );
        if (originalWorkOrder) {
          originalWorkOrder.priority = newPriority;
        }

        // Update IndexedDB cache
        await this.updateLocalDatabase(this.workOrders);

        // Find the new index of this work order after filtering/sorting
        this.$nextTick(() => {
          const newIndex = this.filteredWorkOrders.findIndex(
            (wo) => wo.id === workOrderId,
          );
          if (newIndex !== -1) {
            this.selectedTileIndex = newIndex;
          }
        });
      } catch (error) {
        console.error('Error incrementing priority:', error);
      }
    },

    async decrementPriority() {
      if (this.selectedTileIndex === null) return;

      const workOrder = this.filteredWorkOrders[this.selectedTileIndex];
      if (!workOrder) return;

      const workOrderId = workOrder.id;
      const currentPriority = workOrder.priority || 0;
      const newPriority = Math.max(0, currentPriority - 1); // Floor of zero

      try {
        await this.updateWorkOrderPriority(workOrderId, newPriority);

        // Update the local data in the workOrders array
        const originalWorkOrder = this.workOrders.find(
          (wo) => wo.id === workOrderId,
        );
        if (originalWorkOrder) {
          originalWorkOrder.priority = newPriority;
        }

        // Update IndexedDB cache
        await this.updateLocalDatabase(this.workOrders);

        // Find the new index of this work order after filtering/sorting
        this.$nextTick(() => {
          const newIndex = this.filteredWorkOrders.findIndex(
            (wo) => wo.id === workOrderId,
          );
          if (newIndex !== -1) {
            this.selectedTileIndex = newIndex;
          }
        });
      } catch (error) {
        console.error('Error decrementing priority:', error);
      }
    },

    async updateWorkOrderPriority(workOrderId, priority) {
      try {
        const response = await axios.put(
          `/api/maintenance-requests/${workOrderId}/priority`,
          { priority },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );

        if (response.status !== 200) {
          throw new Error('Failed to update priority');
        }
      } catch (error) {
        console.error('Error updating work order priority:', error);
        throw error;
      }
    },

    shareListViewLink() {
      const group = this.listViewGroupBy === 'building' ? 'building' : 'date';
      const groupLabel = group === 'building' ? 'by property' : 'by date';
      const url = `${window.location.origin}/mworkorders?listView=${group}`;
      const shareText = `Work Orders list view (${groupLabel}):\n${url}`;

      const textArea = document.createElement('textarea');
      textArea.value = shareText;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      textArea.setSelectionRange(0, 99999);

      try {
        const successful = document.execCommand('copy');
        if (successful) {
          this.showShareDialog(
            'Copied!',
            'The list view link has been copied to your clipboard.',
          );
        } else {
          this.showShareDialog('Unable to copy to clipboard', shareText);
        }
      } catch (err) {
        console.error('Failed to copy list view link:', err);
        this.showShareDialog('Unable to copy to clipboard', shareText);
      } finally {
        document.body.removeChild(textArea);
      }
    },

    openListViewFromRoute() {
      const listView = this.$route.query.listView;
      if (listView !== 'date' && listView !== 'building') {
        return;
      }

      this.listViewGroupBy = listView;
      this.showListView = true;
    },

    share(workOrder) {
      // Classic icon bar passes the click. Review passes the open order.
      const selected =
        workOrder && workOrder.id
          ? workOrder
          : this.filteredWorkOrders[this.selectedTileIndex];
      const id = selected?.id;

      // if id exists, proceed
      if (id && selected) {
        // Get tenant name and property address, stripping any <mark> tags from highlighting
        const tenantName =
          this.stripMarkTags(selected.name) || 'Unknown Tenant';
        const propertyAddress =
          this.stripMarkTags(selected.property_address) || 'Unknown Property';

        // Always use production URL for sharing
        const url = `https://pixie.bigtapapp.com/mworkorders?search=:${id}`;

        // Create the formatted message with house emoji and formatting
        const shareText = `🏠 New work order for:\n${tenantName} at ${propertyAddress}\n\n${url}`;

        // Use textarea + execCommand technique for better cross-environment support
        const textArea = document.createElement('textarea');
        textArea.value = shareText;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        textArea.setSelectionRange(0, 99999); // For mobile devices

        try {
          const successful = document.execCommand('copy');
          if (successful) {
            console.log('Share text copied to clipboard:', shareText);
            this.showShareDialog(
              'Copied!',
              'The share text has been copied to your clipboard.',
            );
          } else {
            console.warn('execCommand copy failed, showing share text');
            this.showShareDialog('Unable to copy to clipboard', shareText);
          }
        } catch (err) {
          console.error('Failed to copy share text: ', err);
          this.showShareDialog('Unable to copy to clipboard', shareText);
        } finally {
          document.body.removeChild(textArea);
        }
      } else {
        console.warn('Work order does not exist');
      }
    },

    showShareDialog(title, text) {
      this.shareTitle = title;
      // Strip HTML tags from text before displaying
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = text;
      this.shareText = tempDiv.textContent || tempDiv.innerText || text;
      this.showSharePopup = true;
    },

    closeShareDialog() {
      this.showSharePopup = false;
      this.shareTitle = '';
      this.shareText = '';
    },

    selectAllShareText() {
      this.$nextTick(() => {
        const textElement = this.$refs.shareTextContent;
        if (textElement) {
          const range = document.createRange();
          range.selectNodeContents(textElement);
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(range);
        }
      });
    },

    // Summary popup methods
    async fetchSummaryStats() {
      try {
        const response = await axios.get('/api/work-orders/summary');
        if (response.status === 200) {
          this.summaryStats = response.data;
          console.log('Summary stats loaded:', this.summaryStats);
        }
      } catch (error) {
        console.error('Error fetching summary stats:', error);
      }
    },

    async openSummaryPopup() {
      this.showSummaryPopup = true;
      if (!this.summaryStats) {
        await this.fetchSummaryStats();
      }
    },

    closeSummaryPopup() {
      this.showSummaryPopup = false;
    },

    filterByAge(statKey, label) {
      if (this.summaryStats && this.summaryStats[statKey]) {
        this.filteredByAgeIds = this.summaryStats[statKey].ids;
        this.ageFilterLabel = label;
        this.workOrderFilter = ''; // Clear text search

        // Show done items if filtering by "Done Today", "Done This Week", or "Done Last Month"
        if (
          statKey === 'doneToday' ||
          statKey === 'doneThisWeek' ||
          statKey === 'doneLastMonth'
        ) {
          this.hideDone = false;
        }

        // Show hint and auto-hide after 5 seconds
        this.showAgeFilterHint = true;

        // Clear any existing timeout
        if (this.ageFilterHintTimeout) {
          clearTimeout(this.ageFilterHintTimeout);
        }

        // Set new timeout to hide hint after 5 seconds
        this.ageFilterHintTimeout = setTimeout(() => {
          this.showAgeFilterHint = false;
        }, 7000);

        this.closeSummaryPopup();
        console.log(
          `Filtering by ${label}, ${this.filteredByAgeIds.length} IDs`,
        );
      }
    },

    clearAgeFilter() {
      this.filteredByAgeIds = null;
      this.ageFilterLabel = '';
      this.workOrderFilter = '';
      this.hideDone = true; // Reset to hide done items
      this.showAgeFilterHint = false; // Hide hint when clearing

      // Clear timeout if exists
      if (this.ageFilterHintTimeout) {
        clearTimeout(this.ageFilterHintTimeout);
        this.ageFilterHintTimeout = null;
      }

      console.log('Age filter cleared');
    },

    async refreshAndShowSummary() {
      await this.fetchWorkOrders(true);
      await this.openSummaryPopup();
    },

    toggleReviewDefaultPin() {
      this.reviewDefaultPinned = !this.reviewDefaultPinned;
      saveReviewDefaultPinned(this.reviewDefaultPinned);
      this.showNotificationMessage(
        this.reviewDefaultPinned
          ? 'Review is now the default view'
          : 'Review is no longer the default view',
      );
    },

    /**
     * Classic toolbar. Review search and location stay set after Classic is
     * closed, and that empty filtered list was disabling this button.
     */
    openReviewFromClassic() {
      this.reviewLocationKey = '';
      this.reviewSearchQuery = '';
      this.reviewSearchDraft = '';
      this.reviewSearchOpen = false;
      this.openReviewPopup({ silentEmpty: true });
    },

    /**
     * Open Review when pinned as default. Never fall back to the summary popup.
     * The panel can open empty; the list fills in as IndexedDB/API data arrives.
     */
    async openPinnedReviewDefault(focusId = null) {
      this.openReviewPopup({ silentEmpty: true, focusId });
      return this.showReviewPopup;
    },

    openReviewPopup({ silentEmpty = false, focusId = null } = {}) {
      this.reviewPersonFilter = null;
      this.reviewAssigneeFilter = null;
      // Restore last preferred sort before computing the open list
      this.reviewSortDir = loadReviewSortDir();
      // Drop stale location key if that building has no open WOs now
      if (
        this.reviewLocationKey &&
        !this.reviewLocationOptions.some(
          (loc) => loc.key === this.reviewLocationKey,
        )
      ) {
        this.reviewLocationKey = '';
      }
      let list = this.reviewOpenWorkOrders;
      if (!list.length && !silentEmpty) {
        this.showNotificationMessage('No open work orders to review');
        return false;
      }
      this.reviewIndex = 0;
      this.reviewIdEditing = false;
      const selectedTile =
        focusId == null && this.selectedTileIndex != null
          ? this.filteredWorkOrders[this.selectedTileIndex]
          : null;
      const selectedOpenId =
        selectedTile?.id &&
        selectedTile.completedOn == null &&
        !selectedTile.deleted
          ? selectedTile.id
          : null;
      const focus = reviewOpenFocusId({
        explicitFocusId: focusId,
        selectedId: selectedOpenId,
      });
      this.reviewUserNavigated = focus != null;
      this.reviewLandOnNewest = focus == null;
      if (focus != null) {
        let idx = list.findIndex((wo) => Number(wo.id) === Number(focus));
        if (idx < 0 && (this.reviewLocationKey || this.reviewSearchQuery)) {
          this.reviewLocationKey = '';
          this.reviewSearchQuery = '';
          this.reviewSearchDraft = '';
          this.reviewSearchOpen = false;
          list = this.reviewOpenWorkOrders;
          idx = list.findIndex((wo) => Number(wo.id) === Number(focus));
        }
        if (idx >= 0) this.reviewIndex = idx;
      } else if (list.length) {
        this.reviewIndex = newestWorkOrderIndex(list);
        this.reviewLandOnNewest = false;
      }
      this.showReviewPopup = true;
      this.showSummaryPopup = false;
      document.addEventListener('keydown', this.handleReviewKeydown);
      return true;
    },

    /**
     * A ?search=:id link lands on that work order in Review.
     * A completed or deleted order shows a notice and stays on open Review.
     */
    syncReviewDeepLink() {
      const id = this.reviewDeepLinkId;
      if (id == null || !this.showReviewPopup) return;
      let idx = this.reviewOpenWorkOrders.findIndex(
        (wo) => Number(wo.id) === Number(id),
      );
      if (idx < 0 && (this.reviewLocationKey || this.reviewSearchQuery)) {
        this.reviewLocationKey = '';
        this.reviewSearchQuery = '';
        this.reviewSearchDraft = '';
        this.reviewSearchOpen = false;
        idx = this.reviewOpenWorkOrders.findIndex(
          (wo) => Number(wo.id) === Number(id),
        );
      }
      if (idx >= 0) {
        this.reviewIndex = idx;
        this.reviewDeepLinkId = null;
        if (this._reviewVoiceOpenFromLink) {
          this._reviewVoiceOpenFromLink = false;
          this.$nextTick(() => this.openReviewVoice());
        }
        return;
      }
      const known = (this.workOrders || []).find(
        (wo) => Number(wo.id) === Number(id),
      );
      if (workOrderLinkIsFinished(known)) {
        this.noticeFinishedWorkOrderLink();
        return;
      }
      if (!known) {
        this.lookupFinishedWorkOrderLink(id);
      }
    },

    noticeFinishedWorkOrderLink() {
      this._reviewVoiceOpenFromLink = false;
      this.reviewDeepLinkId = null;
      this.workOrderFilter = '';
      this.hideDone = true;
      this.showCompletedWorkOrderNotice = true;
      const list = this.reviewOpenWorkOrders;
      if (list.length) {
        this.reviewIndex = newestWorkOrderIndex(list);
        this.reviewLandOnNewest = false;
      }
    },

    dismissCompletedWorkOrderNotice() {
      this.showCompletedWorkOrderNotice = false;
    },

    async lookupFinishedWorkOrderLink(id) {
      if (this._reviewLinkLookedUp === id) return;
      this._reviewLinkLookedUp = id;
      try {
        const { data } = await axios.get(`/api/maintenance-requests/${id}`);
        if (this.reviewDeepLinkId !== id || !this.showReviewPopup) return;
        const wo = Array.isArray(data) ? data[0] : data;
        if (workOrderLinkIsFinished(wo)) {
          this.noticeFinishedWorkOrderLink();
        }
      } catch (err) {
        console.warn(
          'Work order link lookup failed:',
          err.response?.status || err.message,
        );
      }
    },

    syncReviewIdInput() {
      const wo = this.reviewCurrentWorkOrder;
      this.reviewIdInput = wo?.id != null ? String(wo.id) : '';
    },

    beginReviewIdEdit() {
      if (this.reviewIdEditing) return;
      this.reviewIdEditing = true;
      this.reviewIdInput = '';
      this.$nextTick(() => {
        const el = this.$refs.reviewIdInput;
        if (el && typeof el.focus === 'function') el.focus();
      });
    },

    onReviewIdMouseDown(event) {
      if (this.reviewIdEditing) return;
      event.preventDefault();
      this.beginReviewIdEdit();
    },

    onReviewIdTap() {
      this.beginReviewIdEdit();
    },

    onReviewIdBlur() {
      if (!this.reviewIdEditing) return;
      const typed = String(this.reviewIdInput || '').trim();
      if (!typed) {
        this.reviewIdEditing = false;
        this.syncReviewIdInput();
        return;
      }
      this.submitReviewIdJump();
    },

    rememberReviewWorkOrder(id) {
      const saved = saveReviewLastWorkOrderId(id);
      this.reviewLastWorkOrderId = saved;
    },

    markReviewNavigation() {
      this.reviewUserNavigated = true;
      this.reviewLandOnNewest = false;
    },

    goBackToStoredReviewWorkOrder() {
      const id = this.reviewLastWorkOrderId || loadReviewLastWorkOrderId();
      if (!id) return;
      this.jumpToReviewWorkOrder(id);
    },

    submitReviewIdJump() {
      const typed = String(this.reviewIdInput || '').trim();
      if (!typed) {
        this.reviewIdEditing = false;
        this.syncReviewIdInput();
        return;
      }
      this.jumpToReviewWorkOrder(typed);
    },

    findWorkOrderById(id) {
      const want = Number(id);
      return (this.workOrders || []).find((wo) => Number(wo.id) === want) || null;
    },

    jumpToOpenReviewIndex(id) {
      const idx = this.reviewOpenWorkOrders.findIndex(
        (wo) => Number(wo.id) === Number(id),
      );
      if (idx < 0) return false;
      this.reviewIndex = idx;
      this.reviewIdEditing = false;
      this.syncReviewIdInput();
      return true;
    },

    async jumpToReviewWorkOrder(raw) {
      this.markReviewNavigation();
      const id = parseReviewWorkOrderId(raw);
      if (!id) {
        this.showNotificationMessage('Enter a work order number');
        this.reviewIdEditing = false;
        this.syncReviewIdInput();
        return;
      }
      if (this.jumpToOpenReviewIndex(id)) return;

      let wo = this.findWorkOrderById(id);
      if (!wo) {
        try {
          const { data } = await axios.get(`/api/maintenance-requests/${id}`);
          wo = Array.isArray(data) ? data[0] : data;
        } catch (err) {
          if (err.response && err.response.status === 404) {
            this.showNotificationMessage(`Work order #${id} not found`);
          } else {
            this.showNotificationMessage(`Could not load work order #${id}`);
          }
          this.reviewIdEditing = false;
          this.syncReviewIdInput();
          return;
        }
      }

      if (!wo || !wo.id) {
        this.showNotificationMessage(`Work order #${id} not found`);
        this.reviewIdEditing = false;
        this.syncReviewIdInput();
        return;
      }
      if (wo.deleted) {
        this.showNotificationMessage(`Work order #${id} was deleted`);
        this.reviewIdEditing = false;
        this.syncReviewIdInput();
        return;
      }
      if (wo.completedOn != null) {
        this.showNotificationMessage(`Work order #${id} is already completed`);
        this.reviewIdEditing = false;
        this.syncReviewIdInput();
        return;
      }

      if (!this.findWorkOrderById(wo.id)) {
        this.workOrders = [...(this.workOrders || []), wo];
      }
      this.reviewPersonFilter = null;
      this.reviewAssigneeFilter = null;
      this.reviewLocationKey = '';
      this.reviewSearchQuery = '';
      this.reviewSearchDraft = '';
      this.reviewSearchOpen = false;
      this.$nextTick(() => {
        if (!this.jumpToOpenReviewIndex(wo.id)) {
          this.showNotificationMessage(`Work order #${id} is not open`);
          this.reviewIdEditing = false;
          this.syncReviewIdInput();
        }
      });
    },

    closeReviewPopup() {
      this.clearReviewLongPress();
      document.removeEventListener('keydown', this.handleReviewKeydown);
      this.reviewIdEditing = false;
      this.showReviewPopup = false;
      this.closeDailyBriefing();
      this.showReviewAssignPopup = false;
      this.reviewAssignPopupMode = 'assign';
      this.reviewPersonFilter = null;
      this.reviewAssigneeFilter = null;
      this.reviewSearchOpen = false;
      this.reviewIndex = 0;
      this.reviewRefreshing = false;
      this.clearReviewPhotoSets();
      this.reviewPhotoUrl = '';
      this.reviewPhotos = [];
      this.reviewPhotoIndex = 0;
      this.showReviewPhotoFullscreen = false;
      this.reviewPhotoLoading = false;
      this.reviewPhotoLoadToken += 1;
      // Refresh summary counts if summary is reopened later
      this.summaryStats = null;
    },

    openReviewVoice(playNow) {
      if (!this.reviewCurrentWorkOrder) return;
      this.reviewVoiceError = '';
      this.showReviewVoice = true;
      this.$nextTick(() => {
        this.paintReviewVoiceSpectrumIdle();
        if (playNow && this.reviewVoiceNote) this.toggleReviewVoicePlay();
      });
    },

    closeReviewVoice() {
      this.showReviewVoice = false;
      this.reviewVoiceError = '';
      this.stopReviewVoicePlayback();
      this.stopReviewVoiceRecorder(false);
    },

    teardownReviewVoice() {
      this._reviewVoiceLoadId = (this._reviewVoiceLoadId || 0) + 1;
      this._reviewVoiceSaveId = (this._reviewVoiceSaveId || 0) + 1;
      this.closeReviewVoice();
      this.reviewVoiceNote = null;
      this._reviewVoiceNoteFor = null;
      this.reviewVoiceSaving = false;
      this.reviewVoiceDeleting = false;
    },

    prepareReviewVoiceForWorkOrder(workOrderId) {
      this.showReviewVoice = false;
      this.reviewVoiceError = '';
      this.stopReviewVoicePlayback();
      this.stopReviewVoiceRecorder(false);
      this.loadReviewVoiceNote(workOrderId);
    },

    async loadReviewVoiceNote(workOrderId) {
      const id = Number(workOrderId);
      const token = (this._reviewVoiceLoadId || 0) + 1;
      this._reviewVoiceLoadId = token;
      if (!Number.isFinite(id) || id <= 0) {
        this.reviewVoiceNote = null;
        this._reviewVoiceNoteFor = null;
        return;
      }
      if (this._reviewVoiceNoteFor !== id) {
        this.reviewVoiceNote = null;
        this._reviewVoiceNoteFor = id;
      }
      try {
        const { data } = await axios.get(`/api/work-order-voice-note/${id}`);
        if (this._reviewVoiceLoadId !== token) return;
        if (Number(this.reviewCurrentWorkOrder?.id) !== id) return;
        this.reviewVoiceNote =
          data && data.id
            ? { id: data.id, title: data.title, createdOn: data.createdOn }
            : null;
      } catch (err) {
        if (this._reviewVoiceLoadId !== token) return;
        this.reviewVoiceNote = null;
      }
    },

    reviewVoiceMime() {
      const types = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg;codecs=opus',
      ];
      if (
        typeof MediaRecorder === 'undefined' ||
        typeof MediaRecorder.isTypeSupported !== 'function'
      ) {
        return '';
      }
      return types.find((type) => MediaRecorder.isTypeSupported(type)) || '';
    },

    reviewVoiceFileName(mime) {
      const type = String(mime || '');
      if (type.includes('mp4')) return 'recording.mp4';
      if (type.includes('ogg')) return 'recording.ogg';
      return 'recording.webm';
    },

    releaseReviewVoiceStream() {
      const stream = this._reviewVoiceStream;
      this._reviewVoiceStream = null;
      if (stream && stream.getTracks) {
        stream.getTracks().forEach((track) => track.stop());
      }
    },

    stopReviewVoiceSpectrum() {
      if (this._reviewVoiceSpectrumFrame) {
        cancelAnimationFrame(this._reviewVoiceSpectrumFrame);
        this._reviewVoiceSpectrumFrame = 0;
      }
      const ctx = this._reviewVoiceAudioCtx;
      this._reviewVoiceAudioCtx = null;
      this._reviewVoiceAnalyser = null;
      this._reviewVoiceSpectrumBins = null;
      if (ctx && ctx.close) ctx.close().catch(() => {});
      this.paintReviewVoiceSpectrumIdle();
    },

    paintReviewVoiceSpectrumIdle() {
      const canvas = this.$refs.reviewVoiceSpectrum;
      if (!canvas || !canvas.getContext) return;
      const width = canvas.clientWidth || 320;
      const height = canvas.clientHeight || 56;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      const g = canvas.getContext('2d');
      if (!g) return;
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      g.clearRect(0, 0, width, height);
      g.strokeStyle = '#d4c8db';
      g.lineWidth = 1;
      g.beginPath();
      g.moveTo(8, height - 8);
      g.lineTo(width - 8, height - 8);
      g.stroke();
    },

    startReviewVoiceSpectrum(stream) {
      this.stopReviewVoiceSpectrum();
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx || !stream) return;
      try {
        const ctx = new AudioCtx();
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.75;
        source.connect(analyser);
        this._reviewVoiceAudioCtx = ctx;
        this._reviewVoiceAnalyser = analyser;
        this._reviewVoiceSpectrumBins = new Uint8Array(analyser.frequencyBinCount);
        if (ctx.state === 'suspended' && ctx.resume) ctx.resume();
        this.drawReviewVoiceSpectrum();
      } catch (err) {
        this.stopReviewVoiceSpectrum();
      }
    },

    drawReviewVoiceSpectrum() {
      const analyser = this._reviewVoiceAnalyser;
      const canvas = this.$refs.reviewVoiceSpectrum;
      if (!analyser || !canvas || !this.reviewVoiceRecording) {
        this.paintReviewVoiceSpectrumIdle();
        return;
      }
      const bins = this._reviewVoiceSpectrumBins;
      analyser.getByteFrequencyData(bins);
      const width = canvas.clientWidth || 320;
      const height = canvas.clientHeight || 56;
      const dpr = window.devicePixelRatio || 1;
      const bitmapW = Math.round(width * dpr);
      const bitmapH = Math.round(height * dpr);
      if (canvas.width !== bitmapW || canvas.height !== bitmapH) {
        canvas.width = bitmapW;
        canvas.height = bitmapH;
      }
      const g = canvas.getContext('2d');
      if (!g) return;
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      g.clearRect(0, 0, width, height);
      const barCount = 28;
      const gap = 3;
      const usable = Math.max(barCount, width - 16);
      const barWidth = (usable - gap * (barCount - 1)) / barCount;
      const used = Math.max(barCount, Math.floor(bins.length * 0.55));
      const step = Math.max(1, Math.floor(used / barCount));
      for (let i = 0; i < barCount; i += 1) {
        let peak = 0;
        const start = i * step;
        for (let j = 0; j < step; j += 1) {
          peak = Math.max(peak, bins[start + j] || 0);
        }
        const mag = peak / 255;
        const barH = Math.max(2, mag * (height - 12));
        const x = 8 + i * (barWidth + gap);
        const y = height - 6 - barH;
        g.fillStyle = mag > 0.72 ? '#6e5875' : '#a080a8';
        const w = Math.max(1, barWidth);
        if (g.roundRect) {
          g.beginPath();
          g.roundRect(x, y, w, barH, 2);
          g.fill();
        } else {
          g.fillRect(x, y, w, barH);
        }
      }
      this._reviewVoiceSpectrumFrame = requestAnimationFrame(() => {
        this.drawReviewVoiceSpectrum();
      });
    },

    stopReviewVoiceRecorder(save) {
      this.stopReviewVoiceSpectrum();
      const rec = this._reviewVoiceRecorder;
      if (rec && rec.state !== 'inactive') {
        this._reviewVoiceRecorder = null;
        this._reviewVoiceSaveOnStop = !!save;
        try {
          rec.stop();
        } catch (err) {
          this._reviewVoiceSaveOnStop = false;
          this.reviewVoiceRecording = false;
          this.releaseReviewVoiceStream();
        }
        return;
      }
      this.reviewVoiceRecording = false;
      if (!this._reviewVoiceSaveOnStop) this.releaseReviewVoiceStream();
    },

    openReviewVoiceOnLocalhost(workOrderId) {
      const url = reviewVoiceLocalhostUrl(workOrderId);
      if (!url) {
        this.reviewVoiceError = 'Microphone is not available.';
        return;
      }
      const child = window.open(url, '_blank', 'noopener,noreferrer');
      if (child) child.opener = null;
      this.reviewVoiceError = child
        ? 'Opened localhost so the microphone can be used.'
        : 'Allow pop-ups, then record again to open localhost.';
    },

    async toggleReviewVoiceRecord() {
      if (this.reviewVoiceSaving) return;
      if (this.reviewVoiceRecording) {
        this.stopReviewVoiceRecorder(true);
        return;
      }
      const wo = this.reviewCurrentWorkOrder;
      if (!wo || !wo.id) return;
      this.stopReviewVoicePlayback();
      this.reviewVoiceError = '';
      if (
        !reviewVoicePageCanRecord(
          window.location.hostname,
          window.isSecureContext,
        )
      ) {
        this.openReviewVoiceOnLocalhost(wo.id);
        return;
      }
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.reviewVoiceError = 'Microphone is not available.';
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        if (
          !this.showReviewVoice ||
          Number(this.reviewCurrentWorkOrder?.id) !== Number(wo.id)
        ) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        const mime = this.reviewVoiceMime();
        const rec = mime
          ? new MediaRecorder(stream, { mimeType: mime })
          : new MediaRecorder(stream);
        this._reviewVoiceStream = stream;
        this._reviewVoiceRecorder = rec;
        this._reviewVoiceChunks = [];
        this._reviewVoiceMime = rec.mimeType || mime || 'audio/webm';
        this._reviewVoiceRecordFor = Number(wo.id);
        this._reviewVoiceSaveOnStop = false;
        rec.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) {
            this._reviewVoiceChunks.push(event.data);
          }
        };
        rec.onstop = () => {
          this.stopReviewVoiceSpectrum();
          const shouldSave = this._reviewVoiceSaveOnStop === true;
          this._reviewVoiceSaveOnStop = false;
          const chunks = this._reviewVoiceChunks || [];
          const type = this._reviewVoiceMime || 'audio/webm';
          const forId = this._reviewVoiceRecordFor;
          this._reviewVoiceChunks = [];
          this.reviewVoiceRecording = false;
          this.releaseReviewVoiceStream();
          if (!shouldSave) return;
          const blob = new Blob(chunks, { type });
          if (!blob.size) {
            this.reviewVoiceError = 'No audio was recorded.';
            return;
          }
          this.saveReviewVoiceBlob(blob, forId, type);
        };
        rec.start();
        this.reviewVoiceRecording = true;
        this.startReviewVoiceSpectrum(stream);
      } catch (err) {
        this.reviewVoiceRecording = false;
        this._reviewVoiceRecorder = null;
        this.stopReviewVoiceSpectrum();
        this.releaseReviewVoiceStream();
        this.reviewVoiceError = 'Microphone is not available.';
      }
    },

    async saveReviewVoiceBlob(blob, workOrderId, mime) {
      const id = Number(workOrderId);
      const saveToken = (this._reviewVoiceSaveId || 0) + 1;
      this._reviewVoiceSaveId = saveToken;
      const previousId = this.reviewVoiceNote && Number(this.reviewVoiceNote.id);
      this.reviewVoiceSaving = true;
      this.reviewVoiceError = '';
      const filename = this.reviewVoiceFileName(mime);
      try {
        const outcome = await deliverOrQueue({
          online: readNavigatorOnline(),
          item: {
            kind: 'voice-note',
            title: `Work order #${id}`,
            filename,
            attachedTo: '1',
            attachedId: String(id),
            replaceVoiceNoteId: previousId || null,
            audio: blob,
          },
          send: async () => {
            const formData = new FormData();
            formData.append('audioBlob', blob, filename);
            formData.append('title', `Work order #${id}`);
            formData.append('attachedTo', 1);
            formData.append('attachedId', id);
            return axios.post('/api/saveVoiceNote', formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
              skipGlobalError: true,
            });
          },
        });
        if (this._reviewVoiceSaveId !== saveToken) return;
        if (outcome.queued) {
          this.reviewVoiceError = OUTBOUND_HOLD_TEXT;
          return;
        }
        const response = outcome.result;
        const metadataId = response && response.data && response.data.metadataId;
        if (!metadataId) throw new Error('Save failed');
        if (previousId && previousId !== Number(metadataId)) {
          try {
            await axios.put(`/api/delete-voice-memo/${previousId}`);
          } catch (delErr) {
            // The new note is already saved.
          }
        }
        if (this._reviewVoiceSaveId !== saveToken) return;
        if (Number(this.reviewCurrentWorkOrder?.id) === id) {
          this.reviewVoiceNote = {
            id: metadataId,
            title: `Work order #${id}`,
          };
          this._reviewVoiceNoteFor = id;
        }
      } catch (err) {
        if (this._reviewVoiceSaveId !== saveToken) return;
        this.reviewVoiceError = 'Could not save this recording.';
      } finally {
        if (this._reviewVoiceSaveId === saveToken) this.reviewVoiceSaving = false;
      }
    },

    reviewVoiceClock(seconds) {
      return formatVoicePlaybackClock(seconds);
    },

    bindReviewVoiceAudio() {
      const audio = this.$refs.reviewVoicePlayer;
      if (!audio) return null;
      this._reviewVoiceAudio = audio;
      audio.volume = 1;
      audio.muted = false;
      if (this._reviewVoiceBoundEl !== audio) {
        this._reviewVoiceBoundEl = audio;
        audio.addEventListener('timeupdate', () => {
          if (this.reviewVoiceSeeking || this._reviewVoiceProbing) return;
          if (!this.reviewVoicePlaying && !this.reviewVoicePaused) return;
          this.reviewVoiceCurrent = audio.currentTime || 0;
          const dur = readAudioDuration(audio);
          if (dur > 0) {
            this.reviewVoiceDuration = Math.max(this.reviewVoiceDuration || 0, dur);
          }
          this.syncReviewVoiceScrub();
        });
        audio.addEventListener('loadedmetadata', () => {
          const dur = readAudioDuration(audio);
          if (dur > 0) this.reviewVoiceDuration = dur;
          this.syncReviewVoiceScrub();
        });
        audio.addEventListener('ended', () => {
          this.reviewVoicePlaying = false;
          this.reviewVoicePaused = false;
          this.reviewVoiceCurrent = 0;
          this.reviewVoiceDuration = 0;
        });
      }
      return audio;
    },

    syncReviewVoiceScrub() {
      if (this.reviewVoiceSeeking) return;
      this._reviewVoiceScrubMax = applyScrubBar(
        this.$refs.reviewVoiceScrub,
        this.reviewVoiceCurrent,
        this.reviewVoiceDuration,
        this._reviewVoiceScrubMax,
      );
    },

    reviewVoiceBlobDuration(blob) {
      return measureBlobDuration(blob, (bytes) => {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return Promise.reject(new Error('no audio context'));
        const ctx = new Ctx();
        return ctx.decodeAudioData(bytes).finally(() => {
          if (typeof ctx.close === 'function') ctx.close();
        });
      });
    },

    onReviewVoiceScrubInput(event) {
      this.reviewVoiceSeeking = true;
      const t = clampVoiceSeekTime(event.target.value, this.reviewVoiceDuration);
      this.reviewVoiceCurrent = t;
      if (this._reviewVoiceAudio) this._reviewVoiceAudio.currentTime = t;
    },

    onReviewVoiceScrubCommit(event) {
      const t = clampVoiceSeekTime(event.target.value, this.reviewVoiceDuration);
      this.reviewVoiceCurrent = t;
      if (this._reviewVoiceAudio) this._reviewVoiceAudio.currentTime = t;
      this.reviewVoiceSeeking = false;
    },

    stopReviewVoicePlayback() {
      this._reviewVoicePlayToken = (this._reviewVoicePlayToken || 0) + 1;
      this._reviewVoiceScrubMax = 0;
      const audio = this._reviewVoiceAudio;
      if (audio) {
        audio.pause();
        audio.removeAttribute('src');
      }
      if (this._reviewVoiceObjectUrl) {
        URL.revokeObjectURL(this._reviewVoiceObjectUrl);
        this._reviewVoiceObjectUrl = null;
      }
      this.reviewVoicePlaying = false;
      this.reviewVoicePaused = false;
      this.reviewVoiceCurrent = 0;
      this.reviewVoiceDuration = 0;
      this.reviewVoiceSeeking = false;
    },

    async reviewVoiceBlob(data) {
      const raw = data instanceof Blob ? data : new Blob([data]);
      if (raw.size < 16) {
        throw new Error('No audio was saved for this recording.');
      }
      const head = new Uint8Array(await raw.slice(0, 16).arrayBuffer());
      if (head[0] === 0x7b || head[0] === 0x3c) {
        throw new Error('Server did not return audio.');
      }
      const mime = sniffAudioMimeFromBytes(head);
      return raw.slice(0, raw.size, mime);
    },

    async toggleReviewVoicePlay() {
      const note = this.reviewVoiceNote;
      if (
        !note ||
        !note.id ||
        this.reviewVoiceRecording ||
        this.reviewVoiceSaving
      ) {
        return;
      }
      this.reviewVoiceError = '';
      const audio = this.bindReviewVoiceAudio();
      if (!audio) return;
      if (this.reviewVoicePlaying || this.reviewVoicePaused) {
        try {
          if (audio.paused) {
            await audio.play();
            this.reviewVoicePaused = false;
            this.reviewVoicePlaying = true;
          } else {
            audio.pause();
            this.reviewVoicePlaying = false;
            this.reviewVoicePaused = true;
          }
        } catch (err) {
          this.reviewVoiceError =
            (err && err.message) || 'Could not play this recording.';
        }
        return;
      }
      this.stopReviewVoicePlayback();
      const playToken = this._reviewVoicePlayToken;
      const player = this.bindReviewVoiceAudio();
      if (!player) return;
      try {
        const response = await axios.get(`/api/get-audioblob/${note.id}`, {
          responseType: 'blob',
          params: voicePlaybackParams(navigator.userAgent),
        });
        if (this._reviewVoicePlayToken !== playToken) return;
        const blob = await this.reviewVoiceBlob(response.data);
        if (this._reviewVoicePlayToken !== playToken) return;
        if (this._reviewVoiceObjectUrl) {
          URL.revokeObjectURL(this._reviewVoiceObjectUrl);
        }
        this._reviewVoiceObjectUrl = URL.createObjectURL(blob);
        // MediaRecorder WebM leaves duration at Infinity, so a range max of 0
        // pins the thumb. Decode the clip before play so max is a real length.
        let duration = await this.reviewVoiceBlobDuration(blob);
        if (this._reviewVoicePlayToken !== playToken) return;
        player.src = this._reviewVoiceObjectUrl;
        player.load();
        if (!(duration > 0)) {
          this._reviewVoiceProbing = true;
          try {
            duration = await revealAudioDuration(player);
          } finally {
            this._reviewVoiceProbing = false;
          }
        }
        if (this._reviewVoicePlayToken !== playToken) return;
        this.reviewVoiceDuration = duration > 0 ? duration : 0;
        this.reviewVoiceCurrent = 0;
        this.reviewVoicePlaying = true;
        this.reviewVoicePaused = false;
        await this.$nextTick();
        if (this._reviewVoicePlayToken !== playToken) return;
        this.syncReviewVoiceScrub();
        await player.play();
        if (!note.listenedOn) {
          note.listenedOn = new Date().toISOString();
          axios.put(`/api/mark-voicememo-listened/${note.id}`).catch(() => {});
        }
      } catch (err) {
        this.reviewVoiceError =
          (err && err.message) || 'Could not play this recording.';
        this.stopReviewVoicePlayback();
      }
    },

    async deleteReviewVoice() {
      const note = this.reviewVoiceNote;
      if (!note || !note.id || this.reviewVoiceDeleting || this.reviewVoiceRecording) {
        return;
      }
      this.reviewVoiceDeleting = true;
      this.reviewVoiceError = '';
      this.stopReviewVoicePlayback();
      try {
        await axios.put(`/api/delete-voice-memo/${note.id}`);
        if (this.reviewVoiceNote && this.reviewVoiceNote.id === note.id) {
          this.reviewVoiceNote = null;
        }
      } catch (err) {
        this.reviewVoiceError = 'Could not delete this recording.';
      } finally {
        this.reviewVoiceDeleting = false;
      }
    },

    newestReviewWorkOrderIds(limit = 20) {
      return (this.reviewOpenWorkOrders || [])
        .slice()
        .sort((a, b) => (b.id || 0) - (a.id || 0))
        .slice(0, limit)
        .map((wo) => wo.id);
    },

    invalidateReviewFirstPhoto(workOrderId) {
      const key = Number(workOrderId);
      if (!Number.isFinite(key)) return;
      this.reviewPrefetchKey = '';
      if (
        this.showReviewPopup &&
        Number(this.reviewCurrentWorkOrder?.id) === key
      ) {
        this.loadReviewPhotos(key);
      }
    },

    dropDisplayedReviewPhoto(workOrderId, photoId) {
      const key = String(workOrderId);
      const previous = this.reviewPhotoSets[key];
      if (!previous) return;
      const next = previous.filter(
        (photo) => String(photo.id) !== String(photoId),
      );
      this.rememberReviewPhotoSet(workOrderId, next);
      if (Number(this.reviewCurrentWorkOrder?.id) !== Number(workOrderId)) {
        return;
      }
      this.reviewPhotos = next;
      if (this.reviewPhotoIndex >= next.length) {
        this.reviewPhotoIndex = Math.max(0, next.length - 1);
      }
      this.reviewPhotoUrl = next[this.reviewPhotoIndex]?.photoUrl || '';
    },

    async prefetchReviewFirstPhotos() {
      if (!this.showReviewPopup) return;
      const ids = this.newestReviewWorkOrderIds(20);
      const key = ids.join(',');
      if (!key || key === this.reviewPrefetchKey) return;
      this.reviewPrefetchKey = key;
      const queue = ids.slice();
      const worker = async () => {
        while (queue.length && this.showReviewPopup) {
          if ((await cachedPhotoCount()) >= PHOTO_CACHE_LIMIT) return;
          const id = queue.shift();
          try {
            const existing = await getCachedPhotosForWorkOrder(id);
            if (existing.length) continue;
            const response = await axios.get(`/api/work-orders/${id}/photos`);
            const photos = Array.isArray(response.data) ? response.data : [];
            if (!photos.length) continue;
            const blob = await this.fetchWorkOrderPhotoBlob(photos[0].id);
            if (!blob) continue;
            await putCachedPhotoPrefill({
              id: photos[0].id,
              workOrderId: id,
              caption: photos[0].caption || '',
              modifiedOn: photos[0].modifiedOn || null,
              blob,
            });
          } catch (err) {
            console.error(`Error prefetching review photo ${id}:`, err);
          }
        }
      };
      await worker();
    },

    async fetchWorkOrderPhotoBlob(photoId) {
      const key = Number(photoId);
      if (!Number.isFinite(key)) return null;
      if (!this.reviewPhotoGiveUp) this.reviewPhotoGiveUp = new Set();
      if (this.reviewPhotoGiveUp.has(key)) return null;
      try {
        const imageResponse = await axios.get(`/api/photos/${photoId}`, {
          responseType: 'blob',
          timeout: 8000,
        });
        const blob = imageResponse.data;
        if (!this.isDisplayableWorkOrderPhotoBlob(blob)) {
          this.reviewPhotoGiveUp.add(key);
          return null;
        }
        return blob;
      } catch (err) {
        this.reviewPhotoGiveUp.add(key);
        console.error(`Error fetching photo ${photoId}:`, err);
        return null;
      }
    },

    isDisplayableWorkOrderPhotoBlob(blob) {
      if (!blob || typeof blob.size !== 'number' || blob.size <= 0) {
        return false;
      }
      const type = String(blob.type || '');
      return !/json|text\/html|text\/plain/i.test(type);
    },

    async loadWorkOrderPhotoPages(
      workOrderId,
      manifest,
      painted,
      onPages,
      stillCurrent,
    ) {
      const list = Array.isArray(manifest) ? manifest : [];
      let current = mergeDisplayedPhotos(painted, list, new Map());
      const publish = (pages) => {
        if (pages.length && typeof onPages === 'function') onPages(pages);
      };
      const active = () =>
        typeof stillCurrent !== 'function' || stillCurrent();
      publish(current);
      const have = new Set(current.map((photo) => String(photo.id)));
      if (have.size) {
        try {
          await touchCachedPhotos(current.map((photo) => photo.id));
        } catch (err) {
          console.error('Error touching work order photo cache:', err);
        }
      }
      for (const item of list) {
        if (!active()) return current;
        if (!item || item.id == null || have.has(String(item.id))) continue;
        const blob = await this.fetchWorkOrderPhotoBlob(item.id);
        if (!blob) continue;
        if (!active()) return current;
        try {
          await putCachedPhoto({
            id: item.id,
            workOrderId,
            caption: item.caption || '',
            modifiedOn: item.modifiedOn || null,
            blob,
          });
        } catch (err) {
          console.error(`Error caching photo ${item.id}:`, err);
        }
        if (!active()) return current;
        current = mergeDisplayedPhotos(
          current,
          list,
          new Map([
            [
              item.id,
              {
                photoUrl: URL.createObjectURL(blob),
                caption: item.caption || '',
                modifiedOn: item.modifiedOn || null,
              },
            ],
          ]),
        );
        have.add(String(item.id));
        publish(current);
      }
      if (!active()) return current;
      try {
        await touchCachedPhotos(current.map((photo) => photo.id));
      } catch (err) {
        console.error('Error touching work order photo cache:', err);
      }
      return current;
    },

    revokePhotoUrlList(photos) {
      for (const photo of photos || []) {
        if (photo?.photoUrl && String(photo.photoUrl).startsWith('blob:')) {
          URL.revokeObjectURL(photo.photoUrl);
        }
      }
    },

    revokeReviewPhotoObjectUrls() {
      this.revokePhotoUrlList(this.reviewPhotos);
    },

    clearReviewPhotoSets() {
      for (const photos of Object.values(this.reviewPhotoSets || {})) {
        this.revokePhotoUrlList(photos);
      }
      this.reviewPhotoSets = {};
      this.reviewPhotoSetOrder = [];
    },

    rememberReviewPhotoSet(workOrderId, photos) {
      const key = String(workOrderId);
      const previous = this.reviewPhotoSets[key];
      const sets = { ...this.reviewPhotoSets, [key]: photos };
      const order = this.reviewPhotoSetOrder.filter((id) => id !== key);
      order.push(key);
      while (order.length > 12) {
        const drop = order.shift();
        if (drop === key) continue;
        this.revokePhotoUrlList(sets[drop]);
        delete sets[drop];
      }
      if (previous && previous !== photos) {
        const keep = new Set(
          (photos || [])
            .map((photo) => photo && photo.photoUrl)
            .filter(Boolean),
        );
        const dropUrls = [];
        for (const photo of previous) {
          const url = photo && photo.photoUrl;
          if (url && String(url).startsWith('blob:') && !keep.has(url)) {
            dropUrls.push(url);
          }
        }
        if (dropUrls.length) {
          this.$nextTick(() => {
            dropUrls.forEach((url) => URL.revokeObjectURL(url));
          });
        }
      }
      this.reviewPhotoSets = sets;
      this.reviewPhotoSetOrder = order;
    },

    publishReviewPhotos(workOrderId, pages) {
      const list = Array.isArray(pages) ? pages : [];
      const currentId = this.reviewCurrentPhoto?.id;
      this.reviewPhotos = list;
      const keepIndex = list.findIndex(
        (photo) => photo && String(photo.id) === String(currentId),
      );
      this.reviewPhotoIndex = keepIndex >= 0 ? keepIndex : 0;
      if (keepIndex < 0) this.resetReviewPhotoZoom();
      this.reviewPhotoUrl = list[this.reviewPhotoIndex]?.photoUrl || '';
      this.reviewPhotoLoading = false;
      this.rememberReviewPhotoSet(workOrderId, list);
    },

    reviewPhotoZoomSnapshot() {
      return {
        scale: this.reviewPhotoScale,
        x: this.reviewPhotoPanX,
        y: this.reviewPhotoPanY,
      };
    },

    applyReviewPhotoZoom(state) {
      this.reviewPhotoScale = state.scale;
      this.reviewPhotoPanX = state.x;
      this.reviewPhotoPanY = state.y;
    },

    resetReviewPhotoZoom() {
      this.applyReviewPhotoZoom({ scale: 1, x: 0, y: 0 });
      this._reviewPhotoGesture = null;
      this._reviewPhotoIgnoreClick = false;
    },

    reviewPhotoTouchPoints(event) {
      return Array.from(event.touches || [], (touch) => ({
        clientX: touch.clientX,
        clientY: touch.clientY,
      }));
    },

    onReviewPhotoTouchStart(event) {
      const points = this.reviewPhotoTouchPoints(event);
      if (points.length >= 2 || !this._reviewPhotoGesture) {
        this._reviewPhotoGesture = startReviewPhotoGesture(
          points,
          this.reviewPhotoZoomSnapshot(),
        );
      }
    },

    onReviewPhotoTouchMove(event) {
      const points = this.reviewPhotoTouchPoints(event);
      if (!this._reviewPhotoGesture) {
        this._reviewPhotoGesture = startReviewPhotoGesture(
          points,
          this.reviewPhotoZoomSnapshot(),
        );
      }
      const next = updateReviewPhotoGesture(
        this._reviewPhotoGesture,
        points,
        this.reviewPhotoZoomSnapshot(),
      );
      this._reviewPhotoGesture = next.gesture;
      this.applyReviewPhotoZoom(next.state);
    },

    onReviewPhotoTouchEnd(event) {
      const next = finishReviewPhotoGesture(
        this._reviewPhotoGesture,
        this.reviewPhotoTouchPoints(event),
        this.reviewPhotoZoomSnapshot(),
      );
      this._reviewPhotoGesture = next.gesture;
      this.applyReviewPhotoZoom(next.state);
      if (next.ignoreClick) this._reviewPhotoIgnoreClick = true;
    },

    onReviewPhotoFullscreenClick() {
      if (this._reviewPhotoIgnoreClick) {
        this._reviewPhotoIgnoreClick = false;
        return;
      }
      if (this.reviewPhotoZoomed) this.resetReviewPhotoZoom();
    },

    reviewPhotoPrev() {
      this.resetReviewPhotoZoom();
      this.reviewPhotoIndex = stepPhotoIndex(
        this.reviewPhotoIndex,
        -1,
        this.reviewPhotos.length,
      );
    },

    reviewPhotoNext() {
      this.resetReviewPhotoZoom();
      this.reviewPhotoIndex = stepPhotoIndex(
        this.reviewPhotoIndex,
        1,
        this.reviewPhotos.length,
      );
    },

    async loadReviewPhotos(workOrderId) {
      const token = ++this.reviewPhotoLoadToken;
      if (!workOrderId || !this.showReviewPopup) {
        this.reviewPhotoUrl = '';
        this.reviewPhotos = [];
        this.reviewPhotoIndex = 0;
        this.reviewPhotoLoading = false;
        return;
      }
      let remembered = rememberedReviewPhotos(
        this.reviewPhotoSets,
        workOrderId,
      );
      if (!remembered) {
        const diskPhotos = await getCachedPhotosForWorkOrder(workOrderId);
        if (token !== this.reviewPhotoLoadToken || !this.showReviewPopup) {
          return;
        }
        if (diskPhotos.length) {
          remembered = diskPhotos.map((photo) => ({
            id: photo.id,
            caption: photo.caption || '',
            modifiedOn: photo.modifiedOn,
            photoUrl: URL.createObjectURL(photo.blob),
          }));
          this.rememberReviewPhotoSet(workOrderId, remembered);
        }
      }
      if (remembered && remembered.length) {
        this.reviewPhotos = remembered;
        this.reviewPhotoIndex = 0;
        this.reviewPhotoUrl = remembered[0].photoUrl || '';
        this.reviewPhotoLoading = false;
      } else {
        this.reviewPhotos = [];
        this.reviewPhotoIndex = 0;
        this.reviewPhotoUrl = '';
        this.reviewPhotoLoading = true;
      }
      try {
        const response = await axios.get(
          `/api/work-orders/${workOrderId}/photos`,
        );
        if (token !== this.reviewPhotoLoadToken || !this.showReviewPopup) {
          return;
        }
        const manifest = Array.isArray(response.data) ? response.data : [];
        const painted =
          rememberedReviewPhotos(this.reviewPhotoSets, workOrderId) || [];
        if (sameReviewPhotoSet(painted, manifest)) {
          await touchCachedPhotos(painted.map((photo) => photo.id));
          return;
        }
        const starting = mergeDisplayedPhotos(painted, manifest, new Map());
        const pages = await this.loadWorkOrderPhotoPages(
          workOrderId,
          manifest,
          starting,
          (next) => {
            if (token !== this.reviewPhotoLoadToken || !this.showReviewPopup) {
              return;
            }
            this.publishReviewPhotos(workOrderId, next);
          },
          () => token === this.reviewPhotoLoadToken && this.showReviewPopup,
        );
        if (token !== this.reviewPhotoLoadToken || !this.showReviewPopup) {
          return;
        }
        if (!pages.length) {
          this.publishReviewPhotos(workOrderId, []);
        }
      } catch (err) {
        console.error(
          `Failed to fetch photos for work order ${workOrderId}:`,
          err,
        );
      } finally {
        if (token === this.reviewPhotoLoadToken) {
          this.reviewPhotoLoading = false;
        }
      }
    },

    async refreshReviewWorkOrders() {
      if (this.reviewRefreshing) return;
      this.reviewRefreshing = true;
      const currentId = this.reviewCurrentWorkOrder?.id;
      try {
        // Kick full list sync (callback-based IndexedDB pipeline)
        this.fetchWorkOrders(true);
        // Refresh the visible card immediately when possible
        if (currentId != null) {
          await this.refreshSingleWorkOrder(currentId);
        } else {
          await new Promise((r) => setTimeout(r, 600));
        }
        this.$nextTick(() => {
          if (currentId != null) {
            const idx = this.reviewOpenWorkOrders.findIndex(
              (wo) => wo.id === currentId,
            );
            this.reviewIndex = idx >= 0 ? idx : 0;
          } else {
            this.clampReviewIndex();
          }
        });
      } catch (err) {
        console.error('Error refreshing review work orders:', err);
        this.showNotificationMessage('Failed to refresh work orders');
      } finally {
        this.reviewRefreshing = false;
      }
    },

    /**
     * Dialogs that sit on Review. Escape leaves those alone so it does not
     * change the work order underneath them.
     */
    reviewOverlayBlocksEscape() {
      return Boolean(
        this.showReviewVoice ||
          this.showReviewThread ||
          this.showDailyBriefing ||
          this.showReviewNoticeDialog ||
          this.showScheduleDialog ||
          this.showEditWorkOrderPopup ||
          this.showNewWorkOrder ||
          this.showTransferDialog ||
          this.showNotesPopup ||
          this.showPhotoUploadDialog ||
          this.showMaintenanceLockCodePopup ||
          this.showSharePopup,
      );
    },

    /**
     * Same landing as opening Review with no focused work order: no location,
     * search, person, or assignee filter, and the newest open item.
     */
    resetReviewFiltersToMount() {
      this.reviewPersonFilter = null;
      this.reviewAssigneeFilter = null;
      this.reviewLocationKey = '';
      this.reviewSearchQuery = '';
      this.reviewSearchDraft = '';
      this.reviewSearchOpen = false;
      this.reviewIdEditing = false;
      this.reviewUserNavigated = false;
      this.reviewSortDir = loadReviewSortDir();
      const list = this.reviewOpenWorkOrders;
      if (list.length) {
        this.reviewIndex = newestWorkOrderIndex(list);
        this.reviewLandOnNewest = false;
      } else {
        this.reviewIndex = 0;
        this.reviewLandOnNewest = true;
      }
      this.syncReviewIdInput();
    },

    /**
     * Desktop keyboard nav while Review is open: ← prev, → next (like mobile swipe).
     * Escape over the panel clears filters and returns to that mount landing.
     * Ignores when typing in inputs/select/textarea or when notes/confirm overlays are open.
     * Yields when the pointer/focus is over another panel (e.g. Quick Email).
     */
    openReviewPhotoFullscreen() {
      if (!this.reviewCurrentPhotoUrl) return;
      this.resetReviewPhotoZoom();
      this.showReviewPhotoFullscreen = true;
    },

    closeReviewPhotoFullscreen() {
      this.showReviewPhotoFullscreen = false;
      this.resetReviewPhotoZoom();
    },

    handleReviewKeydown(event) {
      if (!this.showReviewPopup) return;
      if (event.key === 'Escape' && this.showReviewAssignPopup) {
        event.preventDefault();
        this.closeReviewAssignPopup();
        return;
      }
      if (event.key === 'Escape' && this.showReviewPhotoFullscreen) {
        event.preventDefault();
        this.closeReviewPhotoFullscreen();
        return;
      }
      if (event.key === 'Escape') {
        const pointerOverReview = Boolean(
          document.querySelector('.review-fullscreen:hover'),
        );
        if (
          reviewEscapeResetsToDefault({
            showReviewPopup: this.showReviewPopup,
            pointerOverReview,
            width: window.innerWidth,
            overlayOpen: this.reviewOverlayBlocksEscape(),
          })
        ) {
          event.preventDefault();
          this.resetReviewFiltersToMount();
        }
        return;
      }
      if (
        this.showReviewPhotoFullscreen &&
        this.reviewPhotoZoomed &&
        (event.key === 'ArrowLeft' || event.key === 'ArrowRight')
      ) {
        event.preventDefault();
        return;
      }
      if (
        this.showReviewPhotoFullscreen &&
        this.reviewPhotos.length > 1 &&
        (event.key === 'ArrowLeft' || event.key === 'ArrowRight')
      ) {
        event.preventDefault();
        if (event.key === 'ArrowLeft') this.reviewPhotoPrev();
        else this.reviewPhotoNext();
        return;
      }
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      // Don't steal arrows while notes/confirm/other dialogs sit on top
      if (
        this.showNotesPopup ||
        this.showConfirm ||
        this.showReviewAssignPopup
      )
        return;

      const el = event.target;
      if (el) {
        const tag = (el.tagName || '').toUpperCase();
        if (
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT' ||
          el.isContentEditable
        ) {
          return;
        }
      }

      // Yield to Quick Email (and compose) when the pointer or focus is there —
      // both use document-level arrow handlers and otherwise fight each other.
      const focusInQuickEmail = Boolean(
        el && typeof el.closest === 'function'
          ? el.closest('.email-list-container, .compose-popup')
          : false,
      );
      if (
        focusInQuickEmail ||
        document.querySelector('.email-list-container:hover') ||
        document.querySelector('.compose-popup:hover')
      ) {
        return;
      }

      event.preventDefault();
      if (event.key === 'ArrowLeft') {
        this.reviewPrev();
      } else {
        this.reviewNext();
      }
    },

    toggleReviewSortById() {
      this.markReviewNavigation();
      this.reviewSortDir = this.reviewSortDir === 'asc' ? 'desc' : 'asc';
      saveReviewSortDir(this.reviewSortDir);
      // Jump to the start of the newly ordered list so the visible WO changes
      // (e.g. lowest id ↔ highest id when on the first item).
      this.reviewIndex = 0;
    },

    onReviewLocationChange() {
      this.markReviewNavigation();
      this.reviewIndex = 0;
      this.prefetchReviewFirstPhotos();
    },

    openReviewSearch() {
      this.reviewSearchDraft = this.reviewSearchQuery;
      this.reviewSearchOpen = true;
      this.$nextTick(() => {
        const el = this.$refs.reviewSearchInput;
        if (el && typeof el.focus === 'function') {
          el.focus();
          if (typeof el.select === 'function') el.select();
        }
      });
    },

    onReviewSearchButtonMouseDown(event) {
      event.preventDefault();
    },

    onReviewSearchButtonClick() {
      if (this.reviewSearchOpen) {
        this.commitReviewSearch();
        return;
      }
      this.openReviewSearch();
    },

    blurReviewSearch() {
      const el = this.$refs.reviewSearchInput;
      if (el && typeof el.blur === 'function') el.blur();
    },

    commitReviewSearch() {
      if (!this.reviewSearchOpen) return;
      const next = String(this.reviewSearchDraft || '').trim();
      const currentId = this.reviewCurrentWorkOrder?.id;
      this.reviewSearchQuery = next;
      this.reviewSearchDraft = next;
      this.reviewSearchOpen = false;
      this.markReviewNavigation();
      const idx = this.reviewOpenWorkOrders.findIndex(
        (order) => order.id === currentId,
      );
      this.reviewIndex = idx >= 0 ? idx : 0;
      this.prefetchReviewFirstPhotos();
    },

    workOrderMatchesReviewPerson(wo, person) {
      if (!wo || !person) return false;
      const personId = Number(person.peopleId);
      const woId = Number(wo.peopleId);
      if (personId && woId && personId === woId) return true;
      const personName = String(person.name || '')
        .trim()
        .toLowerCase();
      const woName = String(wo.name || '')
        .trim()
        .toLowerCase();
      return !!(personName && woName && personName === woName);
    },

    filterReviewByPerson(wo) {
      this.markReviewNavigation();
      const name = String(wo?.name || '').trim();
      if (!wo || (!wo.peopleId && !name)) return;
      const currentId = wo.id;
      this.reviewAssigneeFilter = null;
      this.reviewPersonFilter = {
        peopleId: wo.peopleId || null,
        name: name || 'This person',
      };
      const idx = this.reviewOpenWorkOrders.findIndex(
        (order) => order.id === currentId,
      );
      this.reviewIndex = idx >= 0 ? idx : 0;
      this.prefetchReviewFirstPhotos();
    },

    clearReviewPersonFilter() {
      this.markReviewNavigation();
      const currentId = this.reviewCurrentWorkOrder?.id;
      this.reviewPersonFilter = null;
      const idx = this.reviewOpenWorkOrders.findIndex(
        (order) => order.id === currentId,
      );
      this.reviewIndex = idx >= 0 ? idx : 0;
      this.prefetchReviewFirstPhotos();
    },

    clearReviewAssigneeFilter() {
      this.markReviewNavigation();
      const currentId = this.reviewCurrentWorkOrder?.id;
      this.reviewAssigneeFilter = null;
      const idx = this.reviewOpenWorkOrders.findIndex(
        (order) => order.id === currentId,
      );
      this.reviewIndex = idx >= 0 ? idx : 0;
      this.prefetchReviewFirstPhotos();
    },

    onReviewAssignedNameClick() {
      if (reviewAssignedNameClick(this.reviewAssignedToLabel) !== 'pick-filter') {
        return;
      }
      this.openReviewAssigneeFilterPopup();
    },

    async openReviewAssigneeFilterPopup() {
      if (!this.users.length) await this.loadUsers();
      this.reviewAssignPopupMode = 'filter';
      this.showReviewAssignPopup = true;
      this.$nextTick(() => {
        const selected = this.$el.querySelector('.review-assign-user-selected');
        if (selected && typeof selected.scrollIntoView === 'function') {
          selected.scrollIntoView({ block: 'nearest' });
        }
      });
    },

    applyReviewAssigneeFilter(user) {
      const filter = assigneeFilterFromStaffUser(user);
      if (!filter) return;
      this.markReviewNavigation();
      this.reviewPersonFilter = null;
      this.reviewAssigneeFilter = filter;
      this.reviewIndex = 0;
      this.showReviewAssignPopup = false;
      this.reviewAssignPopupMode = 'assign';
      this.prefetchReviewFirstPhotos();
    },

    reviewAssignUserSelected(user) {
      const selectedId =
        this.reviewAssignPopupMode === 'filter'
          ? reviewAssigneePickerId(
              this.reviewCurrentWorkOrder,
              this.reviewAssigneeFilter,
            )
          : Number(this.reviewCurrentWorkOrder && this.reviewCurrentWorkOrder.assignedTo);
      const id = Number(selectedId);
      return id > 0 && id === Number(user && user.id);
    },

    onReviewAssignUserClick(user) {
      if (this.reviewAssignPopupMode === 'filter') {
        this.applyReviewAssigneeFilter(user);
        return;
      }
      this.assignReviewWorkOrder(user);
    },

    filterReviewByLocation(wo) {
      this.markReviewNavigation();
      if (!wo) return;
      const currentId = wo.id;
      this.reviewPersonFilter = null;
      this.reviewAssigneeFilter = null;
      this.reviewLocationKey = reviewLocationKeyForOrder(wo);
      const idx = this.reviewOpenWorkOrders.findIndex(
        (order) => order.id === currentId,
      );
      this.reviewIndex = idx >= 0 ? idx : 0;
      this.prefetchReviewFirstPhotos();
    },

    onReviewDetailsClick(event) {
      if (isTransferButtonTarget(event)) {
        event.stopPropagation();
        this.openTransferDialog(this.reviewCurrentWorkOrder);
        return;
      }
      const kind = reviewDetailsInteractiveTarget(event);
      if (!kind) return;
      event.stopPropagation();
      if (kind === 'person') {
        this.filterReviewByPerson(this.reviewCurrentWorkOrder);
      } else if (kind === 'address') {
        this.filterReviewByLocation(this.reviewCurrentWorkOrder);
      } else if (kind === 'scheduled') {
        this.openCalendarToScheduledDate(this.reviewCurrentWorkOrder);
      }
    },

    onReviewDetailsDblClick(event) {
      if (
        isTransferButtonTarget(event) ||
        reviewDetailsInteractiveTarget(event)
      ) {
        event.stopPropagation();
      }
    },

    onReviewSliderInput(event) {
      this.markReviewNavigation();
      const n = parseInt(event.target.value, 10);
      if (Number.isNaN(n)) return;
      const max = Math.max(this.reviewOpenWorkOrders.length - 1, 0);
      this.reviewIndex = Math.min(Math.max(n, 0), max);
    },

    reviewPrev() {
      this.markReviewNavigation();
      if (this.reviewIndex > 0) this.reviewIndex -= 1;
    },

    reviewNext() {
      this.markReviewNavigation();
      if (this.reviewIndex < this.reviewOpenWorkOrders.length - 1) {
        this.reviewIndex += 1;
      }
    },

    /**
     * Leave Review and open the current WO as a selected tile in the main list.
     */
    openReviewWorkOrderInNormalView() {
      const wo = this.reviewCurrentWorkOrder;
      if (!wo?.id) return;

      // Make sure filters are not hiding this open order
      if (
        this.filteredByAgeIds &&
        this.filteredByAgeIds.length &&
        !this.filteredByAgeIds.includes(wo.id)
      ) {
        this.filteredByAgeIds = null;
        this.ageFilterLabel = '';
        this.showAgeFilterHint = false;
      }
      // Show open orders (hide completed); this WO is open
      this.hideDone = true;
      // Clear free-text search if it would hide the tile
      if (this.workOrderFilter && this.workOrderFilter.trim()) {
        this.workOrderFilter = '';
      }

      this.closeReviewPopup();
      if (this.showListView) {
        this.showListView = false;
      }

      this.$nextTick(() => {
        let idx = this.filteredWorkOrders.findIndex((w) => w.id === wo.id);
        if (idx === -1) {
          // Last resort: clear age filter path already done; try again
          idx = this.filteredWorkOrders.findIndex((w) => w.id === wo.id);
        }
        if (idx === -1) {
          this.showNotificationMessage(
            `Could not open work order #${wo.id} in the list`,
          );
          return;
        }
        this.selectTile(idx);
        this.$nextTick(() => {
          const el = document.querySelector(
            `.maint-tile[data-work-order-id="${wo.id}"]`,
          );
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }
        });
      });
    },

    clearReviewLongPress() {
      if (this.reviewLongPressTimer) {
        clearTimeout(this.reviewLongPressTimer);
        this.reviewLongPressTimer = null;
      }
    },

    onReviewTouchStart(event) {
      if (!event.touches || event.touches.length !== 1) return;
      const t = event.touches[0];
      this.reviewTouchStartX = t.clientX;
      this.reviewTouchStartY = t.clientY;
      this.reviewTouchStartTime = Date.now();
      this.reviewLongPressFired = false;
      this.clearReviewLongPress();
      this.reviewLongPressTimer = setTimeout(() => {
        this.reviewLongPressTimer = null;
        this.reviewLongPressFired = true;
        // Haptic feedback when available
        if (typeof navigator !== 'undefined' && navigator.vibrate) {
          try {
            navigator.vibrate(30);
          } catch {
            /* ignore */
          }
        }
        this.openReviewWorkOrderInNormalView();
      }, 550);
    },

    onReviewTouchMove(event) {
      if (this.reviewTouchStartX == null || !event.touches?.length) return;
      const t = event.touches[0];
      const dx = Math.abs(t.clientX - this.reviewTouchStartX);
      const dy = Math.abs(t.clientY - this.reviewTouchStartY);
      // Cancel long-press once the finger moves (scroll or swipe)
      if (dx > 12 || dy > 12) {
        this.clearReviewLongPress();
      }
    },

    onReviewTouchCancel() {
      this.clearReviewLongPress();
      this.reviewTouchStartX = null;
      this.reviewTouchStartY = null;
      this.reviewTouchStartTime = 0;
      this.reviewLongPressFired = false;
    },

    /**
     * Horizontal swipe on review body: left → next, right → prev.
     * Ignores mostly-vertical gestures so scrolling still works.
     * Skipped after a long-press open.
     */
    onReviewTouchEnd(event) {
      this.clearReviewLongPress();

      if (this.reviewLongPressFired) {
        this.reviewLongPressFired = false;
        this.reviewTouchStartX = null;
        this.reviewTouchStartY = null;
        this.reviewTouchStartTime = 0;
        return;
      }

      if (this.reviewTouchStartX == null || this.reviewTouchStartY == null) {
        return;
      }
      const touch =
        (event.changedTouches && event.changedTouches[0]) || null;
      if (!touch) {
        this.reviewTouchStartX = null;
        this.reviewTouchStartY = null;
        return;
      }

      const dx = touch.clientX - this.reviewTouchStartX;
      const dy = touch.clientY - this.reviewTouchStartY;
      const elapsed = Date.now() - this.reviewTouchStartTime;
      this.reviewTouchStartX = null;
      this.reviewTouchStartY = null;
      this.reviewTouchStartTime = 0;

      const minDistance = 50;
      const maxDuration = 800;
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);

      // Require clear horizontal swipe (not vertical scroll)
      if (
        elapsed > maxDuration ||
        absX < minDistance ||
        absX < absY * 1.4
      ) {
        return;
      }

      if (dx < 0) {
        this.reviewNext(); // swipe left → next
      } else {
        this.reviewPrev(); // swipe right → prev
      }
    },

    /** Clamp review index after the open list changes (e.g. after mark done). */
    clampReviewIndex() {
      const len = this.reviewOpenWorkOrders.length;
      if (len === 0) {
        this.closeReviewPopup();
        this.showNotificationMessage('All open work orders reviewed');
        return;
      }
      if (this.reviewIndex >= len) {
        this.reviewIndex = len - 1;
      }
      if (this.reviewIndex < 0) this.reviewIndex = 0;
    },

    async reviewMarkDone() {
      const wo = this.reviewCurrentWorkOrder;
      if (!wo) return;
      await this.markDone(wo);
    },

    reviewStaffUserLabel(user) {
      return staffUserLabel(user);
    },

    briefingNoteSources() {
      const sources = {};
      for (const order of this.workOrders || []) {
        if (!order || order.id == null) continue;
        const text = [order.problem_description, order.note]
          .filter((part) => part)
          .join('\n');
        if (text) sources[order.id] = text;
      }
      return sources;
    },

    briefingDisplayText(text) {
      return repairTruncatedMarkdownLinks(text, this.briefingNoteSources());
    },

    applyDailyBriefing(row) {
      const data = row || {};
      this.dailyBriefingBody = data.body || '';
      this.dailyBriefingDraft = this.dailyBriefingBody;
      this.dailyBriefingUpdatedAt = data.updatedAt || null;
      this.dailyBriefingUpdatedByName = data.updatedByName || '';
    },

    async removeDoneWorkOrdersFromDailyBriefing(doneId) {
      const id = Number(doneId);
      if (!id) return { closedDuplicates: 0, failed: [] };
      try {
        const response = await axios.get('/api/daily-briefing');
        const data = response.data || {};
        const body = String(data.body || '');
        const together = briefingIdsToCloseTogether(body, id);
        const duplicates = together.filter((other) => other !== id);
        let nextReviewId = null;
        if (this.showReviewPopup) {
          const open = this.reviewOpenWorkOrders;
          const start =
            open.length === 0
              ? 0
              : Math.min(Math.max(this.reviewIndex, 0), open.length - 1);
          const closing = new Set(together);
          const ahead = open
            .slice(start)
            .find((wo) => !closing.has(Number(wo.id)));
          const stay =
            ahead ||
            [...open].reverse().find((wo) => !closing.has(Number(wo.id)));
          nextReviewId = stay ? stay.id : null;
        }
        const failed = [];
        for (const other of duplicates) {
          const ok = await this.completeOpenDuplicate(other);
          if (!ok) failed.push(other);
        }
        if (this.showReviewPopup && nextReviewId != null) {
          const idx = this.reviewOpenWorkOrders.findIndex(
            (wo) => Number(wo.id) === Number(nextReviewId),
          );
          if (idx >= 0) this.reviewIndex = idx;
        }
        const closed = together.filter((other) => !failed.includes(other));
        const next = removeClosedWorkOrdersFromBriefing(body, closed);
        if (next !== body) {
          const editing = this.dailyBriefingEditing;
          const draft = editing
            ? removeClosedWorkOrdersFromBriefing(this.dailyBriefingDraft, closed)
            : '';
          const saved = await axios.put('/api/daily-briefing', { body: next });
          this.applyDailyBriefing(saved.data || {});
          if (editing) {
            this.dailyBriefingDraft = draft;
            this.dailyBriefingEditing = true;
          }
        }
        if (failed.length) {
          this.showNotificationMessage(
            `Work order is done. Could not close ${failed
              .map((other) => `#${other}`)
              .join(', ')}.`,
          );
        }
        return {
          closedDuplicates: duplicates.length - failed.length,
          failed,
        };
      } catch (err) {
        console.error('Error removing closed work orders from daily briefing:', err);
        this.showNotificationMessage(
          'Work order is done. The daily briefing was not updated.',
        );
        return { closedDuplicates: 0, failed: [] };
      }
    },

    async completeOpenDuplicate(id) {
      try {
        const existing = this.findWorkOrderById(id);
        if (existing && (existing.deleted || existing.completedOn != null)) {
          return true;
        }
        if (!existing) {
          try {
            const { data } = await axios.get(`/api/maintenance-requests/${id}`);
            const wo = Array.isArray(data) ? data[0] : data;
            if (!wo || wo.completedOn != null) return true;
          } catch (err) {
            if (err.response && err.response.status === 404) return true;
            return false;
          }
        }
        let response = await axios.put(
          `/api/maintenance-requests/${id}/complete`,
        );
        if (response.data && response.data.isCompleted === false) {
          response = await axios.put(`/api/maintenance-requests/${id}/complete`);
        }
        if (!response.data || response.data.isCompleted !== true) return false;
        const row = this.findWorkOrderById(id);
        if (row) {
          row.completedOn = new Date().toISOString();
          row.completedBy = response.data.completedBy || null;
        }
        return true;
      } catch (err) {
        console.error('Error closing duplicate work order', id, err);
        return false;
      }
    },

    async openDailyBriefing() {
      this.showDailyBriefing = true;
      this.dailyBriefingEditing = false;
      this.dailyBriefingLoading = true;
      try {
        const response = await axios.get('/api/daily-briefing');
        this.applyDailyBriefing(response.data);
      } catch (err) {
        this.dailyBriefingBody = '';
        this.dailyBriefingDraft = '';
        this.showNotificationMessage('Could not load the daily briefing');
      } finally {
        this.dailyBriefingLoading = false;
      }
    },

    hideBriefingHint() {
      this.showBriefingHint = false;
    },

    hideBriefingHintForever() {
      this.showBriefingHint = false;
      markBriefingHintSeen(localStorage);
    },

    closeDailyBriefing() {
      this.showDailyBriefing = false;
      this.dailyBriefingEditing = false;
      this.dailyBriefingSaving = false;
    },

    printDailyBriefing() {
      if (this.dailyBriefingLoading) return;
      // Open in this click, before any await, so the browser allows the window.
      const printWindow = window.open('', '_blank', 'width=960,height=720');
      if (!printWindow) {
        window.alert(
          'Please allow pop-ups for this site to use print preview.',
        );
        return;
      }
      const raw = this.dailyBriefingEditing
        ? String(this.dailyBriefingDraft || '')
        : String(this.dailyBriefingBody || '');
      const text = this.briefingDisplayText(raw);
      const bodyHtml = text.trim()
        ? renderNoteMarkdown(text)
        : '<p>No briefing yet.</p>';
      const meta = this.listViewEscapeHtml(this.dailyBriefingMeta || '');
      const printHtml = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>daily briefing</title>
    <style>
      body {
        font-family: Arial, sans-serif;
        line-height: 1.6;
        margin: 0;
        padding: 0.5in;
        color: #333;
        font-size: 14px;
      }
      h1, h2, h3, h4, h5, h6 { color: #444; margin-top: 1.2em; margin-bottom: 0.4em; }
      h1 { font-size: 1.8em; border-bottom: 2px solid #eee; padding-bottom: 0.3em; }
      h2 { font-size: 1.5em; border-bottom: 1px solid #eee; padding-bottom: 0.3em; }
      .print-title {
        margin: 0 0 4px;
        font-size: 22px;
        color: #2c3e50;
        border: none;
        padding: 0;
      }
      .print-meta { margin: 0 0 16px; color: #999; font-size: 12px; }
      ul, ol { padding-left: 1.4em; }
      a.note-wo-chip, a[href^='#calendar-'] {
        color: #007bff;
        text-decoration: none;
        display: inline-block;
        padding: 2px 8px;
        margin: 0 1px;
        border-radius: 3px;
        background: rgba(0, 123, 255, 0.08);
        font-size: 1.2em;
        line-height: 1.3;
      }
      a[href^='tel:'] {
        color: #28a745;
        text-decoration: none;
        padding: 1px 3px;
        border-radius: 3px;
        background: rgba(40, 167, 69, 0.08);
      }
      table { border-collapse: collapse; width: 97%; font-size: 0.75em; }
      th, td { border: 1px solid #e8e8e8; padding: 1px; text-align: left; }
      th { background: #f5f5f5; }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
    </style>
  </head>
  <body>
    <h1 class="print-title">daily briefing</h1>
    ${meta ? `<p class="print-meta">${meta}</p>` : ''}
    <div>${bodyHtml}</div>
  </body>
</html>`;
      printWindow.document.open();
      printWindow.document.write(printHtml);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    },

    async onDailyBriefingEdit() {
      if (!this.dailyBriefingEditing) {
        this.dailyBriefingDraft = this.briefingDisplayText(this.dailyBriefingBody);
        this.dailyBriefingEditing = true;
        return;
      }
      this.dailyBriefingSaving = true;
      try {
        const response = await axios.put('/api/daily-briefing', {
          body: this.dailyBriefingDraft,
        });
        this.applyDailyBriefing(response.data);
        this.dailyBriefingEditing = false;
      } catch (err) {
        this.showNotificationMessage('Could not save the daily briefing');
      } finally {
        this.dailyBriefingSaving = false;
      }
    },

    async openReviewAssignPopup() {
      if (!this.reviewCurrentWorkOrder) return;
      if (!this.users.length) await this.loadUsers();
      this.reviewAssignPopupMode = 'assign';
      this.showReviewAssignPopup = true;
    },

    closeReviewAssignPopup() {
      this.showReviewAssignPopup = false;
      this.reviewAssignSaving = false;
      this.reviewAssignPopupMode = 'assign';
    },

    reviewAssignNobodySelected() {
      const wo = this.reviewCurrentWorkOrder;
      return !(wo && Number(wo.assignedTo) > 0);
    },

    async assignReviewWorkOrder(user) {
      const wo = this.reviewCurrentWorkOrder;
      const clearing = !user;
      if (!wo || this.reviewAssignSaving) return;
      this.reviewAssignSaving = true;
      try {
        const response = await axios.put(
          `/api/maintenance-requests/${wo.id}/assign`,
          { assignedTo: clearing ? null : user.id },
        );
        const assignedTo = clearing
          ? null
          : response.data.assignedTo != null
            ? response.data.assignedTo
            : user.id;
        const assignedToName = clearing
          ? ''
          : response.data.assignedToName || staffUserLabel(user);
        const local = this.workOrders.find((w) => w.id === wo.id);
        if (local) {
          local.assignedTo = assignedTo;
          local.assignedToName = assignedToName;
        }
        wo.assignedTo = assignedTo;
        wo.assignedToName = assignedToName;
        this.updateLocalDatabase(this.workOrders);
        this.showReviewAssignPopup = false;
      } catch (err) {
        console.error('Error assigning work order:', err);
        this.showNotificationMessage('Failed to assign work order');
      } finally {
        this.reviewAssignSaving = false;
      }
    },

    toggleNewWorkOrder() {
      this.showNewWorkOrder = !this.showNewWorkOrder;
    },

    // Receipts Methods
    async toggleReceiptsPopup() {
      this.showReceiptsPopup = !this.showReceiptsPopup;
      if (this.showReceiptsPopup) {
        // Reset search and load current year by default
        this.receiptSearchInput = '';
        this.receiptSearchQuery = '';
        this.receiptYear = new Date().getFullYear();
        this.receiptLoadAll = false;
        await this.fetchReceipts(this.receiptYear, false);
        await this.fetchPropertyOptions();
      } else {
        this.editingReceiptId = null;
      }
    },

    async fetchReceipts(year = null, loadAll = false) {
      try {
        const params = {};
        if (!loadAll && year) {
          params.year = year;
        }
        const response = await axios.get('/api/receipts', { params });
        this.receipts = response.data || [];
      } catch (error) {
        console.error('Error fetching receipts:', error);
        this.receipts = [];
      }
    },

    async fetchPropertyOptions() {
      try {
        const response = await axios.get('/api/properties', {
          params: { query: '', includeHouses: '1' },
        });
        this.propertyOptions = response.data || [];
      } catch (error) {
        console.error('Error fetching properties:', error);
        this.propertyOptions = [];
      }
    },

    addNewReceipt() {
      // Create a new empty receipt at the beginning of the list
      const newReceipt = {
        id: 'new-' + Date.now(),
        supplier: '',
        description: '',
        address: '',
        amount: 0,
        createdByInitials: this.getInitials(this.currentUserDisplayName),
        modifiedOn: new Date().toISOString(),
        isNew: true,
      };
      this.receipts.unshift(newReceipt);
      this.startEditReceipt(newReceipt);
    },

    startEditReceipt(receipt) {
      this.editingReceiptId = receipt.id;
      this.editingReceipt = {
        id: receipt.id,
        supplier: receipt.supplier || '',
        description: receipt.description || '',
        address: receipt.address || '',
        amount: receipt.amount || 0,
        isNew: receipt.isNew || false,
      };
    },

    cancelEditReceipt() {
      // If it was a new receipt, remove it from the list
      if (this.editingReceipt.isNew) {
        this.receipts = this.receipts.filter(
          (r) => r.id !== this.editingReceiptId,
        );
      }
      this.editingReceiptId = null;
      this.editingReceipt = {
        id: null,
        supplier: '',
        description: '',
        address: '',
        amount: 0,
      };
    },

    async saveReceipt() {
      try {
        const receiptData = {
          supplier: this.editingReceipt.supplier,
          description: this.editingReceipt.description,
          address: this.editingReceipt.address,
          amount: parseFloat(this.editingReceipt.amount) || 0,
        };

        if (this.editingReceipt.isNew) {
          // Create new receipt
          const response = await axios.post('/api/receipts', receiptData);
          // Replace the temporary receipt with the saved one
          const index = this.receipts.findIndex(
            (r) => r.id === this.editingReceiptId,
          );
          if (index !== -1) {
            this.receipts[index] = response.data;
          }
        } else {
          // Update existing receipt
          const response = await axios.put(
            `/api/receipts/${this.editingReceipt.id}`,
            receiptData,
          );
          // Update the receipt in the list
          const index = this.receipts.findIndex(
            (r) => r.id === this.editingReceiptId,
          );
          if (index !== -1) {
            this.receipts[index] = response.data;
          }
        }

        this.editingReceiptId = null;
        this.editingReceipt = {
          id: null,
          supplier: '',
          description: '',
          address: '',
          amount: 0,
        };
      } catch (error) {
        console.error('Error saving receipt:', error);
        alert('Failed to save receipt. Please try again.');
      }
    },

    deleteReceipt(receipt) {
      this.receiptToDelete = receipt;
      this.showDeleteConfirm = true;
    },

    cancelDelete() {
      this.showDeleteConfirm = false;
      this.receiptToDelete = null;
    },

    async confirmDelete() {
      if (!this.receiptToDelete) return;
      try {
        await axios.delete(`/api/receipts/${this.receiptToDelete.id}`);
        this.receipts = this.receipts.filter(
          (r) => r.id !== this.receiptToDelete.id,
        );
      } catch (error) {
        console.error('Error deleting receipt:', error);
        alert('Failed to delete receipt. Please try again.');
      } finally {
        this.showDeleteConfirm = false;
        this.receiptToDelete = null;
      }
    },

    onReceiptSearchInput() {
      if (this.receiptSearchDebounceTimer) {
        clearTimeout(this.receiptSearchDebounceTimer);
      }
      this.receiptSearchDebounceTimer = setTimeout(() => {
        this.receiptSearchQuery = this.receiptSearchInput;
      }, 300);
    },

    onReceiptYearChange() {
      this.receiptLoadAll = false;
      this.fetchReceipts(this.receiptYear, false);
    },

    loadAllReceipts() {
      this.receiptLoadAll = true;
      this.fetchReceipts(null, true);
    },

    stripMarkTags(text) {
      return text ? String(text).replace(/<\/?mark[^>]*>/gi, '') : text;
    },

    highlightSearch(text) {
      if (!text) return '';
      const str = String(text);
      if (!this.receiptSearchQuery) {
        return this.escapeHtml(str);
      }

      // Get non-negated tokens for highlighting
      const tokens = this.receiptSearchQuery
        .split(',')
        .map((token) => token.trim().toLowerCase())
        .filter((token) => token !== '' && !token.startsWith('!'));

      if (tokens.length === 0) {
        return this.escapeHtml(str);
      }

      // Build regex pattern from all non-negated tokens
      const escapedTokens = tokens.map((token) =>
        token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
      );
      const regex = new RegExp(`(${escapedTokens.join('|')})`, 'gi');

      // Split by matches, escape each part, then rejoin with highlighted matches
      const parts = str.split(regex);
      return parts
        .map((part, i) => {
          if (i % 2 === 1) {
            // This is a match
            return `<mark class="search-highlight">${this.escapeHtml(part)}</mark>`;
          }
          return this.escapeHtml(part);
        })
        .join('');
    },

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },

    getInitials(displayName) {
      if (!displayName) return '??';
      const parts = displayName.trim().split(' ');
      if (parts.length === 1) {
        return parts[0].substring(0, 2).toUpperCase();
      }
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    },

    getCompletedByInitials(completedBy) {
      if (!completedBy) return '';
      const raw = String(completedBy).trim();
      if (!raw) return '';

      // Handle legacy/new payloads where completedBy can be a numeric user id.
      if (/^\d+$/.test(raw) && Array.isArray(this.users)) {
        const userId = Number(raw);
        const matchedUser = this.users.find((u) => Number(u.id) === userId);
        if (matchedUser) {
          const displayName =
            matchedUser.displayName || matchedUser.username || '';
          if (displayName) {
            return this.getInitials(displayName);
          }
        }
      }

      const compact = raw.replace(/[^a-zA-Z]/g, '');
      if (compact.length >= 2 && compact.length <= 4 && compact === raw) {
        return raw.toUpperCase();
      }

      return this.getInitials(raw);
    },

    formatAmount(amount) {
      const num = parseFloat(amount) || 0;
      return num.toFixed(2);
    },

    formatReceiptDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const diffInMs = now - date;
      const diffInHours = Math.max(0, Math.floor(diffInMs / (1000 * 60 * 60)));
      const diffInDays = Math.floor(diffInHours / 24);

      let formattedDate = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

      if (diffInHours < 24) {
        formattedDate += ` (${diffInHours} hr${diffInHours !== 1 ? 's' : ''} ago)`;
      } else {
        formattedDate += ` (${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago)`;
      }

      return formattedDate;
    },

    async fetchCurrentUserInfo() {
      try {
        const response = await axios.get('/api/user-info');
        if (response.data) {
          this.currentUserRole = response.data.role ?? 99;
          this.currentUserId = response.data.id;
          this.currentUserDisplayName = response.data.displayName || '';
          this.currentUserPhone = response.data.phone || '';
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    },

    fillMyInfo() {
      this.newWorkOrder.name = this.currentUserDisplayName;
      this.newWorkOrder.phone = this.currentUserPhone;
      this.newWorkOrder.peopleId = null;
      this.fieldErrors.name = false;
      this.fieldErrors.phone = false;
    },

    validatePhone() {
      const phoneRegex = /^[0-9]{10}$/;
      if (!this.newWorkOrder.phone) {
        this.validationError = 'Phone number is required.';
        this.fieldErrors.phone = true;
        return false;
      } else if (!phoneRegex.test(this.newWorkOrder.phone.replace(/\D/g, ''))) {
        this.validationError = 'Phone number must contain 10 digits.';
        this.fieldErrors.phone = true;
        return false;
      } else {
        this.fieldErrors.phone = false;
        return true;
      }
    },

    validateForm() {
      // Reset validation state
      this.fieldErrors = {
        name: false,
        phone: false,
        propertyAddress: false,
        problemDescription: false,
      };
      this.validationError = '';

      // Check name field
      if (!this.newWorkOrder.name.trim()) {
        this.validationError = 'Name is required.';
        this.fieldErrors.name = true;
        return false;
      }

      // Check phone field
      if (!this.validatePhone()) {
        return false;
      }

      // Check property address field
      if (!this.newWorkOrder.selectedPropertyId) {
        this.validationError = 'Please select a property address.';
        this.fieldErrors.propertyAddress = true;
        return false;
      }

      // Check problem description field
      if (!this.newWorkOrder.problemDescription.trim()) {
        this.validationError = 'Problem description is required.';
        this.fieldErrors.problemDescription = true;
        return false;
      }

      return true;
    },

    /**
     * Create a work order from the new-WO form. Returns the created record or null.
     */
    async createWorkOrderFromForm() {
      if (!this.validateForm()) {
        return null;
      }
      if (this.isCreatingWorkOrder) {
        return null;
      }

      this.isCreatingWorkOrder = true;
      try {
        const payload = {
          ...this.newWorkOrder,
          propertyAddress: this.stripMarkTags(
            this.newWorkOrder.propertyAddress,
          ),
          problemDescription: this.stripMarkTags(
            this.newWorkOrder.problemDescription,
          ),
        };
        this.newWorkOrder.propertyAddress = payload.propertyAddress;
        this.newWorkOrder.problemDescription = payload.problemDescription;

        const response = await axios.post(
          '/api/maintenance-requests',
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );

        if (response.status === 201) {
          return response.data;
        }

        console.error('Failed to add new work order');
        this.validationError = 'Failed to add work order. Please try again.';
        return null;
      } catch (error) {
        console.error('Error adding new work order:', error);
        this.validationError = 'Error submitting work order. Please try again.';
        return null;
      } finally {
        this.isCreatingWorkOrder = false;
      }
    },

    async addNewWorkOrder() {
      const createdWorkOrder = await this.createWorkOrderFromForm();
      if (!createdWorkOrder) {
        return;
      }

      this.workOrders.push(createdWorkOrder);
      this.resetNewWorkOrder();
      this.toggleNewWorkOrder();
      this.fetchWorkOrders(true);
    },

    /**
     * Camera icon on new WO form: save the order, then open the same
     * notes/photos dialog used by the card + control.
     */
    async saveNewWorkOrderAndOpenNotes() {
      const createdWorkOrder = await this.createWorkOrderFromForm();
      if (!createdWorkOrder?.id) {
        return;
      }

      const createdId = createdWorkOrder.id;
      const alreadyListed = this.workOrders.some(
        (wo) => Number(wo.id) === Number(createdId),
      );
      if (!alreadyListed) {
        this.workOrders.push({
          ...createdWorkOrder,
          note: createdWorkOrder.note || '',
          photoCount: createdWorkOrder.photoCount || 0,
          commentCount: createdWorkOrder.commentCount || 0,
        });
      }

      this.resetNewWorkOrder();
      this.showNewWorkOrder = false;
      this.showNotesForWorkOrder(createdId);
      this.fetchWorkOrders(true);
    },

    resetNewWorkOrder() {
      this.newWorkOrder = {
        name: '',
        phone: '',
        date: new Date().toISOString().split('T')[0],
        propertyAddress: '',
        selectedPropertyId: null,
        propertyId: null,
        peopleId: null,
        problemDescription: '',
        allowEntry: false,
      };
      // Reset autocomplete state
      this.nameSuggestions = [];
      this.showNameSuggestions = false;
      this.selectedSuggestionIndex = -1;
    },

    onPropertySelected() {
      if (this.newWorkOrder.selectedPropertyId === 'other') {
        this.newWorkOrder.propertyId = null;
        this.newWorkOrder.propertyAddress = '';
        return;
      }
      const selected = this.propertyOptions.find(
        (p) => p.id === this.newWorkOrder.selectedPropertyId,
      );
      if (selected) {
        this.newWorkOrder.propertyAddress = selected.display.trim();
        this.newWorkOrder.propertyId = selected.id;
      }
    },

    // Autocomplete methods
    async handleNameInput() {
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      const query = this.newWorkOrder.name.trim();
      this.newWorkOrder.peopleId = null;

      if (query.length < 2) {
        this.nameSuggestions = [];
        this.showNameSuggestions = false;
        return;
      }

      // Debounce the search to avoid too many API calls
      this.searchTimeout = setTimeout(async () => {
        try {
          console.log('Searching for tenants with query:', query);
          const response = await axios.get('/api/tenants/search', {
            params: { query },
          });

          console.log('Tenant search results:', response.data);
          this.nameSuggestions = response.data;
          this.showNameSuggestions = true;
          this.selectedSuggestionIndex = -1;
        } catch (error) {
          console.error('Error searching tenants:', error);
          this.nameSuggestions = [];
        }
      }, 300); // 300ms delay
    },

    handleNameBlur() {
      // Hide suggestions after a short delay to allow for selection
      setTimeout(() => {
        this.showNameSuggestions = false;
        this.selectedSuggestionIndex = -1;
      }, 200);
    },

    handleNameKeydown(event) {
      if (!this.showNameSuggestions || this.nameSuggestions.length === 0) {
        return;
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.selectedSuggestionIndex = Math.min(
            this.selectedSuggestionIndex + 1,
            this.nameSuggestions.length - 1,
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.selectedSuggestionIndex = Math.max(
            this.selectedSuggestionIndex - 1,
            -1,
          );
          break;
        case 'Enter':
        case 'Tab':
          event.preventDefault();
          if (this.selectedSuggestionIndex >= 0) {
            this.selectSuggestion(
              this.nameSuggestions[this.selectedSuggestionIndex],
            );
          } else if (this.nameSuggestions.length === 1) {
            // Auto-select if there's only one suggestion
            this.selectSuggestion(this.nameSuggestions[0]);
          }
          break;
        case 'Escape':
          this.showNameSuggestions = false;
          this.selectedSuggestionIndex = -1;
          break;
      }
    },

    async selectSuggestion(suggestion) {
      this.newWorkOrder.name = suggestion.fullName;
      this.showNameSuggestions = false;
      this.selectedSuggestionIndex = -1;
      const selectedPeopleId = suggestion.id || null;
      this.$nextTick(() => {
        this.newWorkOrder.peopleId = selectedPeopleId;
      });

      // Auto-fill phone and address if available
      if (suggestion.Phone) {
        this.newWorkOrder.phone = suggestion.Phone.split(';')[0].trim(); // Take first phone if multiple
      }
      if (suggestion.Address) {
        this.newWorkOrder.propertyAddress = suggestion.Address.trim();
        // Try to match to a property in the dropdown
        const addr = suggestion.Address.trim().toLowerCase();
        const match = this.propertyOptions.find(
          (p) => p.display.trim().toLowerCase() === addr,
        );
        if (match) {
          this.newWorkOrder.selectedPropertyId = match.id;
          this.newWorkOrder.propertyId = match.id;
        }
      }

      // If we don't have all the info from the search, fetch detailed contact info
      if (!suggestion.Phone || !suggestion.Address) {
        try {
          const response = await axios.get('/api/tenants/contact-info', {
            params: { name: suggestion.fullName },
          });

          const contactInfo = response.data;
          if (contactInfo.Phone && !this.newWorkOrder.phone) {
            this.newWorkOrder.phone = contactInfo.Phone.split(';')[0].trim();
          }
          if (contactInfo.Address && !this.newWorkOrder.propertyAddress) {
            this.newWorkOrder.propertyAddress = contactInfo.Address.trim();
            const addr = contactInfo.Address.trim().toLowerCase();
            const match = this.propertyOptions.find(
              (p) => p.display.trim().toLowerCase() === addr,
            );
            if (match) {
              this.newWorkOrder.selectedPropertyId = match.id;
              this.newWorkOrder.propertyId = match.id;
            }
          }
        } catch (error) {
          console.error('Error fetching tenant contact info:', error);
        }
      }
    },

    onTileContentClick(event, workOrder) {
      if (!isTransferButtonTarget(event)) return;
      event.stopPropagation();
      this.openTransferDialog(workOrder);
    },

    onTileContentDblClick(event) {
      if (isTransferButtonTarget(event)) event.stopPropagation();
    },

    openTransferDialog(workOrder) {
      if (!workOrder || !workOrder.nameIsStaff || !workOrder.id) return;
      if (this.transferSearchTimeout) clearTimeout(this.transferSearchTimeout);
      this.transferWorkOrderId = workOrder.id;
      this.transferQuery = '';
      this.transferSuggestions = [];
      this.transferSelected = null;
      this.transferSuggestionIndex = -1;
      this.transferSaving = false;
      this.transferError = '';
      this.showTransferDialog = true;
      this.$nextTick(() => {
        if (this.$refs.transferNameInput) this.$refs.transferNameInput.focus();
      });
    },

    closeTransferDialog() {
      if (this.transferSearchTimeout) clearTimeout(this.transferSearchTimeout);
      this.showTransferDialog = false;
      this.transferWorkOrderId = null;
      this.transferQuery = '';
      this.transferSuggestions = [];
      this.transferSelected = null;
      this.transferSuggestionIndex = -1;
      this.transferSaving = false;
      this.transferError = '';
    },

    handleTransferNameInput() {
      if (this.transferSearchTimeout) clearTimeout(this.transferSearchTimeout);
      this.transferSelected = null;
      const query = this.transferQuery.trim();
      if (query.length < 2) {
        this.transferSuggestions = [];
        return;
      }
      this.transferSearchTimeout = setTimeout(async () => {
        try {
          const response = await axios.get('/api/tenants/search', {
            params: { query },
          });
          this.transferSuggestions = response.data || [];
          this.transferSuggestionIndex = -1;
        } catch (error) {
          console.error('Error searching tenants:', error);
          this.transferSuggestions = [];
        }
      }, 300);
    },

    handleTransferNameKeydown(event) {
      if (!this.transferSuggestions.length) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.transferSuggestionIndex = Math.min(
          this.transferSuggestionIndex + 1,
          this.transferSuggestions.length - 1,
        );
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.transferSuggestionIndex = Math.max(
          this.transferSuggestionIndex - 1,
          0,
        );
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (this.transferSuggestionIndex >= 0) {
          this.chooseTransferTenant(
            this.transferSuggestions[this.transferSuggestionIndex],
          );
        } else if (this.transferSuggestions.length === 1) {
          this.chooseTransferTenant(this.transferSuggestions[0]);
        }
      } else if (event.key === 'Escape') {
        this.transferSuggestions = [];
        this.transferSuggestionIndex = -1;
      }
    },

    chooseTransferTenant(suggestion) {
      this.transferSelected = suggestion;
      this.transferQuery = suggestion.fullName || '';
      this.transferSuggestions = [];
      this.transferSuggestionIndex = -1;
    },

    async confirmTransfer() {
      if (
        !this.transferSelected ||
        !this.transferWorkOrderId ||
        this.transferSaving
      ) {
        return;
      }
      this.transferSaving = true;
      this.transferError = '';
      const workOrderId = this.transferWorkOrderId;
      try {
        const response = await axios.post(
          `/api/maintenance-requests/${workOrderId}/transfer`,
          {
            peopleId: this.transferSelected.id,
            propertyId: this.transferSelected.propertyId || null,
          },
        );
        const data = response.data || {};
        const index = this.workOrders.findIndex((wo) => wo.id === workOrderId);
        if (index !== -1) {
          this.workOrders.splice(index, 1, {
            ...this.workOrders[index],
            name: data.name,
            phone: data.phone,
            peopleId: data.peopleId,
            propertyId: data.propertyId,
            property_address: data.property_address,
            nameIsStaff: false,
          });
        }
        const name = data.name || this.transferSelected.fullName;
        this.closeTransferDialog();
        this.showNotificationMessage(
          data.emailSent
            ? `Transferred to ${name}. Confirmation email sent.`
            : `Transferred to ${name}. Confirmation email was not sent.`,
        );
        await this.refreshSingleWorkOrder(workOrderId);
      } catch (error) {
        const message =
          error &&
          error.response &&
          error.response.data &&
          error.response.data.error;
        this.transferError = message || 'Transfer failed.';
        this.transferSaving = false;
      }
    },

    /**
     * Mark done / re-open. Optional workOrder for Review mode; otherwise uses
     * the selected tile in filteredWorkOrders.
     * (Ignore click Event when called as @click="markDone".)
     */
    async markDone(workOrderArg) {
      try {
        let workOrder =
          workOrderArg &&
          typeof workOrderArg === 'object' &&
          workOrderArg.id != null &&
          !workOrderArg.target
            ? workOrderArg
            : null;
        if (!workOrder) {
          if (this.selectedTileIndex === null) return;
          workOrder = this.filteredWorkOrders[this.selectedTileIndex];
        }
        if (!workOrder) return;

        // Keep tile selection in sync when possible (voice notes in notes dialog)
        const tileIdx = this.filteredWorkOrders.findIndex(
          (w) => w.id === workOrder.id,
        );
        if (tileIdx !== -1) {
          this.selectedTileIndex = tileIdx;
        }

        const isCurrentlyCompleted = workOrder.completedOn !== null;

        // Dynamic confirmation message based on current state
        const confirmMessage = isCurrentlyCompleted
          ? 'Mark this as undone?'
          : 'Mark this done?';

        // Wait for user confirmation
        const isConfirmed = await this.openConfirmationDialog(confirmMessage);

        // Proceed only if confirmed
        if (isConfirmed) {
          const id = workOrder.id;

          // If marking as undone, do it immediately
          if (isCurrentlyCompleted) {
            // Make API call to toggle the completion state
            const response = await axios.put(
              `/api/maintenance-requests/${id}/complete`,
            );

            if (response.status === 200) {
              // Update the local work order object immediately for responsive UI
              const localWorkOrder = this.workOrders.find((wo) => wo.id === id);
              if (localWorkOrder) {
                localWorkOrder.completedOn = null;
                localWorkOrder.completedBy = null;
              }

              // Also update the filtered work order for immediate UI feedback
              workOrder.completedOn = null;
              workOrder.completedBy = null;

              // Update local database
              this.updateLocalDatabase(this.workOrders);

              // Show success notification
              this.showNotificationMessage('Work order marked as undone');

              if (!this.showReviewPopup) {
                // Deselect the tile and restore scroll position
                this.selectedTileIndex = null;
                this.$nextTick(() => {
                  if (this.$refs.innerWrapper && this.savedScrollPosition) {
                    this.$refs.innerWrapper.scrollTop = this.savedScrollPosition;
                  }
                });
              }
            }
          } else {
            // For marking as done, defer the completion until notes are saved
            // Store the work order ID to be completed after notes dialog
            this.pendingCompletionWorkOrderId = id;
            this.currentWorkOrderId = id;
            console.log(
              '[markDone] Set pendingCompletionWorkOrderId:',
              this.pendingCompletionWorkOrderId,
              'type:',
              typeof this.pendingCompletionWorkOrderId,
            );
            console.log(
              '[markDone] Set currentWorkOrderId:',
              this.currentWorkOrderId,
              'type:',
              typeof this.currentWorkOrderId,
            );
            this.workOrderNote = workOrder.note || '';
            this.showNotesPopup = true;

            // Check if this work order has photos before fetching
            if (workOrder.photoCount && workOrder.photoCount > 0) {
              this.isLoadingPhotos = true;
            }

            // Fetch photos for the selected work order
            this.fetchWorkOrderPhotos(id);

            // Don't deselect yet - we need selectedTileIndex for audio recording
          }
        }
      } catch (error) {
        if (!error) {
          // console.log('User cancelled action.');
          // Handle cancellation
        } else {
          console.error(
            'Error toggling maintenance request completion:',
            error,
          );
          // Handle other errors, show notification to the user, etc.
          this.showNotificationMessage('Error updating work order status');
        }
      }
    },

    openConfirmationDialog(message) {
      this.confirmMessage = message;
      this.showConfirm = true;

      return new Promise((resolve, reject) => {
        this.resolveConfirm = (isConfirmed) => {
          this.showConfirm = false;
          isConfirmed ? resolve(true) : reject(false);
        };
      });
    },

    handleOk() {
      if (this.resolveConfirm) this.resolveConfirm(true);
    },

    handleCancel() {
      if (this.resolveConfirm) this.resolveConfirm(false);
    },

    contactTenant() {
      const personId = this.filteredWorkOrders[this.selectedTileIndex].peopleId;

      // Use $router.push to navigate with query parameters
      this.$router.push({
        path: '/mtenantsearch',
        query: { personId: personId },
      });
    },

    // ---- Scheduling Methods ----

    openScheduleDialog(workOrder) {
      // If called from icon bar (no argument), use selected work order
      const wo =
        workOrder && workOrder.id
          ? workOrder
          : this.filteredWorkOrders[this.selectedTileIndex];
      if (!wo) return;
      this.selectedScheduleWO = wo;
      this.scheduleStep = 'choice';
      this.scheduleMode = null;
      this.scheduleWorkDays = [];
      this.scheduleSelectedDay = null;
      this.scheduleSelectedBlock = null;
      this.scheduleSelectedBlocks = [];
      this.scheduleLastRejectedStart = null;
      this.scheduleFee = '0';
      this.scheduleError = '';
      this.showScheduleConfirm = false;
      this.showScheduleDialog = true;
    },

    closeScheduleDialog() {
      this.showScheduleDialog = false;
      this.showScheduleConfirm = false;
    },

    async loadScheduleSlots() {
      this.scheduleLoading = true;
      this.scheduleError = '';
      try {
        const params = {};
        if (this.scheduleStartDate) params.startDate = this.scheduleStartDate;
        if (this.scheduleMode === 'self') params.blockSize = 30;
        if (this.selectedScheduleWO?.id)
          params.workOrderId = this.selectedScheduleWO.id;
        const response = await axios.get(
          '/api/workorder-schedule/available-slots',
          { params },
        );
        this.scheduleWorkDays = response.data.workDays;
      } catch (error) {
        console.error('Error loading schedule slots:', error);
        this.scheduleError = 'Failed to load available slots';
      }
      this.scheduleLoading = false;
    },

    async startSelfSchedule() {
      this.scheduleMode = 'self';
      this.scheduleFee = '0';
      this.scheduleStep = 'dayPicker';
      this.scheduleStartDate = null;
      await this.loadScheduleSlots();
    },

    async startAskTenant() {
      this.scheduleMode = 'tenant';
      this.scheduleFee = '25';
      this.scheduleStep = 'dayPicker';
      this.scheduleStartDate = null;
      await this.loadScheduleSlots();
    },

    async scheduleNextDays() {
      if (this.scheduleWorkDays.length === 0) return;
      const lastDay = this.scheduleWorkDays[this.scheduleWorkDays.length - 1];
      // Start from the day after the last displayed day
      const d = new Date(lastDay.date + 'T00:00:00');
      d.setDate(d.getDate() + 1);
      this.scheduleStartDate =
        d.getFullYear() +
        '-' +
        String(d.getMonth() + 1).padStart(2, '0') +
        '-' +
        String(d.getDate()).padStart(2, '0');
      await this.loadScheduleSlots();
    },

    async schedulePreviousDays() {
      if (!this.scheduleStartDate) return;
      // Go back: startDate is the first day of current page.
      // We need to find 15 business days before that.
      const d = new Date(this.scheduleStartDate + 'T00:00:00');
      let count = 0;
      while (count < 15) {
        d.setDate(d.getDate() - 1);
        const day = d.getDay();
        if (day !== 0 && day !== 6) count++;
      }
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      // Don't go before tomorrow
      if (d <= tomorrow) {
        this.scheduleStartDate = null;
      } else {
        this.scheduleStartDate =
          d.getFullYear() +
          '-' +
          String(d.getMonth() + 1).padStart(2, '0') +
          '-' +
          String(d.getDate()).padStart(2, '0');
      }
      await this.loadScheduleSlots();
    },

    selectScheduleDay(day) {
      this.scheduleSelectedDay = day;
      this.scheduleSelectedBlock = null;
      this.scheduleSelectedBlocks = [];
      this.scheduleLastRejectedStart = null;
      this.scheduleStep = 'timePicker';
    },

    onScheduleDayCardClick(day) {
      if (!day || !Array.isArray(day.blocks)) return;
      if (day.blocks.some((b) => b.available || b.conflictTitle)) {
        this.selectScheduleDay(day);
      }
    },

    selectScheduleBlock(block) {
      if (!block.available && !block.conflictTitle) return;

      if (
        this.scheduleMode === 'self' &&
        this.isBlockSelected(block)
      ) {
        const next = nextScheduleBlocks(
          this.scheduleSelectedDay?.blocks || [],
          this.scheduleSelectedBlocks,
          block,
        );
        this.scheduleError = '';
        this.scheduleLastRejectedStart = null;
        this.scheduleSelectedBlocks = next.blocks;
        return;
      }

      // If block has a conflict, show double-book confirmation first
      if (block.conflictTitle && !block._doubleBookConfirmed) {
        this.doubleBookBlock = block;
        this.showDoubleBookConfirm = true;
        return;
      }

      if (this.scheduleMode === 'tenant') {
        // Worker selects a start time; option 1 in tenant email uses a 3-hour window.
        this.scheduleSelectedBlock = {
          ...block,
          end: this.addMinutesToTime(block.start, 180),
          label: `${this.formatTime12(block.start)} - ${this.formatTime12(
            this.addMinutesToTime(block.start, 180),
          )}`,
        };
        this.showScheduleConfirm = true;
        return;
      }

      const next = nextScheduleBlocks(
        this.scheduleSelectedDay?.blocks || [],
        this.scheduleSelectedBlocks,
        block,
        {
          allowSingle: this.scheduleLastRejectedStart === block.start,
          allowConflicts: Boolean(block._doubleBookConfirmed),
        },
      );
      if (next.error) {
        this.scheduleError = next.error;
        this.scheduleLastRejectedStart = block.start;
        return;
      }
      this.scheduleError = '';
      this.scheduleLastRejectedStart = null;
      this.scheduleSelectedBlocks = next.blocks;
    },

    isBlockSelected(block) {
      return this.scheduleSelectedBlocks.some((b) => b.start === block.start);
    },

    clearScheduleBlocks() {
      this.scheduleSelectedBlocks = [];
      this.scheduleLastRejectedStart = null;
    },

    confirmSelectedBlocks() {
      if (this.scheduleSelectedBlocks.length === 0) return;
      const first = this.scheduleSelectedBlocks[0];
      const last =
        this.scheduleSelectedBlocks[this.scheduleSelectedBlocks.length - 1];
      this.scheduleSelectedBlock = {
        start: first.start,
        end: last.end,
        label: this.scheduleSelectionLabel,
      };
      this.showScheduleConfirm = true;
    },

    cancelScheduleConfirm() {
      this.showScheduleConfirm = false;
    },

    confirmDoubleBook() {
      const block = this.doubleBookBlock;
      this.showDoubleBookConfirm = false;
      this.doubleBookBlock = null;
      if (block) {
        block._doubleBookConfirmed = true;
        this.selectScheduleBlock(block);
      }
    },

    cancelDoubleBook() {
      this.showDoubleBookConfirm = false;
      this.doubleBookBlock = null;
    },

    addMinutesToTime(time, minutesToAdd) {
      const [h, m] = time.split(':').map((v) => parseInt(v, 10));
      const total = h * 60 + m + minutesToAdd;
      const wrapped = ((total % 1440) + 1440) % 1440;
      const nh = Math.floor(wrapped / 60);
      const nm = wrapped % 60;
      return `${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`;
    },

    formatTime12(time) {
      const [h, m] = time.split(':').map((v) => parseInt(v, 10));
      const ampm = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 === 0 ? 12 : h % 12;
      return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
    },

    async confirmSchedule() {
      this.scheduleLoading = true;
      this.scheduleError = '';
      try {
        const wo = this.selectedScheduleWO;
        const day = this.scheduleSelectedDay;
        const block = this.scheduleSelectedBlock;

        if (this.scheduleMode === 'self') {
          await axios.post('/api/workorder-schedule/self-schedule', {
            workOrderId: wo.id,
            date: day.date,
            startTime: block.start,
            endTime: block.end,
            fee: this.normalizedScheduleFee,
          });
          this.showNotificationMessage(
            'Work order scheduled and added to calendar',
          );
        } else {
          await axios.post('/api/workorder-schedule/ask-tenant', {
            workOrderId: wo.id,
            tentativeDate: day.date,
            tentativeStartTime: block.start,
            tentativeEndTime: block.end,
            fee: this.normalizedScheduleFee,
          });
          this.showNotificationMessage('Scheduling email sent to tenant');
        }

        this.showScheduleConfirm = false;
        this.showScheduleDialog = false;

        // Refresh the work order to show updated scheduling fields
        await this.refreshSingleWorkOrder(wo.id);
      } catch (error) {
        console.error('Error confirming schedule:', error);
        this.scheduleError =
          error.response?.data?.error || 'Failed to schedule';
      }
      this.scheduleLoading = false;
    },

    initDB() {
      return new Promise((resolve, reject) => {
        const request = indexedDB.open('WorkOrders', 1);

        request.onupgradeneeded = (event) => {
          const db = event.target.result;
          if (!db.objectStoreNames.contains('workOrders')) {
            db.createObjectStore('workOrders', { keyPath: 'id' });
          }
        };

        request.onsuccess = (event) => {
          this.db = event.target.result;
          resolve();
        };

        request.onerror = (event) => {
          reject(event.target.errorCode);
        };
      });
    },

    switchIcons() {
      if (this.showIconBar == 2) this.showIconBar = 1;
      else this.showIconBar = 2;
    },
    appendLog(string) {
      this.logMessage += '<br>' + string;
    },

    voiceRecorder() {
      if (this.showIconBar !== 3) this.showIconBar = 3;
      else this.showIconBar = 1;
    },

    voiceRecorderInNotes() {
      // Toggle voice recorder functionality within notes dialog
      // This will use the same voice recording functionality
      if (this.showIconBar !== 3) this.showIconBar = 3;
      else this.showIconBar = 1;
    },

    selectTile(index) {
      if (this.selectedTileIndex === null) {
        // Save scroll position before selecting
        if (this.$refs.innerWrapper) {
          this.savedScrollPosition = this.$refs.innerWrapper.scrollTop;
        }
        this.selectedTileIndex = index;
        // Fetch latest version in case someone else changed it
        const workOrder = this.filteredWorkOrders[index];
        if (workOrder?.id) {
          this.refreshSingleWorkOrder(workOrder.id);
          this.prefetchTileLatestComment(workOrder.id);
        }
      } else if (this.selectedTileIndex === index) {
        // If clicking the already-selected tile, do nothing (keep it selected)
        // Only the close button will deselect it
        return;
      } else {
        this.selectedTileIndex = index;
        // Fetch latest version for newly selected tile
        const workOrder = this.filteredWorkOrders[index];
        if (workOrder?.id) {
          this.refreshSingleWorkOrder(workOrder.id);
          this.prefetchTileLatestComment(workOrder.id);
        }
      }
    },

    async prefetchTileLatestComment(workOrderId) {
      const local = this.workOrders.find((wo) => wo.id === workOrderId);
      if (!local || local.latestCommentLoaded) return;
      if (this.reviewCommentsForId === workOrderId) {
        local.latestComment = this.reviewComments[0] || null;
        local.latestCommentLoaded = true;
        local.commentCount = this.reviewComments.length;
        return;
      }
      try {
        const { data } = await axios.get(
          `/api/maintenance-requests/${workOrderId}/comments`,
        );
        const comments = data.comments || [];
        local.latestComment = comments[0] || null;
        local.latestCommentLoaded = true;
        local.commentCount = comments.length;
      } catch (err) {
        console.error('Failed to prefetch work order comments:', err);
        local.latestCommentLoaded = true;
      }
    },

    closeTileAndRestoreScroll() {
      this.selectedTileIndex = null;
      if (this.openedFromListView) {
        this.openedFromListView = false;
        this.showListView = true;
        return;
      }
      this.$nextTick(() => {
        if (this.$refs.innerWrapper && this.savedScrollPosition) {
          this.$refs.innerWrapper.scrollTop = this.savedScrollPosition;
        }
      });
    },

    nothing() {
      return null;
    },

    async hardDeleteWorkOrder() {
      if (this.selectedTileIndex === null) return;
      const wo = this.filteredWorkOrders[this.selectedTileIndex];
      if (!wo) return;
      if (!confirm(`Permanently delete work order #${wo.id}?`)) return;
      try {
        await axios.delete(`/api/maintenance-requests/${wo.id}`);
        this.workOrders = this.workOrders.filter((w) => w.id !== wo.id);
        this.selectedTileIndex = null;
      } catch (err) {
        alert('Failed to delete work order.');
      }
    },

    async refreshSingleWorkOrder(workOrderId) {
      try {
        const response = await axios.get(
          `/api/maintenance-requests/${workOrderId}`,
        );
        const updatedWorkOrder = response.data;

        // Find and update the work order in the local array
        const index = this.workOrders.findIndex((wo) => wo.id === workOrderId);
        if (index !== -1) {
          const prev = this.workOrders[index] || {};
          this.workOrders.splice(index, 1, {
            ...prev,
            ...updatedWorkOrder,
            latestComment: prev.latestComment,
            latestCommentLoaded: prev.latestCommentLoaded,
            latestTenantComment: prev.latestTenantComment,
            latestTenantCommentAt: prev.latestTenantCommentAt,
            commentCount:
              updatedWorkOrder.commentCount != null
                ? updatedWorkOrder.commentCount
                : prev.commentCount,
          });
        }

        // Update IndexedDB cache
        const transaction = this.db.transaction(['workOrders'], 'readwrite');
        const objectStore = transaction.objectStore('workOrders');
        objectStore.put(updatedWorkOrder);

        this.appendLog(`Refreshed work order ${workOrderId}`);
      } catch (error) {
        console.error('Error refreshing single work order:', error);
        this.appendLog(`Error refreshing work order ${workOrderId}`);
      }
    },

    fetchWorkOrders(force = false) {
      console.log(`fetchWorkOrders called with force=${force}`);
      const transaction = this.db.transaction(['workOrders'], 'readwrite');
      const objectStore = transaction.objectStore('workOrders');

      if (force) {
        console.log('Force refresh: clearing IndexedDB cache');
        // Clear the objectStore if force is true
        const clearRequest = objectStore.clear();

        clearRequest.onsuccess = () => {
          // console.log('Successfully truncated workOrders objectStore');
          this.reloadData(objectStore);
        };

        clearRequest.onerror = (event) => {
          console.error(
            'Error truncating workOrders objectStore:',
            event.target.error,
          );
        };
      } else {
        console.log('Normal load: using cached data if available');
        this.reloadData(objectStore);
      }
    },

    reloadData(objectStore) {
      const getAllRequest = objectStore.getAll();

      getAllRequest.onsuccess = (event) => {
        let storedData = event.target.result;
        console.log(
          `Found ${storedData.length} cached work orders in IndexedDB`,
        );

        if (storedData && storedData.length > 0) {
          // Sort storedData in descending order based on modifiedOn date
          storedData.sort(
            (a, b) => new Date(b.modifiedOn) - new Date(a.modifiedOn),
          );
          this.workOrders = storedData;
          console.log('Loaded cached work orders, checking for updates...');
        } else {
          console.log('No cached data found, will fetch all work orders');
          this.workOrders = [];
        }

        let latestModifiedOn = '1970-01-01T00:00:00Z'; // Default to "zero" date
        if (storedData.length > 0) {
          latestModifiedOn = storedData[0].modifiedOn; // After sorting, the first item has the latest date
        }

        // Backfill cache rows saved before completedBy, commentCount, startedOn, or tenant flags.
        if (workOrderCacheNeedsFullReload(storedData)) {
          latestModifiedOn = '1970-01-01T00:00:00Z';
        }

        axios
          .get('/api/maintenance-requests', {
            params: { modifiedAfter: latestModifiedOn },
          })
          .then((response) => {
            const newItems = response.data || [];
            console.log(
              `API returned ${newItems.length} new/updated work orders since ${latestModifiedOn}`,
            );

            if (newItems.length > 0) {
              // Build a map of new items by ID so updated records replace stale cached ones
              const newItemMap = new Map(
                newItems.map((item) => [item.id, item]),
              );
              const merged = storedData.map(
                (wo) => newItemMap.get(wo.id) || wo,
              );
              // Add any genuinely new records (IDs not in cache)
              newItems.forEach((item) => {
                if (!storedData.some((wo) => wo.id === item.id)) {
                  merged.push(item);
                }
              });
              const updatedWorkOrders = merged;
              this.updateLocalDatabase(updatedWorkOrders);
              this.workOrders = updatedWorkOrders;
              console.log(
                `Updated work orders list with ${newItems.length} new items`,
              );

              // Track the highest ID seen
              const maxId = Math.max(...updatedWorkOrders.map((wo) => wo.id));
              localStorage.setItem('workOrderMaxId', maxId.toString());
              console.log(`Updated workOrderMaxId to ${maxId}`);
            } else {
              console.log('No new work orders, using cached data');
              // Still update maxId from cached data so badge clears when user views the list
              if (storedData.length > 0) {
                const maxId = Math.max(...storedData.map((wo) => wo.id));
                localStorage.setItem('workOrderMaxId', maxId.toString());
                console.log(`Updated workOrderMaxId from cache to ${maxId}`);
              }
            }
          })
          .catch((error) => {
            console.error('Error fetching maintenance requests:', error);
          });
      };

      getAllRequest.onerror = (event) => {
        console.error('Error retrieving work orders:', event.target.error);
      };
    },

    updateLocalDatabase(workOrderArray) {
      const transaction = this.db.transaction(['workOrders'], 'readwrite');
      const objectStore = transaction.objectStore('workOrders');

      const sanitizedWorkOrders = workOrderArray.map((order) =>
        workOrderCacheRecord(order),
      );

      // Store only the sanitized objects
      sanitizedWorkOrders.forEach((order) => {
        try {
          objectStore.put(order);
        } catch (error) {
          console.error('Error storing work order:', error, order);
        }
      });

      transaction.oncomplete = () => {
        // console.log('Local database updated successfully.');
      };

      transaction.onerror = (event) => {
        console.error('Error updating local database:', event.target.error);
      };
    },

    highlightMatch(filter, content) {
      if (!filter) return content;

      // Ensure content is a string to avoid TypeError when non-string values are processed
      if (typeof content !== 'string') {
        return content;
      }

      const query = filter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${query})`, 'gi');
      return content.replace(regex, '<mark>$1</mark>');
    },

    formatScheduledDate(wo) {
      if (!wo.scheduledDate) return '';
      const dateStr = wo.scheduledDate.split('T')[0];
      const parts = dateStr.split('-');
      const month = parseInt(parts[1], 10);
      const day = parseInt(parts[2], 10);
      const year = parts[0].slice(-2);
      let time = '';
      if (wo.scheduledTime) {
        const [h, m] = wo.scheduledTime.split(':');
        const hr = parseInt(h, 10);
        const ampm = hr >= 12 ? 'pm' : 'am';
        const hr12 = hr % 12 || 12;
        time = ` at ${hr12}:${m}${ampm}`;
      }
      return `${month}/${day}/${year}${time}`;
    },

    formatTentativeDateTime(wo) {
      if (!wo || !wo.tentativeDate) return '';
      const dateStr = String(wo.tentativeDate).split('T')[0];
      const parts = dateStr.split('-');
      if (parts.length !== 3) return '';
      const month = parseInt(parts[1], 10);
      const day = parseInt(parts[2], 10);
      const year = parts[0].slice(-2);
      let time = '';
      if (wo.tentativeTime) {
        const [h, m] = String(wo.tentativeTime).split(':');
        const hr = parseInt(h, 10);
        if (!Number.isNaN(hr) && m !== undefined) {
          const ampm = hr >= 12 ? 'pm' : 'am';
          const hr12 = hr % 12 || 12;
          time = ` at ${hr12}:${m}${ampm}`;
        }
      }
      return `${month}/${day}/${year}${time}`;
    },

    openCalendarToScheduledDate(wo) {
      let dateStr = wo.scheduledDate;
      if (dateStr instanceof Date) {
        dateStr = dateStr.toISOString();
      }
      dateStr = String(dateStr).split('T')[0];
      const parts = dateStr.split('-');
      const month = parseInt(parts[1], 10);
      const year = parseInt(parts[0], 10);
      const day = parseInt(parts[2], 10);
      eventBus.emit('openCalendarToDate', {
        month,
        year,
        day,
        personal: false,
      });
    },

    formattedRequestContent(request, options = {}) {
      const completedOnText = this.formatDate(request.completedOn);
      const completedByInitials = this.getCompletedByInitials(
        request.completedBy,
      );
      const completedBySuffix =
        completedOnText && completedByInitials
          ? ` <span style="font-size: 0.75em; color: #888;">(${completedByInitials})</span>`
          : '';
      const displayName = request.name || '';
      const nameHtml = options.clickableName
        ? `<span class="wo-person-name">${displayName}</span>`
        : displayName;
      const transferHtml = workOrderTransferButtonHtml(request);
      const flagHtml = options.showPersonFlags
        ? reviewPersonFlagHtml(request)
        : '';
      const addressLine = formatPropertyAddressLine(request, {
        clickableAddress: options.clickableAddress,
      });
      const scheduledWhen = options.showScheduled
        ? this.formatScheduledDate(request)
        : '';
      const scheduledHtml = String(scheduledWhen).replace(/\//g, '&#47;');
      const scheduledLine = scheduledWhen
        ? `**Scheduled:** <span class="wo-scheduled-date">${scheduledHtml}</span><br>`
        : '';
      let content = `${scheduledLine}**Name**: ${nameHtml}${transferHtml}${flagHtml}<br>**Phone**: ${request.phone}<br>**Date:** ${this.formatDate(workOrderCardDate(request))} <br>**Completed On:** ${completedOnText}${completedBySuffix}<br> ${addressLine}<br><br>**Problem Description:**<br> ${request.problem_description}<br><br> **Allow Entry:** ${request.allow_entry ? 'Yes' : 'No'}`;

      // Add pet and special instruction fields if peopleId exists
      if (request.peopleId) {
        if (request.pet !== undefined) {
          content += `<br> **Has Pet:** ${request.pet ? 'Yes' : 'No'}`;
          if (request.pet && request.petName) {
            content += `<br> **Pet Names:** ${request.petName}`;
          }
        }

        if (
          request.workOrderSpecialInstr &&
          request.workOrderSpecialInstr.trim()
        ) {
          content += `<br> **Special Instr:** ${request.workOrderSpecialInstr}`;
        }
      }

      // Add "seen by" information at the bottom
      if (request.viewedBy && request.viewedBy.trim()) {
        content += `<br><span style="font-size: 0.75em; color: #888;">seen by ${request.viewedBy}</span>`;
      }

      content += ' ';
      return this.markedCleanBody(content);
    },

    clearMaintenanceLockCodeTimer() {
      if (this.maintenanceLockCodeTimerId) {
        clearInterval(this.maintenanceLockCodeTimerId);
        this.maintenanceLockCodeTimerId = null;
      }
      this.maintenanceLockCodeCountdown = 0;
    },

    startMaintenanceLockCodeCountdown() {
      this.clearMaintenanceLockCodeTimer();
      this.maintenanceLockCodeCountdown = 10;
      this.maintenanceLockCodeTimerId = setInterval(() => {
        if (this.maintenanceLockCodeCountdown <= 1) {
          this.closeMaintenanceLockCodePopup();
          return;
        }
        this.maintenanceLockCodeCountdown -= 1;
      }, 1000);
    },

    closeMaintenanceLockCodePopup() {
      this.clearMaintenanceLockCodeTimer();
      this.showMaintenanceLockCodePopup = false;
      this.maintenanceLockCodeLoading = false;
      this.maintenanceLockCodeError = '';
      this.maintenanceLockCodeValue = '';
      this.maintenanceLockCodeAddress = '';
    },

    openTenantEmailDialog(workOrder) {
      if (!workOrder?.id) return;
      if (!workOrder.peopleId && !workOrder.name) {
        this.showNotificationMessage(
          'No tenant linked to this work order.',
        );
        return;
      }
      this.tenantEmailWorkOrder = workOrder;
      this.tenantEmailMessage = '';
      this.tenantEmailError = '';
      this.tenantEmailSending = false;
      this.showTenantEmailDialog = true;
    },

    closeTenantEmailDialog() {
      if (this.tenantEmailSending) return;
      this.showTenantEmailDialog = false;
      this.tenantEmailWorkOrder = null;
      this.tenantEmailMessage = '';
      this.tenantEmailError = '';
    },

    async sendTenantEmailMessage() {
      const wo = this.tenantEmailWorkOrder;
      const message = (this.tenantEmailMessage || '').trim();
      if (!wo?.id || !message || this.tenantEmailSending) return;

      this.tenantEmailSending = true;
      this.tenantEmailError = '';
      try {
        // SMTP can exceed the global 20s axios timeout on prod relays
        const response = await axios.post(
          `/api/maintenance-requests/${wo.id}/email-tenant`,
          { message },
          { timeout: 60000 },
        );
        const sentTo = response.data?.emailSentTo;
        this.showNotificationMessage(
          sentTo
            ? `Email sent to ${sentTo}`
            : 'Email sent to tenant',
        );
        this.tenantEmailSending = false;
        this.showTenantEmailDialog = false;
        this.tenantEmailWorkOrder = null;
        this.tenantEmailMessage = '';
      } catch (error) {
        console.error('Error emailing tenant:', error);
        const timedOut =
          error?.code === 'ECONNABORTED' ||
          /timeout/i.test(error?.message || '');
        this.tenantEmailError = timedOut
          ? 'Request timed out waiting for the mail server. Please try again.'
          : error.response?.data?.error ||
            'Failed to send email. Please try again.';
        this.tenantEmailSending = false;
      }
    },

    async openMaintenanceLockCode(workOrder) {
      if (!workOrder?.propertyId) {
        this.showNotificationMessage(
          'No property linked to this work order.',
        );
        return;
      }

      this.clearMaintenanceLockCodeTimer();
      this.showMaintenanceLockCodePopup = true;
      this.startMaintenanceLockCodeCountdown();
      this.maintenanceLockCodeLoading = true;
      this.maintenanceLockCodeError = '';
      this.maintenanceLockCodeValue = '';
      this.maintenanceLockCodeAddress = this.stripHtml(
        workOrder.property_address || '',
      );

      try {
        const response = await axios.get('/api/lock-codes/maintenance-code', {
          params: {
            propertyId: workOrder.propertyId,
            _ts: Date.now(),
          },
        });

        if (!response.data || Object.keys(response.data).length === 0) {
          this.maintenanceLockCodeValue = '';
          return;
        }

        this.maintenanceLockCodeValue = response.data.maintCode || '';
        if (response.data.address) {
          this.maintenanceLockCodeAddress = response.data.address;
        }
      } catch (error) {
        console.error('Error fetching maintenance lock code:', error);
        if (error.response?.status === 403) {
          this.maintenanceLockCodeError =
            'You do not have permission to view lock codes.';
        } else {
          this.maintenanceLockCodeError =
            'Unable to load maintenance lock code.';
        }
      } finally {
        this.maintenanceLockCodeLoading = false;
      }
    },

    async speakProblemDescription(workOrder) {
      if (!workOrder) return;
      if (!window.speechSynthesis) {
        this.showNotificationMessage(
          'Text-to-speech is not supported on this device.',
        );
        return;
      }
      // If already speaking this work order, stop it
      if (this.speakingWorkOrderId === workOrder.id) {
        this._speechToken = (this._speechToken || 0) + 1;
        window.speechSynthesis.cancel();
        this.speakingWorkOrderId = null;
        return;
      }

      window.speechSynthesis.cancel();

      const description = this.prepareTextForSpeech(
        workOrder.problem_description,
      );
      if (!description) {
        this.showNotificationMessage('No problem description to read.');
        return;
      }

      const address = this.prepareTextForSpeech(workOrder.property_address);
      const spokenText = address
        ? `at ${address}, ${description}`
        : description;

      const token = (this._speechToken || 0) + 1;
      this._speechToken = token;
      this.speakingWorkOrderId = workOrder.id;

      const voices = await speechVoicesFrom(window.speechSynthesis);
      if (this._speechToken !== token) return;

      const utterance = new SpeechSynthesisUtterance(spokenText);
      const voice = pickFemaleSpeechVoice(voices);
      if (voice) {
        utterance.voice = voice;
        utterance.lang = voice.lang || 'en-US';
      } else {
        utterance.lang = 'en-US';
      }
      utterance.rate = 1.0;
      utterance.pitch = 1.0;

      utterance.onend = () => {
        if (this.speakingWorkOrderId === workOrder.id) {
          this.speakingWorkOrderId = null;
        }
      };
      utterance.onerror = () => {
        if (this.speakingWorkOrderId === workOrder.id) {
          this.speakingWorkOrderId = null;
        }
      };

      window.speechSynthesis.speak(utterance);
    },

    markedCleanBody(body) {
      const sanitizedBody = body.replace(
        /[^a-zA-Z0-9.,?!;:'"(){}[\]<>%@#$&*\-=_+~`|\\/\s✓]/g,
        '',
      );
      // Process date links before markdown parsing
      const bodyWithDateLinks = this.processDateLinks(sanitizedBody);
      return marked.parse(bodyWithDateLinks);
    },

    processDateLinks(text) {
      if (!text) return text;

      // Pattern 1: Month Day (e.g., "October 15", "Dec 3")
      const monthDayPattern =
        /(January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)\s+(\d{1,2})/gi;

      // Pattern 2: M/D or M/D/YY or M/D/YYYY (e.g., "10/15", "10/15/25", "10/15/2025")
      // Use word boundaries to avoid matching within URLs or other patterns
      const slashDatePattern = /\b(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?\b/g;

      // Pattern 3: M-D or M-D-YY or M-D-YYYY (e.g., "10-15", "10-15-25", "10-15-2025")
      // Use negative lookbehind/lookahead to avoid matching within calendar links or longer numbers
      const dashDatePattern =
        /(?<![\d-])(\d{1,2})-(\d{1,2})(?:-(\d{2,4}))?(?![\d-])/g;

      let processedText = text;

      // Process month-day pattern
      processedText = processedText.replace(
        monthDayPattern,
        (match, monthStr, day) => {
          const monthMap = {
            january: 1,
            jan: 1,
            february: 2,
            feb: 2,
            march: 3,
            mar: 3,
            april: 4,
            apr: 4,
            may: 5,
            june: 6,
            jun: 6,
            july: 7,
            jul: 7,
            august: 8,
            aug: 8,
            september: 9,
            sep: 9,
            sept: 9,
            october: 10,
            oct: 10,
            november: 11,
            nov: 11,
            december: 12,
            dec: 12,
          };

          const month = monthMap[monthStr.toLowerCase()];
          const currentYear = new Date().getFullYear();

          return `[${match}](#calendar-${month}-${currentYear}-${day})`;
        },
      );

      // Process slash date pattern
      processedText = processedText.replace(
        slashDatePattern,
        (match, month, day, year) => {
          let fullYear = year
            ? year.length === 2
              ? 2000 + parseInt(year)
              : parseInt(year)
            : new Date().getFullYear();
          return `[${match}](#calendar-${month}-${fullYear}-${day})`;
        },
      );

      // Process dash date pattern
      processedText = processedText.replace(
        dashDatePattern,
        (match, month, day, year) => {
          let fullYear = year
            ? year.length === 2
              ? 2000 + parseInt(year)
              : parseInt(year)
            : new Date().getFullYear();
          return `[${match}](#calendar-${month}-${fullYear}-${day})`;
        },
      );

      return processedText;
    },

    onDailyBriefingCalendarChip(event) {
      const link = event.target.closest('a[href^="#calendar-"]');
      if (!link || !link.closest('.daily-briefing')) return;
      const chip = calendarChipDate(link.getAttribute('href'));
      const path = chip ? calendarListPath(chip) : null;
      if (!path) return;
      event.preventDefault();
      event.stopPropagation();
      this.$router.push(path);
    },

    handleCalendarLinkClick(event) {
      // Check if clicked element or its parent is a calendar link
      const link = event.target.closest('a[href^="#calendar-"]');
      if (!link) return;

      event.preventDefault();
      event.stopPropagation(); // Prevent click from bubbling to work order card

      const chip = calendarChipDate(link.getAttribute('href'));
      if (!chip) return;

      // The briefing panel stops clicks before they reach document.
      // Card notes keep the shared calendar event.
      if (link.closest('.daily-briefing')) {
        const path = calendarListPath(chip);
        if (path) this.$router.push(path);
        return;
      }

      eventBus.emit('openCalendarToDate', {
        month: chip.month,
        year: chip.year,
        day: chip.day,
        personal: false, // Open shared calendar by default
      });
    },

    // for voice recorder
    toggleRecording() {
      this.appendLog('toggleRecording');
      if (!this.isRecording) {
        this.startRecording();
        this.startCountdown();
      } else {
        this.stopRecording();
      }
    },

    startCountdown() {
      this.recordingLabel = '2';
      this.countdownValue = 2;
      this.countdownInterval = setInterval(() => {
        this.countdownValue -= 1;
        if (this.countdownValue > 0) {
          this.recordingLabel = String(this.countdownValue);
        } else if (this.countdownValue === 0) {
          this.recordingLabel = 'Stop'; // Set to blank when the countdown reaches 0
          clearInterval(this.countdownInterval);
        }
      }, 1000);
    },

    startRecording() {
      console.log('startRecording called');
      this.isRecording = true;
      this.chunks = [];
      this.recordingStartTime = Date.now(); // Added this line to initialize recording start time

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.appendLog('getUserMedia not supported on this browser.');
        return;
      }

      console.log('Requesting microphone access...');
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          console.log('Got media stream:', stream);
          if (!stream) {
            this.appendLog('Received an undefined stream');
            return;
          }

          this.mediaRecorder = new MediaRecorder(stream);
          console.log('Created MediaRecorder:', this.mediaRecorder);

          // Ensure mediaRecorder is defined before using it
          if (this.mediaRecorder) {
            this.mediaRecorder.ondataavailable = (e) => {
              console.log('Data available, chunk size:', e.data.size);
              this.chunks.push(e.data);
            };
            this.mediaRecorder.onstop = () => this.onRecordingStop();

            try {
              this.mediaRecorder.start();
              console.log(
                'MediaRecorder started, state:',
                this.mediaRecorder.state,
              );
            } catch (error) {
              this.isRecording = false;
              this.appendLog(
                `Error starting media recording: ${error.message}`,
              );
            }
          } else {
            this.appendLog('Failed to create MediaRecorder');
          }
        })
        .catch((error) => {
          console.error('Error accessing media devices:', error);
          this.isRecording = false;
          this.recordingLabel = 'Record';
          this.appendLog(`Error accessing media devices: ${error.message}`);
          this.showNotificationMessage(
            'Microphone access denied. Please use HTTPS or localhost, or check browser permissions.',
          );
        });
    },

    stopRecording() {
      console.log('stopRecording called');
      this.recordingLabel = 'Record';
      this.isRecording = false;

      if (this.mediaRecorder) {
        console.log('Stopping mediaRecorder, state:', this.mediaRecorder.state);
        this.calculateRecordingDuration();
        this.mediaRecorder.stop();
      } else {
        console.log(
          'No mediaRecorder to stop - recording never started properly',
        );
        this.recordingDuration = 0; // Reset duration since recording failed
        this.showNotificationMessage(
          'Recording failed. Microphone access may be blocked.',
        );
      }
    },

    calculateRecordingDuration() {
      const endTime = Date.now();
      this.recordingDuration = Math.floor(
        (endTime - this.recordingStartTime) / 1000,
      ); // Calculate in seconds
    },

    onRecordingStop() {
      console.log('onRecordingStop called, chunks length:', this.chunks.length);
      const blob = new Blob(this.chunks, { type: 'audio/ogg; codecs=opus' });
      console.log('Created blob, size:', blob.size, 'bytes');
      this.audioData = blob; // Store the Blob directly
      console.log('Set audioData:', this.audioData);
      this.chunks = [];

      // If you want to keep using the URL for playing audio:
      this.audioDataURL = URL.createObjectURL(blob);
      console.log('Created audioDataURL:', this.audioDataURL);
    },

    playAudio() {
      if (!this.audioData) return;

      try {
        // If we don't have a URL yet or we're using a new audio blob, create a new URL
        if (!this.audioDataURL && this.audioData) {
          this.audioDataURL = URL.createObjectURL(this.audioData);
        }

        // Create the audio element if it doesn't exist yet
        if (!this.audioElement) {
          this.audioElement = new Audio();

          // Set up event listeners only once
          this.audioElement.addEventListener('play', () => {
            this.isPlaying = true;
          });

          this.audioElement.addEventListener('ended', () => {
            this.isPlaying = false;
          });

          this.audioElement.addEventListener('pause', () => {
            this.isPlaying = false;
          });

          this.audioElement.addEventListener('error', (e) => {
            this.appendLog(
              `Audio playback error: ${e.target.error.message || 'Unknown error'}`,
            );
            this.isPlaying = false;
          });
        }

        // Only set the source if it's a new URL or audio was reset
        if (this.audioElement.src !== this.audioDataURL) {
          this.audioElement.src = this.audioDataURL;
        }

        // Use a promise to detect playback errors
        const playPromise = this.audioElement.play();

        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            this.appendLog(`iOS playback error: ${error.message}`);

            // Special handling for iOS devices
            if (
              error.name === 'NotAllowedError' ||
              /The operation is not supported/.test(error.message)
            ) {
              this.appendLog('Attempting iOS workaround...');

              // iOS often requires user interaction before playing audio
              // This is a common workaround using a different audio context approach
              const AudioContext =
                window.AudioContext || window.webkitAudioContext;
              if (AudioContext && this.audioData) {
                const audioContext = new AudioContext();
                const reader = new FileReader();

                reader.onload = (e) => {
                  audioContext.decodeAudioData(e.target.result, (buffer) => {
                    const source = audioContext.createBufferSource();
                    source.buffer = buffer;
                    source.connect(audioContext.destination);
                    source.start(0);
                    this.isPlaying = true;

                    // When the buffer source ends
                    source.onended = () => {
                      this.isPlaying = false;
                    };
                  });
                };

                reader.readAsArrayBuffer(this.audioData);
              }
            }
          });
        }
      } catch (error) {
        this.appendLog(`Error in playAudio: ${error.message}`);
        this.isPlaying = false;
      }
    },

    deleteRecording() {
      if (this.isPlaying && this.audioElement) {
        this.audioElement.pause();
      }
      this.audioData = null;
      this.recordingDuration = 0; // Reset the duration when deleting

      // Clean up resources
      if (this.audioDataURL) {
        URL.revokeObjectURL(this.audioDataURL);
        this.audioDataURL = null;
      }

      this.isPlaying = false;
    },

    async sendRecording() {
      // First validate that we have both audio data and a selected work order
      if (!this.audioData) {
        this.appendLog('No audio data to send');
        this.showNotificationMessage('No audio data to send');
        return;
      }

      // Ensure selected tile index is valid
      if (
        typeof this.selectedTileIndex !== 'number' ||
        this.selectedTileIndex < 0 ||
        !this.filteredWorkOrders ||
        this.selectedTileIndex >= this.filteredWorkOrders.length
      ) {
        this.appendLog('No work order selected');
        this.showNotificationMessage('Please select a work order first');
        return;
      }

      try {
        const workOrder = this.filteredWorkOrders[this.selectedTileIndex];

        // Validate required properties exist
        if (!workOrder || !workOrder.id) {
          this.appendLog('Invalid work order data');
          return;
        }

        // Helper function to strip HTML tags
        const stripHtmlTags = (str) => {
          if (!str) return str;
          return str.replace(/<[^>]*>/g, '');
        };

        // Safely construct title with fallbacks for missing properties
        // Strip HTML tags from name and address to avoid markup in filenames
        const name = stripHtmlTags(workOrder.name) || 'Unknown';
        const date = workOrder.date || new Date().toISOString().split('T')[0];
        const address =
          stripHtmlTags(workOrder.property_address) || 'Unknown Address';
        const title = `${name}_${date}_${address}`;
        const workOrderId = workOrder.id;

        this.appendLog(`Creating voice memo for work order #${workOrderId}`);

        const recording = this.audioData;
        const outcome = await deliverOrQueue({
          online: readNavigatorOnline(),
          item: {
            kind: 'voice-note',
            title,
            filename: 'recording.ogg',
            attachedTo: '1',
            attachedId: String(workOrderId),
            audio: recording,
          },
          send: async () => {
            const formData = new FormData();
            formData.append('audioBlob', recording, 'recording.ogg');
            formData.append('title', title);
            formData.append('attachedTo', 1);
            formData.append('attachedId', workOrderId);
            return axios.post('/api/saveVoiceNote', formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
              skipGlobalError: true,
            });
          },
        });

        if (outcome.queued) {
          this.appendLog(OUTBOUND_HOLD_TEXT);
          this.showNotificationMessage(OUTBOUND_HOLD_TEXT);
          this.showIconBar = 1;
          this.deleteRecording();
        } else if (outcome.result && outcome.result.status === 200) {
          this.appendLog('Voice memo sent successfully');
          this.showIconBar = 1;
          this.deleteRecording();
        } else {
          const response = outcome.result;
          this.appendLog(
            `Unexpected response: ${response ? response.status : 'none'}`,
          );
        }
      } catch (error) {
        console.error('Error sending audio recording:', error);
        this.appendLog(
          `Error: ${error.message || 'Unknown error sending recording'}`,
        );
      }
    },

    showNotes() {
      if (this.selectedTileIndex !== null) {
        const workOrder = this.filteredWorkOrders[this.selectedTileIndex];
        this.currentWorkOrderId = workOrder.id;
        this.workOrderNote = workOrder.note || '';
        this.showNotesPopup = true;

        // Check if this work order has photos before fetching
        if (workOrder.photoCount && workOrder.photoCount > 0) {
          this.isLoadingPhotos = true;
        }

        // Fetch photos for the selected work order
        this.fetchWorkOrderPhotos(workOrder.id);
      }
    },

    closeNotes() {
      // Clear pending completion flag (user cancelled)
      this.pendingCompletionWorkOrderId = null;

      this.showNotesPopup = false;
      this.workOrderNote = '';
      this.currentWorkOrderId = null;
      this.workOrderPhotos = [];
      this.currentPhotoIndex = 0;

      // Keep Review dialog selection; otherwise deselect tile
      if (!this.showReviewPopup) {
        this.selectedTileIndex = null;
        this.$nextTick(() => {
          if (this.$refs.innerWrapper && this.savedScrollPosition) {
            this.$refs.innerWrapper.scrollTop = this.savedScrollPosition;
          }
        });
      }
    },

    async saveWorkOrderNotes(andMarkDone = false) {
      if (!this.currentWorkOrderId || this.savingWorkOrderNotes) return;

      this.savingWorkOrderNotes = true;
      const workOrderId = this.currentWorkOrderId;
      const noteText = this.workOrderNote;
      try {
        // If user chose "Save and Done", mark it as done first
        if (
          andMarkDone &&
          this.pendingCompletionWorkOrderId === workOrderId
        ) {
          console.log('[saveWorkOrderNotes] Marking work order as complete...');
          const response = await axios.put(
            `/api/maintenance-requests/${workOrderId}/complete`,
          );

          console.log(
            '[saveWorkOrderNotes] Complete API response:',
            response.status,
            response.data,
          );

          if (response.status === 200) {
            // Update the local work order object
            const localWorkOrder = this.workOrders.find(
              (wo) => wo.id === workOrderId,
            );
            if (localWorkOrder) {
              localWorkOrder.completedOn = new Date().toISOString();
              localWorkOrder.completedBy = response.data.completedBy || null;
              console.log(
                '[saveWorkOrderNotes] Updated local work order:',
                localWorkOrder.id,
                'completedOn:',
                localWorkOrder.completedOn,
              );
            } else {
              console.log(
                '[saveWorkOrderNotes] WARNING: Could not find local work order with id:',
                workOrderId,
              );
            }

            // Also update in filteredWorkOrders if it exists
            const filteredWorkOrder = this.filteredWorkOrders.find(
              (wo) => wo.id === workOrderId,
            );
            if (filteredWorkOrder) {
              filteredWorkOrder.completedOn = new Date().toISOString();
              filteredWorkOrder.completedBy = response.data.completedBy || null;
            }

            // Show success notification
            this.showNotificationMessage('Work order marked as complete');
            const closedWith = await this.removeDoneWorkOrdersFromDailyBriefing(
              workOrderId,
            );
            if (
              closedWith &&
              !closedWith.failed.length &&
              closedWith.closedDuplicates > 0
            ) {
              const n = closedWith.closedDuplicates;
              this.showNotificationMessage(
                `Closed this work order and ${n} duplicate${n === 1 ? '' : 's'}.`,
              );
            }
          }
        }

        // Now save the note
        const response = await axios.post('/api/save-workorder-note', {
          workOrderId,
          note: noteText,
        });

        const steps = notesDialogStepsAfterSave(response.status);
        if (steps[0] !== 'close-dialog') return;

        const workOrder = this.workOrders.find((wo) => wo.id === workOrderId);
        if (workOrder) {
          workOrder.note = noteText;
        }

        // Close before the local cache write so a slow or failed cache
        // update cannot leave the mark-done dialog on screen.
        this.pendingCompletionWorkOrderId = null;
        this.showNotesPopup = false;
        this.workOrderNote = '';
        this.currentWorkOrderId = null;
        this.workOrderPhotos = [];
        this.currentPhotoIndex = 0;

        if (this.showReviewPopup) {
          // Stay in review; list shrinks when completedOn is set — clamp index
          this.$nextTick(() => this.clampReviewIndex());
        } else {
          this.selectedTileIndex = null;
          this.$nextTick(() => {
            if (this.$refs.innerWrapper && this.savedScrollPosition) {
              this.$refs.innerWrapper.scrollTop = this.savedScrollPosition;
            }
          });
        }
        await this.$nextTick();

        if (steps.includes('write-local-cache') && this.db) {
          try {
            this.updateLocalDatabase(this.workOrders);
          } catch (dbErr) {
            console.error('Error updating local work order cache:', dbErr);
          }
        }
      } catch (error) {
        console.error('Error saving notes:', error);
      } finally {
        this.savingWorkOrderNotes = false;
      }
    },

    openWorkOrderMerge() {
      const primary =
        this.selectedTileIndex !== null
          ? this.filteredWorkOrders[this.selectedTileIndex]
          : null;
      this.beginWorkOrderMerge(primary);
    },

    openWorkOrderMergeFromReview() {
      this.beginWorkOrderMerge(this.reviewCurrentWorkOrder);
    },

    beginWorkOrderMerge(primary) {
      if (!primary || primary.id == null) {
        window.alert('Select a work order first.');
        return;
      }
      this.workOrderMergePrimaryId = primary.id;
      this.workOrderMergeQuery = '';
      this.workOrderMergeSelected = [];
      this.workOrderMergeError = '';
      this.workOrderMergeSaving = false;
      this.showWorkOrderMerge = true;
    },

    closeWorkOrderMerge() {
      this.showWorkOrderMerge = false;
      this.workOrderMergeSaving = false;
      this.workOrderMergeError = '';
    },

    async confirmWorkOrderMerge() {
      const primary = this.workOrderMergePrimary;
      const chosen = new Set(
        (this.workOrderMergeSelected || []).map((id) => String(id)),
      );
      const sources = this.workOrderMergePool.filter((order) =>
        chosen.has(String(order.id)),
      );
      if (!primary || !sources.length || this.workOrderMergeSaving) return;
      const problemDescription = mergedProblemDescription(
        primary.problem_description,
        sources,
      );
      this.workOrderMergeSaving = true;
      this.workOrderMergeError = '';
      try {
        const response = await axios.post('/api/maintenance-requests-update', {
          id: primary.id,
          name: primary.name,
          phone: primary.phone,
          property_address: primary.property_address,
          propertyId: primary.propertyId,
          problem_description: problemDescription,
          allow_entry: primary.allow_entry,
        });
        if (response.status === 200) {
          primary.problem_description = problemDescription;
          const failed = [];
          for (const source of sources) {
            const closed = await this.closeMergedWorkOrder(source, primary.id);
            if (!closed) failed.push(source.id);
          }
          this.updateLocalDatabase(this.workOrders);
          if (failed.length) {
            this.workOrderMergeError = `Could not close ${failed
              .map((id) => `#${id}`)
              .join(', ')}`;
            this.workOrderMergeSaving = false;
            return;
          }
          this.closeWorkOrderMerge();
        }
      } catch (error) {
        console.error('Error merging work orders:', error);
        this.workOrderMergeError = 'Could not merge work orders';
        this.workOrderMergeSaving = false;
      }
    },

    async closeMergedWorkOrder(source, primaryId) {
      const note = mergeCloseNote(source.note, primaryId);
      try {
        await axios.post('/api/save-workorder-note', {
          workOrderId: source.id,
          note,
        });
        const url = `/api/maintenance-requests/${source.id}/complete`;
        let done = await axios.put(url, { skipNotice: true });
        if (done.data && done.data.isCompleted === false) {
          done = await axios.put(url, { skipNotice: true });
        }
        if (!done.data || done.data.isCompleted !== true) return false;
        source.note = note;
        source.completedOn = new Date().toISOString();
        source.completedBy = done.data.completedBy || null;
        return true;
      } catch (error) {
        console.error('Error closing merged work order', source.id, error);
        return false;
      }
    },

    attachDocument() {
      if (this.selectedTileIndex !== null) {
        this.showDocumentPopup = true;
        this.$nextTick(() => {
          if (this.$refs.documentIdInput) {
            this.$refs.documentIdInput.focus();
          }
        });
      }
    },

    closeDocumentPopup() {
      this.showDocumentPopup = false;
      this.documentIdToAttach = '';
    },

    async confirmAttachDocument() {
      if (!this.documentIdToAttach.trim()) {
        return;
      }

      try {
        const workOrder = this.filteredWorkOrders[this.selectedTileIndex];
        const docId = this.documentIdToAttach.trim();

        // Get the current description
        let updatedDescription = workOrder.problem_description || '';

        // Find if there's already a Photos link - update regex to match relative URLs
        const hasExistingPhotosLink =
          /\[Photos\]\(\/mdocs\?search=:[0-9]+\)/g.test(updatedDescription);

        if (hasExistingPhotosLink) {
          // Replace any existing Photos links - update regex to match relative URLs
          updatedDescription = updatedDescription.replace(
            /(\n+)?\[Photos\]\(\/mdocs\?search=:[0-9]+\)/g,
            '',
          );

          // Clean up any extra newlines that might be left
          updatedDescription = updatedDescription.replace(/\n{3,}/g, '\n\n');
          updatedDescription = updatedDescription.trim();
        }

        // Create the new link and append it with a relative URL
        const documentLink = `\n\n[Photos](/mdocs?search=:${docId})`;
        updatedDescription = updatedDescription + documentLink;

        // Save the updated problem description
        const response = await axios.post('/api/maintenance-requests-update', {
          id: workOrder.id,
          problem_description: updatedDescription,
        });

        if (response.status === 200) {
          // Update the local workOrder object
          const localWorkOrder = this.workOrders.find(
            (wo) => wo.id === workOrder.id,
          );
          if (localWorkOrder) {
            localWorkOrder.problem_description = updatedDescription;
          }

          // Update in local database
          this.updateLocalDatabase(this.workOrders);

          // Close the popup
          this.closeDocumentPopup();
        }
      } catch (error) {
        console.error('Error attaching document:', error);
      }
    },

    editWorkOrder() {
      if (this.selectedTileIndex !== null) {
        this.beginEditWorkOrder(
          this.filteredWorkOrders[this.selectedTileIndex],
        );
      }
    },

    /** Double-click in Review edits this work order without leaving Review. */
    editReviewWorkOrder() {
      const workOrder = this.reviewCurrentWorkOrder;
      if (!workOrder || workOrder.id == null) return;
      this.beginEditWorkOrder(workOrder);
    },

    beginEditWorkOrder(workOrder) {
      if (!workOrder) return;
      this.editingWorkOrderId = workOrder.id;
      this.editedName = workOrder.name || '';
      this.editedPhone = workOrder.phone || '';
      this.editedPropertyAddress = workOrder.property_address || '';
      this.editedPropertyId = workOrder.propertyId || null;
      this.editedProblemDescription = workOrder.problem_description || '';
      this.editedAllowEntry = Boolean(workOrder.allow_entry);

      if (this.propertyOptions.length === 0) {
        this.fetchPropertyOptions();
      }

      this.currentEditPeopleId = workOrder.peopleId || null;

      if (workOrder.peopleId) {
        this.editedPet = Boolean(workOrder.pet);
        this.editedPetName = workOrder.petName || '';
        this.editedWorkOrderSpecialInstr =
          workOrder.workOrderSpecialInstr || '';
      }

      this.showEditWorkOrderPopup = true;
    },

    workOrderBeingEdited() {
      const id = this.editingWorkOrderId;
      if (id != null && id !== '') {
        return (
          (this.workOrders || []).find(
            (wo) => String(wo.id) === String(id),
          ) || null
        );
      }
      if (this.selectedTileIndex === null) return null;
      return this.filteredWorkOrders[this.selectedTileIndex] || null;
    },

    /** Double-click a maint tile → same as Edit toolbar button. */
    editWorkOrderFromTile(index) {
      if (this.selectedTileIndex !== index) {
        this.selectTile(index);
      }
      this.editWorkOrder();
    },

    onEditPropertySelected() {
      if (this.editedPropertyId === 'other') {
        this.editedPropertyId = null;
        this.editedPropertyAddress = '';
        return;
      }
      const selected = this.propertyOptions.find(
        (p) => p.id === this.editedPropertyId,
      );
      this.editedPropertyAddress = selected ? selected.display : '';
    },

    closeEditWorkOrder() {
      this.showEditWorkOrderPopup = false;
      this.editingWorkOrderId = null;
      this.editedName = '';
      this.editedPhone = '';
      this.editedPropertyAddress = '';
      this.editedPropertyId = null;
      this.editedProblemDescription = '';
      this.editedAllowEntry = false;
      this.editedPet = false;
      this.editedPetName = '';
      this.editedWorkOrderSpecialInstr = '';
      this.currentEditPeopleId = null;
    },

    async saveEditedWorkOrder() {
      const workOrder = this.workOrderBeingEdited();
      if (!workOrder) return;

      try {
        const propertyAddress = this.stripMarkTags(
          this.editedPropertyAddress,
        );
        const problemDescription = this.stripMarkTags(
          this.editedProblemDescription,
        );
        this.editedPropertyAddress = propertyAddress;
        this.editedProblemDescription = problemDescription;

        // Save all edited fields
        const response = await axios.post('/api/maintenance-requests-update', {
          id: workOrder.id,
          name: this.editedName,
          phone: this.editedPhone,
          property_address: propertyAddress,
          propertyId: this.editedPropertyId,
          problem_description: problemDescription,
          allow_entry: this.editedAllowEntry,
        });

        if (response.status === 200) {
          // Update the local work order object
          const localWorkOrder = this.workOrders.find(
            (wo) => wo.id === workOrder.id,
          );
          if (localWorkOrder) {
            localWorkOrder.name = this.editedName;
            localWorkOrder.phone = this.editedPhone;
            localWorkOrder.property_address = propertyAddress;
            localWorkOrder.propertyId = this.editedPropertyId;
            localWorkOrder.problem_description = problemDescription;
            localWorkOrder.allow_entry = this.editedAllowEntry;
          }

          // Update pet and special instruction fields if peopleId exists
          if (this.currentEditPeopleId) {
            const peopleResponse = await axios.post('/api/people-update', {
              peopleId: this.currentEditPeopleId,
              pet: this.editedPet,
              petName: this.editedPetName,
              workOrderSpecialInstr: this.editedWorkOrderSpecialInstr,
            });

            if (peopleResponse.status === 200) {
              // Update local data with new pet/special instruction values
              if (localWorkOrder) {
                localWorkOrder.pet = this.editedPet;
                localWorkOrder.petName = this.editedPetName;
                localWorkOrder.workOrderSpecialInstr =
                  this.editedWorkOrderSpecialInstr;
              }

              // Also update the filtered work order for immediate UI feedback
              workOrder.pet = this.editedPet;
              workOrder.petName = this.editedPetName;
              workOrder.workOrderSpecialInstr =
                this.editedWorkOrderSpecialInstr;
            }
          }

          // Update in local database
          this.updateLocalDatabase(this.workOrders);

          // Close the popup
          this.closeEditWorkOrder();

          // Show success notification
          this.showNotificationMessage('Work order updated successfully');
        }
      } catch (error) {
        console.error('Error updating work order:', error);
        this.showNotificationMessage('Error updating work order');
      }
    },

    goBack() {
      window.history.back();
    },

    async fetchWorkOrderPhotos(workOrderId) {
      const notesToken = (this.notesPhotoLoadToken =
        (this.notesPhotoLoadToken || 0) + 1);
      try {
        const response = await axios.get(
          `/api/work-orders/${workOrderId}/photos`,
        );
        if (notesToken !== this.notesPhotoLoadToken) return;
        if (response.status === 200) {
          const manifest = Array.isArray(response.data) ? response.data : [];
          const disk = await getCachedPhotosForWorkOrder(workOrderId);
          if (notesToken !== this.notesPhotoLoadToken) return;
          const ids = new Set(
            manifest.map((photo) => String(photo && photo.id)),
          );
          const painted = disk
            .filter((photo) => ids.has(String(photo.id)))
            .map((photo) => ({
              id: photo.id,
              caption: photo.caption || '',
              modifiedOn: photo.modifiedOn,
              photoUrl: URL.createObjectURL(photo.blob),
            }));
          this.currentPhotoIndex = 0;
          const pages = await this.loadWorkOrderPhotoPages(
            workOrderId,
            manifest,
            painted,
            (next) => {
              if (notesToken !== this.notesPhotoLoadToken) return;
              this.replaceWorkOrderPhotos(next);
            },
            () => notesToken === this.notesPhotoLoadToken,
          );
          if (notesToken !== this.notesPhotoLoadToken) return;
          this.replaceWorkOrderPhotos(pages);
          if (this.currentPhotoIndex >= pages.length) {
            this.currentPhotoIndex = 0;
          }
        }
      } catch (error) {
        console.error('Error fetching work order photos:', error);
      } finally {
        if (notesToken === this.notesPhotoLoadToken) {
          this.isLoadingPhotos = false;
        }
      }
    },

    replaceWorkOrderPhotos(pages) {
      const next = pages || [];
      const keep = new Set(next.map((photo) => photo && photo.photoUrl));
      for (const photo of this.workOrderPhotos || []) {
        const url = photo && photo.photoUrl;
        if (url && String(url).startsWith('blob:') && !keep.has(url)) {
          URL.revokeObjectURL(url);
        }
      }
      this.workOrderPhotos = next;
    },

    prevPhoto() {
      if (this.currentPhotoIndex > 0) {
        this.currentPhotoIndex--;
      }
    },

    nextPhoto() {
      if (this.currentPhotoIndex < this.workOrderPhotos.length - 1) {
        this.currentPhotoIndex++;
      }
    },

    async deletePhoto(photoId) {
      try {
        const response = await axios.delete(`/api/photos/${photoId}`);
        if (response.status === 200) {
          const removed = (this.workOrderPhotos || []).find(
            (photo) => photo.id === photoId,
          );
          if (
            removed?.photoUrl &&
            String(removed.photoUrl).startsWith('blob:')
          ) {
            URL.revokeObjectURL(removed.photoUrl);
          }
          this.workOrderPhotos = this.workOrderPhotos.filter(
            (photo) => photo.id !== photoId,
          );
          if (this.currentPhotoIndex >= this.workOrderPhotos.length) {
            this.currentPhotoIndex = this.workOrderPhotos.length - 1;
          }
          removeCachedPhoto(photoId);
          this.dropDisplayedReviewPhoto(this.currentWorkOrderId, photoId);
          this.invalidateReviewFirstPhoto(this.currentWorkOrderId);
        }
      } catch (error) {
        console.error('Error deleting photo:', error);
      }
    },

    selectPhoto() {
      this.showPhotoUploadDialog = true;
    },

    closePhotoUploadDialog() {
      this.revokePhotoPreviews();
      this.showPhotoUploadDialog = false;
      this.photoPreviewUrls = [];
      this.photoFiles = [];
      this.photoCaption = '';
    },

    revokePhotoPreviews(previews = this.photoPreviewUrls) {
      for (const preview of previews || []) {
        if (preview && preview.url) {
          try {
            URL.revokeObjectURL(preview.url);
          } catch {
            /* the preview URL may already be gone */
          }
        }
      }
    },

    applyGallerySaveResult(preview, result) {
      preview.gallerySave = gallerySaveState(result);
      const message = gallerySaveMessage(result);
      if (message) this.showNotificationMessage(message);
    },

    // Starts the device save in this turn. iPhone share and Android download
    // both need the camera change, or a later tap, before any await.
    beginCameraGallerySave(file) {
      const galleryFile = cameraPhotoFile(file, this.currentWorkOrderId);
      const promise = saveCameraPhotoToGallery(galleryFile);
      return { galleryFile, promise };
    },

    savePreviewToPhotos(index) {
      const preview = this.photoPreviewUrls[index];
      if (!preview || !preview.galleryFile) return;
      if (
        preview.gallerySave === 'saved' ||
        preview.gallerySave === 'pending'
      ) {
        return;
      }
      const promise = saveCameraPhotoToGallery(preview.galleryFile);
      preview.gallerySave = 'pending';
      preview.gallerySavePromise = promise;
      promise.then((result) => this.applyGallerySaveResult(preview, result));
    },

    // Upload is a fresh tap. If the camera change could not open Photos,
    // try again here before the request starts. A dismissed sheet still uploads.
    finishCameraGallerySaves() {
      const waits = [];
      for (const preview of this.photoPreviewUrls) {
        if (!preview || !preview.galleryFile) continue;
        if (
          preview.gallerySave === 'saved' ||
          preview.gallerySave === 'declined'
        ) {
          continue;
        }
        if (preview.gallerySave === 'needs-tap' || !preview.gallerySavePromise) {
          preview.gallerySavePromise = saveCameraPhotoToGallery(
            preview.galleryFile,
          );
          preview.gallerySave = 'pending';
          preview.gallerySavePromise.then((result) =>
            this.applyGallerySaveResult(preview, result),
          );
        }
        if (preview.gallerySave === 'pending' && preview.gallerySavePromise) {
          waits.push(preview.gallerySavePromise);
        }
      }
      return Promise.all(waits);
    },

    async handlePhotoUpload(event, source) {
      const fromCamera = source === 'camera';
      const files = Array.from(event.target.files || []);
      event.target.value = '';
      if (files.length === 0) return;

      // Start device saves before the first await so the tap still counts.
      const gallerySaves = files.map((file) =>
        fromCamera ? this.beginCameraGallerySave(file) : null,
      );

      try {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const name = (file.name || '').toLowerCase();
          const mime = (file.type || '').toLowerCase();
          const isHeic =
            name.endsWith('.heic') ||
            name.endsWith('.heif') ||
            mime.includes('heic') ||
            mime.includes('heif');
          const isImage =
            mime.startsWith('image/') ||
            isHeic ||
            mime === 'application/octet-stream' ||
            mime === '';

          if (!isImage) {
            this.uploadErrorMessage = 'Please select an image file';
            this.showUploadErrorDialog = true;
            return;
          }

          // HEIC cannot be decoded by canvas in most browsers — send as-is;
          // the server converts to JPEG. Other types still compress locally.
          let fileToUpload = file;
          if (!isHeic) {
            const options = {
              maxSizeMB: 1.5,
              maxWidthOrHeight: 1800,
              useWebWorker: true,
              fileType: 'image/jpeg',
            };
            fileToUpload = await imageCompression(file, options);
          }

          // Create blob URL for preview
          const blobUrl = URL.createObjectURL(fileToUpload);
          const gallerySave = gallerySaves[i];
          const preview = {
            url: blobUrl,
            isHeic,
            galleryFile: gallerySave ? gallerySave.galleryFile : null,
            gallerySave: gallerySave ? 'pending' : '',
            gallerySavePromise: gallerySave ? gallerySave.promise : null,
          };
          this.photoPreviewUrls.push(preview);
          this.photoFiles.push(fileToUpload);
          if (gallerySave) {
            gallerySave.promise.then((result) =>
              this.applyGallerySaveResult(preview, result),
            );
          }
        }
      } catch (error) {
        console.error('Error processing photo:', error);
        this.uploadErrorMessage = 'Error processing photo';
        this.uploadErrorDetails = error.message || 'Unknown error occurred';
        this.showUploadErrorDialog = true;
      }
    },

    async heicToJpegFile(file) {
      if (typeof createImageBitmap !== 'function') return null;
      try {
        const bitmap = await createImageBitmap(file);
        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        canvas.getContext('2d').drawImage(bitmap, 0, 0);
        if (bitmap.close) bitmap.close();
        const blob = await new Promise((resolve) =>
          canvas.toBlob(resolve, 'image/jpeg', 0.82),
        );
        if (!blob) return null;
        const fileName = (file.name || 'photo').replace(
          /\.(heic|heif)$/i,
          '.jpg',
        );
        return new File([blob], fileName.endsWith('.jpg') ? fileName : `${fileName}.jpg`, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
      } catch {
        return null;
      }
    },

    formatPhotoUploadError(error) {
      const status = error.response && error.response.status;
      const data = error.response && error.response.data;
      if (typeof data === 'string' && /<\s*html/i.test(data)) {
        if (status === 404) {
          return `${status}: upload session was not found (often a load-balanced host). Try the photo again.`;
        }
        return `${status}: the server returned an HTML error page instead of a JSON response`;
      }
      if (error.response) {
        return `${status}: ${
          (data && (data.error || data.message)) ||
          JSON.stringify(data)
        }`;
      }
      return error.message || 'Unknown error occurred';
    },

    async uploadPhoto() {
      if (this.photoPreviewUrls.length === 0 || !this.currentWorkOrderId)
        return;

      try {
        await this.finishCameraGallerySaves();
        this.isUploading = true;
        // HEIC decode on the server often exceeds the global 20s axios timeout.
        const photoUploadTimeout = 180000;

        for (const fileToUpload of this.photoFiles) {
          // Split file into chunks of 950KB (just under 1MB NGINX limit)
          const CHUNK_SIZE = 950 * 1024; // 950KB in bytes

          // Make sure we have a valid file to upload with correct MIME type
          let file = fileToUpload;

          const name = (file.name || '').toLowerCase();
          const mime = (file.type || '').toLowerCase();
          let isHeic =
            name.endsWith('.heic') ||
            name.endsWith('.heif') ||
            mime.includes('heic') ||
            mime.includes('heif');

          if (isHeic) {
            const jpegFile = await this.heicToJpegFile(file);
            if (jpegFile) {
              file = jpegFile;
              isHeic = false;
            }
          }

          // HEIC: skip canvas compress — server converts. Others: JPEG normalize.
          if (!isHeic) {
            const options = {
              maxSizeMB: 1.5,
              maxWidthOrHeight: 1920,
              useWebWorker: true,
              fileType: 'image/jpeg',
            };
            try {
              const compressedFile = await imageCompression(file, options);
              const fileName =
                (file.name || 'photo').replace(/\.[^/.]+$/, '') + '.jpg';
              file = new File([compressedFile], fileName, {
                type: 'image/jpeg',
                lastModified: new Date().getTime(),
              });
            } catch (compressionError) {
              console.error('Error compressing image:', compressionError);
            }
          }

          const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

          // Generate a unique ID for this upload session
          const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;

          // If file is small enough to send in one request, use the original method
          if (totalChunks === 1) {
            const formData = new FormData();
            formData.append('photo', file);
            formData.append('workOrderId', this.currentWorkOrderId);
            formData.append('caption', this.photoCaption);

            const response = await axios.post('/api/photos', formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: photoUploadTimeout,
            });

            if (response.status === 201) {
              this.appendLog('Photo uploaded successfully');
            }
          } else {
            // Otherwise, use chunked upload

            // Start by informing the server we're beginning a chunked upload
            const initResponse = await axios.post(
              '/api/photos/chunked/init',
              {
                fileName: file.name,
                fileType: file.type || (isHeic ? 'image/heic' : 'image/jpeg'),
                totalChunks,
                totalSize: file.size,
                uploadId,
                workOrderId: this.currentWorkOrderId,
              },
              { timeout: photoUploadTimeout },
            );

            if (initResponse.status !== 200) {
              throw new Error('Failed to initialize chunked upload');
            }

            // Process each chunk
            for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
              const start = chunkIndex * CHUNK_SIZE;
              const end = Math.min(start + CHUNK_SIZE, file.size);
              const chunk = file.slice(start, end);

              const chunkFormData = new FormData();
              chunkFormData.append('chunk', chunk, 'chunk');
              chunkFormData.append('chunkIndex', chunkIndex.toString());
              chunkFormData.append('uploadId', uploadId);

              const chunkResponse = await axios.post(
                '/api/photos/chunked/upload',
                chunkFormData,
                {
                  headers: {
                    'Content-Type': 'multipart/form-data',
                  },
                  timeout: photoUploadTimeout,
                },
              );

              if (chunkResponse.status !== 200) {
                throw new Error(`Failed to upload chunk ${chunkIndex + 1}`);
              }
            }

            // Finalize the upload
            const finalizeResponse = await axios.post(
              '/api/photos/chunked/finalize',
              {
                uploadId,
              },
              { timeout: photoUploadTimeout },
            );

            if (finalizeResponse.status === 201) {
              this.appendLog('Photo uploaded successfully');
            } else {
              throw new Error('Failed to finalize chunked upload');
            }
          }
        }

        this.closePhotoUploadDialog();
        this.invalidateReviewFirstPhoto(this.currentWorkOrderId);
        this.fetchWorkOrderPhotos(this.currentWorkOrderId);
      } catch (error) {
        console.error('Error uploading photo:', error);
        this.uploadErrorMessage = 'Error uploading photo';
        this.uploadErrorDetails = this.formatPhotoUploadError(error);
        this.showUploadErrorDialog = true;
      } finally {
        this.isUploading = false;
      }
    },

    openFullSizePhoto() {
      this.showFullSizePhoto = true;
    },

    closeFullSizePhoto() {
      this.showFullSizePhoto = false;
      // Reset zoom state
      this.photoScale = 1;
      this.photoTranslateX = 0;
      this.photoTranslateY = 0;
    },

    openEditCaptionDialog() {
      this.editingCaption = this.currentPhoto.caption || '';
      this.showEditCaptionDialog = true;
    },

    cancelEditCaption() {
      this.showEditCaptionDialog = false;
      this.editingCaption = '';
    },

    async updatePhotoCaption() {
      if (!this.currentPhoto || !this.currentPhoto.id) return;

      try {
        const response = await axios.post('/api/photos/update-caption', {
          photoId: this.currentPhoto.id,
          caption: this.editingCaption,
        });

        if (response.status === 200) {
          this.currentPhoto.caption = this.editingCaption;
          this.showEditCaptionDialog = false;
        }
      } catch (error) {
        console.error('Error updating photo caption:', error);
      }
    },

    showNotificationMessage(message) {
      // Clear any existing timeout
      if (this.notificationTimeout) {
        clearTimeout(this.notificationTimeout);
      }

      // Set message and show notification
      this.notificationMessage = message;
      this.showNotification = true;
      this.notificationFading = false;

      // After 3.5 seconds, start fading
      this.notificationTimeout = setTimeout(() => {
        this.notificationFading = true;

        // After fade animation completes, hide notification
        setTimeout(() => {
          this.showNotification = false;
        }, 1500); // Match this to the CSS transition time
      }, 3500);
    },

    removePreviewPhoto(index) {
      const removed = this.photoPreviewUrls.splice(index, 1);
      this.revokePhotoPreviews(removed);
      this.photoFiles.splice(index, 1);
    },

    // Add method to open notes popup directly from the title icon
    showNotesForWorkOrder(workOrderId) {
      // Find the work order by ID
      const workOrder = this.workOrders.find(
        (wo) => Number(wo.id) === Number(workOrderId),
      );
      if (!workOrder) return;

      // Open notes popup with the selected work order
      this.currentWorkOrderId = workOrder.id;
      this.workOrderNote = workOrder.note || '';
      this.showNotesPopup = true;

      // Check if this work order has photos before fetching
      if (workOrder.photoCount && workOrder.photoCount > 0) {
        this.isLoadingPhotos = true;
      }

      // Fetch photos for the selected work order
      this.fetchWorkOrderPhotos(workOrderId);
    },

    handleTouchStart(event) {
      if (event.touches.length === 1) {
        // Single touch - prepare for panning
        this.isPanning = true;
        const touch = event.touches[0];
        this.lastTouchX = touch.clientX;
        this.lastTouchY = touch.clientY;
      } else if (event.touches.length === 2) {
        // Double touch - prepare for zooming
        this.isZooming = true;
        this.isPanning = false;

        // Calculate the initial distance between the two touches
        const touch1 = event.touches[0];
        const touch2 = event.touches[1];
        this.initialTouchDistance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY,
        );
        this.initialScale = this.photoScale;
      }
    },

    handleTouchMove(event) {
      event.preventDefault(); // Prevent browser gestures

      if (this.isZooming && event.touches.length === 2) {
        // Handle zooming - calculate new scale based on change in distance
        const touch1 = event.touches[0];
        const touch2 = event.touches[1];
        const currentDistance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY,
        );

        // Calculate new scale
        const newScale =
          this.initialScale * (currentDistance / this.initialTouchDistance);

        // Apply limits to the zoom scale
        this.photoScale = Math.min(Math.max(newScale, 1), 5);
      } else if (
        this.isPanning &&
        event.touches.length === 1 &&
        this.photoScale > 1
      ) {
        // Only allow panning when zoomed in
        const touch = event.touches[0];

        // Calculate the distance moved
        const deltaX = touch.clientX - this.lastTouchX;
        const deltaY = touch.clientY - this.lastTouchY;

        // Update the translations
        this.photoTranslateX += deltaX;
        this.photoTranslateY += deltaY;

        // Update the last touch position
        this.lastTouchX = touch.clientX;
        this.lastTouchY = touch.clientY;
      }
    },

    handleTouchEnd() {
      // Reset the touch states
      this.isPanning = false;
      this.isZooming = false;
    },

    handleDoubleTap(event) {
      if (this.photoScale > 1) {
        // If already zoomed in, reset to normal
        this.photoScale = 1;
        this.photoTranslateX = 0;
        this.photoTranslateY = 0;
      } else {
        // Zoom in to 2x at the tap location
        this.photoScale = 2;

        // Calculate tap position relative to the image center
        const img = event.target;
        const rect = img.getBoundingClientRect();
        const tapX = event.clientX - rect.left - rect.width / 2;
        const tapY = event.clientY - rect.top - rect.height / 2;

        // Center the zoomed view on the tap location
        this.photoTranslateX = -tapX;
        this.photoTranslateY = -tapY;
      }
    },

    async validateCachedData() {
      try {
        console.log('Validating cached work order data...');

        // Get statistics from the API
        const statsResponse = await axios.get('/api/work-orders/stats');
        const dbStats = statsResponse.data;
        console.log('Database stats:', dbStats);

        // Get cached work orders from IndexedDB
        const transaction = this.db.transaction(['workOrders'], 'readonly');
        const objectStore = transaction.objectStore('workOrders');
        const getAllRequest = objectStore.getAll();

        await new Promise((resolve, reject) => {
          getAllRequest.onsuccess = async (event) => {
            const cachedData = event.target.result;
            console.log('Cached work orders:', cachedData.length);

            // Calculate cache statistics
            const cacheTotal = cachedData.length;
            const cacheDone = cachedData.filter(
              (wo) => wo.completedOn !== null,
            ).length;
            const cacheLargestId =
              cachedData.length > 0
                ? Math.max(...cachedData.map((wo) => wo.id))
                : 0;

            console.log('Cache stats:', {
              total: cacheTotal,
              done: cacheDone,
              largestId: cacheLargestId,
            });

            // Compare statistics
            const totalMismatch = cacheTotal !== dbStats.totalCount;
            const doneMismatch = cacheDone !== dbStats.doneCount;
            const idMismatch = cacheLargestId !== dbStats.largestId;

            if (totalMismatch || doneMismatch || idMismatch) {
              console.warn('Cache validation failed! Discrepancies found:', {
                totalMismatch: totalMismatch
                  ? `Cache: ${cacheTotal}, DB: ${dbStats.totalCount}`
                  : 'OK',
                doneMismatch: doneMismatch
                  ? `Cache: ${cacheDone}, DB: ${dbStats.doneCount}`
                  : 'OK',
                idMismatch: idMismatch
                  ? `Cache: ${cacheLargestId}, DB: ${dbStats.largestId}`
                  : 'OK',
              });

              // Reload only not-done work orders to update the cache
              console.log('Fetching not-done work orders to update cache...');
              const notDoneResponse = await axios.get(
                '/api/maintenance-requests',
                {
                  params: { completedOn: 'null' },
                },
              );

              if (notDoneResponse.status === 200) {
                const notDoneWorkOrders = notDoneResponse.data;
                console.log(
                  `Fetched ${notDoneWorkOrders.length} not-done work orders`,
                );

                // Update cache with not-done work orders
                // Keep done work orders from cache and add/update not-done from DB
                const doneWorkOrders = cachedData.filter(
                  (wo) => wo.completedOn !== null,
                );

                // Merge: keep done from cache, use not-done from DB
                const mergedWorkOrders = [
                  ...doneWorkOrders,
                  ...notDoneWorkOrders,
                ];

                // Update IndexedDB with merged data
                this.updateLocalDatabase(mergedWorkOrders);

                console.log('Cache updated with fresh not-done work orders');
              }
            } else {
              console.log('Cache validation passed - data is in sync');
            }

            resolve();
          };

          getAllRequest.onerror = (event) => {
            console.error(
              'Error reading cache for validation:',
              event.target.error,
            );
            reject(event.target.error);
          };
        });
      } catch (error) {
        console.error('Error validating cached data:', error);
        // Don't throw - allow the app to continue with existing cache
      }
    },

    async fetchRecentUsageData() {
      try {
        const response = await axios.get('/api/work-orders/recent-usage');
        if (response.status === 200) {
          this.recentUsageData = response.data;
          console.log('Recent usage data fetched:', this.recentUsageData);
        }
      } catch (error) {
        console.error('Error fetching recent usage data:', error);
        this.recentUsageData = [];
      }
    },

    async toggleDebugPanel() {
      this.showDebug = !this.showDebug;
      // Only fetch data when opening the panel (not when closing)
      if (this.showDebug && this.recentUsageData.length === 0) {
        await this.fetchRecentUsageData();
      }
    },

    async logUserActivity() {
      try {
        // Get user information from the user-info API (same as MHomePage.vue)
        const userResponse = await axios.get('/api/user-info');
        const userId = userResponse.data?.id || null;
        const displayName = userResponse.data?.displayName || null;

        // Send the usage data to the server with user information and version
        await axios.post('/api/log-usage', {
          usageText: 'Work Orders',
          userId: userId,
          displayName: displayName,
          version: process.env.VUE_APP_VERSION || '1.0.0',
        });

        console.log('User activity logged for Work Orders');
      } catch (error) {
        console.error('Error logging user activity:', error);
      }
    },

    setupViewTracking() {
      // Create IntersectionObserver to track when tiles come into view
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // Extract work order ID from the data attribute
              const workOrderId =
                entry.target.getAttribute('data-work-order-id');
              if (workOrderId && !this.viewedWorkOrders.has(workOrderId)) {
                // Mark as viewed and track it
                this.viewedWorkOrders.add(workOrderId);
                this.trackWorkOrderView(workOrderId);
              }
            }
          });
        },
        {
          root: null, // Use viewport as root
          rootMargin: '0px',
          threshold: 0.5, // Trigger when 50% of tile is visible
        },
      );

      // Observe all current tiles
      this.$nextTick(() => {
        this.observeWorkOrderTiles();
      });
    },

    observeWorkOrderTiles() {
      // Find all work order tiles and observe them
      const tiles = document.querySelectorAll('.maint-tile');
      tiles.forEach((tile) => {
        if (this.intersectionObserver) {
          this.intersectionObserver.observe(tile);
        }
      });
    },

    async trackWorkOrderView(workOrderId) {
      try {
        const response = await axios.post('/api/work-orders/track-view', {
          workOrderId: parseInt(workOrderId),
        });

        // Update the local work order with the new viewedBy value
        if (response.data.viewedBy) {
          const workOrder = this.workOrders.find(
            (wo) => wo.id === parseInt(workOrderId),
          );
          if (workOrder) {
            workOrder.viewedBy = response.data.viewedBy;
          }
        }
      } catch (error) {
        console.error('Error tracking work order view:', error);
      }
    },

    // Confirmation Methods
    async toggleConfirmationsDialog() {
      console.log(
        'toggleConfirmationsDialog called, current state:',
        this.showConfirmationsDialog,
      );
      this.showConfirmationsDialog = !this.showConfirmationsDialog;
      console.log('New dialog state:', this.showConfirmationsDialog);

      if (this.showConfirmationsDialog) {
        console.log('Dialog opened, loading data...');
        // Clear any previous response state
        this.showingResponseFor = null;
        this.responseText = '';

        await this.loadUsers();
        await this.loadConfirmations();
        await this.loadUnconfirmedItems();

        // Set default tab: 'needed' if there are unconfirmed items, 'manage' if none
        this.activeTab = this.unconfirmedItems.length > 0 ? 'needed' : 'manage';
        console.log('Active tab set to:', this.activeTab);
      }
    },

    async loadUsers() {
      try {
        console.log('Loading users...');
        const token = this.getAuthToken();
        console.log(
          'Using token:',
          token ? token.substring(0, 20) + '...' : 'NO TOKEN',
        );

        const response = await axios.get('/api/users', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        console.log('Response status:', response.status);
        console.log('Response data:', response.data);

        if (response.status === 200) {
          console.log(
            'Setting users array with',
            response.data.length,
            'users',
          );
          this.users = response.data;
        }
      } catch (error) {
        console.error('Error loading users - full error:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
      }
    },

    async loadConfirmations() {
      try {
        // Get all confirmations for this user
        const response = await axios.get('/api/work-orders/confirmations', {
          headers: {
            Authorization: `Bearer ${this.getAuthToken()}`,
          },
        });

        if (response.status === 200) {
          this.confirmationsList = response.data;
        }
      } catch (error) {
        console.error('Error loading confirmations:', error);
        this.confirmationsList = [];
      }
    },

    async loadUnconfirmedItems() {
      console.log(
        'loadUnconfirmedItems() called at:',
        new Date().toISOString(),
      );
      try {
        const response = await axios.get(
          '/api/work-orders/confirmations/unconfirmed',
          {
            headers: {
              Authorization: `Bearer ${this.getAuthToken()}`,
            },
          },
        );

        if (response.status === 200) {
          console.log(
            'Received unconfirmed items:',
            response.data.length,
            'items',
          );
          console.log(
            'Unconfirmed items:',
            response.data.map((item) => ({
              id: item.id,
              confirmationText: item.confirmationText,
              confirmationState: item.confirmationState,
              confirmedBy: item.confirmedBy,
              assignedToName: item.assignedToName,
            })),
          );
          this.unconfirmedItems = response.data;
        }
      } catch (error) {
        console.error('Error loading unconfirmed items:', error);
        this.unconfirmedItems = [];
      }
    },

    showResponseFor(confirmationId) {
      this.showingResponseFor = confirmationId;
      this.confirmationResponse = '';
    },

    async confirmItem(confirmation, response) {
      const confirmationState = response === 'yes' ? 'yes' : 'no';

      try {
        const updateResponse = await axios.put(
          `/api/work-orders/confirmations/${confirmation.id}`,
          {
            confirmationState,
            response: this.confirmationResponse,
          },
          {
            headers: {
              Authorization: `Bearer ${this.getAuthToken()}`,
            },
          },
        );

        if (updateResponse.status === 200) {
          // Update local state
          if (this.confirmationsList) {
            const index = this.confirmationsList.findIndex(
              (c) => c.id === confirmation.id,
            );
            if (index !== -1) {
              this.confirmationsList[index] = {
                ...this.confirmationsList[index],
                confirmationState,
                response: this.confirmationResponse,
                confirmedOn: new Date().toISOString(),
                confirmedBy: this.getCurrentUserDisplayName(),
              };
            }
          }

          this.confirmationResponse = '';
          this.showingResponseFor = null; // Hide response section
          await this.loadUnconfirmedItems(); // Refresh unconfirmed list

          // Auto-close dialog if no more unconfirmed items
          if (this.unconfirmedItems.length === 0) {
            this.showConfirmationsDialog = false;
            console.log(
              'Auto-closing confirmations dialog - no more unconfirmed items',
            );
          }
        }
      } catch (error) {
        console.error('Error confirming item:', error);
      }
    },

    async submitResponse(confirmation, responseType) {
      await this.confirmItem(confirmation, responseType);
    },

    async createConfirmation() {
      if (!this.newConfirmationText.trim()) {
        alert('Please enter confirmation text.');
        return;
      }

      if (!this.selectedAssignedTo) {
        alert('Please select who this confirmation is assigned to.');
        return;
      }

      try {
        const response = await axios.post(
          '/api/work-orders/confirmations',
          {
            confirmationText: this.newConfirmationText,
            assignedTo: this.selectedAssignedTo,
          },
          {
            headers: {
              Authorization: `Bearer ${this.getAuthToken()}`,
            },
          },
        );

        if (response.status === 201) {
          this.newConfirmationText = '';
          this.selectedAssignedTo = null;
          await this.loadConfirmations();
          await this.loadUnconfirmedItems();
        }
      } catch (error) {
        console.error('Error creating confirmation:', error);
      }
    },

    async deleteConfirmation(confirmationId) {
      console.log('Attempting to delete confirmation ID:', confirmationId);

      // Check if confirmation still exists in the list
      const confirmationExists = this.confirmationsList.find(
        (c) => c.id === confirmationId,
      );
      if (!confirmationExists) {
        console.log(
          'Confirmation already removed from list, skipping delete request',
        );
        return;
      }

      try {
        const response = await axios.delete(
          `/api/work-orders/confirmations/${confirmationId}`,
          {
            headers: {
              Authorization: `Bearer ${this.getAuthToken()}`,
            },
          },
        );

        // Socket listener will handle UI updates via 'confirmationDeleted' event
        if (response.status === 200) {
          console.log('Confirmation deleted successfully');
        }
      } catch (error) {
        console.error('Error deleting confirmation:', error);
        // If it's a 404 error, the item might have been deleted already
        if (error.response?.status === 404) {
          console.log(
            'Confirmation not found on server, removing from local list',
          );
          this.confirmationsList = this.confirmationsList.filter(
            (c) => c.id !== confirmationId,
          );
          this.selectedConfirmationId = null;
        }
      }
    },

    toggleConfirmationActions(confirmationId) {
      // Toggle showing edit/delete actions for clicked confirmation
      if (this.selectedConfirmationId === confirmationId) {
        this.selectedConfirmationId = null; // Hide actions if already selected
      } else {
        this.selectedConfirmationId = confirmationId; // Show actions for this confirmation
      }
    },

    editConfirmation(confirmation) {
      // Set the confirmation for editing
      this.editingConfirmationId = confirmation.id;
      this.newConfirmationText = confirmation.confirmationText;
      this.selectedAssignedTo = confirmation.assignedTo;
      this.selectedConfirmationId = null; // Hide the actions
      // Scroll to the form
      this.$nextTick(() => {
        const formElement = document.querySelector(
          '.create-confirmation-section',
        );
        if (formElement) {
          formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
      console.log('Editing confirmation:', confirmation);
    },

    async updateConfirmation() {
      if (!this.newConfirmationText.trim()) {
        alert('Please enter confirmation text');
        return;
      }

      try {
        const response = await axios.put(
          `/api/work-orders/confirmations/${this.editingConfirmationId}`,
          {
            confirmationText: this.newConfirmationText,
            assignedTo: this.selectedAssignedTo || null,
          },
          {
            headers: {
              Authorization: `Bearer ${this.getAuthToken()}`,
            },
          },
        );

        if (response.status === 200) {
          console.log('Confirmation updated successfully:', response.data);
          this.cancelEdit();
          await this.loadConfirmations();
          await this.loadUnconfirmedItems();
        }
      } catch (error) {
        console.error('Error updating confirmation:', error);
      }
    },

    cancelEdit() {
      this.editingConfirmationId = null;
      this.newConfirmationText = '';
      this.selectedAssignedTo = null;
    },

    getConfirmationStateColor(state) {
      switch (state) {
        case 'pending':
          return '#ffa500'; // Orange
        case 'yes':
          return '#4caf50'; // Green
        case 'no':
          return '#f44336'; // Red
        default:
          return '#666';
      }
    },

    confirmHardReload() {
      this.showHardReloadConfirm = false;
      // Set timestamp when hard reload is performed
      localStorage.setItem('lastHardReload', new Date().toISOString());
      this.fetchWorkOrders(true);
    },

    cancelHardReload() {
      this.showHardReloadConfirm = false;
    },

    setupConfirmationSocketListeners() {
      if (socket) {
        socket.on('confirmationAdded', (confirmation) => {
          if (this.confirmationsList) {
            this.confirmationsList.unshift(confirmation);
          }
          this.loadUnconfirmedItems(); // Refresh unconfirmed list
        });

        socket.on('confirmationUpdated', (data) => {
          const updatedConfirmation = data.confirmation || data;
          console.log(
            'Received confirmationUpdated event:',
            updatedConfirmation,
          );
          console.log('Current user:', this.getCurrentUserDisplayName());
          console.log(
            'Confirmation state:',
            updatedConfirmation.confirmationState,
          );
          console.log('Confirmed by:', updatedConfirmation.confirmedByName);

          if (this.confirmationsList) {
            const index = this.confirmationsList.findIndex(
              (c) => c.id === updatedConfirmation.id,
            );
            if (index !== -1) {
              // Create a new object to ensure Vue reactivity
              this.confirmationsList.splice(index, 1, {
                ...this.confirmationsList[index],
                ...updatedConfirmation,
              });
              console.log('Updated confirmation in list at index:', index);
            } else {
              console.log(
                'Confirmation not found in list, ID:',
                updatedConfirmation.id,
              );
            }
          } else {
            console.log(
              'confirmationsList is not available, reloading confirmations',
            );
            // If confirmationsList is not available, reload it
            this.loadConfirmations();
          }
          console.log(
            'About to call loadUnconfirmedItems() due to confirmationUpdated event',
          );
          this.loadUnconfirmedItems(); // Refresh unconfirmed list
        });

        socket.on('confirmationDeleted', (data) => {
          const confirmationId = data.confirmationId || data;
          console.log(
            'Received confirmationDeleted event for ID:',
            confirmationId,
          );
          if (this.confirmationsList) {
            this.confirmationsList = this.confirmationsList.filter(
              (c) => c.id !== confirmationId,
            );
          }
          // Also hide any selected confirmation actions
          if (this.selectedConfirmationId === confirmationId) {
            this.selectedConfirmationId = null;
          }
          this.loadUnconfirmedItems(); // Refresh unconfirmed list
        });
      }
    },
  },

  watch: {
    reviewCurrentWorkOrder: {
      immediate: true,
      handler(wo) {
        if (!this.reviewIdEditing) {
          this.reviewIdInput = wo?.id != null ? String(wo.id) : '';
        }
      },
    },
    isRecording(newVal) {
      this.appendLog('isRecording:' + (newVal ? 'true' : 'false'));
    },

    showWorkOrder(newVal) {
      if (newVal) {
        this.fetchWorkOrders();
      }
    },

    selectedTileIndex(newVal) {
      if (newVal !== null) {
        if (this.showIconBar !== 3) this.showIconBar = 1;
      } else {
        this.showIconBar = 1;
      }
    },

    showNewWorkOrder(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.$refs.workOrderName.focus();
        });
      }
    },

    filteredWorkOrders() {
      // Re-observe tiles when filtered work orders change
      this.$nextTick(() => {
        this.observeWorkOrderTiles();
      });
    },

    showReviewPopup(open) {
      if (open) {
        this.showBriefingHint = !briefingHintSeen(localStorage);
        this.loadReviewVoiceNote(this.reviewCurrentWorkOrder?.id);
        this.loadReviewPhotos(this.reviewCurrentWorkOrder?.id);
        this.prefetchReviewFirstPhotos();
        if (!this.isInitialLoading) {
          this.loadReviewComments(this.reviewCurrentWorkOrder?.id);
        }
      } else {
        this.showBriefingHint = false;
        this.teardownReviewVoice();
        this.clearReviewPhotoSets();
        this.reviewPhotoUrl = '';
        this.reviewPhotos = [];
        this.reviewPhotoIndex = 0;
        this.showReviewPhotoFullscreen = false;
        this.reviewPhotoLoading = false;
        this.showReviewThread = false;
        this.reviewComments = [];
        this.reviewCommentsForId = null;
        this.reviewCommentDraft = '';
        this.reviewSelectedCommentId = null;
        this.reviewEditingCommentId = null;
        this.reviewEditDraft = '';
      }
    },

    reviewOpenWorkOrders() {
      if (
        this.showReviewPopup &&
        this.reviewLandOnNewest &&
        !this.reviewUserNavigated &&
        this.reviewOpenWorkOrders.length
      ) {
        const idx = newestWorkOrderIndex(this.reviewOpenWorkOrders);
        if (this.reviewIndex !== idx) this.reviewIndex = idx;
        this.reviewLandOnNewest = false;
      }
      if (this.showReviewPopup) {
        this.prefetchReviewFirstPhotos();
        this.syncReviewDeepLink();
      }
    },

    'reviewCurrentWorkOrder.id'(id) {
      if (this.showReviewPopup && this.reviewUserNavigated && id != null) {
        this.rememberReviewWorkOrder(id);
      }
      if (
        this.speakingWorkOrderId &&
        this.speakingWorkOrderId !== id
      ) {
        this._speechToken = (this._speechToken || 0) + 1;
        window.speechSynthesis.cancel();
        this.speakingWorkOrderId = null;
      }
      if (this.showReviewPopup) {
        this.prepareReviewVoiceForWorkOrder(id);
        this.loadReviewPhotos(id);
        if (!this.isInitialLoading) {
          this.loadReviewComments(id);
        }
      }
    },
  },

  async mounted() {
    this._onOutboundSent = (event) => {
      const detail = event && event.detail;
      if (!detail || detail.kind !== 'voice-note') return;
      if (this.reviewVoiceError !== OUTBOUND_HOLD_TEXT) return;
      const current = Number(
        this.reviewCurrentWorkOrder && this.reviewCurrentWorkOrder.id,
      );
      if (!current || String(detail.attachedId) !== String(current)) return;
      this.reviewVoiceError = '';
      this.loadReviewVoiceNote(current);
    };
    window.addEventListener('pixie-outbound-sent', this._onOutboundSent);
    this.isInitialLoading = true;
    forgetOldPhotoDatabases();
    this.fetchPropertyOptions();
    try {
      await this.initDB();

      // Check if a hard reload is needed (once per day)
      const lastHardReload = localStorage.getItem('lastHardReload');
      const today = new Date().toDateString();
      const lastReloadDate = lastHardReload
        ? new Date(lastHardReload).toDateString()
        : null;

      if (!lastHardReload || lastReloadDate !== today) {
        console.log(
          'Daily hard reload needed - last reload:',
          lastReloadDate || 'never',
          'today:',
          today,
        );
        // Set timestamp before the reload
        localStorage.setItem('lastHardReload', new Date().toISOString());
        // Set flag to show daily refresh message
        this.isDailyRefresh = true;
        // Force a hard reload to ensure cache is fresh
        await this.fetchWorkOrders(true);
        this.isDailyRefresh = false;
      }

      // Set default priority sorting to ascending order (lowest priority first, 0 at top)
      this.prioritySortOrder = 'asc';

      // Call the service-check API to get service status (rate limited to prevent 500 errors)
      const now = Date.now();
      if (now - this.lastServiceCheckTime > 6000) {
        // Only call if more than 6 seconds since last call
        try {
          this.lastServiceCheckTime = now;
          const serviceCheckResponse = await axios.get('/api/service-check');
          if (serviceCheckResponse?.data?.message) {
            this.logMessage = serviceCheckResponse.data.message;
          }
        } catch (serviceCheckError) {
          console.warn(
            'Service check failed:',
            serviceCheckError.response?.status || serviceCheckError.message,
          );
          this.logMessage = 'Service status check failed';
        }
      } else {
        console.log('Skipping service check - too soon since last call');
        this.logMessage = 'Service status: cached';
      }

      // Log user activity for accessing Work Orders
      await this.logUserActivity();

      // Fetch current user info for role-based actions (like receipts delete)
      await this.fetchCurrentUserInfo();

      // Check if 'search' query parameter exists and assign it to 'workOrderFilter'.
      // A ?search=:id link still does this, and also opens that work order in Review.
      if (this.$route.query.search) {
        this.workOrderFilter = this.$route.query.search;
        this.hideDone = false;
      }

      // Perform cache cross-check validation
      await this.validateCachedData();

      // Log user activity when component is accessed
      await this.logUserActivity();
    } catch (error) {
      console.error('Error initializing database:', error);
      this.logMessage = 'Error initializing database: ' + error.message;
    }

    // Use cached data on mount for faster loading, only force refresh if needed
    // Bump WO_CACHE_VERSION when the API schema changes (e.g. new fields added)
    const WO_CACHE_VERSION = '6'; // v6: staff phone smsOptIn on unlinked work orders
    const cachedVersion = localStorage.getItem('woCacheVersion');
    const forceReload = cachedVersion !== WO_CACHE_VERSION;
    if (forceReload) {
      localStorage.setItem('woCacheVersion', WO_CACHE_VERSION);
    }
    this.fetchWorkOrders(forceReload);
    this.connectSocket(); // Connect to WebSocket server on mount
    socket.on('newWorkOrders', this.handleNewWorkOrders); // Register event handler

    // Setup confirmation socket listeners
    this.setupConfirmationSocketListeners();

    // Auto-show confirmations dialog if there are unconfirmed items.
    // A ?review=id handoff opens that work order instead, so recording can continue.
    await this.loadUnconfirmedItems();
    const reviewLinkId = workOrderIdFromReviewQuery(this.$route.query.review);
    if (reviewLinkId != null) {
      this._reviewVoiceOpenFromLink = true;
      this.reviewDeepLinkId = reviewLinkId;
      await this.openPinnedReviewDefault(reviewLinkId);
      this.syncReviewDeepLink();
    } else if (this.unconfirmedItems.length > 0) {
      console.log(
        'Auto-opening confirmations dialog with',
        this.unconfirmedItems.length,
        'unconfirmed items',
      );
      this.showConfirmationsDialog = true;
      await this.loadUsers();
      await this.loadConfirmations();
      this.activeTab = 'needed'; // Default to needed tab when auto-opening
    } else if (shouldOpenReviewForWorkOrderLink(this.$route.query.search)) {
      const focusId = workOrderIdFromSearchQuery(this.$route.query.search);
      this.reviewDeepLinkId = focusId;
      await this.openPinnedReviewDefault(focusId);
      this.syncReviewDeepLink();
    } else if (!this.$route.query.search && !this.$route.query.listView) {
      // Prefer pinned Review as the default landing view when set.
      // Confirmations (above) and list-view links still take priority.
      if (this.reviewDefaultPinned) {
        await this.openPinnedReviewDefault();
      } else {
        // If no unconfirmed items and no specific work order requested, show summary
        await this.openSummaryPopup();
      }
    }

    this.openListViewFromRoute();

    // Turn off loading state after everything is initialized
    this.isInitialLoading = false;
    if (this.showReviewPopup) {
      this.loadReviewComments(this.reviewCurrentWorkOrder?.id);
    }

    // Set up IntersectionObserver for view tracking
    this.setupViewTracking();

    // Add click listener for calendar links
    document.addEventListener('click', this.handleCalendarLinkClick);

    // Log user location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          axios.post('/api/log-location', {
            componentName: 'MWorkOrders',
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
        },
        (error) => {
          axios.post('/api/log-location', {
            componentName: 'MWorkOrders',
            locationError: error.message,
          });
        },
      );
    }
  },

  beforeUnmount() {
    if (this._onOutboundSent) {
      window.removeEventListener('pixie-outbound-sent', this._onOutboundSent);
      this._onOutboundSent = null;
    }
    this.teardownReviewVoice();
    this.clearReviewPhotoSets();
    this.clearMaintenanceLockCodeTimer();
    document.removeEventListener('keydown', this.handleReviewKeydown);
    socket?.off('newChatMessages', this.handleNewWorkOrders); // Register event handler
    socket?.off('workOrderComment', this.handleWorkOrderComment);
    if (this.intervalId) clearInterval(this.intervalId);

    // Remove calendar link click listener
    document.removeEventListener('click', this.handleCalendarLinkClick);

    // Clear age filter hint timeout
    if (this.ageFilterHintTimeout) {
      clearTimeout(this.ageFilterHintTimeout);
    }

    // Disconnect IntersectionObserver
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    // Release all blob URLs to prevent memory leaks
    this.workOrderPhotos.forEach((photo) => {
      if (photo.photoUrl && photo.photoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(photo.photoUrl);
      }
    });

    // Clear any notification timeouts
    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout);
    }
  },
};
</script>

<style scoped>
.initial-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.initial-loading-spinner {
  text-align: center;
  color: #a080a8;
}

.initial-loading-spinner i {
  font-size: 3em;
  margin-bottom: 20px;
}

.initial-loading-spinner .loading-text {
  font-size: 1.2em;
  font-weight: 500;
  margin-top: 15px;
}

.people-page {
  /* margin-left: 5px; */
  padding: 0px;
  width: 100%;
  max-width: 440px;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  background: #f0f0f0;
}

.subpanel-form {
  margin-top: 10px;
  /* padding: 15px; */
  border-radius: 5px;
  padding-top: 0;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  /* min-height: 340px; */
  /* overflow-x: hidden; */
}

.title-bar {
  /* margin: -8px; */
  background: #6e5875;
  color: #fff;
  font-size: 1.4em;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top-left-radius: 5px;
  /* border-top-right-radius: 5px; */
  margin-left: -6px;
  padding-left: 40px;
}

.title-buttons {
  display: flex;
  gap: 10px;
  margin-right: 15px;
}

.title-button {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid #e1dede;
  color: #eee;
  background: #907793;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-size: 0.7em;
}

.search-bar {
  margin-top: 5px;
  font-weight: bold;
  font-size: 0.9em;
  margin-bottom: 5px;
}

.search-bar input,
.subpanel-form input {
  height: 32px;
  border-radius: 4px;
  border-color: #ccc;
  border: 1px solid #ddd;
  padding-left: 8px;
  /* max-width: 38%; */
}

.search-bar button {
  height: 30px;
  border-radius: 3px;
  padding: 4px;
  border-color: #ccc;
  background-color: #ccc;
  margin-left: 1px;
}

.search-bar .label {
  margin-bottom: 13px;
}

.email-search-bar {
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  width: 91%;
  justify-content: flex-start;
}

.search-action-buttons {
  display: flex;
  align-items: center;
  gap: 3px;
  margin-bottom: 10px;
  width: 91%;
}

.search-action-buttons .clear-search {
  height: 36px;
  border: 1px solid #b1afaf;
  border-radius: 5px;
  width: 50px;
  background: #a080a8;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
}

.email-search-bar input {
  height: 32px;
  border-radius: 4px;
  width: 279px;
  border: 1px solid #ddd;
  padding-left: 8px;
  margin-right: 4px;
  font-size: 16px;
}

.email-search-bar .clear-search {
  height: 36px;
  border: 1px solid #b1afaf;
  border-radius: 5px;
  width: 30px;
  color: #aaa;
  margin-right: 3px;
  background: #a080a8;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
}

.email-search-bar .clear-search:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Review open WOs — wider, lighter mauve than standard clear-search */
.email-search-bar .review-toolbar-btn {
  width: 48px;
  background: #c4a8cc;
  border-color: #b595bd;
}

.email-search-bar .review-toolbar-btn:hover:not(:disabled) {
  background: #d2b8d8;
}

.email-search-bar .review-toolbar-btn:disabled {
  background: #c4a8cc;
}

/* Fix for FontAwesome icons in buttons */
.email-search-bar button i {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.clear-search {
  height: 37px;
  border: 1px solid #b1afaf;
  border-radius: 5px;
  width: 32px;
  color: #aaa;
}

.age-filter-hint {
  position: absolute;
  top: 100px;
  right: 35px;
  background: #493b4d;
  color: white;
  border: 1px solid #aaa;
  padding: 10px 10px;
  border-radius: 4px;
  font-size: 0.85em;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  animation: fadeIn 0.3s ease-in;
}

.age-filter-hint i {
  font-size: 0.9em;
  margin-left: 4px;
}

.age-filter-hint.fade-out {
  animation: fadeOut 0.5s ease-out forwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-5px);
  }
}

.sort-active {
  background-color: #7a5a7a !important; /* Darker shade when active */
}

.age-filter-status {
  height: 30px;
  display: flex;
  align-items: center;
  margin-bottom: 5px;
  font-size: 0.75em;
  color: #555;
}

.age-filter-status span {
  background: #ecdcf2;
  padding: 5px 12px;
  border-radius: 4px;
  border-left: 3px solid #a080a8;
  width: 84%;
}

.age-filter-status strong {
  color: #a080a8;
}

.maint-tile {
  position: relative;
  font-size: 0.8em;
  border: 2px solid #ccc;
  padding: 10px;
  margin-bottom: 10px;
  background-color: #f9f9f9;
  width: 85%;
  border-radius: 5px;
  padding-top: 0;
  line-height: 20px;
  box-shadow: 7px 8px 7px 2px rgba(0, 0, 0, 0.1);
}

.maint-tile .wo-id-label {
  position: absolute;
  bottom: 2px;
  left: 4px;
  font-size: 0.75em;
  color: #aaa;
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;
  z-index: 1;
}

.maint-tile:hover .wo-id-label {
  opacity: 1;
  background: white;
  padding: 1px 4px;
  border-radius: 3px;
}

.maint-tile.selected {
  border-color: #15e935;
  height: 50vh;
  display: flex;
  flex-direction: column;
}

.tile-content {
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  padding-bottom: 56px;
}

.tile-comment-block {
  margin-top: 12px;
  margin-right: 44px;
  margin-bottom: 8px;
}

.tile-comment-block .review-comment-start,
.tile-comment-block .review-comment-start-row,
.tile-comment-block .review-tenant-reply {
  margin-top: 0;
}

.tile-content::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.tile-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
  border-top: 1px solid #e0e0e0;
  background-color: #f9f9f9;
  position: relative;
}

.inner-wrapper {
  overflow-y: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  flex: 1;
  min-height: 0;
  padding-bottom: 20px; /* Add padding to prevent clipping of last tile */
}

.inner-wrapper.is-mobile {
  /* flex handles height automatically */
}

.inner-wrapper.no-scroll {
  overflow-y: hidden; /* Prevent scrolling when a tile is selected */
}

.inner-wrapper::-webkit-scrollbar {
  display: none; /* Chrome, Safari, and Opera */
}

.icons-bar {
  font-size: 1.2em;
  height: 38px;
  display: flex;
  justify-content: space-around;
  align-items: center;
  margin-top: 0px;
  background-color: #434343;
  padding: 5px;
  border-radius: 5px;
  width: 89%;
  color: #eee;
  border: 1px solid #fff;
  box-shadow: 7px 8px 7px 2px rgba(0, 0, 0, 0.1);
  display: flex;
  gap: 5px;
  margin-bottom: 10px;
}

.schedule-this-btn {
  background-color: #eee;
  color: #888;
  border: 2px solid #ecdcf2;
  border-radius: 4px;
  padding: 0 10px;
  height: 30px;
  font-size: 0.95em;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 4px;
}

.schedule-this-btn:active {
  background: #555;
}

.awaiting-tenant-btn {
  background-color: #fff8e1;
  color: #b8860b;
  border-color: #f0d060;
}

.scheduled-on-btn {
  background-color: #f0f7f0;
  color: #888;
  border-color: #c8dcc8;
}

.icon-item {
  text-align: center;
}

.icon-label {
  display: block;
  font-size: 9px; /* Size of the label text */
  color: #fff; /* Color of the label text */
  width: 40px;
}

.action-buttons-top {
  position: relative;
  right: 0px;
  top: -1px;
  width: 175px;
}

.action-buttons-top button {
  margin-left: 5px;
  background: #a080a8;
  color: white;
  width: 40px;
  height: 37px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

/* Fix for FontAwesome icons in buttons */
.title-bar button i,
.action-buttons-top button i {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

/* voice recording */
.voice-recorder-widget {
  font-size: 1.2em;
  height: 38px;
  display: flex;
  justify-content: space-around;
  align-items: center;
  margin-top: 10px;
  background-color: #434343;
  padding: 5px;
  border-radius: 5px;
  width: 89%;
  color: #fff;
  border: 1px solid #fff;
  box-shadow: 7px 8px 7px 2px rgba(0, 0, 0, 0.1);
  gap: 10px;
  margin-bottom: 10px;
}

.icon-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
}

.icon-item .fa {
  font-size: 1.5em; /* Adjust icon size */
  margin-bottom: 5px; /* Space between icon and label */
}

.icon-label {
  font-size: 9px;
  color: #fff;
  margin-top: 4px;
}

.icon-item.disabled .fa,
.icon-item.disabled .icon-label {
  opacity: 0.5; /* Visual indication of disable state */
  pointer-events: none; /* Disable interaction */
}

.log-message-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 999;
  width: 80%;
  max-width: 600px;
}

.log-message {
  padding: 8px;
  border-radius: 5px;
  font-size: 0.7em;
  min-height: 100px;
  max-height: 50vh;
  background: #333333ee;
  color: #18fb18;
  overflow-y: auto;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.5);
}

.log-message-refresh {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #18fb18;
  border-radius: 4px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 1.2em;
  transition: background 0.2s;
  z-index: 1;
}

.log-message-refresh:hover {
  background: rgba(255, 255, 255, 0.2);
}

.red-record {
  color: #ff6666 !important;
}

.white-recording {
  color: #fff !important;
}

.green-playing {
  color: green;
}

.completed {
  color: rgb(86, 244, 86);
}

.confirm-dialog {
  width: 250px;
  position: absolute;
  top: 40%;
  left: 57px;
  font-size: 1.2em;
  background-color: #604e63;
  border: 1px solid #ccc;
  padding: 20px;
  z-index: 5000;
  color: #fff;
  border-radius: 5px;
  box-shadow: 6px 10px 7px 4px rgba(0, 0, 0, 0.3);
  border: 1px solid #535353;
}

.confirm-dialog button {
  border-radius: 4px;
  padding: 6px;
  color: #000;
  background: #cca7d5;
  border: 1px solid #fff;
  width: 60px;
}

/* Apply margin-left only to buttons that are not the first child */
.confirm-dialog button:not(:first-child) {
  margin-left: 10px;
}

.confirm-dialog-content {
  text-align: center;
}

.new-work-order-form input {
  border: 1px solid #ddd;
  border-radius: #aaa;
  outline: none;
}

.new-work-order-form textarea {
  border: 1px solid #ddd;
  border-radius: #aaa;
  outline: none;
}

.new-work-order-form input:focus {
  background: #ddd;
}

.new-work-order-form textarea:focus {
  background: #ddd;
}

.new-work-order-title {
  margin: -20px;
  margin-bottom: 15px;
  color: #fff;
  background: #333;
  padding: 8px;
  font-size: 1.2em;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
}

.new-work-order-form {
  background: #fdfdfd;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 7px 8px 7px 2px rgba(0, 0, 0, 0.35);
  width: 312px;
  margin: auto;
  position: absolute;
  left: 25px;
  top: 55px;
  border: 1px solid #aaa;
  z-index: 10;
}

.new-work-order-form-over-review {
  z-index: 3300;
}

.wo-transfer-dialog {
  z-index: 3400;
}

.wo-transfer-chosen {
  font-size: 0.85em;
  color: #444;
  margin-bottom: 10px;
}

.wo-transfer-chosen span {
  display: block;
  color: #666;
}

.new-work-order-form label {
  font-size: 0.8em;
}

.form-group {
  margin-bottom: 10px;
}
.form-group label {
  display: block;
  font-weight: bold;
  margin-bottom: 5px;
}

.form-group input,
.form-group textarea {
  width: 95%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.form-group textarea {
  height: 180px;
}

.form-group.checkbox {
  display: flex;
  align-items: center;
}
.form-group.checkbox label {
  margin-left: 10px;
}

.form-group.checkbox-allow-entry {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
}
.form-group.checkbox-allow-entry label {
  margin: 0;
  white-space: nowrap;
  flex-shrink: 0;
}
.form-group.checkbox-allow-entry input[type='checkbox'] {
  margin-left: auto;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}

.form-actions {
  display: flex;
  gap: 10px;
}
.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
.btn-primary {
  background: #a080a8;
  color: white;
}
.btn-secondary {
  background: #a080a8;
  color: white;
}
.btn-success {
  background: #4caf50;
  color: white;
}

.hide-done {
  background: #a080a8;
}

.validation-error-message {
  color: red;
  font-size: 0.9em;
  margin-top: 10px;
  margin-bottom: 8px;
}

.input-error {
  border-color: red;
}

.content-wrapper {
  /* padding-left: 10px; */
  width: 100%;
  margin-left: 11px;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  margin: 4px;
  /* box-shadow: 7px 5px 13px 2px rgba(0, 0, 0, 0.25); */
  /* padding: 8px; */
  padding-left: 10px;
}

.notes-popup {
  position: absolute;
  z-index: 4500;
  left: 0;
  top: 0;
  width: 440px;
  max-width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.25);
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 0;
}

.notes-popup-content {
  background: #fdfdfd;
  max-width: 370px;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 7px 8px 7px 2px rgba(0, 0, 0, 0.35);
  width: 71%;
  border: 1px solid #aaa;
  margin-left: -5%;
}

.wo-merge-overlay {
  position: fixed;
  z-index: 4600;
  width: 100%;
  max-width: 440px;
}

.wo-merge-dialog {
  width: 88%;
  max-width: 400px;
  margin-left: 0;
}

.wo-merge-search {
  width: 100%;
  box-sizing: border-box;
  font-size: 16px;
  margin: 0 0 10px;
  padding: 8px;
}

.wo-merge-list {
  max-height: 50vh;
  overflow-y: auto;
  margin-bottom: 12px;
}

.wo-merge-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}

.wo-merge-check {
  margin-top: 4px;
  flex: 0 0 auto;
}

.wo-merge-copy {
  min-width: 0;
  flex: 1;
}

.wo-merge-id {
  display: block;
  font-weight: 600;
  color: #6e5875;
}

.wo-merge-summary {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
  max-height: 2.6em;
}

.wo-merge-empty,
.wo-merge-error {
  margin: 0 0 12px;
  color: #666;
}

.wo-merge-error {
  color: #a33;
}

.edit-wo-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 4700;
  flex-direction: column;
  justify-content: center;
}

.edit-wo-popup {
  width: 95% !important;
  max-width: none !important;
  flex: 0 0 85%;
  height: 85%;
  max-height: 85%;
  margin: 0 !important;
  padding: 0 !important;
  display: flex;
  flex-direction: column;
  min-height: 0;
  box-sizing: border-box;
}

.edit-wo-popup .notes-popup-title {
  margin: 0;
  border-radius: 8px 8px 0 0;
  padding: 12px 16px;
  background: #a080a8;
  flex-shrink: 0;
}

.edit-wo-scrollable {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 16px;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.edit-wo-popup .notes-textarea {
  flex: 1;
  width: 100%;
  height: auto;
  min-height: 140px;
  box-sizing: border-box;
}

.edit-wo-scrollable::-webkit-scrollbar {
  display: none;
}

.notes-popup-title {
  margin: -20px;
  margin-bottom: 15px;
  color: #fff;
  background: #333;
  padding: 8px;
  font-size: 1.2em;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
}

.notes-button-row {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
  width: 99%;
}

.voice-recorder-btn {
  background-color: #a080a8;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.voice-recorder-btn:hover {
  opacity: 0.9;
}

.voice-recorder-btn i {
  font-size: 18px;
}

.notes-textarea {
  width: 95%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  height: 140px;
  margin-bottom: 5px;
}

.notes-popup-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 0;
  flex-shrink: 0;
  padding: 12px 16px;
}

.photo-carousel-section {
  margin-top: 0px;
}

.photos-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.photos-header h4 {
  margin: 0;
}

.header-buttons {
  display: flex;
  gap: 10px;
}

.round-action-button {
  width: 28px;
  height: 28px;
  background-color: #a080a8;
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-size: 14px;
}

.round-action-button i {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.round-action-button:hover {
  opacity: 0.9;
}

.photo-carousel {
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 10px;
  background-color: #f9f9f9;
  height: 30vh;
}

.no-photos {
  text-align: center;
  color: #999;
}

.photo-carousel-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.carousel-nav {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.5em;
  color: #333;
}

.carousel-nav:disabled {
  color: #ccc;
  cursor: not-allowed;
}

.photo-carousel-item {
  text-align: center;
}

.carousel-photo {
  max-width: 100%;
  max-height: 200px;
  border-radius: 4px;
}

.photo-caption {
  margin-top: 10px;
  font-size: 0.9em;
  color: #666;
}

.delete-photo {
  background: none;
  border: none;
  cursor: pointer;
  color: #d9534f;
  font-size: 1.2em;
}

/* Full-size photo overlay styles */
.full-size-photo-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.full-size-photo-content {
  text-align: center;
}

.full-size-photo {
  max-width: 95%;
  max-height: 95%;
  object-fit: contain;
}

.photo-viewer-caption {
  margin-top: 10px;
  font-size: 1em;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.edit-caption-button {
  margin-left: 10px;
  background: none;
  border: none;
  cursor: pointer;
  color: #fff;
  font-size: 1em;
}

.edit-caption-button i {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

/* Edit Caption Dialog */
.dialog-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 430px;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.25);
  display: flex;
  justify-content: center;
  align-items: center;
  /* Above Edit Notes (.notes-popup is 4500) so Upload Photos / caption dialogs are reachable */
  z-index: 5000;
}

.dialog-content {
  background: #fdfdfd;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 7px 8px 7px 2px rgba(0, 0, 0, 0.35);
  width: 300px;
  text-align: center;
}

.summary-dialog-content {
  background: #fdfdfd;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 7px 8px 7px 2px rgba(0, 0, 0, 0.35);
  width: 270px;
  max-width: 90%;
  text-align: center;
  margin-left: -25px;
}

.summary-dialog-header {
  margin: -20px;
  margin-bottom: 15px;
  color: #fff;
  background: #a080a8;
  padding: 8px;
  font-size: 1.2em;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
}

.review-open-btn {
  margin-right: 6px;
}

.review-open-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Full-screen open work order review */
.review-fullscreen {
  position: absolute;
  inset: 0;
  z-index: 3200;
  background: #f5f3f6;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 440px;
  height: 100%;
  box-sizing: border-box;
}

.review-fullscreen-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.review-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: #6e5875;
  color: #fff;
  padding: 10px 12px;
}

.review-title {
  font-weight: 600;
  font-size: 1.05em;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.review-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.review-header-btn {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.55);
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  font-size: 0.95em;
}

.review-header-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.32);
}

.review-header-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* Pushed/pinned: darker so the thumbtack reads as “on” */
.review-header-btn.review-header-btn-pinned {
  background: rgba(0, 0, 0, 0.42);
  border-color: rgba(255, 255, 255, 0.9);
}

.review-header-btn.review-header-btn-pinned:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.55);
}

.review-controls {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 12px 10px;
  background: #ebe6ee;
  border-bottom: 1px solid #d4cad8;
}

.review-controls-top {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
}

.review-counter {
  margin-left: auto;
  font-size: 1.25em;
  font-weight: 700;
  color: #4a3a50;
  white-space: nowrap;
}

.review-control-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-sizing: border-box;
  height: 34px;
  border: 1px solid #a080a8;
  background: #fff;
  color: #4a3a50;
  border-radius: 6px;
  padding: 0 10px;
  font-size: 0.85em;
  font-weight: 600;
  cursor: pointer;
}

.review-control-btn:hover {
  background: #f3eef6;
}

.review-control-btn .fas {
  font-size: 1em;
}

.review-control-label {
  white-space: nowrap;
}

.review-location-slot {
  flex: 1;
  min-width: 0;
  max-width: 52%;
  position: relative;
  height: 34px;
  overflow: hidden;
}

.review-location-slot > .review-location-select,
.review-location-slot > .review-search {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  max-width: none;
  box-sizing: border-box;
}

.review-location-select {
  border: 1px solid #a080a8;
  background: #fff;
  color: #4a3a50;
  border-radius: 6px;
  padding: 6px 8px;
  font-size: 0.8em;
  font-weight: 600;
  cursor: pointer;
}

.review-search {
  display: flex;
  align-items: center;
  gap: 4px;
  border: 1px solid #a080a8;
  background: #fff;
  border-radius: 6px;
  padding: 0 4px 0 8px;
}

.review-search-input {
  flex: 1;
  min-width: 0;
  height: 100%;
  border: 0;
  background: transparent;
  color: #4a3a50;
  font-size: 16px;
  font-weight: 600;
  padding: 0;
  outline: none;
}

.review-search-clear {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  color: #6a5570;
  border-radius: 4px;
  cursor: pointer;
  padding: 0;
}

.review-search-clear:hover {
  background: #f3eef6;
}

.review-search-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid #a080a8;
  background: #fff;
  color: #4a3a50;
  border-radius: 6px;
  cursor: pointer;
  padding: 0;
}

.review-search-btn:hover,
.review-search-btn-active {
  background: #f3eef6;
  border-color: #6e5875;
}

.review-person-filter {
  flex: 1;
  min-width: 0;
  max-width: 52%;
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #a080a8;
  background: #fff;
  color: #4a3a50;
  border-radius: 6px;
  padding: 4px 6px 4px 8px;
  font-size: 0.8em;
  font-weight: 600;
}

.review-person-filter-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.review-person-filter-clear {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  color: #6a5570;
  border-radius: 4px;
  cursor: pointer;
  padding: 0;
}

.review-person-filter-clear:hover {
  background: #f3eef6;
}

.review-details :deep(.wo-person-name),
.review-details :deep(.wo-property-address) {
  color: #1a73e8;
  text-decoration: none;
  cursor: pointer;
  font-weight: 600;
}

.review-details :deep(.wo-scheduled-date) {
  color: #1a73e8;
  text-decoration: underline;
  cursor: pointer;
  font-weight: 600;
}

.review-details :deep(.wo-person-name:hover),
.review-details :deep(.wo-property-address:hover),
.review-details :deep(.wo-scheduled-date:hover) {
  color: #1557b0;
}

.review-details :deep(.person-flags) {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 4px;
  vertical-align: middle;
  white-space: nowrap;
}

.review-details :deep(.person-flag) {
  flex: 0 0 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  box-sizing: border-box;
  font-size: 8px;
  font-weight: 700;
  line-height: 1;
  padding: 0;
  border: 1px solid #8a8a8a;
  border-radius: 50%;
  background: transparent;
  color: #555;
}

.review-details :deep(.person-flag-on) {
  background: #d4edda;
  border-color: #28a745;
  color: #155724;
}

.review-slider-wrap {
  width: 100%;
  padding: 0 2px;
}

.review-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: #c4b6cc;
  outline: none;
  margin: 0;
  cursor: pointer;
}

.review-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #a080a8;
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
  cursor: grab;
}

.review-slider::-moz-range-thumb {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #a080a8;
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
  cursor: grab;
}

.review-slider::-moz-range-track {
  height: 6px;
  border-radius: 3px;
  background: #c4b6cc;
}

.review-slider-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
  font-size: 0.7em;
  color: #6a5a72;
  text-transform: lowercase;
  letter-spacing: 0.02em;
  padding: 0 2px;
}

.review-body {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  padding: 14px 16px 12px;
  font-size: 0.85em;
  line-height: 1.4;
  color: #333;
  -webkit-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
}

.review-card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.review-card-top-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.review-photo-btn {
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 2px solid #6e5875;
  border-radius: 50%;
  background: #fff;
  color: #6e5875;
  cursor: pointer;
  font-size: 1.05em;
  line-height: 1;
}

.review-notify-btn {
  height: 44px;
  min-width: 5.9rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 0 13px;
  border: 2px solid #6e5875;
  border-radius: 22px;
  background: #fff;
  color: #6e5875;
  cursor: pointer;
  line-height: 1;
}

.review-notify-bar {
  width: 1px;
  height: 18px;
  background: #6e5875;
}

.review-notice-popup {
  width: 100%;
  max-width: none;
  margin: 0 !important;
}

.review-notice-popup .notes-popup-title {
  background: #a080a8;
  color: #fff;
}

.review-notice-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.review-notice-when {
  margin: 0 0 12px;
  color: #4a3a52;
}

.review-notice-divider {
  border: none;
  border-top: 1px solid #d4c8db;
  margin: 14px 0;
}

.review-notice-time,
.review-notice-send {
  width: 100%;
  margin: 0;
  margin-left: 0;
  border: none;
  background: #a080a8;
  color: #fff;
  border-radius: 8px;
  padding: 14px 16px;
  font-weight: 600;
  cursor: pointer;
}

.review-notice-time:hover,
.review-notice-send:hover {
  opacity: 0.9;
}

.review-notice-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 0;
}

.review-notice-send:disabled,
.review-notice-time:disabled {
  opacity: 0.65;
  cursor: wait;
}

.review-notice-result {
  margin: 8px 0 0;
  min-height: 1.4em;
  line-height: 1.4;
  color: #4a3a52;
  font-size: 0.9em;
}

.review-start-btn {
  height: 44px;
  padding: 0 14px;
  border: 2px solid #a080a8;
  border-radius: 22px;
  background: #a080a8;
  color: #fff;
  font-size: 0.95em;
  font-weight: 600;
  cursor: pointer;
  line-height: 1;
}

.review-start-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.review-start-btn.review-start-btn-done,
.review-start-btn.review-start-btn-done:disabled {
  background: transparent;
  color: #6a5570;
  opacity: 1;
  cursor: default;
}

.review-started-line {
  font-size: 0.9em;
  color: #6a5570;
  margin: 0 0 10px;
}

.review-copy {
  display: flow-root;
}

.review-copy-tools {
  float: right;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0 0 8px 10px;
}

.review-tts-btn {
  width: 44px;
  height: 44px;
  border: 2px solid #6e5875;
  border-radius: 50%;
  background: #fff;
  color: #6e5875;
  font-size: 1.35em;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.review-tts-btn-active,
.review-tts-btn-active:hover {
  color: #fff;
  border-color: #6e5875;
  background: #6e5875;
}

.review-voice-slot {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 44px;
  flex: 0 0 44px;
}

.review-voice-waiting {
  display: block;
  width: 44px;
  margin-top: 6px;
  color: #6e5875;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.15;
  text-align: center;
  white-space: normal;
}

.review-voice-btn.review-voice-btn-has-note {
  animation: review-voice-border-pulse 3.2s ease-in-out infinite;
}

@keyframes review-voice-border-pulse {
  0%,
  100% {
    border-color: #6e5875;
    box-shadow: 0 0 0 0 rgba(110, 88, 117, 0.5);
  }
  50% {
    border-color: rgba(110, 88, 117, 0.28);
    box-shadow: 0 0 0 4px rgba(110, 88, 117, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .review-voice-btn.review-voice-btn-has-note {
    animation: none;
  }
}

.review-voice-square {
  width: 14px;
  height: 14px;
  background: currentColor;
  border-radius: 2px;
  display: block;
  flex-shrink: 0;
}

.review-voice-square-live {
  background: #c0392b;
}

.review-voice-overlay {
  position: absolute;
  inset: 0;
  z-index: 40;
  background: rgba(20, 16, 24, 0.55);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.review-voice-dialog {
  width: 100%;
  background: #fff;
  border-radius: 12px 12px 0 0;
  padding: 14px 14px 18px;
  box-sizing: border-box;
}

.review-voice-dialog-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  color: #6e5875;
  font-weight: 600;
}

.review-voice-close {
  width: 44px;
  height: 44px;
  border: none;
  background: transparent;
  color: #6e5875;
  font-size: 1.2em;
  cursor: pointer;
}

.review-voice-player {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}

.review-voice-spectrum {
  display: block;
  width: 100%;
  height: 56px;
  margin: 0 0 10px;
  border-radius: 6px;
  background: #f3eef6;
  border: 1px solid #e0d6e4;
  box-sizing: border-box;
}

.review-voice-spectrum-live {
  border-color: #6e5875;
}

.review-voice-actions {
  display: flex;
  gap: 8px;
}

.review-voice-action {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid #c8b7ce;
  background: #fff;
  color: #4a3a52;
  border-radius: 6px;
  padding: 8px 6px;
  min-height: 44px;
  font-size: 0.82em;
  cursor: pointer;
}

.review-voice-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.review-voice-scrub {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  min-height: 28px;
}

.review-voice-scrub-idle {
  visibility: hidden;
}

.review-voice-scrub-time {
  flex-shrink: 0;
  font-size: 0.72em;
  color: #6a5570;
  font-variant-numeric: tabular-nums;
  min-width: 2.2em;
}

.review-voice-scrub-bar {
  flex: 1;
  min-width: 0;
  height: 18px;
  margin: 0;
  accent-color: #a080a8;
  cursor: pointer;
}

.review-voice-error {
  margin-top: 8px;
  color: #8b3a3a;
  font-size: 0.85em;
}

.review-body::-webkit-scrollbar {
  display: none;
}

.review-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #888;
  font-style: italic;
}

.review-wo-id-col {
  flex: 0 0 auto;
  width: auto;
  max-width: none;
  min-width: 0;
}

.review-wo-back {
  flex-shrink: 0;
  width: 28px;
  height: 44px;
  margin-left: 2px;
  border: none;
  background: transparent;
  color: #6e5875;
  font-size: 1.05em;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.review-wo-back:disabled {
  opacity: 0.35;
  cursor: default;
}

.review-wo-id {
  display: flex;
  align-items: center;
  height: 44px;
  font-size: 1.7em;
  font-weight: 700;
  color: #6a5570;
  margin-bottom: 0;
  padding-top: 0;
  line-height: 1;
}

.review-wo-hash {
  flex-shrink: 0;
  margin-right: 4px;
}

.review-wo-id-input {
  box-sizing: border-box;
  width: 7ch;
  min-width: 0;
  max-width: 100%;
  height: 44px;
  margin: 0;
  padding: 0 1px;
  border: 1px solid #c8c8c8;
  border-radius: 6px;
  background: transparent;
  font: inherit;
  font-size: inherit;
  font-weight: 700;
  color: inherit;
  outline: none;
  line-height: 42px;
}

.review-wo-id-input:focus {
  border-color: #a080a8;
}

.review-wo-scheduled {
  font-size: 0.95em;
  font-weight: 500;
  color: #6a5570;
  margin-top: 2px;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.review-details {
  word-break: break-word;
}

.review-note-block {
  margin-top: 14px;
  padding: 10px;
  background: #fff;
  border: 1px solid #e0d6e4;
  border-radius: 6px;
}

.review-note-text {
  margin-top: 6px;
  white-space: pre-wrap;
  color: #444;
}

.review-photo-loading {
  margin-top: 14px;
  text-align: center;
  color: #8c7c91;
  font-size: 1.2em;
}

.review-photo-carousel {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
}

.review-photo-frame {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.review-photo-nav {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border: 1px solid #a080a8;
  border-radius: 50%;
  background: #f3eef6;
  color: #6a5570;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.review-first-photo {
  display: block;
  max-width: 100%;
  max-height: 280px;
  width: auto;
  height: auto;
  object-fit: contain;
  margin-top: 0;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #e0d6e4;
  cursor: pointer;
}

.review-photo-caption,
.review-photo-count {
  margin: 6px 0 0;
  text-align: center;
  color: #6a5570;
}

.review-photo-caption {
  font-size: 0.92em;
}

.review-photo-count {
  font-size: 0.8em;
}

.review-photo-fullscreen {
  position: fixed;
  inset: 0;
  z-index: 5000;
  background: rgba(0, 0, 0, 0.94);
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 48px 16px 16px;
}

.review-photo-fullscreen-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  touch-action: none;
  transform-origin: center center;
  user-select: none;
  -webkit-user-select: none;
}

.review-photo-fullscreen-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: #a080a8;
  color: #fff;
  font-size: 1.2em;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.review-photo-fullscreen-nav {
  position: absolute;
  top: 50%;
  width: 40px;
  height: 40px;
  transform: translateY(-50%);
  border: none;
  border-radius: 50%;
  background: #a080a8;
  color: #fff;
  font-size: 1.1em;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.review-photo-fullscreen-prev {
  left: 12px;
}

.review-photo-fullscreen-next {
  right: 12px;
}

.review-photo-fullscreen-frame {
  max-width: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  touch-action: none;
}

.review-photo-fullscreen-caption,
.review-photo-fullscreen-count {
  margin: 0;
  color: #fff;
  text-align: center;
}

.review-photo-fullscreen-caption {
  font-size: 0.95em;
}

.review-photo-fullscreen-count {
  font-size: 0.8em;
  opacity: 0.8;
}

.review-tenant-reply {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  margin-top: 14px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #c9b8d0;
  border-radius: 8px;
  cursor: pointer;
}

.review-tenant-reply-main {
  flex: 1;
  min-width: 0;
}

.review-tenant-reply-icon-wrap {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  line-height: 1;
}

.review-tenant-reply-icon {
  color: #a080a8;
  font-size: 2.1em;
  line-height: 1;
}

.review-comment-count {
  font-size: 0.85em;
  font-weight: 600;
  color: #6a5570;
  line-height: 1;
  margin-bottom: 6px;
}

.review-comment-start-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  margin-top: 14px;
  width: fit-content;
  line-height: 1;
}

.review-comment-start-row .review-comment-start {
  margin-top: 0;
}

.review-comment-start {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 14px;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: #a080a8;
  color: #fff;
  font-size: 1.15em;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(74, 58, 80, 0.28);
}

.review-comment-start:hover {
  background: #8e6e96;
}

.review-tenant-reply-label {
  font-size: 0.75em;
  font-weight: 700;
  color: #6a5570;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 4px;
}

.review-tenant-reply-text {
  font-size: 0.9em;
  line-height: 1.4;
  color: #333;
  white-space: pre-wrap;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.review-thread-overlay {
  position: absolute;
  inset: 0;
  z-index: 4600;
  width: auto;
  height: auto;
  max-width: none;
  margin: 0;
  box-sizing: border-box;
  padding: 16px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
}

.review-thread-popup {
  max-width: none;
  width: 100%;
  max-height: 100%;
  margin: 0 !important;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  box-sizing: border-box;
}

.review-thread-popup .notes-popup-title {
  flex-shrink: 0;
  margin-bottom: 0;
}

.review-thread-tenant-notice {
  flex-shrink: 0;
  margin: 0 -20px 12px;
  padding: 3px 8px;
  background: #c0392b;
  color: #fff;
  font-size: 12px;
  line-height: 1.3;
  text-align: center;
}

.review-thread-status {
  padding: 12px 0;
  color: #888;
  font-style: italic;
  font-size: 0.9em;
}

.review-thread-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 10px;
  padding: 4px 4px 20px;
}

.review-thread-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.review-thread-item {
  border-radius: 8px;
  padding: 8px 10px;
  cursor: pointer;
  border: 2px solid transparent;
  box-sizing: border-box;
}

.review-thread-item-selected {
  border-color: #a080a8;
  box-shadow: 0 0 0 1px #a080a8;
}

.review-thread-reply {
  margin-left: 22px;
  border-left: 3px solid #d4c6dc;
}

.review-thread-tenant {
  background: #f3eef6;
}

.review-thread-staff {
  background: #eef3ee;
}

.review-thread-meta {
  font-size: 0.75em;
  color: #6a5570;
  margin-bottom: 4px;
}

.review-thread-text {
  font-size: 0.9em;
  line-height: 1.4;
  white-space: pre-wrap;
  color: #333;
}

.review-thread-text :deep(a.review-comment-link),
.review-tenant-reply-text :deep(a.review-comment-link) {
  color: #1565c0;
  text-decoration: underline;
}

.review-thread-text :deep(.review-phone-chip),
.review-tenant-reply-text :deep(.review-phone-chip) {
  display: inline-block;
  text-decoration: none;
  background: #d6ebff;
  border-radius: 3px;
  padding: 0 4px;
  color: #1a4f9c;
  cursor: pointer;
  white-space: nowrap;
}

.review-thread-text :deep(.review-phone-chip:hover),
.review-tenant-reply-text :deep(.review-phone-chip:hover) {
  background: #c5e0fb;
}

.review-thread-edit {
  width: 100%;
  box-sizing: border-box;
  margin-top: 4px;
  padding: 8px;
  border: 1px solid #c9b8d0;
  border-radius: 6px;
  font: inherit;
  font-size: 0.9em;
  resize: vertical;
  min-height: 64px;
}

.review-thread-edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.review-thread-input {
  flex-shrink: 0;
  min-height: 72px;
}

.review-thread-popup .notes-popup-actions {
  flex-shrink: 0;
  padding-bottom: 4px;
}

.briefing-hint {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: calc(74px + env(safe-area-inset-bottom, 0px));
  z-index: 25;
  background: rgba(40, 32, 48, 0.82);
  overflow: visible;
}

.briefing-hint-reminder {
  position: absolute;
  left: 50%;
  top: 36%;
  transform: translate(-50%, -50%);
  width: min(440px, calc(100% - 28px));
  margin: 0;
  color: #fff;
  pointer-events: none;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.45);
}

.briefing-hint-reminder-title {
  margin: 0 0 10px;
  font-size: 1.2em;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
}

.briefing-hint-reminder ul {
  margin: 0;
  padding-left: 1.15em;
  font-size: 0.98em;
  font-weight: 600;
  line-height: 1.35;
}

.briefing-hint-reminder li + li {
  margin-top: 8px;
}

.briefing-hint-callout {
  position: absolute;
  left: 10px;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  color: #fff;
  pointer-events: none;
}

.briefing-hint-text {
  margin: 0 0 8px 0;
  max-width: 210px;
  color: #fff;
  font-size: 1.05em;
  font-weight: 700;
  line-height: 1.3;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.45);
}

.briefing-hint-dismiss {
  pointer-events: auto;
  margin: 0 0 3.2em 0;
  min-height: 44px;
  padding: 12px 14px;
  border: 1px solid rgba(255, 255, 255, 0.75);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  font-size: 0.85em;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
}

.briefing-hint-arrow {
  width: 40px;
  height: 72px;
  margin-left: 1px;
  overflow: visible;
  filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.35));
}

.review-footer {
  flex-shrink: 0;
  display: grid;
  grid-template-columns: 42px 1fr 1fr 1fr minmax(0, 1fr);
  gap: 6px;
  padding: 12px 10px calc(12px + env(safe-area-inset-bottom, 0px));
  background: #fff;
  border-top: 1px solid #ddd;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
}

.review-btn {
  border: 1px solid #a080a8;
  background: #f3eef6;
  color: #4a3a50;
  border-radius: 6px;
  padding: 10px 6px;
  font-size: 0.9em;
  font-weight: 600;
  cursor: pointer;
}

.review-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.review-btn-bar {
  background: #a080a8;
  border-color: #8d6e98;
  color: #fff;
}

.review-btn-bar:hover:not(:disabled) {
  background: #8d6e98;
}

.review-briefing-btn {
  padding: 0;
  font-size: 1em;
}

.daily-briefing {
  position: absolute;
  inset: 0;
  z-index: 30;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #f5f3f6;
  color: #222;
}

.daily-briefing-title {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: #6e5875;
  color: #fff;
  padding: 10px 12px;
  font-weight: 600;
  font-size: 1.05em;
}

.daily-briefing-title-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.daily-briefing-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 14px 16px 8px;
  user-select: text;
  -webkit-user-select: text;
}

.daily-briefing-scroll::-webkit-scrollbar {
  display: none;
}

.daily-briefing-note,
.daily-briefing-note * {
  user-select: text;
  -webkit-user-select: text;
}

/* Match Mobile notes: card text is 0.8em; opened-note headings, tables, and chips keep those sizes. */
.daily-briefing-note {
  font-size: 0.8em;
  line-height: 1.6;
  color: #333;
}

.daily-briefing-note :deep(h1),
.daily-briefing-note :deep(h2),
.daily-briefing-note :deep(h3),
.daily-briefing-note :deep(h4),
.daily-briefing-note :deep(h5),
.daily-briefing-note :deep(h6) {
  color: #444;
  margin-top: 1.5em;
  margin-bottom: 0.5em;
}

.daily-briefing-note :deep(h1) {
  font-size: 1.8em;
  border-bottom: 2px solid #eee;
  padding-bottom: 0.3em;
}

.daily-briefing-note :deep(h2) {
  font-size: 1.5em;
  border-bottom: 1px solid #eee;
  padding-bottom: 0.3em;
}

.daily-briefing-note :deep(p) {
  margin-bottom: 1em;
}

.daily-briefing-note :deep(ul),
.daily-briefing-note :deep(ol) {
  margin-bottom: 1em;
  padding-left: 2em;
}

.daily-briefing-note :deep(li) {
  margin-bottom: 0.5em;
}

.daily-briefing-note :deep(blockquote) {
  border-left: 4px solid #ddd;
  margin: 1em 0;
  padding: 0.5em 1em;
  background: #f9f9f9;
  color: #666;
}

.daily-briefing-note :deep(code) {
  background: #f4f4f4;
  padding: 2px 4px;
  border-radius: 3px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.9em;
}

.daily-briefing-note :deep(pre) {
  background: #f4f4f4;
  padding: 1em;
  border-radius: 6px;
  overflow-x: auto;
  margin: 1em 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
}

.daily-briefing-note :deep(pre code) {
  background: transparent;
  padding: 0;
}

.daily-briefing-note :deep(table) {
  border-collapse: collapse;
  width: 97%;
  margin: 1em 0;
  table-layout: fixed;
  font-size: 0.75em;
}

.daily-briefing-note :deep(th),
.daily-briefing-note :deep(td) {
  border: 1px solid #e8e8e8;
  padding: 1px;
  text-align: left;
  word-wrap: break-word;
  min-width: 80px;
}

.daily-briefing-note :deep(th) {
  background: #f5f5f5;
  font-weight: 600;
}

.daily-briefing-note :deep(th[style*='text-align: center']),
.daily-briefing-note :deep(td[style*='text-align: center']),
.daily-briefing-note :deep(th[align='center']),
.daily-briefing-note :deep(td[align='center']) {
  text-align: center !important;
}

.daily-briefing-note :deep(.done-checkmark) {
  padding-right: 4px;
  font-size: 1em;
}

.daily-briefing-note :deep(a[href^='#calendar-']),
.daily-briefing-note :deep(a.note-wo-chip) {
  color: #007bff;
  text-decoration: none;
  border: none;
  display: inline-block;
  padding: 2px 8px;
  margin: 0 1px;
  border-radius: 3px;
  transition: all 0.2s ease;
  background: rgba(0, 123, 255, 0.08);
  font-size: 1.2em;
  line-height: 1.3;
  vertical-align: baseline;
}

.daily-briefing-note :deep(a[href^='#calendar-']:hover),
.daily-briefing-note :deep(a.note-wo-chip:hover) {
  background: rgba(0, 123, 255, 0.15);
  border-bottom-color: rgba(0, 123, 255, 0.6);
  transform: translateY(-1px);
}

.daily-briefing-note :deep(a[href^='#calendar-']:active),
.daily-briefing-note :deep(a.note-wo-chip:active) {
  transform: translateY(0);
  background: rgba(0, 123, 255, 0.2);
}

.daily-briefing-note :deep(a[href^='tel:']) {
  color: #28a745;
  text-decoration: none;
  border-radius: 3px;
  transition: all 0.2s ease;
  background: rgba(40, 167, 69, 0.08);
  font-size: 0.9em;
  padding: 1px 3px;
}

.daily-briefing-note :deep(a[href^='tel:']:hover) {
  background: rgba(40, 167, 69, 0.15);
  text-decoration: underline;
  transform: translateY(-1px);
}

.daily-briefing-note :deep(a[href^='tel:']:active) {
  transform: translateY(0);
  background: rgba(40, 167, 69, 0.2);
}

.daily-briefing-note :deep(h1:first-child),
.daily-briefing-note :deep(h2:first-child),
.daily-briefing-note :deep(h3:first-child),
.daily-briefing-note :deep(p:first-child),
.daily-briefing-note :deep(ul:first-child),
.daily-briefing-note :deep(ol:first-child) {
  margin-top: 0;
}

.daily-briefing-status {
  color: #888;
  font-style: italic;
}

.daily-briefing-editor {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 100%;
  box-sizing: border-box;
  border: 1px solid #d9d0df;
  border-radius: 6px;
  background: #fff;
  color: #222;
  font: inherit;
  line-height: 1.45;
  padding: 10px;
  resize: none;
  overflow: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  user-select: text;
  -webkit-user-select: text;
}

.daily-briefing-editor::-webkit-scrollbar {
  display: none;
}

.daily-briefing-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 12px calc(10px + env(safe-area-inset-bottom, 0px));
  background: #fff;
  border-top: 1px solid #ddd;
}

.daily-briefing-meta {
  min-width: 0;
  color: #b0b0b0;
  font-size: 0.75em;
  line-height: 1.3;
}

.daily-briefing-edit {
  flex-shrink: 0;
  height: 26px;
  padding: 0 12px;
  border: 1px solid #c4b4cc;
  border-radius: 4px;
  background: #f3eef6;
  color: #4a3a50;
  font-size: 0.8em;
  font-weight: 600;
  cursor: pointer;
}

.daily-briefing-edit:disabled {
  opacity: 0.5;
  cursor: default;
}

.review-assigned-name {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  border: 1px solid #a080a8;
  background: #f3eef6;
  border-radius: 6px;
  padding: 10px 6px;
  font-size: 0.9em;
  font-weight: 600;
  color: #4a3a50;
  text-align: center;
  overflow: hidden;
  white-space: normal;
  line-height: 1.15;
  box-sizing: border-box;
}

.review-assigned-name-label {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  white-space: normal;
  overflow-wrap: break-word;
  line-height: 1.15;
  text-align: center;
}

.review-assigned-name-clickable {
  color: #1a73e8;
  cursor: pointer;
}

.review-assigned-name-clickable:hover {
  color: #1557b0;
}

.review-assigned-name .review-assigned-filter-icon,
.review-assigned-name-clickable:hover .review-assigned-filter-icon {
  color: #b0b0b0;
  font-size: 1.05em;
}

.review-assign-popup {
  max-height: 80%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.review-assign-popup .notes-popup-title {
  background: #a080a8;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.review-assign-close {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: #fff;
  cursor: pointer;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1em;
  padding: 0;
}

.review-assign-close:hover {
  background: rgba(255, 255, 255, 0.22);
}

.review-assign-list {
  overflow-y: auto;
  max-height: 60vh;
  margin: 0 -8px;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.review-assign-list::-webkit-scrollbar {
  display: none;
}

.review-assign-user {
  display: block;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  padding: 12px 14px;
  border-radius: 6px;
  font-size: 1em;
  font-weight: 600;
  color: #4a3a50;
  cursor: pointer;
}

.review-assign-user:hover:not(:disabled),
.review-assign-user-selected {
  background: #ebe6ee;
}

.review-assign-user-selected {
  color: #a080a8;
}

.review-assign-empty {
  padding: 12px 8px;
  color: #888;
  font-style: italic;
}

.summary-table {
  width: 100%;
  border-collapse: collapse;
  margin: 10px 0;
}

.summary-row {
  border-bottom: 1px solid #e0e0e0;
}

.summary-row.clickable {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.summary-row.clickable:hover {
  background-color: #f5f5f5;
}

.summary-row.active {
  background-color: #e8d8f0;
}

.summary-label {
  text-align: left;
  padding: 10px 5px;
  font-weight: 500;
  color: #555;
}

.summary-value {
  text-align: right;
  padding: 10px 5px;
  font-weight: bold;
  color: #a080a8;
  font-size: 1.1em;
}

.loading-summary {
  padding: 20px;
  color: #666;
  font-size: 1.1em;
}

.loading-summary i {
  margin-right: 10px;
}

.dialog-header {
  font-size: 1.2em;
  margin-bottom: 15px;
  color: #333;
}

.dialog-body {
  margin-bottom: 15px;
}

.caption-input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.dialog-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.dialog-actions button {
  background: #a080a8;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  cursor: pointer;
  font-size: 1em;
}

.dialog-actions button:hover {
  opacity: 0.9;
}

.confirm-button {
  background: #a080a8;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  cursor: pointer;
}

.confirm-button:hover {
  opacity: 0.9;
}

/* Notification popup styles */
.notification-popup {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 15px 25px;
  border-radius: 8px;
  z-index: 4800;
  font-size: 1.2em;
  max-width: 80%;
  text-align: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  opacity: 1;
  transition: opacity 1.5s ease;
}

.notification-popup.fadeOut {
  opacity: 0;
}

/* Option button styles */
.option-button {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #a080a8;
  color: white;
  padding: 10px;
  border-radius: 4px;
  cursor: pointer;
  text-align: center;
  justify-content: center;
  transition: background-color 0.2s ease;
}

.option-button:hover {
  background: #8a6b93;
}

.option-button i {
  font-size: 1.2em;
}

.upload-instructions {
  margin-bottom: 10px;
  font-size: 0.9em;
  color: #666;
}

.upload-options {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.upload-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
}

.hidden-input {
  display: none;
}

.preview-container {
  margin-top: 10px;
  text-align: center;
  max-height: 300px;
  overflow-y: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.preview-container::-webkit-scrollbar {
  display: none; /* Chrome, Safari, and Opera */
}

.photo-preview {
  max-width: 100%;
  max-height: 200px;
  border-radius: 4px;
}

.photo-preview-heic {
  width: 120px;
  height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eee;
  color: #6e5875;
  font-size: 2.2em;
}

.photo-preview-item {
  margin-bottom: 10px;
  position: relative;
}

.remove-photo-btn {
  position: absolute;
  top: 5px;
  right: 5px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.remove-photo-btn i {
  font-size: 0.8em;
}

.remove-photo-btn:hover {
  background: rgba(0, 0, 0, 0.8);
}

.save-to-photos-btn {
  display: block;
  margin: 6px auto 0;
  border: 2px solid #6e5875;
  background: #fff;
  color: #6e5875;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 0.85em;
  cursor: pointer;
}

.add-icon {
  color: #a080a8;
}

/* Updated CSS classes for the icon buttons */
.maint-tile-icons {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 6px 8px;
  /* Keep schedule button clear of the tile edge (extra icon widened this row) */
  padding: 6px 2px 4px 4px;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
}

.schedule-status-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 1 auto;
  min-width: 0;
  margin-right: 4px;
  max-width: 100%;
}

.maint-tile-icons .schedule-this-btn,
.maint-tile-icons > .schedule-this-btn {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  margin: 0;
  /* Slightly tighter so it fits beside the icon strip on narrow tiles */
  padding: 0 8px;
  box-sizing: border-box;
}

.tentative-note {
  margin-top: 2px;
  margin-left: 2px;
  font-size: 0.72em;
  color: #888;
  line-height: 1.1;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.tile-icon-container {
  display: flex;
  flex-wrap: nowrap;
  gap: 5px; /* Exactly 5px gap between icons as requested */
  flex: 0 0 auto;
  margin-left: auto;
  /* Nudge icons slightly in from the right edge for balance */
  padding-right: 1px;
}

.tile-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px; /* Exactly 30px width as requested */
  height: 30px; /* Exactly 30px height as requested */
  border-radius: 4px;
  background-color: #eee;
  color: #888;
  cursor: pointer;
  font-size: 14px;
  border: 1px solid #ddd;
  transition: background-color 0.2s ease;
}

.tile-icon:hover {
  background-color: #8a6b93;
}

.tile-icon.add-icon {
  background-color: #eee;
  color: #888;
}

.tile-icon.refresh-icon {
  background-color: #eee;
  color: #888;
  font-size: 0.7rem;
  border: 1px solid #ddd;
}

.tile-icon.refresh-icon:hover {
  background-color: #f0f0f0;
  color: #888;
}

.tile-icon.tts-icon {
  background-color: #eee;
  color: #888;
}

.tile-icon.tts-icon.tts-active {
  background-color: #4a90d9;
  color: #fff;
}

.tile-icon.tenant-email-icon:hover {
  background-color: #8a6b93;
  color: #fff;
}

.tile-icon.tenant-email-icon-disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.tile-icon.lock-code-icon:hover {
  background-color: #8a6b93;
  color: #fff;
}

.tile-icon.lock-code-icon-disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.wo-tenant-email-dialog {
  background: #fdfdfd;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  width: min(360px, 92vw);
  overflow: hidden;
  text-align: left;
}

.wo-tenant-email-body {
  padding: 14px 16px 8px;
}

.wo-tenant-email-label {
  display: block;
  font-size: 0.85em;
  color: #555;
  margin-bottom: 6px;
  font-weight: 600;
}

.wo-tenant-email-textarea {
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
  min-height: 110px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 1em;
  font-family: inherit;
  line-height: 1.35;
}

.wo-tenant-email-textarea:focus {
  outline: none;
  border-color: #a080a8;
  box-shadow: 0 0 0 2px rgba(160, 128, 168, 0.25);
}

.wo-tenant-email-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 16px 16px;
}

.wo-tenant-email-actions button {
  min-width: 90px;
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.95em;
}

.wo-tenant-email-actions button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.wo-tenant-email-cancel {
  background: #e8e8e8;
  color: #444;
}

.wo-tenant-email-cancel:hover:not(:disabled) {
  background: #ddd;
}

.wo-tenant-email-send {
  background: #a080a8;
  color: #fff;
}

.wo-tenant-email-send:hover:not(:disabled) {
  background: #8a6b93;
}

.wo-lock-code-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 5000;
}

.wo-lock-code-dialog {
  background: #fdfdfd;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  width: min(320px, 90vw);
  overflow: hidden;
  text-align: center;
}

.wo-lock-code-header {
  background: #a080a8;
  color: #fff;
  padding: 10px 16px;
  font-size: 1.05em;
  font-weight: 600;
}

.wo-lock-code-body {
  padding: 18px 16px 8px;
}

.wo-lock-code-address {
  font-size: 0.9em;
  color: #555;
  margin-bottom: 10px;
}

.wo-lock-code-value {
  font-size: 2em;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #333;
  word-break: break-word;
}

.wo-lock-code-status {
  color: #666;
  font-size: 0.95em;
  padding: 8px 0;
}

.wo-lock-code-error {
  color: #c0392b;
}

.wo-lock-code-actions {
  padding: 12px 16px 16px;
}

.wo-lock-code-actions button {
  min-width: 90px;
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  background: #a080a8;
  color: #fff;
  cursor: pointer;
  font-size: 0.95em;
}

.wo-lock-code-actions button:hover {
  background: #8a6b93;
}

.wo-lock-code-countdown {
  margin-top: 8px;
  font-size: 0.75em;
  color: #888;
}

/* Priority display styles */
.priority-container {
  position: absolute;
  right: 8px;
  bottom: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 10;
}

.priority-display {
  background-color: #a080a8;
  color: #fff;
  font-size: 1.3em;
  font-weight: bold;
  border-radius: 50%;
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.priority-label {
  font-size: 0.8em;
  color: #a080a8;
  font-weight: bold;
  line-height: 1;
  margin-bottom: 2px;
  text-align: center;
}

.close-tile-button {
  background-color: #a080a8;
  color: #fff;
  font-size: 0.9em;
  font-weight: bold;
  padding: 4px 12px;
  border-radius: 12px;
  cursor: pointer;
  user-select: none;
  width: 74px;
  text-align: center;
}

.close-tile-button:hover {
  background-color: #8a6a8f;
}

/* Loading spinner styles */
.loading-photos {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.spinner {
  font-size: 2em;
  color: #a080a8;
  margin-bottom: 10px;
}

.loading-text {
  color: #666;
  font-size: 0.9em;
}

/* Autocomplete styles */
.autocomplete-wrapper {
  position: relative;
}

.autocomplete-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ccc;
  border-top: none;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
}

.autocomplete-item {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
}

.autocomplete-item:last-child {
  border-bottom: none;
}

.autocomplete-item:hover,
.autocomplete-item.highlighted {
  background-color: #f5f5f5;
}

.suggestion-name {
  font-weight: bold;
  color: #333;
  margin-bottom: 2px;
}

.suggestion-details {
  font-size: 0.85em;
  color: #666;
}

.suggestion-phone {
  margin-right: 8px;
}

.suggestion-address {
  font-style: italic;
}

.share-text-content {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 12px;
  font-family: monospace;
  white-space: pre-wrap;
  word-break: break-all;
  user-select: all;
  -webkit-user-select: all;
  -moz-user-select: all;
  -ms-user-select: all;
  min-height: 60px;
  max-height: 200px;
  overflow-y: auto;
  line-height: 1.4;
}

.share-text-content:hover {
  background-color: #e9ecef;
}

/* Pet and Special Instructions Section */
.pet-special-section {
  border-top: 1px solid #ddd;
  padding-top: 15px;
  margin-top: 15px;
}

.form-input {
  width: 95%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
}

.form-textarea {
  width: 95%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  height: 80px;
  resize: vertical;
}

/* Label with action button (e.g., Problem Description + photo button) */
.label-with-action {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

/* Label with Me button */
.label-with-me {
  display: flex;
  align-items: center;
  gap: 8px;
}

.label-with-me label {
  margin-bottom: 0;
}

.me-button {
  background: none;
  border: 1px solid #a080a8;
  border-radius: 4px;
  color: #a080a8;
  padding: 0 8px;
  font-size: 0.75em;
  cursor: pointer;
  line-height: 1.2;
  margin-bottom: 4px;
}

.me-button:active {
  background: #a080a8;
  color: #fff;
}

.label-with-action label {
  margin-bottom: 0;
}

.photo-attach-btn {
  background: none;
  border: 1px solid #a080a8;
  border-radius: 4px;
  color: #a080a8;
  padding: 2px 8px;
  font-size: 0.85em;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
}

.photo-attach-btn:hover:not(:disabled) {
  background: #a080a8;
  color: #fff;
}

.photo-attach-btn:disabled {
  opacity: 0.65;
  cursor: default;
}

/* Property address select styling */
.form-group select {
  width: 100%;
  padding: 8px 4px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  background: #fff;
  height: 36px;
}

/* Confirmation Dialog Styles */
.confirmation-dialog {
  position: relative;
  width: 95%;
  max-width: 400px;
  margin: 0px 0;
  z-index: 10;
}

.confirmation-dialog-content {
  background-color: white;
  border-radius: 8px;
  border: 1px solid #ddd;
  width: 100%;
  max-height: 664px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.confirmation-dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
  background-color: #f8f9fa;
}

.confirmation-dialog-title {
  margin: 0;
  font-size: 1.5em;
  color: #333;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button:hover {
  background-color: #e9ecef;
}

.confirmation-tabs {
  display: flex;
  border-bottom: 1px solid #eee;
}

.confirmation-tab {
  flex: 1;
  padding: 15px;
  text-align: center;
  cursor: pointer;
  border: none;
  background-color: #f8f9fa;
  font-size: 14px;
  font-weight: 500;
  margin-left: 15px;
}

.confirmation-tab.active {
  background-color: white;
  border-bottom: 2px solid #007bff;
  color: #007bff;
  margin-right: 15px;
}

.confirmation-tab:hover:not(.active) {
  background-color: #e9ecef;
}

.confirmation-dialog-body {
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

.confirmation-dialog-body::-webkit-scrollbar {
  display: none; /* WebKit */
}

.tab-content {
  padding: 20px;
}

.confirmation-card {
  border: 1px solid #ddd;
  border-radius: 8px;
  margin-bottom: 16px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.confirmation-card-header {
  padding: 15px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.confirmation-text {
  font-weight: 500;
  margin: 0;
  flex: 1;
}

.confirmation-status {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  margin-left: 10px;
}

.confirmation-status.pending {
  background-color: #fff3cd;
  color: #856404;
}

.confirmation-status.yes {
  background-color: #d4edda;
  color: #155724;
}

.confirmation-status.no {
  background-color: #f8d7da;
  color: #721c24;
}

.confirmation-card-body {
  padding: 15px;
}

.confirmation-info {
  margin-bottom: 10px;
  font-size: 14px;
  color: #666;
}

.confirmation-info strong {
  color: #333;
}

.confirmation-actions {
  display: flex !important;
  gap: 10px;
  margin-top: 15px;
  flex-direction: row !important; /* Ensure horizontal layout */
}

.confirmation-actions button {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
  flex: 0 0 auto !important; /* Don't grow or shrink, use natural size */
  white-space: nowrap; /* Prevent text wrapping */
  width: auto !important; /* Override any global width settings */
  display: inline-block !important; /* Ensure inline-block display */
}

.response-section {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #eee;
}

.response-textarea {
  width: 93%;
  min-height: 80px;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  resize: vertical;
  font-family: inherit;
  margin-bottom: 8px;
}

.submit-response-button {
  margin-top: 10px;
  padding: 8px 16px;
  background-color: #007bff;
  color: white;
  border: 1px solid #007bff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.submit-response-button:hover {
  background-color: #0056b3;
}

.create-confirmation-section {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  background-color: #f8f9fa;
}

.create-confirmation-section h4 {
  margin-top: 0;
  margin-bottom: 15px;
  color: #333;
}

.confirmation-form-actions {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

.create-confirmation-textarea {
  width: 100%;
  min-height: 100px;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  resize: vertical;
  font-family: inherit;
  margin-bottom: 10px;
}

.create-confirmation-button {
  padding: 10px 20px;
  background-color: #28a745;
  color: white;
  border: 1px solid #28a745;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.create-confirmation-button:hover {
  background-color: #218838;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #666;
  font-size: 16px;
}

.confirmation-button {
  background: none;
  border: none;
  color: #ff6b35;
  font-size: 18px;
  cursor: pointer;
  padding: 5px;
  border-radius: 3px;
  margin-left: 5px;
}

.confirmation-button:hover {
  background-color: rgba(255, 107, 53, 0.1);
}

/* Responsive design */
@media (max-width: 768px) {
  .confirmation-dialog {
    max-width: 100%;
    margin: 10px 0;
  }

  .confirmation-dialog-content {
    width: 100%;
    margin: 0;
  }

  .confirmation-dialog-header {
    padding: 15px;
  }

  .confirmation-dialog-title {
    font-size: 1.2em;
  }

  .tab-content {
    padding: 15px;
  }

  .confirmation-actions {
    flex-direction: row !important; /* Keep buttons horizontal on mobile */
    flex-wrap: wrap; /* Allow wrapping if needed */
    justify-content: flex-start; /* Align buttons to the left */
  }

  .confirmation-actions button {
    flex: 0 0 auto !important; /* Don't grow, use natural size */
    width: auto !important; /* Override any width settings */
    margin-bottom: 0 !important; /* Remove vertical margin */
    display: inline-block !important; /* Ensure proper display */
  }
}

/* Calendar date link styling */
:deep(a[href^='#calendar-']) {
  color: #007bff;
  text-decoration: none;
  /* font-weight: 500; */
  padding: 3px 3px;
  border-radius: 3px;
  transition: all 0.2s ease;
  background: rgba(0, 123, 255, 0.08);
  border-left: 2px solid rgba(0, 123, 255, 0.3);
  font-size: 0.9em;
  border-right: 2px solid rgba(0, 123, 255, 0.3);
}

:deep(a[href^='#calendar-']:hover) {
  background: rgba(0, 123, 255, 0.15);
  border-left-color: rgba(0, 123, 255, 0.6);
  border-right-color: rgba(0, 123, 255, 0.6);
  transform: translateY(-1px);
}

:deep(a[href^='#calendar-']:active) {
  transform: translateY(0);
  background: rgba(0, 123, 255, 0.2);
}

/* Hard Reload Confirmation Dialog */
.dialog-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgb(120 120 120 / 60%);
  display: flex;
  justify-content: center;
  align-items: center;
  /* Keep in sync with earlier .dialog-overlay — must beat .notes-popup (4500) */
  z-index: 5000;
}

.dialog-content {
  background: #fdfdfd;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 7px 8px 7px 2px rgba(0, 0, 0, 0.35);
  width: 300px;
  text-align: center;
}

.dialog-header {
  font-size: 1.2em;
  margin-bottom: 15px;
  color: #333;
}

.dialog-body {
  margin-bottom: 15px;
}

.dialog-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.dialog-actions button {
  background: #a080a8;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  cursor: pointer;
}

.dialog-actions button:hover {
  opacity: 0.9;
}

/* Delete Confirmation Popup */
.delete-confirm-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
}

.delete-confirm-dialog {
  background: white;
  border-radius: 10px;
  padding: 20px 24px;
  max-width: 300px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.delete-confirm-message {
  font-size: 15px;
  color: #333;
  margin-bottom: 20px;
  line-height: 1.4;
}

.delete-confirm-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.delete-confirm-actions .btn {
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 14px;
  min-width: 80px;
}

.delete-confirm-actions .btn-danger {
  background: #dc3545;
  border: none;
  color: white;
}

.delete-confirm-actions .btn-secondary {
  background: #6c757d;
  border: none;
  color: white;
}

/* Receipts Popup Styles */
.receipts-popup {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 50px;
  z-index: 1000;
}

.receipts-popup-content {
  background: #fdfdfd;
  border-radius: 8px;
  box-shadow: 7px 8px 7px 2px rgba(0, 0, 0, 0.35);
  width: 340px;
  height: 80vh;
  display: flex;
  flex-direction: column;
  border: 1px solid #aaa;
}

.receipts-popup-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  background: #a080a8;
  color: white;
  font-weight: bold;
  font-size: 1.1em;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.receipts-title-actions {
  display: flex;
  gap: 8px;
}

.add-receipt-btn,
.close-receipts-btn {
  background: transparent;
  border: 1px solid white;
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.add-receipt-btn:hover,
.close-receipts-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.receipts-filter-row {
  display: flex;
  gap: 8px;
  padding: 10px 15px;
  background: #f5f5f5;
  border-bottom: 1px solid #ddd;
}

.receipt-search-input {
  flex: 1;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
}

.receipt-search-input:focus {
  outline: none;
  border-color: #a080a8;
}

.receipt-year-select {
  width: 80px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  background: white;
}

.receipt-year-select:focus {
  outline: none;
  border-color: #a080a8;
}

.load-all-btn {
  height: 32px;
  padding: 0 12px;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
}

.load-all-btn:hover {
  background: #5a6268;
}

.search-highlight {
  background-color: #fff3cd;
  padding: 0 2px;
  border-radius: 2px;
}

.receipts-list {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.receipts-list::-webkit-scrollbar {
  display: none;
}

.receipt-card {
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: box-shadow 0.2s;
}

.receipt-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.receipt-card.editing {
  cursor: default;
  border-color: #a080a8;
  box-shadow: 0 0 0 2px rgba(160, 128, 168, 0.3);
}

.receipt-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.receipt-supplier {
  font-weight: bold;
  color: #333;
}

.receipt-amount {
  font-weight: bold;
  color: #28a745;
  font-size: 1.1em;
}

.receipt-card-body {
  margin-bottom: 8px;
}

.receipt-description {
  color: #555;
  font-size: 0.9em;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.receipt-address {
  color: #888;
  font-size: 0.85em;
  padding-top: 6px;
}

.receipt-card-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.8em;
  color: #888;
  border-top: 1px solid #eee;
  padding-top: 6px;
}

.receipt-initials {
  background: #a080a8;
  color: white;
  padding: 2px 6px;
  border-radius: 3px;
  font-weight: bold;
  font-size: 0.85em;
}

.receipt-date {
  flex: 1;
}

.delete-receipt-btn {
  background: transparent;
  border: none;
  color: #aaa;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
}

.delete-receipt-btn:hover {
  background: #aaa;
  color: white;
}

/* Receipt Edit Form */
.receipt-edit-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.receipt-form-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.receipt-form-group label {
  font-size: 0.8em;
  font-weight: bold;
  color: #666;
}

.receipt-form-group input,
.receipt-form-group select {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
}

.receipt-form-group textarea {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 12px;
  resize: vertical;
  font-family: inherit;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
}

.receipt-form-group textarea::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.receipt-form-group input:focus,
.receipt-form-group select:focus,
.receipt-form-group textarea:focus {
  outline: none;
  border-color: #a080a8;
}

.receipt-edit-actions {
  display: flex;
  gap: 10px;
  margin-top: 5px;
}

.receipt-edit-actions .btn {
  flex: 1;
  padding: 8px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.receipt-edit-actions .btn-primary {
  background: #a080a8;
  color: white;
}

.receipt-edit-actions .btn-secondary {
  background: #6c757d;
  color: white;
}

.no-receipts {
  text-align: center;
  color: #888;
  padding: 30px;
  font-style: italic;
}

/* List View Styles */
.list-view-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: stretch;
  justify-content: center;
}

.list-view-print-loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  z-index: 1001;
}

.list-view-print-loading-spinner {
  text-align: center;
  color: #6e5875;
  padding: 24px 32px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
}

.list-view-print-loading-spinner i {
  font-size: 2.5em;
  margin-bottom: 12px;
}

.list-view-print-loading-spinner .loading-text {
  font-size: 1em;
  font-weight: 500;
}

.list-view-modal {
  background: white;
  border-radius: 0;
  width: 100%;
  max-width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.list-view-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
  background-color: #6e5875;
  color: white;
  border-radius: 12px 12px 0 0;
}

.list-view-header-actions {
  display: flex;
  gap: 6px;
  align-items: center;
}

.list-view-header .menu-btn {
  background: #907793;
  color: white;
  border: 1px solid #d9d5d7;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.list-view-header .menu-btn:hover {
  background: #7a6a7d;
}

.list-view-search {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  padding: 6px 12px;
  gap: 4px;
  border-bottom: 1px solid #eee;
}

.list-view-search-input {
  flex: 1 1 auto;
  min-width: 0;
  width: auto;
  height: 28px;
  border-radius: 6px;
  border: 1px solid #ddd;
  padding: 0 8px;
  font-size: 13px;
  outline: none;
}

.list-view-search-input:focus {
  border-color: #a080a8;
}

.list-view-search-clear {
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 4px;
  font-size: 16px;
}

.list-view-search-clear:hover {
  color: #666;
}

.list-view-search-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.list-view-group-btn {
  background: #ddd;
  border: none;
  border-radius: 6px;
  height: 28px;
  padding: 0 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  color: #666;
  font-size: 12px;
  flex-shrink: 0;
  white-space: nowrap;
}

.list-view-group-btn.active {
  background: #a080a8;
  color: white;
}

.list-view-btn-label {
  font-size: 10px;
  line-height: 1;
  font-weight: 500;
}

.list-view-building-header {
  cursor: pointer;
}

.list-view-building-header:hover {
  background: #8a7390;
}

.list-view-header h3 {
  margin: 0;
  font-size: 1.3rem;
  color: white;
}

.list-view-container {
  flex: 1;
  overflow-y: auto;
  padding: 8px 16px;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.list-view-container::-webkit-scrollbar {
  display: none;
}

.list-view-empty {
  text-align: center;
  color: #888;
  padding: 30px;
  font-style: italic;
}

.list-view-date-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  font-size: 0.9rem;
  color: #fff;
  padding: 10px 10px 6px 8px;
  border-bottom: 1px solid #e0d8e3;
  margin-bottom: 0;
  background: #9f8ba5;
  border-radius: 4px 4px 0 0;
}

.list-view-date-count {
  font-weight: 400;
  color: #fff;
  font-size: 0.8rem;
}

.list-view-card {
  padding: 8px 10px;
  margin-bottom: 4px;
  border-radius: 6px;
  background: #e4e4e4;
  border: 1px solid #eee;
  cursor: pointer;
}

.list-view-date-header + .list-view-card {
  border-radius: 0 0 6px 6px;
}

.list-view-card:hover {
  background: #efe8f2;
  border-color: #d0c0d5;
}

.list-view-card:active {
  background: #e0d0e6;
}

.list-view-card-address {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 0.85rem;
  color: #333;
}

.list-view-card-address-text {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.list-view-entry-icon {
  margin-left: auto;
  flex-shrink: 0;
  font-size: 0.75rem;
  margin-left: 6px;
}
.list-view-entry-icon.entry-allowed {
  color: #4caf50;
}
.list-view-entry-icon.entry-denied {
  color: #e53935;
}
.list-view-entry-icon.list-view-scheduled {
  color: #4caf50;
}

.list-view-card-desc {
  font-size: 0.8rem;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
}

.list-view-card-date {
  font-size: 0.7rem;
  color: #999;
  margin-top: 3px;
  text-align: left;
}

.list-view-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  border-top: 1px solid #eee;
  background: #f9f9f9;
  border-radius: 0 0 12px 12px;
}

.list-view-footer .menu-btn {
  background: #a080a8;
  color: white;
  border: none;
  border-radius: 20px;
  padding: 6px 20px;
  font-size: 0.85rem;
  cursor: pointer;
}

.list-view-footer .menu-btn:hover {
  background: #8a6b91;
}

.list-view-count {
  font-size: 0.85rem;
  color: #888;
}

/* Calendar icon on tile */
.tile-icon.calendar-linked {
  background-color: #4caf50;
  color: #fff;
}

.tile-icon.has-content {
  background-color: #4caf50;
  color: #fff;
}

.tile-icon.calendar-unlinked {
  background-color: #eee;
  color: #ccc;
}

/* Schedule dialog */
.sched-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 40px;
  z-index: 9999;
}

.sched-dialog {
  background: #fff;
  border-radius: 12px;
  max-width: 420px;
  width: 92%;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
}

.sched-dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background: #6e5875;
  color: #fff;
  border-radius: 12px 12px 0 0;
}

.sched-dialog-header h3 {
  margin: 0;
  color: #fff;
  font-size: 1.1em;
}

.sched-close {
  cursor: pointer;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.2em;
  padding: 4px;
}

.sched-dialog-body {
  padding: 20px;
}

.sched-stage {
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.sched-instruction {
  color: #555;
  margin: 0 0 16px;
  font-size: 0.95em;
}

.sched-info-box {
  background: #f3eef6;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  color: #555;
  font-size: 0.95em;
}

.sched-choice-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sched-choice-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 20px 16px;
  min-height: 110px;
  background: #fff;
  border: 2px solid #d4c8db;
  border-radius: 10px;
  cursor: pointer;
  transition:
    border-color 0.15s,
    transform 0.1s;
}

.sched-choice-btn:active {
  transform: scale(0.97);
}

.sched-choice-btn:hover {
  border-color: #6e5875;
}

.sched-choice-btn i {
  font-size: 1.6em;
  color: #6e5875;
}

.sched-choice-btn span {
  font-size: 1.05em;
  font-weight: 600;
  color: #333;
}

.sched-choice-btn small {
  font-size: 0.82em;
  color: #888;
}

.sched-fee-line {
  background: #f3eef6;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  color: #555;
  font-size: 0.95em;
}

.sched-fee-input {
  width: 60px;
  padding: 4px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.95em;
  margin-left: 4px;
}

.sched-loading {
  text-align: center;
  color: #888;
  padding: 24px;
}

.sched-day-gallery {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin-bottom: 16px;
}

.sched-day-card {
  background: #fff;
  border: 2px solid #d4c8db;
  border-radius: 10px;
  padding: 12px 4px;
  text-align: center;
  cursor: pointer;
  transition:
    border-color 0.15s,
    transform 0.1s;
}

.sched-day-card:active {
  transform: scale(0.96);
}

.sched-day-card:hover:not(.sched-day-unavailable) {
  border-color: #6e5875;
}

.sched-day-unavailable {
  opacity: 0.4;
  cursor: not-allowed;
}

.sched-day-name {
  font-size: 1.1em;
  font-weight: 700;
  color: #6e5875;
  margin-bottom: 4px;
}

.sched-day-date {
  font-size: 0.9em;
  color: #888;
}

.sched-time-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 50vh;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.sched-time-list::-webkit-scrollbar {
  display: none;
}

.sched-time-card {
  background: #fff;
  border: 2px solid #d4c8db;
  border-radius: 8px;
  padding: 14px 16px;
  font-size: 1em;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: border-color 0.15s;
}

.sched-time-card:hover:not(.sched-time-unavailable) {
  border-color: #6e5875;
}

.sched-time-selected {
  background: #f0eaf3;
  border-color: #6e5875;
}

.sched-selection-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f0eaf3;
  border-radius: 6px;
  margin-bottom: 8px;
  font-size: 0.9em;
  color: #6e5875;
}

.sched-clear-btn {
  background: none;
  border: 1px solid #6e5875;
  color: #6e5875;
  border-radius: 4px;
  padding: 2px 10px;
  cursor: pointer;
  font-size: 0.85em;
}

.sched-time-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
  gap: 8px;
}

.sched-btn-confirm-time {
  background: #6e5875;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 0.95em;
}

.sched-time-unavailable {
  background: #f0f0f0;
  color: #aaa;
  cursor: not-allowed;
  border-color: #e0e0e0;
}

.sched-time-badge {
  font-size: 0.8em;
  color: #c97878;
}

.sched-time-badge-conflict {
  color: #b08820;
}

.sched-time-conflict {
  background: #fff8e8;
  color: #333;
  border-color: #e0c860;
  cursor: pointer;
}

.sched-day-nav {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 16px;
}

.sched-btn-back {
  display: inline-block;
  padding: 8px 16px;
  background: none;
  border: 1px solid #c4b5ce;
  border-radius: 6px;
  color: #6e5875;
  cursor: pointer;
  font-size: 0.9em;
}

.sched-confirm-popup {
  background: #fff;
  padding: 28px;
  border-radius: 12px;
  max-width: 360px;
  width: 90%;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
}

.sched-confirm-popup h3 {
  margin-top: 0;
  color: #6e5875;
}

.sched-confirm-popup p {
  margin: 8px 0;
  color: #555;
}

.sched-confirm-buttons {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}

.sched-btn-cancel {
  flex: 1;
  padding: 12px;
  background: #eee;
  border: none;
  border-radius: 6px;
  color: #666;
  cursor: pointer;
  font-size: 1em;
}

.sched-btn-confirm {
  flex: 1;
  padding: 12px;
  background: #6e5875;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1em;
  font-weight: 600;
}

.sched-btn-confirm:disabled {
  background: #bbb;
}

.sched-error {
  color: #d32f2f;
  margin: 12px 0;
  font-size: 0.9em;
  text-align: center;
}
</style>
